/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> =
    | T
    | { [P in keyof T]?: P extends " $fragmentName" | "__typename" ? T[P] : never };
import type * as Types from "./graphql-types";

import {
    AccessControlId,
    AccessControlEntityId,
    ActivityId,
    ActivityParentEntityId,
    ActivitySourceId,
    AnswerEntityId,
    AreaCode,
    AudienceId,
    AuditId,
    BillableEntityId,
    CalendarDateSerialized,
    CalendarEventId,
    CalendarEventParentId,
    PlacementId,
    ClientTransferRequestId,
    CommunityId,
    CommunityLicenseId,
    CommunityMasterId,
    CommunityMasterLicenseTypeId,
    ContactId,
    ConversationThreadId,
    CurrencyInCents,
    CustomListId,
    CustomListItemId,
    CustomListCategoryId,
    DisclosureAcknowledgementId,
    DisclosureId,
    EmailEntityId,
    EmailTemplateId,
    EntitySummaryId,
    EvergreenCommunityId,
    EvergreenCommunityListingId,
    EvergreenLicenseTypeId,
    EvergreenReviewId,
    FileEntityId,
    FileId,
    GooglePlaceId,
    HomeCareLeadId,
    HostedFormId,
    HostedFormEntityId,
    HostedFormResponseId,
    HostedFormSlugId,
    HostedFormSessionId,
    HostedFormTokenId,
    IdentityId,
    ImportId,
    InternalId,
    InvoiceId,
    InvoiceLineItemId,
    InvoicePaymentScheduleItemId,
    IsoTimestamp,
    LayoutContainerId,
    LayoutItemId,
    LayoutSectionId,
    LayoutSectionSettingId,
    LicenseId,
    NoteId,
    NoteEntityId,
    OrganizationId,
    PhoneNumberE164,
    PhotoId,
    PipelineId,
    PipelineStageId,
    PromptId,
    PublicRecordId,
    PublicRecordContactId,
    QuestionId,
    ReferenceBusinessId,
    ReferenceContactId,
    ReferralId,
    RegionId,
    RegionLicenseMapId,
    ReportExportId,
    SavedQueryId,
    SentMessageId,
    SignatureRequestFieldId,
    SignatureRequestId,
    SignatureRequestRecipientId,
    SignatureTemplateId,
    SignatureTemplateFieldId,
    SignatureTemplateRecipientId,
    SimpleSearchEntityId,
    SmsMessageId,
    SmtpCredentialId,
    TaskId,
    TaskTypeId,
    TaskParentId,
    TelecomContactId,
    TelecomPhoneLineId,
    TenantCredentialId,
    TenantId,
    TokenId,
    TourId,
    TwoFactorVerificationId,
    Url,
    UserId,
    UserGroupId,
    UserMailboxId,
    UserMailboxContactId,
    UserMailboxMessageId,
    UserMailboxMessageRecipientId,
    UserProfileId,
    UserTerritoryId,
    UUID,
    VoiceCallEventId,
    VoiceCallId,
    WorkflowExecutionId,
    WorkflowId,
    WorkflowTargetId,
} from "@sp-crm/core";
import { DocumentTypeDecoration } from "@graphql-typed-document-node/core";
import {
    useQuery,
    useInfiniteQuery,
    useMutation,
    UseQueryOptions,
    UseInfiniteQueryOptions,
    UseMutationOptions,
} from "react-query";
import { graphqlFetcher } from "../backend/fetcher";
export * from "./graphql-types";
export type GetActivityFeedQueryVariables = Exact<{
    entityId: ActivityParentEntityId;
}>;

export type GetActivityFeedQuery = {
    getActivityFeed: {
        activities: Array<{
            sourceType: Types.ActivitySourceType;
            activity: {
                id: ActivityId;
                activityType: Types.ActivityType;
                sourceId: ActivitySourceId;
                voiceCallId: VoiceCallId | null;
                createdAt: any;
                parentEntityId: ActivityParentEntityId;
                createdByEntity: { preferredName: string };
                parentReferenceContact: {
                    id: ReferenceContactId;
                    appLink: string;
                    contactEntity: { name: string | null } | null;
                } | null;
                parentReferenceBusiness: {
                    id: ReferenceBusinessId;
                    appLink: string;
                    businessContactEntity: { name: string | null } | null;
                } | null;
                parentCommunity: {
                    id: CommunityId;
                    name: string | null;
                    appLink: string;
                } | null;
                metadata: {
                    events: Array<{
                        type: Types.ActivityMetadataEventType;
                        entityFieldChange: {
                            field: string;
                            oldStringValue: string | null;
                            newStringValue: string | null;
                        } | null;
                        entityReassign: { from: UserId | null; to: UserId | null } | null;
                        entityCollaboratorAdded: { collaboratorId: UserId } | null;
                        entityCollaboratorRemoved: { collaboratorId: UserId } | null;
                        outgoingCall: { phoneNumber: string } | null;
                        smsSent: { body: string } | null;
                        touchpointLogged: { channel: string; note: string | null } | null;
                        touchpointSnoozed: {
                            scope: Types.TouchpointSnoozeScope;
                            snoozeUntil: string;
                            reason: string | null;
                        } | null;
                        visitScheduled: {
                            eventDate: string;
                            title: string | null;
                        } | null;
                        pipelineStageChange: {
                            pipelineName: string;
                            fromStageName: string | null;
                            toStageName: string | null;
                        } | null;
                        sentToPartner: { integration: string; url: string } | null;
                    }>;
                } | null;
            };
            note: { text: string; effectiveCreatedAt: any; publishedAt: any } | null;
            task: {
                id: TaskId;
                status: string;
                dueDate: string | null;
                dueDateTime: any;
                completedAt: any;
                type: string | null;
                text: string | null;
                taskType: { id: TaskTypeId; name: string; category: string } | null;
            } | null;
            file: { originalFilename: string | null } | null;
            message: {
                subject: string | null;
                intakeCommunity: { name: string | null } | null;
            } | null;
        }>;
    };
};

export type CreateSavedQueryMutationVariables = Exact<{
    savedQuery: Types.SavedQueryInput;
}>;

export type CreateSavedQueryMutation = { createSavedQuery: { id: SavedQueryId } };

export type UpdateSavedQueryMutationVariables = Exact<{
    savedQuery: Types.SavedQueryInput;
}>;

export type UpdateSavedQueryMutation = { updateSavedQuery: { id: SavedQueryId } };

export type DeleteSavedQueryMutationVariables = Exact<{
    savedQueryId: SavedQueryId;
}>;

export type DeleteSavedQueryMutation = { deleteSavedQuery: boolean };

export type UpdateQueryFavoriteStatusMutationVariables = Exact<{
    savedQueryId: SavedQueryId;
    isFavorite: boolean;
}>;

export type UpdateQueryFavoriteStatusMutation = { updateQueryFavoriteStatus: boolean };

export type UpsertSavedQueryScheduleMutationVariables = Exact<{
    schedule: Types.SavedQueryScheduleInput;
}>;

export type UpsertSavedQueryScheduleMutation = {
    upsertSavedQuerySchedule: {
        schedule: string;
        audience: {
            type: Types.UserAudienceType;
            specific: { userIds: Array<UserId>; groupIds: Array<UserGroupId> } | null;
        };
    };
};

export type DeleteSavedQueryScheduleMutationVariables = Exact<{
    savedQueryId: SavedQueryId;
}>;

export type DeleteSavedQueryScheduleMutation = { deleteSavedQuerySchedule: boolean };

export type SendSavedQueryEmailMutationVariables = Exact<{
    payload: Types.SavedQuerySendEmailInput;
}>;

export type SendSavedQueryEmailMutation = { sendSavedQueryEmail: boolean };

export type GetSavedQueryFoldersQueryVariables = Exact<{ [key: string]: never }>;

export type GetSavedQueryFoldersQuery = {
    getSavedQueries: {
        savedQueries: Array<{
            folderName: string | null;
            isMyQuery: boolean;
            isShared: boolean;
        }>;
    };
};

export type GetSavedQueriesQueryVariables = Exact<{ [key: string]: never }>;

export type GetSavedQueriesQuery = {
    getSavedQueries: {
        canSaveAdditional: boolean;
        savedQueries: Array<{
            id: SavedQueryId;
            folderName: string | null;
            name: string;
            isMyQuery: boolean;
            isShared: boolean;
            isFavorite: boolean;
            editable: boolean;
            entityType: Types.AdvancedSearchEntityType;
            createdBy: UserId;
            regionVisibility: {
                editable: boolean;
                regions: Array<{
                    mode: Types.EntityRegionModeOutput;
                    regionId: RegionId;
                }>;
            };
            warnings: Array<{
                location: Types.SavedQueryWarningLocation;
                message: string;
            }> | null;
            schedule: {
                schedule: string;
                emailDescription: string | null;
                audience: {
                    type: Types.UserAudienceType;
                    specific: {
                        userIds: Array<UserId>;
                        groupIds: Array<UserGroupId>;
                    } | null;
                };
            } | null;
            sort: {
                ascending: boolean;
                column: string | null;
                questionId: QuestionId | null;
            };
            select: Array<{ fieldName: string | null; questionId: QuestionId | null }>;
            summaries: Array<{
                groupBy: Array<string> | null;
                aggregates: Array<{
                    operator: Types.AdvancedSearchAggregateOperator;
                    column: string;
                }>;
                sort: { aggregateIndex: number; ascending: boolean } | null;
            }> | null;
            condition: {
                nodeType: Types.AdvancedSearchConditionNodeType;
                fieldName: string | null;
                operator: Types.FieldConditionOperator | null;
                textValue: string | null;
                dateValue: any;
                calendarDateValue: string | null;
                numberValue: number | null;
                questionId: QuestionId | null;
                promptId: PromptId | null;
                children: Array<{
                    nodeType: Types.AdvancedSearchConditionNodeType;
                    fieldName: string | null;
                    operator: Types.FieldConditionOperator | null;
                    textValue: string | null;
                    dateValue: any;
                    calendarDateValue: string | null;
                    numberValue: number | null;
                    questionId: QuestionId | null;
                    promptId: PromptId | null;
                    children: Array<{
                        nodeType: Types.AdvancedSearchConditionNodeType;
                        fieldName: string | null;
                        operator: Types.FieldConditionOperator | null;
                        textValue: string | null;
                        dateValue: any;
                        calendarDateValue: string | null;
                        numberValue: number | null;
                        questionId: QuestionId | null;
                        promptId: PromptId | null;
                        children: Array<{
                            nodeType: Types.AdvancedSearchConditionNodeType;
                            fieldName: string | null;
                            operator: Types.FieldConditionOperator | null;
                            textValue: string | null;
                            dateValue: any;
                            calendarDateValue: string | null;
                            numberValue: number | null;
                            questionId: QuestionId | null;
                            promptId: PromptId | null;
                            children: Array<{
                                nodeType: Types.AdvancedSearchConditionNodeType;
                                fieldName: string | null;
                                operator: Types.FieldConditionOperator | null;
                                textValue: string | null;
                                dateValue: any;
                                calendarDateValue: string | null;
                                numberValue: number | null;
                                questionId: QuestionId | null;
                                promptId: PromptId | null;
                                children: Array<{
                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                    fieldName: string | null;
                                    operator: Types.FieldConditionOperator | null;
                                    textValue: string | null;
                                    dateValue: any;
                                    calendarDateValue: string | null;
                                    numberValue: number | null;
                                    questionId: QuestionId | null;
                                    promptId: PromptId | null;
                                    children: Array<{
                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                        fieldName: string | null;
                                        operator: Types.FieldConditionOperator | null;
                                        textValue: string | null;
                                        dateValue: any;
                                        calendarDateValue: string | null;
                                        numberValue: number | null;
                                        questionId: QuestionId | null;
                                        promptId: PromptId | null;
                                        children: Array<{
                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                            fieldName: string | null;
                                            operator: Types.FieldConditionOperator | null;
                                            textValue: string | null;
                                            dateValue: any;
                                            calendarDateValue: string | null;
                                            numberValue: number | null;
                                            questionId: QuestionId | null;
                                            promptId: PromptId | null;
                                            children: Array<{
                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                fieldName: string | null;
                                                operator: Types.FieldConditionOperator | null;
                                                textValue: string | null;
                                                dateValue: any;
                                                calendarDateValue: string | null;
                                                numberValue: number | null;
                                                questionId: QuestionId | null;
                                                promptId: PromptId | null;
                                                children: Array<{
                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                    fieldName: string | null;
                                                    operator: Types.FieldConditionOperator | null;
                                                    textValue: string | null;
                                                    dateValue: any;
                                                    calendarDateValue: string | null;
                                                    numberValue: number | null;
                                                    questionId: QuestionId | null;
                                                    promptId: PromptId | null;
                                                    children: Array<{
                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                        fieldName: string | null;
                                                        operator: Types.FieldConditionOperator | null;
                                                        textValue: string | null;
                                                        dateValue: any;
                                                        calendarDateValue: string | null;
                                                        numberValue: number | null;
                                                        questionId: QuestionId | null;
                                                        promptId: PromptId | null;
                                                        children: Array<{
                                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                                            fieldName: string | null;
                                                            operator: Types.FieldConditionOperator | null;
                                                            textValue: string | null;
                                                            dateValue: any;
                                                            calendarDateValue:
                                                                | string
                                                                | null;
                                                            numberValue: number | null;
                                                            questionId: QuestionId | null;
                                                            promptId: PromptId | null;
                                                            children: Array<{
                                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                                fieldName: string | null;
                                                                operator: Types.FieldConditionOperator | null;
                                                                textValue: string | null;
                                                                dateValue: any;
                                                                calendarDateValue:
                                                                    | string
                                                                    | null;
                                                                numberValue:
                                                                    | number
                                                                    | null;
                                                                questionId: QuestionId | null;
                                                                promptId: PromptId | null;
                                                                children: Array<{
                                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                                    fieldName:
                                                                        | string
                                                                        | null;
                                                                    operator: Types.FieldConditionOperator | null;
                                                                    textValue:
                                                                        | string
                                                                        | null;
                                                                    dateValue: any;
                                                                    calendarDateValue:
                                                                        | string
                                                                        | null;
                                                                    numberValue:
                                                                        | number
                                                                        | null;
                                                                    questionId: QuestionId | null;
                                                                    promptId: PromptId | null;
                                                                    children: Array<{
                                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                                        fieldName:
                                                                            | string
                                                                            | null;
                                                                        operator: Types.FieldConditionOperator | null;
                                                                        textValue:
                                                                            | string
                                                                            | null;
                                                                        dateValue: any;
                                                                        calendarDateValue:
                                                                            | string
                                                                            | null;
                                                                        numberValue:
                                                                            | number
                                                                            | null;
                                                                        questionId: QuestionId | null;
                                                                        promptId: PromptId | null;
                                                                        children: Array<{
                                                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                                                            fieldName:
                                                                                | string
                                                                                | null;
                                                                            operator: Types.FieldConditionOperator | null;
                                                                            textValue:
                                                                                | string
                                                                                | null;
                                                                            dateValue: any;
                                                                            calendarDateValue:
                                                                                | string
                                                                                | null;
                                                                            numberValue:
                                                                                | number
                                                                                | null;
                                                                            questionId: QuestionId | null;
                                                                            promptId: PromptId | null;
                                                                            relativeDate: {
                                                                                value: number;
                                                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                                                            } | null;
                                                                        }> | null;
                                                                        relativeDate: {
                                                                            value: number;
                                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                                        } | null;
                                                                    }> | null;
                                                                    relativeDate: {
                                                                        value: number;
                                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                                    } | null;
                                                                }> | null;
                                                                relativeDate: {
                                                                    value: number;
                                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                                } | null;
                                                            }> | null;
                                                            relativeDate: {
                                                                value: number;
                                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                                            } | null;
                                                        }> | null;
                                                        relativeDate: {
                                                            value: number;
                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                        } | null;
                                                    }> | null;
                                                    relativeDate: {
                                                        value: number;
                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                    } | null;
                                                }> | null;
                                                relativeDate: {
                                                    value: number;
                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                } | null;
                                            }> | null;
                                            relativeDate: {
                                                value: number;
                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                            } | null;
                                        }> | null;
                                        relativeDate: {
                                            value: number;
                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                        } | null;
                                    }> | null;
                                    relativeDate: {
                                        value: number;
                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                    } | null;
                                }> | null;
                                relativeDate: {
                                    value: number;
                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                } | null;
                            }> | null;
                            relativeDate: {
                                value: number;
                                interval: Types.AdvancedSearchRelativeDateInterval;
                            } | null;
                        }> | null;
                        relativeDate: {
                            value: number;
                            interval: Types.AdvancedSearchRelativeDateInterval;
                        } | null;
                    }> | null;
                    relativeDate: {
                        value: number;
                        interval: Types.AdvancedSearchRelativeDateInterval;
                    } | null;
                }> | null;
                relativeDate: {
                    value: number;
                    interval: Types.AdvancedSearchRelativeDateInterval;
                } | null;
            };
        }>;
    };
};

export type ConditionFragmentFragment = {
    nodeType: Types.AdvancedSearchConditionNodeType;
    fieldName: string | null;
    operator: Types.FieldConditionOperator | null;
    textValue: string | null;
    dateValue: any;
    calendarDateValue: string | null;
    numberValue: number | null;
    questionId: QuestionId | null;
    promptId: PromptId | null;
    relativeDate: {
        value: number;
        interval: Types.AdvancedSearchRelativeDateInterval;
    } | null;
};

export type SelectFragmentFragment = {
    fieldName: string | null;
    questionId: QuestionId | null;
};

export type AudienceFragmentFragment = {
    type: Types.UserAudienceType;
    specific: { userIds: Array<UserId>; groupIds: Array<UserGroupId> } | null;
};

export type SummaryFragmentFragment = {
    groupBy: Array<string> | null;
    aggregates: Array<{
        operator: Types.AdvancedSearchAggregateOperator;
        column: string;
    }>;
    sort: { aggregateIndex: number; ascending: boolean } | null;
};

export type GetEmailProviderListsQueryVariables = Exact<{
    input: Types.GetEmailProviderListsInput;
}>;

export type GetEmailProviderListsQuery = {
    getEmailProviderLists: Array<{ id: string; name: string; memberCount: number }>;
};

export type TriggerAudienceSyncMutationVariables = Exact<{
    input: Types.TriggerAudienceSyncGraphQlInput;
}>;

export type TriggerAudienceSyncMutation = {
    triggerAudienceSync: {
        historyId: string;
        status: string;
        queuedAt: any;
        startedAt: any;
        completedAt: any;
        errorMessage: string | null;
        membersAdded: number;
        membersUpdated: number;
        membersRemoved: number;
        totalMembers: number;
        durationMs: number | null;
    };
};

export type RecentAudienceSyncsQueryVariables = Exact<{
    input: Types.RecentAudienceSyncsInput;
}>;

export type RecentAudienceSyncsQuery = {
    recentAudienceSyncs: Array<{
        historyId: string;
        status: string;
        queuedAt: any;
        startedAt: any;
        completedAt: any;
        errorMessage: string | null;
        membersAdded: number;
        membersUpdated: number;
        membersRemoved: number;
        totalMembers: number;
        durationMs: number | null;
    }>;
};

export type CreateAudienceMutationVariables = Exact<{
    input: Types.CreateAudienceGraphQlInput;
}>;

export type CreateAudienceMutation = {
    createAudience: {
        id: AudienceId;
        name: string;
        createdBy: UserId;
        updatedBy: UserId;
        dateAdded: any;
        updatedAt: any;
        mailchimp: {
            tag: string | null;
            latestSyncResult: {
                historyId: string;
                status: string;
                queuedAt: any;
                startedAt: any;
                completedAt: any;
                errorMessage: string | null;
                membersAdded: number;
                membersUpdated: number;
                membersRemoved: number;
                totalMembers: number;
                durationMs: number | null;
            } | null;
        };
        constantContact: {
            tag: string | null;
            latestSyncResult: {
                historyId: string;
                status: string;
                queuedAt: any;
                startedAt: any;
                completedAt: any;
                errorMessage: string | null;
                membersAdded: number;
                membersUpdated: number;
                membersRemoved: number;
                totalMembers: number;
                durationMs: number | null;
            } | null;
        };
        query: {
            queries: Array<{
                entityType: Types.AdvancedSearchEntityType;
                sort: {
                    ascending: boolean;
                    column: string | null;
                    questionId: QuestionId | null;
                };
                select: Array<{
                    fieldName: string | null;
                    questionId: QuestionId | null;
                }>;
                condition: {
                    nodeType: Types.AdvancedSearchConditionNodeType;
                    fieldName: string | null;
                    operator: Types.FieldConditionOperator | null;
                    textValue: string | null;
                    dateValue: any;
                    calendarDateValue: string | null;
                    numberValue: number | null;
                    questionId: QuestionId | null;
                    promptId: PromptId | null;
                    children: Array<{
                        nodeType: Types.AdvancedSearchConditionNodeType;
                        fieldName: string | null;
                        operator: Types.FieldConditionOperator | null;
                        textValue: string | null;
                        dateValue: any;
                        calendarDateValue: string | null;
                        numberValue: number | null;
                        questionId: QuestionId | null;
                        promptId: PromptId | null;
                        children: Array<{
                            nodeType: Types.AdvancedSearchConditionNodeType;
                            fieldName: string | null;
                            operator: Types.FieldConditionOperator | null;
                            textValue: string | null;
                            dateValue: any;
                            calendarDateValue: string | null;
                            numberValue: number | null;
                            questionId: QuestionId | null;
                            promptId: PromptId | null;
                            children: Array<{
                                nodeType: Types.AdvancedSearchConditionNodeType;
                                fieldName: string | null;
                                operator: Types.FieldConditionOperator | null;
                                textValue: string | null;
                                dateValue: any;
                                calendarDateValue: string | null;
                                numberValue: number | null;
                                questionId: QuestionId | null;
                                promptId: PromptId | null;
                                children: Array<{
                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                    fieldName: string | null;
                                    operator: Types.FieldConditionOperator | null;
                                    textValue: string | null;
                                    dateValue: any;
                                    calendarDateValue: string | null;
                                    numberValue: number | null;
                                    questionId: QuestionId | null;
                                    promptId: PromptId | null;
                                    children: Array<{
                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                        fieldName: string | null;
                                        operator: Types.FieldConditionOperator | null;
                                        textValue: string | null;
                                        dateValue: any;
                                        calendarDateValue: string | null;
                                        numberValue: number | null;
                                        questionId: QuestionId | null;
                                        promptId: PromptId | null;
                                        children: Array<{
                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                            fieldName: string | null;
                                            operator: Types.FieldConditionOperator | null;
                                            textValue: string | null;
                                            dateValue: any;
                                            calendarDateValue: string | null;
                                            numberValue: number | null;
                                            questionId: QuestionId | null;
                                            promptId: PromptId | null;
                                            children: Array<{
                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                fieldName: string | null;
                                                operator: Types.FieldConditionOperator | null;
                                                textValue: string | null;
                                                dateValue: any;
                                                calendarDateValue: string | null;
                                                numberValue: number | null;
                                                questionId: QuestionId | null;
                                                promptId: PromptId | null;
                                                children: Array<{
                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                    fieldName: string | null;
                                                    operator: Types.FieldConditionOperator | null;
                                                    textValue: string | null;
                                                    dateValue: any;
                                                    calendarDateValue: string | null;
                                                    numberValue: number | null;
                                                    questionId: QuestionId | null;
                                                    promptId: PromptId | null;
                                                    children: Array<{
                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                        fieldName: string | null;
                                                        operator: Types.FieldConditionOperator | null;
                                                        textValue: string | null;
                                                        dateValue: any;
                                                        calendarDateValue: string | null;
                                                        numberValue: number | null;
                                                        questionId: QuestionId | null;
                                                        promptId: PromptId | null;
                                                        children: Array<{
                                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                                            fieldName: string | null;
                                                            operator: Types.FieldConditionOperator | null;
                                                            textValue: string | null;
                                                            dateValue: any;
                                                            calendarDateValue:
                                                                | string
                                                                | null;
                                                            numberValue: number | null;
                                                            questionId: QuestionId | null;
                                                            promptId: PromptId | null;
                                                            relativeDate: {
                                                                value: number;
                                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                                            } | null;
                                                        }> | null;
                                                        relativeDate: {
                                                            value: number;
                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                        } | null;
                                                    }> | null;
                                                    relativeDate: {
                                                        value: number;
                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                    } | null;
                                                }> | null;
                                                relativeDate: {
                                                    value: number;
                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                } | null;
                                            }> | null;
                                            relativeDate: {
                                                value: number;
                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                            } | null;
                                        }> | null;
                                        relativeDate: {
                                            value: number;
                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                        } | null;
                                    }> | null;
                                    relativeDate: {
                                        value: number;
                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                    } | null;
                                }> | null;
                                relativeDate: {
                                    value: number;
                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                } | null;
                            }> | null;
                            relativeDate: {
                                value: number;
                                interval: Types.AdvancedSearchRelativeDateInterval;
                            } | null;
                        }> | null;
                        relativeDate: {
                            value: number;
                            interval: Types.AdvancedSearchRelativeDateInterval;
                        } | null;
                    }> | null;
                    relativeDate: {
                        value: number;
                        interval: Types.AdvancedSearchRelativeDateInterval;
                    } | null;
                };
            }>;
        };
    };
};

export type UpdateAudienceMutationVariables = Exact<{
    input: Types.UpdateAudienceGraphQlInput;
}>;

export type UpdateAudienceMutation = {
    updateAudience: {
        id: AudienceId;
        name: string;
        createdBy: UserId;
        updatedBy: UserId;
        dateAdded: any;
        updatedAt: any;
        mailchimp: {
            tag: string | null;
            latestSyncResult: {
                historyId: string;
                status: string;
                queuedAt: any;
                startedAt: any;
                completedAt: any;
                errorMessage: string | null;
                membersAdded: number;
                membersUpdated: number;
                membersRemoved: number;
                totalMembers: number;
                durationMs: number | null;
            } | null;
        };
        constantContact: {
            tag: string | null;
            latestSyncResult: {
                historyId: string;
                status: string;
                queuedAt: any;
                startedAt: any;
                completedAt: any;
                errorMessage: string | null;
                membersAdded: number;
                membersUpdated: number;
                membersRemoved: number;
                totalMembers: number;
                durationMs: number | null;
            } | null;
        };
        query: {
            queries: Array<{
                entityType: Types.AdvancedSearchEntityType;
                sort: {
                    ascending: boolean;
                    column: string | null;
                    questionId: QuestionId | null;
                };
                select: Array<{
                    fieldName: string | null;
                    questionId: QuestionId | null;
                }>;
                condition: {
                    nodeType: Types.AdvancedSearchConditionNodeType;
                    fieldName: string | null;
                    operator: Types.FieldConditionOperator | null;
                    textValue: string | null;
                    dateValue: any;
                    calendarDateValue: string | null;
                    numberValue: number | null;
                    questionId: QuestionId | null;
                    promptId: PromptId | null;
                    children: Array<{
                        nodeType: Types.AdvancedSearchConditionNodeType;
                        fieldName: string | null;
                        operator: Types.FieldConditionOperator | null;
                        textValue: string | null;
                        dateValue: any;
                        calendarDateValue: string | null;
                        numberValue: number | null;
                        questionId: QuestionId | null;
                        promptId: PromptId | null;
                        children: Array<{
                            nodeType: Types.AdvancedSearchConditionNodeType;
                            fieldName: string | null;
                            operator: Types.FieldConditionOperator | null;
                            textValue: string | null;
                            dateValue: any;
                            calendarDateValue: string | null;
                            numberValue: number | null;
                            questionId: QuestionId | null;
                            promptId: PromptId | null;
                            children: Array<{
                                nodeType: Types.AdvancedSearchConditionNodeType;
                                fieldName: string | null;
                                operator: Types.FieldConditionOperator | null;
                                textValue: string | null;
                                dateValue: any;
                                calendarDateValue: string | null;
                                numberValue: number | null;
                                questionId: QuestionId | null;
                                promptId: PromptId | null;
                                children: Array<{
                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                    fieldName: string | null;
                                    operator: Types.FieldConditionOperator | null;
                                    textValue: string | null;
                                    dateValue: any;
                                    calendarDateValue: string | null;
                                    numberValue: number | null;
                                    questionId: QuestionId | null;
                                    promptId: PromptId | null;
                                    children: Array<{
                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                        fieldName: string | null;
                                        operator: Types.FieldConditionOperator | null;
                                        textValue: string | null;
                                        dateValue: any;
                                        calendarDateValue: string | null;
                                        numberValue: number | null;
                                        questionId: QuestionId | null;
                                        promptId: PromptId | null;
                                        children: Array<{
                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                            fieldName: string | null;
                                            operator: Types.FieldConditionOperator | null;
                                            textValue: string | null;
                                            dateValue: any;
                                            calendarDateValue: string | null;
                                            numberValue: number | null;
                                            questionId: QuestionId | null;
                                            promptId: PromptId | null;
                                            children: Array<{
                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                fieldName: string | null;
                                                operator: Types.FieldConditionOperator | null;
                                                textValue: string | null;
                                                dateValue: any;
                                                calendarDateValue: string | null;
                                                numberValue: number | null;
                                                questionId: QuestionId | null;
                                                promptId: PromptId | null;
                                                children: Array<{
                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                    fieldName: string | null;
                                                    operator: Types.FieldConditionOperator | null;
                                                    textValue: string | null;
                                                    dateValue: any;
                                                    calendarDateValue: string | null;
                                                    numberValue: number | null;
                                                    questionId: QuestionId | null;
                                                    promptId: PromptId | null;
                                                    children: Array<{
                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                        fieldName: string | null;
                                                        operator: Types.FieldConditionOperator | null;
                                                        textValue: string | null;
                                                        dateValue: any;
                                                        calendarDateValue: string | null;
                                                        numberValue: number | null;
                                                        questionId: QuestionId | null;
                                                        promptId: PromptId | null;
                                                        children: Array<{
                                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                                            fieldName: string | null;
                                                            operator: Types.FieldConditionOperator | null;
                                                            textValue: string | null;
                                                            dateValue: any;
                                                            calendarDateValue:
                                                                | string
                                                                | null;
                                                            numberValue: number | null;
                                                            questionId: QuestionId | null;
                                                            promptId: PromptId | null;
                                                            relativeDate: {
                                                                value: number;
                                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                                            } | null;
                                                        }> | null;
                                                        relativeDate: {
                                                            value: number;
                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                        } | null;
                                                    }> | null;
                                                    relativeDate: {
                                                        value: number;
                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                    } | null;
                                                }> | null;
                                                relativeDate: {
                                                    value: number;
                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                } | null;
                                            }> | null;
                                            relativeDate: {
                                                value: number;
                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                            } | null;
                                        }> | null;
                                        relativeDate: {
                                            value: number;
                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                        } | null;
                                    }> | null;
                                    relativeDate: {
                                        value: number;
                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                    } | null;
                                }> | null;
                                relativeDate: {
                                    value: number;
                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                } | null;
                            }> | null;
                            relativeDate: {
                                value: number;
                                interval: Types.AdvancedSearchRelativeDateInterval;
                            } | null;
                        }> | null;
                        relativeDate: {
                            value: number;
                            interval: Types.AdvancedSearchRelativeDateInterval;
                        } | null;
                    }> | null;
                    relativeDate: {
                        value: number;
                        interval: Types.AdvancedSearchRelativeDateInterval;
                    } | null;
                };
            }>;
        };
    };
};

export type DeleteAudienceMutationVariables = Exact<{
    input: Types.DeleteAudienceGraphQlInput;
}>;

export type DeleteAudienceMutation = { deleteAudience: boolean };

export type GetAudienceQueryVariables = Exact<{
    input: Types.GetAudienceGraphQlInput;
}>;

export type GetAudienceQuery = {
    getAudience: {
        id: AudienceId;
        name: string;
        createdBy: UserId;
        updatedBy: UserId;
        dateAdded: any;
        updatedAt: any;
        mailchimp: {
            tag: string | null;
            latestSyncResult: {
                historyId: string;
                status: string;
                queuedAt: any;
                startedAt: any;
                completedAt: any;
                errorMessage: string | null;
                membersAdded: number;
                membersUpdated: number;
                membersRemoved: number;
                totalMembers: number;
                durationMs: number | null;
            } | null;
        };
        constantContact: {
            tag: string | null;
            latestSyncResult: {
                historyId: string;
                status: string;
                queuedAt: any;
                startedAt: any;
                completedAt: any;
                errorMessage: string | null;
                membersAdded: number;
                membersUpdated: number;
                membersRemoved: number;
                totalMembers: number;
                durationMs: number | null;
            } | null;
        };
        query: {
            queries: Array<{
                entityType: Types.AdvancedSearchEntityType;
                sort: {
                    ascending: boolean;
                    column: string | null;
                    questionId: QuestionId | null;
                };
                select: Array<{
                    fieldName: string | null;
                    questionId: QuestionId | null;
                }>;
                condition: {
                    nodeType: Types.AdvancedSearchConditionNodeType;
                    fieldName: string | null;
                    operator: Types.FieldConditionOperator | null;
                    textValue: string | null;
                    dateValue: any;
                    calendarDateValue: string | null;
                    numberValue: number | null;
                    questionId: QuestionId | null;
                    promptId: PromptId | null;
                    children: Array<{
                        nodeType: Types.AdvancedSearchConditionNodeType;
                        fieldName: string | null;
                        operator: Types.FieldConditionOperator | null;
                        textValue: string | null;
                        dateValue: any;
                        calendarDateValue: string | null;
                        numberValue: number | null;
                        questionId: QuestionId | null;
                        promptId: PromptId | null;
                        children: Array<{
                            nodeType: Types.AdvancedSearchConditionNodeType;
                            fieldName: string | null;
                            operator: Types.FieldConditionOperator | null;
                            textValue: string | null;
                            dateValue: any;
                            calendarDateValue: string | null;
                            numberValue: number | null;
                            questionId: QuestionId | null;
                            promptId: PromptId | null;
                            children: Array<{
                                nodeType: Types.AdvancedSearchConditionNodeType;
                                fieldName: string | null;
                                operator: Types.FieldConditionOperator | null;
                                textValue: string | null;
                                dateValue: any;
                                calendarDateValue: string | null;
                                numberValue: number | null;
                                questionId: QuestionId | null;
                                promptId: PromptId | null;
                                children: Array<{
                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                    fieldName: string | null;
                                    operator: Types.FieldConditionOperator | null;
                                    textValue: string | null;
                                    dateValue: any;
                                    calendarDateValue: string | null;
                                    numberValue: number | null;
                                    questionId: QuestionId | null;
                                    promptId: PromptId | null;
                                    children: Array<{
                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                        fieldName: string | null;
                                        operator: Types.FieldConditionOperator | null;
                                        textValue: string | null;
                                        dateValue: any;
                                        calendarDateValue: string | null;
                                        numberValue: number | null;
                                        questionId: QuestionId | null;
                                        promptId: PromptId | null;
                                        children: Array<{
                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                            fieldName: string | null;
                                            operator: Types.FieldConditionOperator | null;
                                            textValue: string | null;
                                            dateValue: any;
                                            calendarDateValue: string | null;
                                            numberValue: number | null;
                                            questionId: QuestionId | null;
                                            promptId: PromptId | null;
                                            children: Array<{
                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                fieldName: string | null;
                                                operator: Types.FieldConditionOperator | null;
                                                textValue: string | null;
                                                dateValue: any;
                                                calendarDateValue: string | null;
                                                numberValue: number | null;
                                                questionId: QuestionId | null;
                                                promptId: PromptId | null;
                                                children: Array<{
                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                    fieldName: string | null;
                                                    operator: Types.FieldConditionOperator | null;
                                                    textValue: string | null;
                                                    dateValue: any;
                                                    calendarDateValue: string | null;
                                                    numberValue: number | null;
                                                    questionId: QuestionId | null;
                                                    promptId: PromptId | null;
                                                    children: Array<{
                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                        fieldName: string | null;
                                                        operator: Types.FieldConditionOperator | null;
                                                        textValue: string | null;
                                                        dateValue: any;
                                                        calendarDateValue: string | null;
                                                        numberValue: number | null;
                                                        questionId: QuestionId | null;
                                                        promptId: PromptId | null;
                                                        children: Array<{
                                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                                            fieldName: string | null;
                                                            operator: Types.FieldConditionOperator | null;
                                                            textValue: string | null;
                                                            dateValue: any;
                                                            calendarDateValue:
                                                                | string
                                                                | null;
                                                            numberValue: number | null;
                                                            questionId: QuestionId | null;
                                                            promptId: PromptId | null;
                                                            relativeDate: {
                                                                value: number;
                                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                                            } | null;
                                                        }> | null;
                                                        relativeDate: {
                                                            value: number;
                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                        } | null;
                                                    }> | null;
                                                    relativeDate: {
                                                        value: number;
                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                    } | null;
                                                }> | null;
                                                relativeDate: {
                                                    value: number;
                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                } | null;
                                            }> | null;
                                            relativeDate: {
                                                value: number;
                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                            } | null;
                                        }> | null;
                                        relativeDate: {
                                            value: number;
                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                        } | null;
                                    }> | null;
                                    relativeDate: {
                                        value: number;
                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                    } | null;
                                }> | null;
                                relativeDate: {
                                    value: number;
                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                } | null;
                            }> | null;
                            relativeDate: {
                                value: number;
                                interval: Types.AdvancedSearchRelativeDateInterval;
                            } | null;
                        }> | null;
                        relativeDate: {
                            value: number;
                            interval: Types.AdvancedSearchRelativeDateInterval;
                        } | null;
                    }> | null;
                    relativeDate: {
                        value: number;
                        interval: Types.AdvancedSearchRelativeDateInterval;
                    } | null;
                };
            }>;
        };
    };
};

export type GetAudiencesQueryVariables = Exact<{
    input: Types.GetAudiencesGraphQlInput;
}>;

export type GetAudiencesQuery = {
    getAudiences: Array<{
        id: AudienceId;
        name: string;
        createdBy: UserId;
        updatedBy: UserId;
        dateAdded: any;
        updatedAt: any;
        mailchimp: {
            tag: string | null;
            latestSyncResult: {
                historyId: string;
                status: string;
                queuedAt: any;
                startedAt: any;
                completedAt: any;
                errorMessage: string | null;
                membersAdded: number;
                membersUpdated: number;
                membersRemoved: number;
                totalMembers: number;
                durationMs: number | null;
            } | null;
        };
        constantContact: {
            tag: string | null;
            latestSyncResult: {
                historyId: string;
                status: string;
                queuedAt: any;
                startedAt: any;
                completedAt: any;
                errorMessage: string | null;
                membersAdded: number;
                membersUpdated: number;
                membersRemoved: number;
                totalMembers: number;
                durationMs: number | null;
            } | null;
        };
        query: {
            queries: Array<{
                entityType: Types.AdvancedSearchEntityType;
                sort: {
                    ascending: boolean;
                    column: string | null;
                    questionId: QuestionId | null;
                };
                select: Array<{
                    fieldName: string | null;
                    questionId: QuestionId | null;
                }>;
                condition: {
                    nodeType: Types.AdvancedSearchConditionNodeType;
                    fieldName: string | null;
                    operator: Types.FieldConditionOperator | null;
                    textValue: string | null;
                    dateValue: any;
                    calendarDateValue: string | null;
                    numberValue: number | null;
                    questionId: QuestionId | null;
                    promptId: PromptId | null;
                    children: Array<{
                        nodeType: Types.AdvancedSearchConditionNodeType;
                        fieldName: string | null;
                        operator: Types.FieldConditionOperator | null;
                        textValue: string | null;
                        dateValue: any;
                        calendarDateValue: string | null;
                        numberValue: number | null;
                        questionId: QuestionId | null;
                        promptId: PromptId | null;
                        children: Array<{
                            nodeType: Types.AdvancedSearchConditionNodeType;
                            fieldName: string | null;
                            operator: Types.FieldConditionOperator | null;
                            textValue: string | null;
                            dateValue: any;
                            calendarDateValue: string | null;
                            numberValue: number | null;
                            questionId: QuestionId | null;
                            promptId: PromptId | null;
                            children: Array<{
                                nodeType: Types.AdvancedSearchConditionNodeType;
                                fieldName: string | null;
                                operator: Types.FieldConditionOperator | null;
                                textValue: string | null;
                                dateValue: any;
                                calendarDateValue: string | null;
                                numberValue: number | null;
                                questionId: QuestionId | null;
                                promptId: PromptId | null;
                                children: Array<{
                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                    fieldName: string | null;
                                    operator: Types.FieldConditionOperator | null;
                                    textValue: string | null;
                                    dateValue: any;
                                    calendarDateValue: string | null;
                                    numberValue: number | null;
                                    questionId: QuestionId | null;
                                    promptId: PromptId | null;
                                    children: Array<{
                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                        fieldName: string | null;
                                        operator: Types.FieldConditionOperator | null;
                                        textValue: string | null;
                                        dateValue: any;
                                        calendarDateValue: string | null;
                                        numberValue: number | null;
                                        questionId: QuestionId | null;
                                        promptId: PromptId | null;
                                        children: Array<{
                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                            fieldName: string | null;
                                            operator: Types.FieldConditionOperator | null;
                                            textValue: string | null;
                                            dateValue: any;
                                            calendarDateValue: string | null;
                                            numberValue: number | null;
                                            questionId: QuestionId | null;
                                            promptId: PromptId | null;
                                            children: Array<{
                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                fieldName: string | null;
                                                operator: Types.FieldConditionOperator | null;
                                                textValue: string | null;
                                                dateValue: any;
                                                calendarDateValue: string | null;
                                                numberValue: number | null;
                                                questionId: QuestionId | null;
                                                promptId: PromptId | null;
                                                children: Array<{
                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                    fieldName: string | null;
                                                    operator: Types.FieldConditionOperator | null;
                                                    textValue: string | null;
                                                    dateValue: any;
                                                    calendarDateValue: string | null;
                                                    numberValue: number | null;
                                                    questionId: QuestionId | null;
                                                    promptId: PromptId | null;
                                                    children: Array<{
                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                        fieldName: string | null;
                                                        operator: Types.FieldConditionOperator | null;
                                                        textValue: string | null;
                                                        dateValue: any;
                                                        calendarDateValue: string | null;
                                                        numberValue: number | null;
                                                        questionId: QuestionId | null;
                                                        promptId: PromptId | null;
                                                        children: Array<{
                                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                                            fieldName: string | null;
                                                            operator: Types.FieldConditionOperator | null;
                                                            textValue: string | null;
                                                            dateValue: any;
                                                            calendarDateValue:
                                                                | string
                                                                | null;
                                                            numberValue: number | null;
                                                            questionId: QuestionId | null;
                                                            promptId: PromptId | null;
                                                            relativeDate: {
                                                                value: number;
                                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                                            } | null;
                                                        }> | null;
                                                        relativeDate: {
                                                            value: number;
                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                        } | null;
                                                    }> | null;
                                                    relativeDate: {
                                                        value: number;
                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                    } | null;
                                                }> | null;
                                                relativeDate: {
                                                    value: number;
                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                } | null;
                                            }> | null;
                                            relativeDate: {
                                                value: number;
                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                            } | null;
                                        }> | null;
                                        relativeDate: {
                                            value: number;
                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                        } | null;
                                    }> | null;
                                    relativeDate: {
                                        value: number;
                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                    } | null;
                                }> | null;
                                relativeDate: {
                                    value: number;
                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                } | null;
                            }> | null;
                            relativeDate: {
                                value: number;
                                interval: Types.AdvancedSearchRelativeDateInterval;
                            } | null;
                        }> | null;
                        relativeDate: {
                            value: number;
                            interval: Types.AdvancedSearchRelativeDateInterval;
                        } | null;
                    }> | null;
                    relativeDate: {
                        value: number;
                        interval: Types.AdvancedSearchRelativeDateInterval;
                    } | null;
                };
            }>;
        };
    }>;
};

export type GetAudienceRecipientsQueryVariables = Exact<{
    input: Types.GetAudienceRecipientsGraphQlInput;
}>;

export type GetAudienceRecipientsQuery = {
    getAudienceRecipients: {
        total: number;
        recipients: Array<{
            entityId: string;
            entityType: Types.AdvancedSearchEntityType;
            name: string;
            firstName: string;
            email: string;
        }>;
    };
};

export type AudienceBasicsFragment = {
    id: AudienceId;
    name: string;
    createdBy: UserId;
    updatedBy: UserId;
    dateAdded: any;
    updatedAt: any;
    mailchimp: {
        tag: string | null;
        latestSyncResult: {
            historyId: string;
            status: string;
            queuedAt: any;
            startedAt: any;
            completedAt: any;
            errorMessage: string | null;
            membersAdded: number;
            membersUpdated: number;
            membersRemoved: number;
            totalMembers: number;
            durationMs: number | null;
        } | null;
    };
    constantContact: {
        tag: string | null;
        latestSyncResult: {
            historyId: string;
            status: string;
            queuedAt: any;
            startedAt: any;
            completedAt: any;
            errorMessage: string | null;
            membersAdded: number;
            membersUpdated: number;
            membersRemoved: number;
            totalMembers: number;
            durationMs: number | null;
        } | null;
    };
    query: {
        queries: Array<{
            entityType: Types.AdvancedSearchEntityType;
            sort: {
                ascending: boolean;
                column: string | null;
                questionId: QuestionId | null;
            };
            select: Array<{ fieldName: string | null; questionId: QuestionId | null }>;
            condition: {
                nodeType: Types.AdvancedSearchConditionNodeType;
                fieldName: string | null;
                operator: Types.FieldConditionOperator | null;
                textValue: string | null;
                dateValue: any;
                calendarDateValue: string | null;
                numberValue: number | null;
                questionId: QuestionId | null;
                promptId: PromptId | null;
                children: Array<{
                    nodeType: Types.AdvancedSearchConditionNodeType;
                    fieldName: string | null;
                    operator: Types.FieldConditionOperator | null;
                    textValue: string | null;
                    dateValue: any;
                    calendarDateValue: string | null;
                    numberValue: number | null;
                    questionId: QuestionId | null;
                    promptId: PromptId | null;
                    children: Array<{
                        nodeType: Types.AdvancedSearchConditionNodeType;
                        fieldName: string | null;
                        operator: Types.FieldConditionOperator | null;
                        textValue: string | null;
                        dateValue: any;
                        calendarDateValue: string | null;
                        numberValue: number | null;
                        questionId: QuestionId | null;
                        promptId: PromptId | null;
                        children: Array<{
                            nodeType: Types.AdvancedSearchConditionNodeType;
                            fieldName: string | null;
                            operator: Types.FieldConditionOperator | null;
                            textValue: string | null;
                            dateValue: any;
                            calendarDateValue: string | null;
                            numberValue: number | null;
                            questionId: QuestionId | null;
                            promptId: PromptId | null;
                            children: Array<{
                                nodeType: Types.AdvancedSearchConditionNodeType;
                                fieldName: string | null;
                                operator: Types.FieldConditionOperator | null;
                                textValue: string | null;
                                dateValue: any;
                                calendarDateValue: string | null;
                                numberValue: number | null;
                                questionId: QuestionId | null;
                                promptId: PromptId | null;
                                children: Array<{
                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                    fieldName: string | null;
                                    operator: Types.FieldConditionOperator | null;
                                    textValue: string | null;
                                    dateValue: any;
                                    calendarDateValue: string | null;
                                    numberValue: number | null;
                                    questionId: QuestionId | null;
                                    promptId: PromptId | null;
                                    children: Array<{
                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                        fieldName: string | null;
                                        operator: Types.FieldConditionOperator | null;
                                        textValue: string | null;
                                        dateValue: any;
                                        calendarDateValue: string | null;
                                        numberValue: number | null;
                                        questionId: QuestionId | null;
                                        promptId: PromptId | null;
                                        children: Array<{
                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                            fieldName: string | null;
                                            operator: Types.FieldConditionOperator | null;
                                            textValue: string | null;
                                            dateValue: any;
                                            calendarDateValue: string | null;
                                            numberValue: number | null;
                                            questionId: QuestionId | null;
                                            promptId: PromptId | null;
                                            children: Array<{
                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                fieldName: string | null;
                                                operator: Types.FieldConditionOperator | null;
                                                textValue: string | null;
                                                dateValue: any;
                                                calendarDateValue: string | null;
                                                numberValue: number | null;
                                                questionId: QuestionId | null;
                                                promptId: PromptId | null;
                                                children: Array<{
                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                    fieldName: string | null;
                                                    operator: Types.FieldConditionOperator | null;
                                                    textValue: string | null;
                                                    dateValue: any;
                                                    calendarDateValue: string | null;
                                                    numberValue: number | null;
                                                    questionId: QuestionId | null;
                                                    promptId: PromptId | null;
                                                    children: Array<{
                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                        fieldName: string | null;
                                                        operator: Types.FieldConditionOperator | null;
                                                        textValue: string | null;
                                                        dateValue: any;
                                                        calendarDateValue: string | null;
                                                        numberValue: number | null;
                                                        questionId: QuestionId | null;
                                                        promptId: PromptId | null;
                                                        relativeDate: {
                                                            value: number;
                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                        } | null;
                                                    }> | null;
                                                    relativeDate: {
                                                        value: number;
                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                    } | null;
                                                }> | null;
                                                relativeDate: {
                                                    value: number;
                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                } | null;
                                            }> | null;
                                            relativeDate: {
                                                value: number;
                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                            } | null;
                                        }> | null;
                                        relativeDate: {
                                            value: number;
                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                        } | null;
                                    }> | null;
                                    relativeDate: {
                                        value: number;
                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                    } | null;
                                }> | null;
                                relativeDate: {
                                    value: number;
                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                } | null;
                            }> | null;
                            relativeDate: {
                                value: number;
                                interval: Types.AdvancedSearchRelativeDateInterval;
                            } | null;
                        }> | null;
                        relativeDate: {
                            value: number;
                            interval: Types.AdvancedSearchRelativeDateInterval;
                        } | null;
                    }> | null;
                    relativeDate: {
                        value: number;
                        interval: Types.AdvancedSearchRelativeDateInterval;
                    } | null;
                }> | null;
                relativeDate: {
                    value: number;
                    interval: Types.AdvancedSearchRelativeDateInterval;
                } | null;
            };
        }>;
    };
};

export type UpdateBillableEntityInternalAttributesMutationVariables = Exact<{
    billableEntityId: BillableEntityId;
    internalAttributes?: Types.DictionaryInput | null | undefined;
    dates?: Types.BillableEntityDatePayload | null | undefined;
    sku?: string | null | undefined;
    featureTrack?: Types.FeatureTrack | null | undefined;
    features?: Array<string> | string | null | undefined;
}>;

export type UpdateBillableEntityInternalAttributesMutation = {
    billableEntityInternalUpdate: boolean;
};

export type BillableEntityCreateMutationVariables = Exact<{
    billableEntity: Types.BillableEntityCreatePayload;
}>;

export type BillableEntityCreateMutation = {
    billableEntityCreate: { id: BillableEntityId };
};

export type GetBillableEntityQueryVariables = Exact<{
    billableEntityId: BillableEntityId;
}>;

export type GetBillableEntityQuery = {
    getBillableEntity: {
        id: BillableEntityId;
        name: string;
        sku: string;
        featureTrack: Types.FeatureTrack;
        tenantId: TenantId;
        regionId: RegionId | null;
        activatedAt: any;
        deactivatedAt: any;
        onboardingStartedAt: any;
        demoStartedAt: any;
        demoEndedAt: any;
        premiumActivatedAt: any;
        premiumDeactivatedAt: any;
        internalAttributes: {
            entries: Array<{ key: string; value: string | null }>;
        } | null;
        features: Array<{ name: string; enabled: boolean; enabledUpstream: boolean }>;
        clientStats: {
            clientsLast30Days: number;
            clientsLast60Days: number;
            clientsLast90Days: number;
        };
    };
};

export type GetBillableEntitiesQueryVariables = Exact<{ [key: string]: never }>;

export type GetBillableEntitiesQuery = {
    getBillableEntities: Array<{
        id: BillableEntityId;
        name: string;
        sku: string;
        featureTrack: Types.FeatureTrack;
        internalAttributes: {
            entries: Array<{ key: string; value: string | null }>;
        } | null;
    }>;
};

export type GetBillableEntitiesWithoutWaveCustomerQueryVariables = Exact<{
    [key: string]: never;
}>;

export type GetBillableEntitiesWithoutWaveCustomerQuery = {
    getBillableEntitiesWithoutWaveCustomer: Array<{
        id: BillableEntityId;
        name: string;
        sku: string;
        featureTrack: Types.FeatureTrack;
        tenantId: TenantId;
        regionId: RegionId | null;
        activatedAt: any;
        deactivatedAt: any;
        internalAttributes: {
            entries: Array<{ key: string; value: string | null }>;
        } | null;
    }>;
};

export type GetEntitiesQueryVariables = Exact<{ [key: string]: never }>;

export type GetEntitiesQuery = {
    getEntities: Array<{
        name: string;
        title: string;
        preferredFilterField: string | null;
        allowAnswers: boolean;
        layoutParents: Array<Types.LayoutSectionParentKey>;
        identifierFields: Array<string>;
        preferredGroupBy: Array<string> | null;
        supportsTasks: boolean;
        supportsWorkflows: boolean;
        priorityFields: Array<{ fieldName: string | null; title: string | null }>;
        fields: Array<{
            name: string;
            title: string;
            questionType: number;
            formatter: Types.BridgeFieldFormatter;
            category: number;
            dependsOn: Array<string> | null;
            customListKey: Types.CustomListKey | null;
            aliases: Array<string> | null;
            naryValues: Array<{ value: string; label: string }> | null;
            reportBehavior: {
                sortable: boolean;
                filterable: boolean;
                selectable: boolean;
                newReportSelectOrder: number | null;
                summaryGroupable: boolean;
                canAggregate: boolean;
            };
        }>;
        relations: Array<{
            name: string;
            title: string;
            otherEntityName: string;
            kind: Types.BridgeRelationKind;
            selectable: boolean;
            aliases: Array<string> | null;
        }>;
        questions: Array<{
            id: QuestionId;
            title: string;
            interaction: string;
            annotation: string;
            questionType: string;
            category: string;
            key: string;
            order: number;
            regionMask: Array<string>;
            rulesString: string | null;
            searchBehavior: string;
            source: Types.QuestionSource;
            editable: boolean;
            regionVisibility: {
                editable: boolean;
                regions: Array<{
                    mode: Types.EntityRegionModeOutput;
                    regionId: RegionId;
                }>;
            } | null;
            prompts: Array<{
                id: PromptId;
                title: string;
                key: string;
                order: number;
                annotation: boolean;
            }> | null;
        }>;
        defaultSelect: Array<{
            fieldName: string | null;
            questionId: QuestionId | null;
        }> | null;
        preferredDisplay: {
            fieldName: string | null;
            relation: { name: string } | null;
        } | null;
    }>;
};

export type GetEntitiesForTenantQueryVariables = Exact<{
    tenantId: TenantId;
}>;

export type GetEntitiesForTenantQuery = {
    getEntitiesForTenant: Array<{
        name: string;
        title: string;
        preferredFilterField: string | null;
        allowAnswers: boolean;
        layoutParents: Array<Types.LayoutSectionParentKey>;
        identifierFields: Array<string>;
        preferredGroupBy: Array<string> | null;
        supportsTasks: boolean;
        supportsWorkflows: boolean;
        priorityFields: Array<{ fieldName: string | null; title: string | null }>;
        fields: Array<{
            name: string;
            title: string;
            questionType: number;
            formatter: Types.BridgeFieldFormatter;
            category: number;
            dependsOn: Array<string> | null;
            customListKey: Types.CustomListKey | null;
            aliases: Array<string> | null;
            naryValues: Array<{ value: string; label: string }> | null;
            reportBehavior: {
                sortable: boolean;
                filterable: boolean;
                selectable: boolean;
                newReportSelectOrder: number | null;
                summaryGroupable: boolean;
                canAggregate: boolean;
            };
        }>;
        relations: Array<{
            name: string;
            title: string;
            otherEntityName: string;
            kind: Types.BridgeRelationKind;
            selectable: boolean;
            aliases: Array<string> | null;
        }>;
        questions: Array<{
            id: QuestionId;
            title: string;
            interaction: string;
            annotation: string;
            questionType: string;
            category: string;
            key: string;
            order: number;
            regionMask: Array<string>;
            rulesString: string | null;
            searchBehavior: string;
            source: Types.QuestionSource;
            editable: boolean;
            regionVisibility: {
                editable: boolean;
                regions: Array<{
                    mode: Types.EntityRegionModeOutput;
                    regionId: RegionId;
                }>;
            } | null;
            prompts: Array<{
                id: PromptId;
                title: string;
                key: string;
                order: number;
                annotation: boolean;
            }> | null;
        }>;
        defaultSelect: Array<{
            fieldName: string | null;
            questionId: QuestionId | null;
        }> | null;
        preferredDisplay: {
            fieldName: string | null;
            relation: { name: string } | null;
        } | null;
    }>;
};

export type UpsertEntityQuestionMutationVariables = Exact<{
    entityType: string;
    question: Types.BridgeQuestionInput;
}>;

export type UpsertEntityQuestionMutation = {
    upsertEntityQuestion: {
        id: QuestionId;
        title: string;
        interaction: string;
        annotation: string;
        questionType: string;
        category: string;
        key: string;
        order: number;
        regionMask: Array<string>;
        rulesString: string | null;
        searchBehavior: string;
        source: Types.QuestionSource;
        editable: boolean;
        regionVisibility: {
            editable: boolean;
            regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
        } | null;
        prompts: Array<{
            id: PromptId;
            title: string;
            key: string;
            order: number;
            annotation: boolean;
        }> | null;
    };
};

export type SetEntityAnswerMutationVariables = Exact<{
    entityId: AnswerEntityId;
    answer: Types.AnswerInput;
}>;

export type SetEntityAnswerMutation = {
    setEntityAnswer: {
        questionId: QuestionId | null;
        booleanAnswer: boolean | null;
        numberAnswer: number | null;
        numberAnswerHigh: number | null;
        textAnswer: string | null;
        dateAnswer: string | null;
        calendarDateAnswer: string | null;
        selections: Array<string> | null;
        selectionAnnotations: Array<{
            selectionId: string | null;
            text: string | null;
        }> | null;
    };
};

export type DeleteQuestionMutationVariables = Exact<{
    questionId: QuestionId;
}>;

export type DeleteQuestionMutation = { deleteQuestion: { questionId: QuestionId } };

export type BridgeEntityFragmentFragment = {
    name: string;
    title: string;
    preferredFilterField: string | null;
    allowAnswers: boolean;
    layoutParents: Array<Types.LayoutSectionParentKey>;
    identifierFields: Array<string>;
    preferredGroupBy: Array<string> | null;
    supportsTasks: boolean;
    supportsWorkflows: boolean;
    priorityFields: Array<{ fieldName: string | null; title: string | null }>;
    fields: Array<{
        name: string;
        title: string;
        questionType: number;
        formatter: Types.BridgeFieldFormatter;
        category: number;
        dependsOn: Array<string> | null;
        customListKey: Types.CustomListKey | null;
        aliases: Array<string> | null;
        naryValues: Array<{ value: string; label: string }> | null;
        reportBehavior: {
            sortable: boolean;
            filterable: boolean;
            selectable: boolean;
            newReportSelectOrder: number | null;
            summaryGroupable: boolean;
            canAggregate: boolean;
        };
    }>;
    relations: Array<{
        name: string;
        title: string;
        otherEntityName: string;
        kind: Types.BridgeRelationKind;
        selectable: boolean;
        aliases: Array<string> | null;
    }>;
    questions: Array<{
        id: QuestionId;
        title: string;
        interaction: string;
        annotation: string;
        questionType: string;
        category: string;
        key: string;
        order: number;
        regionMask: Array<string>;
        rulesString: string | null;
        searchBehavior: string;
        source: Types.QuestionSource;
        editable: boolean;
        regionVisibility: {
            editable: boolean;
            regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
        } | null;
        prompts: Array<{
            id: PromptId;
            title: string;
            key: string;
            order: number;
            annotation: boolean;
        }> | null;
    }>;
    defaultSelect: Array<{
        fieldName: string | null;
        questionId: QuestionId | null;
    }> | null;
    preferredDisplay: {
        fieldName: string | null;
        relation: { name: string } | null;
    } | null;
};

export type BridgeQuestionFragmentFragment = {
    id: QuestionId;
    title: string;
    interaction: string;
    annotation: string;
    questionType: string;
    category: string;
    key: string;
    order: number;
    regionMask: Array<string>;
    rulesString: string | null;
    searchBehavior: string;
    source: Types.QuestionSource;
    editable: boolean;
    regionVisibility: {
        editable: boolean;
        regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
    } | null;
    prompts: Array<{
        id: PromptId;
        title: string;
        key: string;
        order: number;
        annotation: boolean;
    }> | null;
};

export type FindClientTransferRequestByClientIdQueryVariables = Exact<{
    clientId: PlacementId;
}>;

export type FindClientTransferRequestByClientIdQuery = {
    findClientTransferRequestByClientId: {
        id: ClientTransferRequestId;
        status: Types.ClientTransferRequestStatus;
        dateAdded: any;
        lastEmailSentAt: IsoTimestamp | null;
        client: { id: PlacementId; name: string | null; bestContactName: string | null };
        toUserProfile: { name: string; email: string };
        createdByEntity: { name: string | null; email: string };
    } | null;
};

export type RequestClientTransferMutationVariables = Exact<{
    params: Types.TransferClientRequest;
}>;

export type RequestClientTransferMutation = { requestClientTransfer: boolean };

export type GetPendingIncomingClientTransfersQueryVariables = Exact<{
    regionId: RegionId;
}>;

export type GetPendingIncomingClientTransfersQuery = {
    getPendingIncomingClientTransfers: Array<{
        id: ClientTransferRequestId;
        status: Types.ClientTransferRequestStatus;
        dateAdded: any;
        client: { name: string | null; bestContactName: string | null };
        createdByEntity: { name: string | null; email: string };
        toUserProfile: { name: string; email: string };
    }>;
};

export type GetPendingOutgoingClientTransfersQueryVariables = Exact<{
    regionId: RegionId;
}>;

export type GetPendingOutgoingClientTransfersQuery = {
    getPendingOutgoingClientTransfers: Array<{
        id: ClientTransferRequestId;
        status: Types.ClientTransferRequestStatus;
        dateAdded: any;
        lastEmailSentAt: IsoTimestamp | null;
        client: { id: PlacementId; name: string | null; bestContactName: string | null };
        toUserProfile: { name: string; email: string };
        createdByEntity: { name: string | null; email: string };
    }>;
};

export type RejectClientTransferMutationVariables = Exact<{
    clientTransferRequestId: ClientTransferRequestId;
}>;

export type RejectClientTransferMutation = { rejectClientTransfer: boolean };

export type CancelClientTransferMutationVariables = Exact<{
    clientTransferRequestId: ClientTransferRequestId;
}>;

export type CancelClientTransferMutation = { cancelClientTransfer: boolean };

export type ResendClientTransferMutationVariables = Exact<{
    clientTransferRequestId: ClientTransferRequestId;
}>;

export type ResendClientTransferMutation = { resendClientTransfer: boolean };

export type AcceptClientTransferMutationVariables = Exact<{
    clientTransferRequestId: ClientTransferRequestId;
    regionId: RegionId;
}>;

export type AcceptClientTransferMutation = {
    acceptClientTransfer: {
        id: PlacementId;
        name: string | null;
        statusListItemId: CustomListItemId;
        status: string;
    };
};

export type GetClientQueryVariables = Exact<{
    id: PlacementId;
}>;

export type GetClientQuery = {
    getClient: {
        id: PlacementId;
        totalInvoiceAmountCents: number;
        bestContactName: string | null;
        bestContactPhone: string | null;
        bestContactEmail: string | null;
        invoices: Array<{
            id: InvoiceId;
            amountCents: CurrencyInCents;
            paidDate: string | null;
            sentDate: string | null;
            dueDate: string | null;
            serial: string;
            status: string;
            placementId: PlacementId;
            clientName: string | null;
            communityId: CommunityId | null;
            fileId: FileId | null;
            notes: string | null;
            externalReference: string | null;
            remainingBalanceCents: number;
            totalPaidCents: number;
            hasOutstandingBalance: boolean;
            isExternallyManaged: boolean;
            lineItems: Array<{
                id: InvoiceLineItemId;
                description: string;
                quantity: number;
                unitPriceInCents: CurrencyInCents;
            }> | null;
            paymentSchedule: {
                items: Array<{
                    id: InvoicePaymentScheduleItemId;
                    amountInCents: CurrencyInCents;
                    dueDate: string;
                    paidDate: string | null;
                    status: Types.InvoicePaymentScheduleItemStatus;
                }>;
            } | null;
            community: { name: string | null; id: CommunityId } | null;
        }> | null;
        disclosures: Array<{
            id: DisclosureId;
            dateAdded: any;
            publicUrl: string;
            acknowledgements: Array<{
                id: DisclosureAcknowledgementId;
                name: string;
                email: string;
                phone: string;
                agreed: boolean;
                response: Types.DisclosureAcknowledgementResponse | null;
                dateAdded: any;
                createdBy: UserId | null;
                ipAddress: string;
            }>;
        }> | null;
    };
};

export type ClientCommunitiesFragment = {
    id: PlacementId;
    baseLocation: { lat: string; lng: string } | null;
    clientCommunities: Array<{
        order: number | null;
        relationship: number;
        publicNotes: string | null;
        privateNotes: string | null;
        referred: boolean;
        community: {
            evergreenCommunityId: EvergreenCommunityId;
            isConnectedToEvergreen: boolean;
            address: string | null;
            caregiverRatio: number | null;
            city: string | null;
            contractStatus: string | null;
            contractExpiresDate: any;
            contractIssuedDate: any;
            contractNotes: string | null;
            dateAdded: any;
            gender: string | null;
            feeType: string | null;
            paymentFee: string | null;
            paymentPercentage: string | null;
            id: CommunityId;
            latitude: string | null;
            licenseStatus: string | null;
            longitude: string | null;
            mainPhone: PhoneNumberE164 | null;
            monthlyPrice: number | null;
            moveInDeposit: number | null;
            name: string | null;
            petDeposit: string | null;
            priceDetails: string | null;
            priceHighEnd: number | null;
            rating: number | null;
            secondPersonFee: string | null;
            state: string | null;
            summary: string | null;
            thumbnailUrl: string | null;
            unitsOrCapacity: number | null;
            updatedAt: any;
            virtualTourURL: string | null;
            website: string | null;
            zip: string | null;
            regionId: RegionId;
            email: string | null;
            county: string | null;
            availability: {
                status: Types.EvergreenCommunityListingAvailability;
                notes: string | null;
                freshness: Types.ObservationFreshness;
                lastUpdatedAt: any;
            } | null;
            pricing: {
                minPrice: string | null;
                maxPrice: string | null;
                notes: string | null;
                files: Array<{
                    id: FileId;
                    name: string;
                    mimeType: string;
                    size: number | null;
                    downloadUrl: string;
                }> | null;
            } | null;
            answers: Array<{
                questionId: QuestionId | null;
                booleanAnswer: boolean | null;
                numberAnswer: number | null;
                numberAnswerHigh: number | null;
                textAnswer: string | null;
                dateAnswer: string | null;
                calendarDateAnswer: string | null;
                selections: Array<string> | null;
                idReferenceAnswer: UserId | null;
                selectionAnnotations: Array<{
                    text: string | null;
                    selectionId: string | null;
                }> | null;
            }>;
            photoEntities: Array<{
                id: PhotoId;
                entityId: FileEntityId;
                order: number;
                dateAdded: any;
                createdBy: UserId;
                originalFile: {
                    id: FileId;
                    entityId: FileEntityId;
                    originalFilename: string | null;
                    mimeType: string | null;
                    description: string | null;
                    byteLength: number | null;
                    shareWithClient: boolean;
                    shareWithCommunity: boolean;
                    shareWithReferral: boolean;
                    shareWithHomeCareLead: boolean;
                    createdAt: any;
                    createdBy: UserId | null;
                    updatedAt: any;
                    updatedBy: UserId | null;
                    hasSignatureTemplate: boolean;
                    isGlobal: boolean;
                    managementLevel: string;
                    regionVisibility: {
                        editable: boolean;
                        regions: Array<{
                            mode: Types.EntityRegionModeOutput;
                            regionId: RegionId;
                        }>;
                    } | null;
                };
            }>;
            licenses: Array<{
                id: CommunityLicenseId;
                licenseNumber: string | null;
                href: string | null;
                licensee: string | null;
                status: string | null;
                licenseStartDate: string | null;
                licenseEndDate: string | null;
            }>;
            googleInfo: { rating: number | null; ratingCount: number | null } | null;
            contacts: Array<{
                cellPhone: PhoneNumberE164 | null;
                name: string | null;
                phone1: PhoneNumberE164 | null;
                role: string | null;
                email1: string | null;
            }>;
            primaryContacts: Array<{
                cellPhone: PhoneNumberE164 | null;
                name: string | null;
                phone1: PhoneNumberE164 | null;
                role: string | null;
                email1: string | null;
            }>;
        };
        tours: Array<{
            id: TourId;
            communityId: CommunityId;
            clientId: PlacementId | null;
            userId: UserId;
            tourDate: CalendarDateSerialized;
            tourTime: string | null;
            tourDuration: number;
            privateNotes: string | null;
            familyNotes: string | null;
            showOnFamilyView: boolean;
            dateAdded: IsoTimestamp;
            updatedAt: IsoTimestamp;
        }> | null;
    }>;
};

export type GetClientCommunitiesQueryVariables = Exact<{
    id: PlacementId;
    questionIds: Array<QuestionId> | QuestionId;
}>;

export type GetClientCommunitiesQuery = {
    getClient: {
        id: PlacementId;
        baseLocation: { lat: string; lng: string } | null;
        clientCommunities: Array<{
            order: number | null;
            relationship: number;
            publicNotes: string | null;
            privateNotes: string | null;
            referred: boolean;
            community: {
                evergreenCommunityId: EvergreenCommunityId;
                isConnectedToEvergreen: boolean;
                address: string | null;
                caregiverRatio: number | null;
                city: string | null;
                contractStatus: string | null;
                contractExpiresDate: any;
                contractIssuedDate: any;
                contractNotes: string | null;
                dateAdded: any;
                gender: string | null;
                feeType: string | null;
                paymentFee: string | null;
                paymentPercentage: string | null;
                id: CommunityId;
                latitude: string | null;
                licenseStatus: string | null;
                longitude: string | null;
                mainPhone: PhoneNumberE164 | null;
                monthlyPrice: number | null;
                moveInDeposit: number | null;
                name: string | null;
                petDeposit: string | null;
                priceDetails: string | null;
                priceHighEnd: number | null;
                rating: number | null;
                secondPersonFee: string | null;
                state: string | null;
                summary: string | null;
                thumbnailUrl: string | null;
                unitsOrCapacity: number | null;
                updatedAt: any;
                virtualTourURL: string | null;
                website: string | null;
                zip: string | null;
                regionId: RegionId;
                email: string | null;
                county: string | null;
                availability: {
                    status: Types.EvergreenCommunityListingAvailability;
                    notes: string | null;
                    freshness: Types.ObservationFreshness;
                    lastUpdatedAt: any;
                } | null;
                pricing: {
                    minPrice: string | null;
                    maxPrice: string | null;
                    notes: string | null;
                    files: Array<{
                        id: FileId;
                        name: string;
                        mimeType: string;
                        size: number | null;
                        downloadUrl: string;
                    }> | null;
                } | null;
                answers: Array<{
                    questionId: QuestionId | null;
                    booleanAnswer: boolean | null;
                    numberAnswer: number | null;
                    numberAnswerHigh: number | null;
                    textAnswer: string | null;
                    dateAnswer: string | null;
                    calendarDateAnswer: string | null;
                    selections: Array<string> | null;
                    idReferenceAnswer: UserId | null;
                    selectionAnnotations: Array<{
                        text: string | null;
                        selectionId: string | null;
                    }> | null;
                }>;
                photoEntities: Array<{
                    id: PhotoId;
                    entityId: FileEntityId;
                    order: number;
                    dateAdded: any;
                    createdBy: UserId;
                    originalFile: {
                        id: FileId;
                        entityId: FileEntityId;
                        originalFilename: string | null;
                        mimeType: string | null;
                        description: string | null;
                        byteLength: number | null;
                        shareWithClient: boolean;
                        shareWithCommunity: boolean;
                        shareWithReferral: boolean;
                        shareWithHomeCareLead: boolean;
                        createdAt: any;
                        createdBy: UserId | null;
                        updatedAt: any;
                        updatedBy: UserId | null;
                        hasSignatureTemplate: boolean;
                        isGlobal: boolean;
                        managementLevel: string;
                        regionVisibility: {
                            editable: boolean;
                            regions: Array<{
                                mode: Types.EntityRegionModeOutput;
                                regionId: RegionId;
                            }>;
                        } | null;
                    };
                }>;
                licenses: Array<{
                    id: CommunityLicenseId;
                    licenseNumber: string | null;
                    href: string | null;
                    licensee: string | null;
                    status: string | null;
                    licenseStartDate: string | null;
                    licenseEndDate: string | null;
                }>;
                googleInfo: { rating: number | null; ratingCount: number | null } | null;
                contacts: Array<{
                    cellPhone: PhoneNumberE164 | null;
                    name: string | null;
                    phone1: PhoneNumberE164 | null;
                    role: string | null;
                    email1: string | null;
                }>;
                primaryContacts: Array<{
                    cellPhone: PhoneNumberE164 | null;
                    name: string | null;
                    phone1: PhoneNumberE164 | null;
                    role: string | null;
                    email1: string | null;
                }>;
            };
            tours: Array<{
                id: TourId;
                communityId: CommunityId;
                clientId: PlacementId | null;
                userId: UserId;
                tourDate: CalendarDateSerialized;
                tourTime: string | null;
                tourDuration: number;
                privateNotes: string | null;
                familyNotes: string | null;
                showOnFamilyView: boolean;
                dateAdded: IsoTimestamp;
                updatedAt: IsoTimestamp;
            }> | null;
        }>;
    };
};

export type GetClientMatchesQueryVariables = Exact<{
    id: PlacementId;
}>;

export type GetClientMatchesQuery = {
    getClient: {
        id: PlacementId;
        potentialDuplicates: Array<{
            id: PlacementId;
            name: string | null;
            bestContactName: string | null;
            dateAdded: any;
            viewable: boolean;
            assignedUser: { id: UserId; preferredName: string } | null;
        }>;
    };
};

export type CreateClientMutationVariables = Exact<{
    regionId: RegionId;
}>;

export type CreateClientMutation = { createClient: { id: PlacementId } };

export type DeleteClientMatchesMutationVariables = Exact<{
    id: PlacementId;
}>;

export type DeleteClientMatchesMutation = { deleteClientMatches: { id: PlacementId } };

export type SetCommunityOrderMutationVariables = Exact<{
    clientId: PlacementId;
    questionIds: Array<QuestionId> | QuestionId;
    ordering: Array<Types.CommunityOrdering> | Types.CommunityOrdering;
}>;

export type SetCommunityOrderMutation = {
    setCommunityOrder: {
        id: PlacementId;
        baseLocation: { lat: string; lng: string } | null;
        clientCommunities: Array<{
            order: number | null;
            relationship: number;
            publicNotes: string | null;
            privateNotes: string | null;
            referred: boolean;
            community: {
                evergreenCommunityId: EvergreenCommunityId;
                isConnectedToEvergreen: boolean;
                address: string | null;
                caregiverRatio: number | null;
                city: string | null;
                contractStatus: string | null;
                contractExpiresDate: any;
                contractIssuedDate: any;
                contractNotes: string | null;
                dateAdded: any;
                gender: string | null;
                feeType: string | null;
                paymentFee: string | null;
                paymentPercentage: string | null;
                id: CommunityId;
                latitude: string | null;
                licenseStatus: string | null;
                longitude: string | null;
                mainPhone: PhoneNumberE164 | null;
                monthlyPrice: number | null;
                moveInDeposit: number | null;
                name: string | null;
                petDeposit: string | null;
                priceDetails: string | null;
                priceHighEnd: number | null;
                rating: number | null;
                secondPersonFee: string | null;
                state: string | null;
                summary: string | null;
                thumbnailUrl: string | null;
                unitsOrCapacity: number | null;
                updatedAt: any;
                virtualTourURL: string | null;
                website: string | null;
                zip: string | null;
                regionId: RegionId;
                email: string | null;
                county: string | null;
                availability: {
                    status: Types.EvergreenCommunityListingAvailability;
                    notes: string | null;
                    freshness: Types.ObservationFreshness;
                    lastUpdatedAt: any;
                } | null;
                pricing: {
                    minPrice: string | null;
                    maxPrice: string | null;
                    notes: string | null;
                    files: Array<{
                        id: FileId;
                        name: string;
                        mimeType: string;
                        size: number | null;
                        downloadUrl: string;
                    }> | null;
                } | null;
                answers: Array<{
                    questionId: QuestionId | null;
                    booleanAnswer: boolean | null;
                    numberAnswer: number | null;
                    numberAnswerHigh: number | null;
                    textAnswer: string | null;
                    dateAnswer: string | null;
                    calendarDateAnswer: string | null;
                    selections: Array<string> | null;
                    idReferenceAnswer: UserId | null;
                    selectionAnnotations: Array<{
                        text: string | null;
                        selectionId: string | null;
                    }> | null;
                }>;
                photoEntities: Array<{
                    id: PhotoId;
                    entityId: FileEntityId;
                    order: number;
                    dateAdded: any;
                    createdBy: UserId;
                    originalFile: {
                        id: FileId;
                        entityId: FileEntityId;
                        originalFilename: string | null;
                        mimeType: string | null;
                        description: string | null;
                        byteLength: number | null;
                        shareWithClient: boolean;
                        shareWithCommunity: boolean;
                        shareWithReferral: boolean;
                        shareWithHomeCareLead: boolean;
                        createdAt: any;
                        createdBy: UserId | null;
                        updatedAt: any;
                        updatedBy: UserId | null;
                        hasSignatureTemplate: boolean;
                        isGlobal: boolean;
                        managementLevel: string;
                        regionVisibility: {
                            editable: boolean;
                            regions: Array<{
                                mode: Types.EntityRegionModeOutput;
                                regionId: RegionId;
                            }>;
                        } | null;
                    };
                }>;
                licenses: Array<{
                    id: CommunityLicenseId;
                    licenseNumber: string | null;
                    href: string | null;
                    licensee: string | null;
                    status: string | null;
                    licenseStartDate: string | null;
                    licenseEndDate: string | null;
                }>;
                googleInfo: { rating: number | null; ratingCount: number | null } | null;
                contacts: Array<{
                    cellPhone: PhoneNumberE164 | null;
                    name: string | null;
                    phone1: PhoneNumberE164 | null;
                    role: string | null;
                    email1: string | null;
                }>;
                primaryContacts: Array<{
                    cellPhone: PhoneNumberE164 | null;
                    name: string | null;
                    phone1: PhoneNumberE164 | null;
                    role: string | null;
                    email1: string | null;
                }>;
            };
            tours: Array<{
                id: TourId;
                communityId: CommunityId;
                clientId: PlacementId | null;
                userId: UserId;
                tourDate: CalendarDateSerialized;
                tourTime: string | null;
                tourDuration: number;
                privateNotes: string | null;
                familyNotes: string | null;
                showOnFamilyView: boolean;
                dateAdded: IsoTimestamp;
                updatedAt: IsoTimestamp;
            }> | null;
        }>;
    };
};

export type AddClientCommunitiesMutationVariables = Exact<{
    clientId: PlacementId;
    questionIds: Array<QuestionId> | QuestionId;
    params: Array<Types.AddClientCommunityRequest> | Types.AddClientCommunityRequest;
}>;

export type AddClientCommunitiesMutation = {
    addClientCommunities: {
        id: PlacementId;
        baseLocation: { lat: string; lng: string } | null;
        clientCommunities: Array<{
            order: number | null;
            relationship: number;
            publicNotes: string | null;
            privateNotes: string | null;
            referred: boolean;
            community: {
                evergreenCommunityId: EvergreenCommunityId;
                isConnectedToEvergreen: boolean;
                address: string | null;
                caregiverRatio: number | null;
                city: string | null;
                contractStatus: string | null;
                contractExpiresDate: any;
                contractIssuedDate: any;
                contractNotes: string | null;
                dateAdded: any;
                gender: string | null;
                feeType: string | null;
                paymentFee: string | null;
                paymentPercentage: string | null;
                id: CommunityId;
                latitude: string | null;
                licenseStatus: string | null;
                longitude: string | null;
                mainPhone: PhoneNumberE164 | null;
                monthlyPrice: number | null;
                moveInDeposit: number | null;
                name: string | null;
                petDeposit: string | null;
                priceDetails: string | null;
                priceHighEnd: number | null;
                rating: number | null;
                secondPersonFee: string | null;
                state: string | null;
                summary: string | null;
                thumbnailUrl: string | null;
                unitsOrCapacity: number | null;
                updatedAt: any;
                virtualTourURL: string | null;
                website: string | null;
                zip: string | null;
                regionId: RegionId;
                email: string | null;
                county: string | null;
                availability: {
                    status: Types.EvergreenCommunityListingAvailability;
                    notes: string | null;
                    freshness: Types.ObservationFreshness;
                    lastUpdatedAt: any;
                } | null;
                pricing: {
                    minPrice: string | null;
                    maxPrice: string | null;
                    notes: string | null;
                    files: Array<{
                        id: FileId;
                        name: string;
                        mimeType: string;
                        size: number | null;
                        downloadUrl: string;
                    }> | null;
                } | null;
                answers: Array<{
                    questionId: QuestionId | null;
                    booleanAnswer: boolean | null;
                    numberAnswer: number | null;
                    numberAnswerHigh: number | null;
                    textAnswer: string | null;
                    dateAnswer: string | null;
                    calendarDateAnswer: string | null;
                    selections: Array<string> | null;
                    idReferenceAnswer: UserId | null;
                    selectionAnnotations: Array<{
                        text: string | null;
                        selectionId: string | null;
                    }> | null;
                }>;
                photoEntities: Array<{
                    id: PhotoId;
                    entityId: FileEntityId;
                    order: number;
                    dateAdded: any;
                    createdBy: UserId;
                    originalFile: {
                        id: FileId;
                        entityId: FileEntityId;
                        originalFilename: string | null;
                        mimeType: string | null;
                        description: string | null;
                        byteLength: number | null;
                        shareWithClient: boolean;
                        shareWithCommunity: boolean;
                        shareWithReferral: boolean;
                        shareWithHomeCareLead: boolean;
                        createdAt: any;
                        createdBy: UserId | null;
                        updatedAt: any;
                        updatedBy: UserId | null;
                        hasSignatureTemplate: boolean;
                        isGlobal: boolean;
                        managementLevel: string;
                        regionVisibility: {
                            editable: boolean;
                            regions: Array<{
                                mode: Types.EntityRegionModeOutput;
                                regionId: RegionId;
                            }>;
                        } | null;
                    };
                }>;
                licenses: Array<{
                    id: CommunityLicenseId;
                    licenseNumber: string | null;
                    href: string | null;
                    licensee: string | null;
                    status: string | null;
                    licenseStartDate: string | null;
                    licenseEndDate: string | null;
                }>;
                googleInfo: { rating: number | null; ratingCount: number | null } | null;
                contacts: Array<{
                    cellPhone: PhoneNumberE164 | null;
                    name: string | null;
                    phone1: PhoneNumberE164 | null;
                    role: string | null;
                    email1: string | null;
                }>;
                primaryContacts: Array<{
                    cellPhone: PhoneNumberE164 | null;
                    name: string | null;
                    phone1: PhoneNumberE164 | null;
                    role: string | null;
                    email1: string | null;
                }>;
            };
            tours: Array<{
                id: TourId;
                communityId: CommunityId;
                clientId: PlacementId | null;
                userId: UserId;
                tourDate: CalendarDateSerialized;
                tourTime: string | null;
                tourDuration: number;
                privateNotes: string | null;
                familyNotes: string | null;
                showOnFamilyView: boolean;
                dateAdded: IsoTimestamp;
                updatedAt: IsoTimestamp;
            }> | null;
        }>;
    };
};

export type RemoveClientCommunitiesMutationVariables = Exact<{
    clientId: PlacementId;
    params:
        | Array<Types.RemoveClientCommunityRequest>
        | Types.RemoveClientCommunityRequest;
}>;

export type RemoveClientCommunitiesMutation = {
    removeClientCommunities: { id: PlacementId };
};

export type DashboardDefaultsFragment = {
    total: number;
    clients: Array<{
        id: PlacementId;
        serial: number | null;
        name: string | null;
        statusListItemId: CustomListItemId;
        bestContactName: string | null;
        bestContactPhone: string | null;
        dateAdded: any;
        invoiceSentDate: string | null;
        moveTimeline: string | null;
        moveTimelineActual: string | null;
        paymentReceivedDate: string | null;
        statusCheckLaterDate: string | null;
        statusClosedDate: string | null;
        updatedAt: any;
        assignedUserId: UserId | null;
        phone: PhoneNumberE164 | null;
        cellPhone: PhoneNumberE164 | null;
        email: string | null;
        additionalClientName: string | null;
        chosenCommunity: { id: CommunityId; name: string | null } | null;
        nextTask: {
            id: TaskId;
            text: string | null;
            status: string;
            dueDate: string | null;
            dueDateTime: any;
        } | null;
        allContacts: Array<{
            name: string | null;
            email1: string | null;
            phone1: PhoneNumberE164 | null;
            cellPhone: PhoneNumberE164 | null;
            relationship: string | null;
        }>;
    }>;
};

export type GetClientsForTimelineQueryVariables = Exact<{
    regionId: RegionId;
    searchString?: string | null | undefined;
    assignedUserId?: UserId | null | undefined;
    assignedReferralUserId?: UserId | null | undefined;
    collaboratorUserId?: UserId | null | undefined;
    searchType?: string | null | undefined;
    openClosed?: string | null | undefined;
    page: number;
    perPage: number;
    includeStatuses?: Array<string> | string | null | undefined;
}>;

export type GetClientsForTimelineQuery = {
    pastDue: {
        total: number;
        clients: Array<{
            id: PlacementId;
            serial: number | null;
            name: string | null;
            statusListItemId: CustomListItemId;
            bestContactName: string | null;
            bestContactPhone: string | null;
            dateAdded: any;
            invoiceSentDate: string | null;
            moveTimeline: string | null;
            moveTimelineActual: string | null;
            paymentReceivedDate: string | null;
            statusCheckLaterDate: string | null;
            statusClosedDate: string | null;
            updatedAt: any;
            assignedUserId: UserId | null;
            phone: PhoneNumberE164 | null;
            cellPhone: PhoneNumberE164 | null;
            email: string | null;
            additionalClientName: string | null;
            chosenCommunity: { id: CommunityId; name: string | null } | null;
            nextTask: {
                id: TaskId;
                text: string | null;
                status: string;
                dueDate: string | null;
                dueDateTime: any;
            } | null;
            allContacts: Array<{
                name: string | null;
                email1: string | null;
                phone1: PhoneNumberE164 | null;
                cellPhone: PhoneNumberE164 | null;
                relationship: string | null;
            }>;
        }>;
    };
    oneWeek: {
        total: number;
        clients: Array<{
            id: PlacementId;
            serial: number | null;
            name: string | null;
            statusListItemId: CustomListItemId;
            bestContactName: string | null;
            bestContactPhone: string | null;
            dateAdded: any;
            invoiceSentDate: string | null;
            moveTimeline: string | null;
            moveTimelineActual: string | null;
            paymentReceivedDate: string | null;
            statusCheckLaterDate: string | null;
            statusClosedDate: string | null;
            updatedAt: any;
            assignedUserId: UserId | null;
            phone: PhoneNumberE164 | null;
            cellPhone: PhoneNumberE164 | null;
            email: string | null;
            additionalClientName: string | null;
            chosenCommunity: { id: CommunityId; name: string | null } | null;
            nextTask: {
                id: TaskId;
                text: string | null;
                status: string;
                dueDate: string | null;
                dueDateTime: any;
            } | null;
            allContacts: Array<{
                name: string | null;
                email1: string | null;
                phone1: PhoneNumberE164 | null;
                cellPhone: PhoneNumberE164 | null;
                relationship: string | null;
            }>;
        }>;
    };
    oneToTwoWeeks: {
        total: number;
        clients: Array<{
            id: PlacementId;
            serial: number | null;
            name: string | null;
            statusListItemId: CustomListItemId;
            bestContactName: string | null;
            bestContactPhone: string | null;
            dateAdded: any;
            invoiceSentDate: string | null;
            moveTimeline: string | null;
            moveTimelineActual: string | null;
            paymentReceivedDate: string | null;
            statusCheckLaterDate: string | null;
            statusClosedDate: string | null;
            updatedAt: any;
            assignedUserId: UserId | null;
            phone: PhoneNumberE164 | null;
            cellPhone: PhoneNumberE164 | null;
            email: string | null;
            additionalClientName: string | null;
            chosenCommunity: { id: CommunityId; name: string | null } | null;
            nextTask: {
                id: TaskId;
                text: string | null;
                status: string;
                dueDate: string | null;
                dueDateTime: any;
            } | null;
            allContacts: Array<{
                name: string | null;
                email1: string | null;
                phone1: PhoneNumberE164 | null;
                cellPhone: PhoneNumberE164 | null;
                relationship: string | null;
            }>;
        }>;
    };
    twoToFourWeeks: {
        total: number;
        clients: Array<{
            id: PlacementId;
            serial: number | null;
            name: string | null;
            statusListItemId: CustomListItemId;
            bestContactName: string | null;
            bestContactPhone: string | null;
            dateAdded: any;
            invoiceSentDate: string | null;
            moveTimeline: string | null;
            moveTimelineActual: string | null;
            paymentReceivedDate: string | null;
            statusCheckLaterDate: string | null;
            statusClosedDate: string | null;
            updatedAt: any;
            assignedUserId: UserId | null;
            phone: PhoneNumberE164 | null;
            cellPhone: PhoneNumberE164 | null;
            email: string | null;
            additionalClientName: string | null;
            chosenCommunity: { id: CommunityId; name: string | null } | null;
            nextTask: {
                id: TaskId;
                text: string | null;
                status: string;
                dueDate: string | null;
                dueDateTime: any;
            } | null;
            allContacts: Array<{
                name: string | null;
                email1: string | null;
                phone1: PhoneNumberE164 | null;
                cellPhone: PhoneNumberE164 | null;
                relationship: string | null;
            }>;
        }>;
    };
    fourOrMoreWeeks: {
        total: number;
        clients: Array<{
            id: PlacementId;
            serial: number | null;
            name: string | null;
            statusListItemId: CustomListItemId;
            bestContactName: string | null;
            bestContactPhone: string | null;
            dateAdded: any;
            invoiceSentDate: string | null;
            moveTimeline: string | null;
            moveTimelineActual: string | null;
            paymentReceivedDate: string | null;
            statusCheckLaterDate: string | null;
            statusClosedDate: string | null;
            updatedAt: any;
            assignedUserId: UserId | null;
            phone: PhoneNumberE164 | null;
            cellPhone: PhoneNumberE164 | null;
            email: string | null;
            additionalClientName: string | null;
            chosenCommunity: { id: CommunityId; name: string | null } | null;
            nextTask: {
                id: TaskId;
                text: string | null;
                status: string;
                dueDate: string | null;
                dueDateTime: any;
            } | null;
            allContacts: Array<{
                name: string | null;
                email1: string | null;
                phone1: PhoneNumberE164 | null;
                cellPhone: PhoneNumberE164 | null;
                relationship: string | null;
            }>;
        }>;
    };
    unscheduled: {
        total: number;
        clients: Array<{
            id: PlacementId;
            serial: number | null;
            name: string | null;
            statusListItemId: CustomListItemId;
            bestContactName: string | null;
            bestContactPhone: string | null;
            dateAdded: any;
            invoiceSentDate: string | null;
            moveTimeline: string | null;
            moveTimelineActual: string | null;
            paymentReceivedDate: string | null;
            statusCheckLaterDate: string | null;
            statusClosedDate: string | null;
            updatedAt: any;
            assignedUserId: UserId | null;
            phone: PhoneNumberE164 | null;
            cellPhone: PhoneNumberE164 | null;
            email: string | null;
            additionalClientName: string | null;
            chosenCommunity: { id: CommunityId; name: string | null } | null;
            nextTask: {
                id: TaskId;
                text: string | null;
                status: string;
                dueDate: string | null;
                dueDateTime: any;
            } | null;
            allContacts: Array<{
                name: string | null;
                email1: string | null;
                phone1: PhoneNumberE164 | null;
                cellPhone: PhoneNumberE164 | null;
                relationship: string | null;
            }>;
        }>;
    };
};

export type GetClientsForDetailsQueryVariables = Exact<{
    regionId: RegionId;
    searchString?: string | null | undefined;
    assignedUserId?: UserId | null | undefined;
    assignedReferralUserId?: UserId | null | undefined;
    collaboratorUserId?: UserId | null | undefined;
    searchType?: string | null | undefined;
    openClosed?: string | null | undefined;
    page: number;
    perPage: number;
    includeStatuses?: Array<string> | string | null | undefined;
    excludeStatuses?: Array<string> | string | null | undefined;
    statusAtOrLater?: string | null | undefined;
    status?: string | null | undefined;
    sort: Types.ClientSearchSort;
}>;

export type GetClientsForDetailsQuery = {
    getClients: {
        total: number;
        clients: Array<{
            id: PlacementId;
            serial: number | null;
            name: string | null;
            statusListItemId: CustomListItemId;
            bestContactName: string | null;
            bestContactPhone: string | null;
            dateAdded: any;
            invoiceSentDate: string | null;
            moveTimeline: string | null;
            moveTimelineActual: string | null;
            paymentReceivedDate: string | null;
            statusCheckLaterDate: string | null;
            statusClosedDate: string | null;
            updatedAt: any;
            assignedUserId: UserId | null;
            phone: PhoneNumberE164 | null;
            cellPhone: PhoneNumberE164 | null;
            email: string | null;
            additionalClientName: string | null;
            chosenCommunity: { id: CommunityId; name: string | null } | null;
            nextTask: {
                id: TaskId;
                text: string | null;
                status: string;
                dueDate: string | null;
                dueDateTime: any;
            } | null;
            allContacts: Array<{
                name: string | null;
                email1: string | null;
                phone1: PhoneNumberE164 | null;
                cellPhone: PhoneNumberE164 | null;
                relationship: string | null;
            }>;
        }>;
    };
};

export type SearchClientsQueryVariables = Exact<{
    regionId: RegionId;
    searchString?: string | null | undefined;
    assignedUserId?: UserId | null | undefined;
    assignedReferralUserId?: UserId | null | undefined;
    searchType?: string | null | undefined;
    openClosed?: string | null | undefined;
    referralCommunityId?: CommunityId | null | undefined;
    referralContactId?: ReferenceContactId | null | undefined;
    referralBusinessId?: ReferenceBusinessId | null | undefined;
    relatedTo?: Array<PlacementId> | PlacementId | null | undefined;
    page: number;
    perPage: number;
}>;

export type SearchClientsQuery = {
    getClients: {
        total: number;
        clients: Array<{
            id: PlacementId;
            serial: number | null;
            name: string | null;
            statusListItemId: CustomListItemId;
            bestContactName: string | null;
            bestContactPhone: string | null;
            dateAdded: any;
            invoiceSentDate: string | null;
            moveTimeline: string | null;
            moveTimelineActual: string | null;
            paymentReceivedDate: string | null;
            statusCheckLaterDate: string | null;
            statusClosedDate: string | null;
            updatedAt: any;
            assignedUserId: UserId | null;
            phone: PhoneNumberE164 | null;
            cellPhone: PhoneNumberE164 | null;
            email: string | null;
            additionalClientName: string | null;
            chosenCommunity: { id: CommunityId; name: string | null } | null;
            nextTask: {
                id: TaskId;
                text: string | null;
                status: string;
                dueDate: string | null;
                dueDateTime: any;
            } | null;
            allContacts: Array<{
                name: string | null;
                email1: string | null;
                phone1: PhoneNumberE164 | null;
                cellPhone: PhoneNumberE164 | null;
                relationship: string | null;
            }>;
        }>;
    };
};

export type GetClientReferralDetailsQueryVariables = Exact<{
    id: PlacementId;
}>;

export type GetClientReferralDetailsQuery = {
    getClient: {
        bestContactName: string | null;
        bestContactPhone: string | null;
        address: string | null;
        city: string | null;
        state: string | null;
        zip: string | null;
    };
};

export type GetOutboundClientReferralsQueryVariables = Exact<{
    id: PlacementId;
}>;

export type GetOutboundClientReferralsQuery = {
    getClientReferrals: Array<{
        id: ReferralId;
        placementId: PlacementId;
        referenceBusinessId: ReferenceBusinessId | null;
        referenceContactId: ReferenceContactId | null;
        communityId: CommunityId | null;
        referralType: string;
        notes: string | null;
        when: any;
        dateAdded: any;
        createdBy: UserId;
        updatedAt: any;
        updatedBy: UserId;
        referenceBusiness: {
            id: ReferenceBusinessId;
            businessContactEntity: { name: string | null } | null;
        } | null;
        referenceContact: {
            id: ReferenceContactId;
            contactEntity: { name: string | null } | null;
        } | null;
        community: { id: CommunityId; name: string | null } | null;
    }>;
};

export type ReferralResponseFragment = {
    id: ReferralId;
    placementId: PlacementId;
    referenceBusinessId: ReferenceBusinessId | null;
    referenceContactId: ReferenceContactId | null;
    communityId: CommunityId | null;
    referralType: string;
    notes: string | null;
    when: any;
    dateAdded: any;
    createdBy: UserId;
    updatedAt: any;
    updatedBy: UserId;
};

export type CreateClientReferralMutationVariables = Exact<{
    clientId: PlacementId;
    referralType: string;
}>;

export type CreateClientReferralMutation = {
    createClientReferral: {
        id: ReferralId;
        placementId: PlacementId;
        referenceBusinessId: ReferenceBusinessId | null;
        referenceContactId: ReferenceContactId | null;
        communityId: CommunityId | null;
        referralType: string;
        notes: string | null;
        when: any;
        dateAdded: any;
        createdBy: UserId;
        updatedAt: any;
        updatedBy: UserId;
    };
};

export type PatchClientReferralMutationVariables = Exact<{
    id: ReferralId;
    clientId: PlacementId;
    notes?: string | null | undefined;
    when?: any;
    deleted?: boolean | null | undefined;
    referenceBusinessId?: ReferenceBusinessId | null | undefined;
    referenceContactId?: ReferenceContactId | null | undefined;
    communityId?: CommunityId | null | undefined;
}>;

export type PatchClientReferralMutation = {
    patchClientReferral: {
        id: ReferralId;
        placementId: PlacementId;
        referenceBusinessId: ReferenceBusinessId | null;
        referenceContactId: ReferenceContactId | null;
        communityId: CommunityId | null;
        referralType: string;
        notes: string | null;
        when: any;
        dateAdded: any;
        createdBy: UserId;
        updatedAt: any;
        updatedBy: UserId;
    };
};

export type GetClientForIntakeEmailQueryVariables = Exact<{
    clientId: PlacementId;
}>;

export type GetClientForIntakeEmailQuery = {
    getClient: {
        name: string | null;
        clientCommunities: Array<{
            relationship: number;
            referred: boolean;
            community: {
                id: CommunityId;
                name: string | null;
                email: string | null;
                emailOptOut: boolean;
                isConnectedToEvergreen: boolean;
                availability: {
                    status: Types.EvergreenCommunityListingAvailability;
                    notes: string | null;
                    freshness: Types.ObservationFreshness;
                    lastUpdatedAt: any;
                } | null;
                communityContacts: Array<{
                    order: number;
                    primary: boolean;
                    contact: {
                        name: string | null;
                        email1: string | null;
                        email1OptOut: boolean;
                    };
                }>;
            };
        }>;
    };
};

export type DetachAdditionalClientMutationVariables = Exact<{
    clientId: PlacementId;
}>;

export type DetachAdditionalClientMutation = { detachAdditionalClient: PlacementId };

export type GetCommunityQueryVariables = Exact<{
    id: CommunityId;
}>;

export type GetCommunityQuery = {
    getCommunity: {
        address: string | null;
        caregiverRatio: number | null;
        city: string | null;
        contractStatus: string | null;
        contractExpiresDate: any;
        contractIssuedDate: any;
        contractNotes: string | null;
        dateAdded: any;
        gender: string | null;
        feeType: string | null;
        paymentFee: string | null;
        paymentPercentage: string | null;
        id: CommunityId;
        latitude: string | null;
        licenseStatus: string | null;
        longitude: string | null;
        mainPhone: PhoneNumberE164 | null;
        monthlyPrice: number | null;
        moveInDeposit: number | null;
        name: string | null;
        petDeposit: string | null;
        priceDetails: string | null;
        priceHighEnd: number | null;
        rating: number | null;
        secondPersonFee: string | null;
        state: string | null;
        summary: string | null;
        thumbnailUrl: string | null;
        unitsOrCapacity: number | null;
        updatedAt: any;
        virtualTourURL: string | null;
        website: string | null;
        zip: string | null;
        regionId: RegionId;
        email: string | null;
        county: string | null;
        invoices: Array<{
            id: InvoiceId;
            amountCents: CurrencyInCents;
            paidDate: string | null;
            sentDate: string | null;
            dueDate: string | null;
            serial: string;
            status: string;
            placementId: PlacementId;
            communityId: CommunityId | null;
            fileId: FileId | null;
            clientName: string | null;
            notes: string | null;
            externalReference: string | null;
            remainingBalanceCents: number;
            totalPaidCents: number;
            hasOutstandingBalance: boolean;
            isExternallyManaged: boolean;
            lineItems: Array<{
                id: InvoiceLineItemId;
                description: string;
                quantity: number;
                unitPriceInCents: CurrencyInCents;
            }> | null;
            paymentSchedule: {
                items: Array<{
                    id: InvoicePaymentScheduleItemId;
                    amountInCents: CurrencyInCents;
                    dueDate: string;
                    paidDate: string | null;
                    status: Types.InvoicePaymentScheduleItemStatus;
                }>;
            } | null;
        }> | null;
        licenses: Array<{
            href: string | null;
            id: CommunityLicenseId;
            licenseEndDate: string | null;
            licenseNumber: string | null;
            licenseStartDate: string | null;
            licensee: string | null;
            status: string | null;
            communityId: CommunityId;
            tenantId: TenantId;
        }>;
        contacts: Array<{
            cellPhone: PhoneNumberE164 | null;
            name: string | null;
            phone1: PhoneNumberE164 | null;
            role: string | null;
            email1: string | null;
        }>;
        primaryContacts: Array<{
            cellPhone: PhoneNumberE164 | null;
            name: string | null;
            phone1: PhoneNumberE164 | null;
            role: string | null;
            email1: string | null;
        }>;
    };
};

export type GetCommunityAdvancedQueryVariables = Exact<{
    id: CommunityId;
}>;

export type GetCommunityAdvancedQuery = {
    getCommunity: {
        id: CommunityId;
        communityMasterId: CommunityMasterId;
        communityMasterVersion: number | null;
        evergreenCommunityId: EvergreenCommunityId;
        matchInfo: {
            matchRule: string | null;
            matchScore: number | null;
            originalMatchDate: string | null;
            evergreenLicenseId: InternalId | null;
            evergreenCommunityAliasId: InternalId | null;
        };
    };
};

export type DeleteCommunityLicenseMutationVariables = Exact<{
    communityId: CommunityId;
    licenseId: CommunityLicenseId;
}>;

export type DeleteCommunityLicenseMutation = { deleteCommunityLicense: boolean };

export type UpdateCommunityLicenseMutationVariables = Exact<{
    licenseId: CommunityLicenseId;
    license: Types.CommunityLicenseInput;
}>;

export type UpdateCommunityLicenseMutation = { updateCommunityLicense: boolean };

export type CreateCommunityLicenseMutationVariables = Exact<{
    communityId: CommunityId;
}>;

export type CreateCommunityLicenseMutation = {
    createCommunityLicense: { id: CommunityLicenseId };
};

export type GetReferralsToCommunityQueryVariables = Exact<{
    communityId: CommunityId;
}>;

export type GetReferralsToCommunityQuery = {
    getReferralsToCommunity: Array<{
        id: ReferralId;
        placementId: PlacementId;
        placement: {
            id: PlacementId;
            name: string | null;
            bestContactName: string | null;
            statusListItemId: CustomListItemId;
            dateAdded: any;
            serial: number | null;
            assignedUserId: UserId | null;
            updatedAt: any;
            moveTimeline: string | null;
            moveTimelineActual: string | null;
            nextTask: {
                id: TaskId;
                text: string | null;
                dueDate: string | null;
                dueDateTime: any;
                compositeDueDateTime: any;
                status: string;
            } | null;
        } | null;
    }>;
};

export type FindOrCreateCommunityInsightsMutationVariables = Exact<{
    communityId: CommunityId;
}>;

export type FindOrCreateCommunityInsightsMutation = {
    findOrCreateCommunityInsights: {
        id: EvergreenCommunityListingId;
        evergreenCommunityId: EvergreenCommunityId;
        name: string;
        address: string;
        city: string;
        state: string;
        zip: string;
        googleMapsUrl: string | null;
        website: string | null;
        averageRating: number;
        ratingCount: number;
        lastGooglePlaceSynchronizedAt: IsoTimestamp | null;
        reviews: Array<{
            id: EvergreenReviewId;
            text: string;
            rating: number;
            authorDisplayName: string;
            authorPhotoUri: string | null;
            publishedAt: IsoTimestamp;
            source: Types.EvergreenReviewSource;
            webUri: string;
        }>;
        phoneNumbers: Array<{ number: string; label: string }> | null;
        availability: {
            status: Types.EvergreenCommunityListingAvailability;
            lastUpdatedAt: IsoTimestamp | null;
            notes: string | null;
            lastRequestedAt: IsoTimestamp | null;
            freshness: Types.ObservationFreshness;
        };
        pricing: {
            minPrice: string | null;
            maxPrice: string | null;
            notes: string;
            lastUpdatedAt: IsoTimestamp | null;
            freshness: Types.ObservationFreshness;
            files: Array<{
                id: FileId;
                name: string;
                size: number | null;
                mimeType: string;
                downloadUrl: string;
            }> | null;
        } | null;
    } | null;
};

export type FindOrCreateCommunityInsightsManyMutationVariables = Exact<{
    communityIds: Array<CommunityId> | CommunityId;
}>;

export type FindOrCreateCommunityInsightsManyMutation = {
    findOrCreateCommunityInsightsMany: Array<{
        id: EvergreenCommunityListingId;
        evergreenCommunityId: EvergreenCommunityId;
        name: string;
        address: string;
        city: string;
        state: string;
        zip: string;
        googleMapsUrl: string | null;
        website: string | null;
        averageRating: number;
        ratingCount: number;
        lastGooglePlaceSynchronizedAt: IsoTimestamp | null;
        phoneNumbers: Array<{ number: string; label: string }> | null;
        availability: {
            status: Types.EvergreenCommunityListingAvailability;
            lastUpdatedAt: IsoTimestamp | null;
            notes: string | null;
            lastRequestedAt: IsoTimestamp | null;
            freshness: Types.ObservationFreshness;
        };
        pricing: {
            minPrice: string | null;
            maxPrice: string | null;
            notes: string;
            lastUpdatedAt: IsoTimestamp | null;
            freshness: Types.ObservationFreshness;
            files: Array<{
                id: FileId;
                name: string;
                size: number | null;
                mimeType: string;
                downloadUrl: string;
            }> | null;
        } | null;
    }> | null;
};

export type RestoreCommunityMutationVariables = Exact<{
    communityId: CommunityId;
}>;

export type RestoreCommunityMutation = { restoreCommunity: boolean };

export type CommunityInsightsFragmentFragment = {
    id: EvergreenCommunityListingId;
    evergreenCommunityId: EvergreenCommunityId;
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    googleMapsUrl: string | null;
    website: string | null;
    averageRating: number;
    ratingCount: number;
    lastGooglePlaceSynchronizedAt: IsoTimestamp | null;
    reviews: Array<{
        id: EvergreenReviewId;
        text: string;
        rating: number;
        authorDisplayName: string;
        authorPhotoUri: string | null;
        publishedAt: IsoTimestamp;
        source: Types.EvergreenReviewSource;
        webUri: string;
    }>;
    phoneNumbers: Array<{ number: string; label: string }> | null;
    availability: {
        status: Types.EvergreenCommunityListingAvailability;
        lastUpdatedAt: IsoTimestamp | null;
        notes: string | null;
        lastRequestedAt: IsoTimestamp | null;
        freshness: Types.ObservationFreshness;
    };
    pricing: {
        minPrice: string | null;
        maxPrice: string | null;
        notes: string;
        lastUpdatedAt: IsoTimestamp | null;
        freshness: Types.ObservationFreshness;
        files: Array<{
            id: FileId;
            name: string;
            size: number | null;
            mimeType: string;
            downloadUrl: string;
        }> | null;
    } | null;
};

export type CommunityInsightsFragmentLiteFragment = {
    id: EvergreenCommunityListingId;
    evergreenCommunityId: EvergreenCommunityId;
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    googleMapsUrl: string | null;
    website: string | null;
    averageRating: number;
    ratingCount: number;
    lastGooglePlaceSynchronizedAt: IsoTimestamp | null;
    phoneNumbers: Array<{ number: string; label: string }> | null;
    availability: {
        status: Types.EvergreenCommunityListingAvailability;
        lastUpdatedAt: IsoTimestamp | null;
        notes: string | null;
        lastRequestedAt: IsoTimestamp | null;
        freshness: Types.ObservationFreshness;
    };
    pricing: {
        minPrice: string | null;
        maxPrice: string | null;
        notes: string;
        lastUpdatedAt: IsoTimestamp | null;
        freshness: Types.ObservationFreshness;
        files: Array<{
            id: FileId;
            name: string;
            size: number | null;
            mimeType: string;
            downloadUrl: string;
        }> | null;
    } | null;
};

export type RequestCommunityListingParticipationMutationVariables = Exact<{
    communityId: CommunityId;
    outboundMessage: Types.OutboundMessage;
    observationFocus?: Types.ObservationKind | null | undefined;
}>;

export type RequestCommunityListingParticipationMutation = {
    requestCommunityListingParticipation: {
        status: string;
        accepted: Array<string>;
        rejected: Array<string>;
        code: string | null;
        sentMessage: { id: SentMessageId } | null;
    };
};

export type RequestCommunityListingUpdateMutationVariables = Exact<{
    communityId: CommunityId;
    message: Types.PredeterminedRecipientMessage;
    observationFocus: Types.ObservationKind;
}>;

export type RequestCommunityListingUpdateMutation = {
    requestCommunityListingUpdate: {
        status: string;
        accepted: Array<string>;
        rejected: Array<string>;
        code: string | null;
        sentMessage: { id: SentMessageId } | null;
    };
};

export type CommunityBasicsFragment = {
    address: string | null;
    caregiverRatio: number | null;
    city: string | null;
    contractStatus: string | null;
    contractExpiresDate: any;
    contractIssuedDate: any;
    contractNotes: string | null;
    dateAdded: any;
    gender: string | null;
    feeType: string | null;
    paymentFee: string | null;
    paymentPercentage: string | null;
    id: CommunityId;
    latitude: string | null;
    licenseStatus: string | null;
    longitude: string | null;
    mainPhone: PhoneNumberE164 | null;
    monthlyPrice: number | null;
    moveInDeposit: number | null;
    name: string | null;
    petDeposit: string | null;
    priceDetails: string | null;
    priceHighEnd: number | null;
    rating: number | null;
    secondPersonFee: string | null;
    state: string | null;
    summary: string | null;
    thumbnailUrl: string | null;
    unitsOrCapacity: number | null;
    updatedAt: any;
    virtualTourURL: string | null;
    website: string | null;
    zip: string | null;
    regionId: RegionId;
    email: string | null;
    county: string | null;
    contacts: Array<{
        cellPhone: PhoneNumberE164 | null;
        name: string | null;
        phone1: PhoneNumberE164 | null;
        role: string | null;
        email1: string | null;
    }>;
    primaryContacts: Array<{
        cellPhone: PhoneNumberE164 | null;
        name: string | null;
        phone1: PhoneNumberE164 | null;
        role: string | null;
        email1: string | null;
    }>;
};

export type CommunityCardBasicsFragment = {
    isConnectedToEvergreen: boolean;
    address: string | null;
    caregiverRatio: number | null;
    city: string | null;
    contractStatus: string | null;
    contractExpiresDate: any;
    contractIssuedDate: any;
    contractNotes: string | null;
    dateAdded: any;
    gender: string | null;
    feeType: string | null;
    paymentFee: string | null;
    paymentPercentage: string | null;
    id: CommunityId;
    latitude: string | null;
    licenseStatus: string | null;
    longitude: string | null;
    mainPhone: PhoneNumberE164 | null;
    monthlyPrice: number | null;
    moveInDeposit: number | null;
    name: string | null;
    petDeposit: string | null;
    priceDetails: string | null;
    priceHighEnd: number | null;
    rating: number | null;
    secondPersonFee: string | null;
    state: string | null;
    summary: string | null;
    thumbnailUrl: string | null;
    unitsOrCapacity: number | null;
    updatedAt: any;
    virtualTourURL: string | null;
    website: string | null;
    zip: string | null;
    regionId: RegionId;
    email: string | null;
    county: string | null;
    availability: {
        status: Types.EvergreenCommunityListingAvailability;
        notes: string | null;
        freshness: Types.ObservationFreshness;
        lastUpdatedAt: any;
    } | null;
    pricing: {
        minPrice: string | null;
        maxPrice: string | null;
        notes: string | null;
        files: Array<{
            id: FileId;
            name: string;
            mimeType: string;
            size: number | null;
            downloadUrl: string;
        }> | null;
    } | null;
    answers: Array<{
        questionId: QuestionId | null;
        booleanAnswer: boolean | null;
        numberAnswer: number | null;
        numberAnswerHigh: number | null;
        textAnswer: string | null;
        dateAnswer: string | null;
        calendarDateAnswer: string | null;
        selections: Array<string> | null;
        idReferenceAnswer: UserId | null;
        selectionAnnotations: Array<{
            text: string | null;
            selectionId: string | null;
        }> | null;
    }>;
    photoEntities: Array<{
        id: PhotoId;
        entityId: FileEntityId;
        order: number;
        dateAdded: any;
        createdBy: UserId;
        originalFile: {
            id: FileId;
            entityId: FileEntityId;
            originalFilename: string | null;
            mimeType: string | null;
            description: string | null;
            byteLength: number | null;
            shareWithClient: boolean;
            shareWithCommunity: boolean;
            shareWithReferral: boolean;
            shareWithHomeCareLead: boolean;
            createdAt: any;
            createdBy: UserId | null;
            updatedAt: any;
            updatedBy: UserId | null;
            hasSignatureTemplate: boolean;
            isGlobal: boolean;
            managementLevel: string;
            regionVisibility: {
                editable: boolean;
                regions: Array<{
                    mode: Types.EntityRegionModeOutput;
                    regionId: RegionId;
                }>;
            } | null;
        };
    }>;
    licenses: Array<{
        id: CommunityLicenseId;
        licenseNumber: string | null;
        href: string | null;
        licensee: string | null;
        status: string | null;
        licenseStartDate: string | null;
        licenseEndDate: string | null;
    }>;
    googleInfo: { rating: number | null; ratingCount: number | null } | null;
    contacts: Array<{
        cellPhone: PhoneNumberE164 | null;
        name: string | null;
        phone1: PhoneNumberE164 | null;
        role: string | null;
        email1: string | null;
    }>;
    primaryContacts: Array<{
        cellPhone: PhoneNumberE164 | null;
        name: string | null;
        phone1: PhoneNumberE164 | null;
        role: string | null;
        email1: string | null;
    }>;
};

export type IdentityDefaultsFragment = {
    id: IdentityId;
    name: string | null;
    successorId: IdentityId | null;
    propagateableLicenseTypes: Array<string> | null;
    thumbnailUrl: string | null;
};

export type LicenseDefaultsFragment = {
    status: string;
    jurisdiction: string;
    licenseNumber: string;
    id: LicenseId;
    licenseType: Array<string> | null;
};

export type PublicRecordDefaultsFragment = {
    id: PublicRecordId | null;
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    capacity: number;
    licenseType: Array<string>;
    licenseNumber: string;
    licensee: string;
    closedDate: string;
    licenseDate: string;
    licenseStatus: string;
    county: string;
    country: string;
    phone: string;
    administrator: string;
    email: string;
    lat: string | null;
    lng: string | null;
};

export type GetLicenseQueryVariables = Exact<{
    id: LicenseId;
}>;

export type GetLicenseQuery = {
    getLicense: {
        status: string;
        jurisdiction: string;
        licenseNumber: string;
        id: LicenseId;
        licenseType: Array<string> | null;
        publicRecords: Array<{
            id: PublicRecordId | null;
            name: string;
            address: string;
            city: string;
            state: string;
            zip: string;
            capacity: number;
            licenseType: Array<string>;
            licenseNumber: string;
            licensee: string;
            closedDate: string;
            licenseDate: string;
            licenseStatus: string;
            county: string;
            country: string;
            phone: string;
            administrator: string;
            email: string;
            lat: string | null;
            lng: string | null;
        }> | null;
        identity: {
            id: IdentityId;
            name: string | null;
            successorId: IdentityId | null;
            propagateableLicenseTypes: Array<string> | null;
            thumbnailUrl: string | null;
        } | null;
    } | null;
};

export type GetIdentityQueryVariables = Exact<{
    id: IdentityId;
}>;

export type GetIdentityQuery = {
    getIdentity: {
        id: IdentityId;
        name: string | null;
        successorId: IdentityId | null;
        propagateableLicenseTypes: Array<string> | null;
        thumbnailUrl: string | null;
        licenses: Array<{
            status: string;
            jurisdiction: string;
            licenseNumber: string;
            id: LicenseId;
            licenseType: Array<string> | null;
            publicRecords: Array<{
                id: PublicRecordId | null;
                name: string;
                address: string;
                city: string;
                state: string;
                zip: string;
                capacity: number;
                licenseType: Array<string>;
                licenseNumber: string;
                licensee: string;
                closedDate: string;
                licenseDate: string;
                licenseStatus: string;
                county: string;
                country: string;
                phone: string;
                administrator: string;
                email: string;
                lat: string | null;
                lng: string | null;
                publicRecordContacts: Array<{
                    address1: string | null;
                    address2: string | null;
                    cellPhone: string | null;
                    city: string | null;
                    contactNotes: string | null;
                    email1: string | null;
                    fax: string | null;
                    id: PublicRecordContactId | null;
                    name: string | null;
                    phone1: string | null;
                    relationship: string | null;
                    role: string | null;
                    state: string | null;
                    website: string | null;
                    zip: string | null;
                }> | null;
            }> | null;
        }> | null;
    } | null;
};

export type PublicRecordSearchQueryVariables = Exact<{
    params: Types.PublicRecordSearchRequest;
}>;

export type PublicRecordSearchQuery = {
    publicRecordSearch: {
        count: number;
        records: Array<{
            id: PublicRecordId | null;
            name: string;
            address: string;
            city: string;
            state: string;
            zip: string;
            capacity: number;
            licenseType: Array<string>;
            licenseNumber: string;
            licensee: string;
            closedDate: string;
            licenseDate: string;
            licenseStatus: string;
            county: string;
            country: string;
            phone: string;
            administrator: string;
            email: string;
            lat: string | null;
            lng: string | null;
            license: {
                status: string;
                jurisdiction: string;
                licenseNumber: string;
                id: LicenseId;
                licenseType: Array<string> | null;
                identity: {
                    id: IdentityId;
                    name: string | null;
                    successorId: IdentityId | null;
                    propagateableLicenseTypes: Array<string> | null;
                    thumbnailUrl: string | null;
                } | null;
            } | null;
        }>;
    };
};

export type GetEvergreenLicenseTypesQueryVariables = Exact<{ [key: string]: never }>;

export type GetEvergreenLicenseTypesQuery = {
    getEvergreenLicenseTypes: Array<{
        id: EvergreenLicenseTypeId;
        state: string;
        licenseType: string;
    }>;
};

export type UpdateEvergreenLicenseTypeMutationVariables = Exact<{
    input: Types.EvergreenLicenseTypeInput;
}>;

export type UpdateEvergreenLicenseTypeMutation = {
    updateEvergreenLicenseType: { id: EvergreenLicenseTypeId };
};

export type DeleteEvergreenLicenseTypeMutationVariables = Exact<{
    id: EvergreenLicenseTypeId;
}>;

export type DeleteEvergreenLicenseTypeMutation = { deleteEvergreenLicenseType: boolean };

export type CommunityNamesQueryVariables = Exact<{
    regionKey: string;
    ids?: Array<CommunityId> | CommunityId | null | undefined;
}>;

export type CommunityNamesQuery = {
    communityNames: { communities: Array<{ id: CommunityId; name: string | null }> };
};

export type CommunitySearchQueryVariables = Exact<{
    params: Types.CommunitySearchRequest;
}>;

export type CommunitySearchQuery = {
    communitySearch: {
        total: number;
        coordinates: { lat: string; lng: string } | null;
        hits: Array<{
            address: string | null;
            contractStatus: string | null;
            contractExpiresDate: any;
            contractIssuedDate: any;
            contractNotes: string | null;
            paymentFee: string | null;
            paymentPercentage: string | null;
            gender: string | null;
            id: CommunityId;
            lat: string | null;
            licenseStatus: string | null;
            lng: string | null;
            mostRecentPlacement: string | null;
            name: string | null;
            phone: PhoneNumberE164 | null;
            placements: number;
            rating: number | null;
            summary: string | null;
            thumbnail: string | null;
            url: string | null;
            unitsOrCapacity: number | null;
            showInSearchResults: boolean;
            monthlyPrice: string | null;
            priceHighEnd: string | null;
            zip: string | null;
            state: string | null;
            city: string | null;
            mainPhone: PhoneNumberE164 | null;
            feeType: string | null;
            secondPersonFee: string | null;
            petDeposit: string | null;
            moveInDeposit: string | null;
            priceDetails: string | null;
            caregiverRatio: number | null;
            updatedAt: any;
            dateAdded: any;
            regionId: RegionId | null;
            referralCount: number;
            email: string | null;
            county: string | null;
            isConnectedToEvergreen: boolean;
            matchedAliases: Array<string>;
            evergreenAliases: Array<string>;
            deleted: boolean;
            pricing: {
                minPrice: string | null;
                maxPrice: string | null;
                notes: string | null;
                files: Array<{
                    id: FileId;
                    name: string;
                    mimeType: string;
                    size: number | null;
                    downloadUrl: string;
                }> | null;
            } | null;
            availability: {
                status: Types.EvergreenCommunityListingAvailability;
                notes: string | null;
                freshness: Types.ObservationFreshness;
                lastUpdatedAt: any;
            } | null;
            contacts: Array<{
                cellPhone: PhoneNumberE164 | null;
                name: string | null;
                phone1: PhoneNumberE164 | null;
                email1: string | null;
                role: string | null;
            }>;
            primaryContacts: Array<{
                cellPhone: PhoneNumberE164 | null;
                name: string | null;
                phone1: PhoneNumberE164 | null;
                email1: string | null;
                role: string | null;
            }>;
            answers: Array<{
                questionId: QuestionId | null;
                booleanAnswer: boolean | null;
                numberAnswer: number | null;
                numberAnswerHigh: number | null;
                textAnswer: string | null;
                dateAnswer: string | null;
                calendarDateAnswer: string | null;
                selections: Array<string> | null;
                idReferenceAnswer: UserId | null;
                selectionAnnotations: Array<{
                    text: string | null;
                    selectionId: string | null;
                }> | null;
            }>;
            licenses: Array<{
                id: CommunityLicenseId;
                licenseNumber: string | null;
                href: string | null;
                licensee: string | null;
                status: string | null;
                licenseStartDate: string | null;
                licenseEndDate: string | null;
            }> | null;
            googleInfo: { rating: number | null; ratingCount: number | null } | null;
        }>;
        deletedHits: Array<{
            address: string | null;
            id: CommunityId;
            lat: string | null;
            lng: string | null;
            name: string | null;
            phone: PhoneNumberE164 | null;
            rating: number | null;
            summary: string | null;
            thumbnail: string | null;
            url: string | null;
            zip: string | null;
            state: string | null;
            city: string | null;
            mainPhone: PhoneNumberE164 | null;
            updatedAt: any;
            dateAdded: any;
            regionId: RegionId | null;
            email: string | null;
            county: string | null;
            deleted: boolean;
        }>;
    };
};

export type CommunitySearchMapViewQueryVariables = Exact<{
    searchParams: Types.CommunitySearchRequest;
    mapView: Types.CommunitySearchRequestMapComponent;
}>;

export type CommunitySearchMapViewQuery = {
    communitySearchMapView: {
        recommendedZoom: number;
        coordinates: { lat: string; lng: string } | null;
        communityCoordinates: Array<{
            lat: number;
            lng: number;
            name: string | null;
            contractStatus: string | null;
            id: CommunityId;
            appLink: string;
        }>;
        communityClusters: Array<{ lat: number; lng: number; count: number }>;
    };
};

export type GetCommunityContactsQueryVariables = Exact<{
    communityId: CommunityId;
    regionId: RegionId;
}>;

export type GetCommunityContactsQuery = {
    getCommunityContacts: Array<{
        contactId: ContactId;
        communityId: CommunityId;
        primary: boolean;
        contact: {
            id: ContactId | null;
            name: string | null;
            preferredName: string | null;
            address1: string | null;
            address2: string | null;
            city: string | null;
            state: string | null;
            zip: string | null;
            relationship: string | null;
            role: string | null;
            cellPhone: PhoneNumberE164 | null;
            phone1: PhoneNumberE164 | null;
            email1: string | null;
            email1OptOut: boolean;
            website: string | null;
            contactType: string | null;
            type: string | null;
            fax: PhoneNumberE164 | null;
            contactNotes: string | null;
            dateAdded: any;
        };
        referenceContact: {
            id: ReferenceContactId;
            summary: string | null;
            regionId: RegionId;
        } | null;
    }>;
};

export type PromoteCommunityContactMutationVariables = Exact<{
    communityId: CommunityId;
    contactId: ContactId;
    regionId: RegionId;
}>;

export type PromoteCommunityContactMutation = {
    promoteCommunityContact: {
        contactId: ContactId;
        communityId: CommunityId;
        primary: boolean;
        contact: {
            id: ContactId | null;
            name: string | null;
            preferredName: string | null;
            address1: string | null;
            address2: string | null;
            city: string | null;
            state: string | null;
            zip: string | null;
            relationship: string | null;
            role: string | null;
            cellPhone: PhoneNumberE164 | null;
            phone1: PhoneNumberE164 | null;
            email1: string | null;
            email1OptOut: boolean;
            website: string | null;
            contactType: string | null;
            type: string | null;
            fax: PhoneNumberE164 | null;
            contactNotes: string | null;
            dateAdded: any;
        };
        referenceContact: {
            id: ReferenceContactId;
            summary: string | null;
            regionId: RegionId;
            ownerId: UserId | null;
            dateAdded: any;
            updatedAt: any;
            updatedBy: UserId;
            createdBy: UserId;
        } | null;
    };
};

export type DeleteCommunityContactMutationVariables = Exact<{
    communityId: CommunityId;
    contactId: ContactId;
}>;

export type DeleteCommunityContactMutation = {
    deleteCommunityContact: { contactId: ContactId; communityId: CommunityId };
};

export type DeleteClientContactMutationVariables = Exact<{
    clientId: PlacementId;
    contactId: ContactId;
    relationship: Types.ClientContactRelationship;
}>;

export type DeleteClientContactMutation = {
    deleteClientContact: { contactId: ContactId; clientId: PlacementId };
};

export type GetClientContactsQueryVariables = Exact<{
    params: Types.GetClientContactsRequest;
}>;

export type GetClientContactsQuery = {
    getClientContacts: Array<{
        contactId: ContactId;
        placementId: PlacementId;
        relationship: Types.ClientContactRelationship;
        powerOfAttorney: Array<string> | null;
        contact: {
            id: ContactId | null;
            name: string | null;
            preferredName: string | null;
            address1: string | null;
            address2: string | null;
            city: string | null;
            state: string | null;
            zip: string | null;
            relationship: string | null;
            role: string | null;
            cellPhone: PhoneNumberE164 | null;
            phone1: PhoneNumberE164 | null;
            email1: string | null;
            email1OptOut: boolean;
            website: string | null;
            contactType: string | null;
            type: string | null;
            fax: PhoneNumberE164 | null;
            contactNotes: string | null;
            dateAdded: any;
            referenceContacts: Array<{ id: ReferenceContactId; appLink: string }>;
        };
    }>;
};

export type ContactFragmentFragment = {
    id: ContactId | null;
    name: string | null;
    preferredName: string | null;
    address1: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    relationship: string | null;
    role: string | null;
    cellPhone: PhoneNumberE164 | null;
    phone1: PhoneNumberE164 | null;
    email1: string | null;
    email1OptOut: boolean;
    website: string | null;
    contactType: string | null;
    type: string | null;
    fax: PhoneNumberE164 | null;
    contactNotes: string | null;
    dateAdded: any;
};

export type ContactWithLinksFragment = {
    id: ContactId | null;
    name: string | null;
    preferredName: string | null;
    address1: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    relationship: string | null;
    role: string | null;
    cellPhone: PhoneNumberE164 | null;
    phone1: PhoneNumberE164 | null;
    email1: string | null;
    email1OptOut: boolean;
    website: string | null;
    contactType: string | null;
    type: string | null;
    fax: PhoneNumberE164 | null;
    contactNotes: string | null;
    dateAdded: any;
    communityContacts: Array<{
        communityId: CommunityId;
        contactId: ContactId;
        primary: boolean;
        community: { id: CommunityId; name: string | null; appLink: string };
    }>;
    clientContacts: Array<{
        placementId: PlacementId;
        contactId: ContactId;
        relationship: Types.ClientContactRelationship;
        powerOfAttorney: Array<string> | null;
        placement: { id: PlacementId; name: string | null; appLink: string };
    }>;
    homeCareLeadContacts: Array<{
        homeCareLeadId: HomeCareLeadId;
        contactId: ContactId;
        relationship: Types.HomeCareLeadContactRelationship;
        powerOfAttorney: Array<string> | null;
        homeCareLead: { id: HomeCareLeadId; appLink: string };
    }>;
    referenceContacts: Array<{
        id: ReferenceContactId;
        summary: string | null;
        regionId: RegionId;
        ownerId: UserId | null;
        dateAdded: any;
        updatedAt: any;
        updatedBy: UserId;
        createdBy: UserId;
        appLink: string;
    }>;
};

export type GetContactQueryVariables = Exact<{
    contactId: ContactId;
}>;

export type GetContactQuery = {
    getContact: {
        id: ContactId | null;
        name: string | null;
        preferredName: string | null;
        address1: string | null;
        address2: string | null;
        city: string | null;
        state: string | null;
        zip: string | null;
        relationship: string | null;
        role: string | null;
        cellPhone: PhoneNumberE164 | null;
        phone1: PhoneNumberE164 | null;
        email1: string | null;
        email1OptOut: boolean;
        website: string | null;
        contactType: string | null;
        type: string | null;
        fax: PhoneNumberE164 | null;
        contactNotes: string | null;
        dateAdded: any;
        communityContacts: Array<{
            communityId: CommunityId;
            contactId: ContactId;
            primary: boolean;
            community: { id: CommunityId; name: string | null; appLink: string };
        }>;
        clientContacts: Array<{
            placementId: PlacementId;
            contactId: ContactId;
            relationship: Types.ClientContactRelationship;
            powerOfAttorney: Array<string> | null;
            placement: { id: PlacementId; name: string | null; appLink: string };
        }>;
        homeCareLeadContacts: Array<{
            homeCareLeadId: HomeCareLeadId;
            contactId: ContactId;
            relationship: Types.HomeCareLeadContactRelationship;
            powerOfAttorney: Array<string> | null;
            homeCareLead: { id: HomeCareLeadId; appLink: string };
        }>;
        referenceContacts: Array<{
            id: ReferenceContactId;
            summary: string | null;
            regionId: RegionId;
            ownerId: UserId | null;
            dateAdded: any;
            updatedAt: any;
            updatedBy: UserId;
            createdBy: UserId;
            appLink: string;
        }>;
    };
};

export type CreateContactMutationVariables = Exact<{
    params: Types.CreateContactInput;
}>;

export type CreateContactMutation = {
    createContact: {
        id: ContactId | null;
        name: string | null;
        preferredName: string | null;
        address1: string | null;
        address2: string | null;
        city: string | null;
        state: string | null;
        zip: string | null;
        relationship: string | null;
        role: string | null;
        cellPhone: PhoneNumberE164 | null;
        phone1: PhoneNumberE164 | null;
        email1: string | null;
        email1OptOut: boolean;
        website: string | null;
        contactType: string | null;
        type: string | null;
        fax: PhoneNumberE164 | null;
        contactNotes: string | null;
        dateAdded: any;
        communityContacts: Array<{
            communityId: CommunityId;
            contactId: ContactId;
            primary: boolean;
            community: { id: CommunityId; name: string | null; appLink: string };
        }>;
        clientContacts: Array<{
            placementId: PlacementId;
            contactId: ContactId;
            relationship: Types.ClientContactRelationship;
            powerOfAttorney: Array<string> | null;
            placement: { id: PlacementId; name: string | null; appLink: string };
        }>;
        homeCareLeadContacts: Array<{
            homeCareLeadId: HomeCareLeadId;
            contactId: ContactId;
            relationship: Types.HomeCareLeadContactRelationship;
            powerOfAttorney: Array<string> | null;
            homeCareLead: { id: HomeCareLeadId; appLink: string };
        }>;
        referenceContacts: Array<{
            id: ReferenceContactId;
            summary: string | null;
            regionId: RegionId;
            ownerId: UserId | null;
            dateAdded: any;
            updatedAt: any;
            updatedBy: UserId;
            createdBy: UserId;
            appLink: string;
        }>;
    };
};

export type PatchContactMutationVariables = Exact<{
    params: Types.PatchContactInput;
}>;

export type PatchContactMutation = {
    patchContact: {
        id: ContactId | null;
        name: string | null;
        preferredName: string | null;
        address1: string | null;
        address2: string | null;
        city: string | null;
        state: string | null;
        zip: string | null;
        relationship: string | null;
        role: string | null;
        cellPhone: PhoneNumberE164 | null;
        phone1: PhoneNumberE164 | null;
        email1: string | null;
        email1OptOut: boolean;
        website: string | null;
        contactType: string | null;
        type: string | null;
        fax: PhoneNumberE164 | null;
        contactNotes: string | null;
        dateAdded: any;
        communityContacts: Array<{
            communityId: CommunityId;
            contactId: ContactId;
            primary: boolean;
            community: { id: CommunityId; name: string | null; appLink: string };
        }>;
        clientContacts: Array<{
            placementId: PlacementId;
            contactId: ContactId;
            relationship: Types.ClientContactRelationship;
            powerOfAttorney: Array<string> | null;
            placement: { id: PlacementId; name: string | null; appLink: string };
        }>;
        homeCareLeadContacts: Array<{
            homeCareLeadId: HomeCareLeadId;
            contactId: ContactId;
            relationship: Types.HomeCareLeadContactRelationship;
            powerOfAttorney: Array<string> | null;
            homeCareLead: { id: HomeCareLeadId; appLink: string };
        }>;
        referenceContacts: Array<{
            id: ReferenceContactId;
            summary: string | null;
            regionId: RegionId;
            ownerId: UserId | null;
            dateAdded: any;
            updatedAt: any;
            updatedBy: UserId;
            createdBy: UserId;
            appLink: string;
        }>;
    };
};

export type GetConversationFeedForEntityQueryVariables = Exact<{
    entityId: EmailEntityId;
    regionId: RegionId;
}>;

export type GetConversationFeedForEntityQuery = {
    getConversationFeedForEntity: {
        threads: Array<{
            id: ConversationThreadId;
            type: Types.ConversationThreadType;
            latestTimestamp: IsoTimestamp;
            ownerUserId: UserId | null;
            smsThread: {
                contact: { id: TelecomContactId; phoneNumber: PhoneNumberE164 };
                phoneLine: {
                    id: TelecomPhoneLineId;
                    userId: UserId;
                    phoneNumber: PhoneNumberE164;
                };
                messages: Array<{
                    id: SmsMessageId;
                    from: PhoneNumberE164;
                    to: PhoneNumberE164;
                    body: string;
                    direction: Types.SmsDirection;
                    dateAdded: IsoTimestamp;
                    status: Types.SmsMessageStatus;
                    createdBy: UserId;
                    createdByUser: { id: UserId; displayName: string };
                }>;
            } | null;
            voiceCall: {
                id: VoiceCallId;
                direction: Types.VoiceCallDirection;
                from: PhoneNumberE164;
                to: PhoneNumberE164;
                dateAdded: IsoTimestamp;
                recordingSid: string | null;
                recordingStatus: string | null;
                recordingDurationSec: number | null;
                transcriptStatus: string | null;
                summary: string | null;
                createdByUser: { id: UserId; displayName: string };
            } | null;
            emailThread: {
                threadId: string | null;
                sentMessages: Array<{
                    id: SentMessageId;
                    messageId: string | null;
                    userId: string | null;
                    from: string | null;
                    subject: string | null;
                    bodyPreview: string | null;
                    status: string;
                    latestEventStatus: string | null;
                    createdAt: any;
                    to: Array<string> | null;
                    cc: Array<string> | null;
                    messageType: string | null;
                    attachments: Array<string> | null;
                    clientIdUuid: PlacementId | null;
                    communityIdUuid: CommunityId | null;
                    clickedLinkEvents: Array<{
                        link: string;
                        events: Array<string>;
                        redacted: boolean | null;
                        eventsV2: Array<{
                            timestamp: string;
                            recipient: string | null;
                        }> | null;
                    }> | null;
                    intakeCommunity: {
                        id: CommunityId;
                        name: string | null;
                        appLink: string;
                    } | null;
                    intakeClient: {
                        id: PlacementId;
                        name: string | null;
                        bestContactName: string | null;
                        appLink: string;
                    } | null;
                    events: Array<{
                        eventType: string;
                        eventTimestamp: any;
                        recipient: string | null;
                    }> | null;
                }>;
                mailboxMessages: Array<{
                    id: UserMailboxMessageId;
                    externalId: string;
                    subject: string;
                    bodyPreview: string;
                    receivedTimestamp: IsoTimestamp;
                    attachmentCount: number;
                    from: {
                        id: UserMailboxContactId;
                        name: string;
                        email: string;
                        resolvedName: string | null;
                    } | null;
                    sender: {
                        id: UserMailboxContactId;
                        name: string;
                        email: string;
                        resolvedName: string | null;
                    } | null;
                    to: Array<{
                        id: UserMailboxMessageRecipientId;
                        contact: {
                            id: UserMailboxContactId;
                            name: string;
                            email: string;
                            resolvedName: string | null;
                        };
                    }>;
                    cc: Array<{
                        id: UserMailboxMessageRecipientId;
                        contact: {
                            id: UserMailboxContactId;
                            name: string;
                            email: string;
                            resolvedName: string | null;
                        };
                    }>;
                    bcc: Array<{
                        id: UserMailboxMessageRecipientId;
                        contact: {
                            id: UserMailboxContactId;
                            name: string;
                            email: string;
                            resolvedName: string | null;
                        };
                    }>;
                    replyTo: Array<{
                        id: UserMailboxMessageRecipientId;
                        contact: {
                            id: UserMailboxContactId;
                            name: string;
                            email: string;
                            resolvedName: string | null;
                        };
                    }>;
                }>;
            } | null;
            contactSearchHits: Array<{
                id: string;
                type: Types.ContactSearchHitType;
                appLink: string;
                fieldMatches: Array<{
                    type: Types.ContactSearchFieldMatchType;
                    phoneNumber: PhoneNumberE164 | null;
                    email: string | null;
                }>;
                client: {
                    id: PlacementId;
                    name: string | null;
                    phone: PhoneNumberE164 | null;
                    cellPhone: PhoneNumberE164 | null;
                } | null;
                community: {
                    id: CommunityId;
                    name: string | null;
                    mainPhone: PhoneNumberE164 | null;
                } | null;
                contact: {
                    id: ContactId | null;
                    name: string | null;
                    phone1: PhoneNumberE164 | null;
                    cellPhone: PhoneNumberE164 | null;
                } | null;
                clientContact: {
                    clientId: PlacementId;
                    contactId: ContactId;
                    relationship: Types.ClientContactRelationship;
                } | null;
                communityContact: {
                    communityId: CommunityId;
                    contactId: ContactId;
                    primary: boolean;
                } | null;
                referenceContact: {
                    id: ReferenceContactId;
                    contactEntity: {
                        id: ContactId | null;
                        name: string | null;
                        phone1: PhoneNumberE164 | null;
                        cellPhone: PhoneNumberE164 | null;
                    } | null;
                } | null;
                referenceBusiness: {
                    id: ReferenceBusinessId;
                    businessContactEntity: {
                        id: ContactId | null;
                        name: string | null;
                        phone1: PhoneNumberE164 | null;
                        cellPhone: PhoneNumberE164 | null;
                    } | null;
                } | null;
            }>;
        }>;
    };
};

export type GetRecentConversationFeedForUserQueryVariables = Exact<{
    regionId: RegionId;
}>;

export type GetRecentConversationFeedForUserQuery = {
    getRecentConversationFeedForUser: {
        threads: Array<{
            id: ConversationThreadId;
            type: Types.ConversationThreadType;
            latestTimestamp: IsoTimestamp;
            ownerUserId: UserId | null;
            smsThread: {
                contact: { id: TelecomContactId; phoneNumber: PhoneNumberE164 };
                phoneLine: {
                    id: TelecomPhoneLineId;
                    userId: UserId;
                    phoneNumber: PhoneNumberE164;
                };
                messages: Array<{
                    id: SmsMessageId;
                    from: PhoneNumberE164;
                    to: PhoneNumberE164;
                    body: string;
                    direction: Types.SmsDirection;
                    dateAdded: IsoTimestamp;
                    status: Types.SmsMessageStatus;
                    createdBy: UserId;
                    createdByUser: { id: UserId; displayName: string };
                }>;
            } | null;
            voiceCall: {
                id: VoiceCallId;
                direction: Types.VoiceCallDirection;
                from: PhoneNumberE164;
                to: PhoneNumberE164;
                dateAdded: IsoTimestamp;
                recordingSid: string | null;
                recordingStatus: string | null;
                recordingDurationSec: number | null;
                transcriptStatus: string | null;
                summary: string | null;
                createdByUser: { id: UserId; displayName: string };
            } | null;
            emailThread: {
                threadId: string | null;
                sentMessages: Array<{
                    id: SentMessageId;
                    messageId: string | null;
                    userId: string | null;
                    from: string | null;
                    subject: string | null;
                    bodyPreview: string | null;
                    status: string;
                    latestEventStatus: string | null;
                    createdAt: any;
                    to: Array<string> | null;
                    cc: Array<string> | null;
                    messageType: string | null;
                    attachments: Array<string> | null;
                    clientIdUuid: PlacementId | null;
                    communityIdUuid: CommunityId | null;
                    clickedLinkEvents: Array<{
                        link: string;
                        events: Array<string>;
                        redacted: boolean | null;
                        eventsV2: Array<{
                            timestamp: string;
                            recipient: string | null;
                        }> | null;
                    }> | null;
                    intakeCommunity: {
                        id: CommunityId;
                        name: string | null;
                        appLink: string;
                    } | null;
                    intakeClient: {
                        id: PlacementId;
                        name: string | null;
                        bestContactName: string | null;
                        appLink: string;
                    } | null;
                    events: Array<{
                        eventType: string;
                        eventTimestamp: any;
                        recipient: string | null;
                    }> | null;
                }>;
                mailboxMessages: Array<{
                    id: UserMailboxMessageId;
                    externalId: string;
                    subject: string;
                    bodyPreview: string;
                    receivedTimestamp: IsoTimestamp;
                    attachmentCount: number;
                    from: {
                        id: UserMailboxContactId;
                        name: string;
                        email: string;
                        resolvedName: string | null;
                    } | null;
                    sender: {
                        id: UserMailboxContactId;
                        name: string;
                        email: string;
                        resolvedName: string | null;
                    } | null;
                    to: Array<{
                        id: UserMailboxMessageRecipientId;
                        contact: {
                            id: UserMailboxContactId;
                            name: string;
                            email: string;
                            resolvedName: string | null;
                        };
                    }>;
                    cc: Array<{
                        id: UserMailboxMessageRecipientId;
                        contact: {
                            id: UserMailboxContactId;
                            name: string;
                            email: string;
                            resolvedName: string | null;
                        };
                    }>;
                    bcc: Array<{
                        id: UserMailboxMessageRecipientId;
                        contact: {
                            id: UserMailboxContactId;
                            name: string;
                            email: string;
                            resolvedName: string | null;
                        };
                    }>;
                    replyTo: Array<{
                        id: UserMailboxMessageRecipientId;
                        contact: {
                            id: UserMailboxContactId;
                            name: string;
                            email: string;
                            resolvedName: string | null;
                        };
                    }>;
                }>;
            } | null;
            contactSearchHits: Array<{
                id: string;
                type: Types.ContactSearchHitType;
                appLink: string;
                fieldMatches: Array<{
                    type: Types.ContactSearchFieldMatchType;
                    phoneNumber: PhoneNumberE164 | null;
                    email: string | null;
                }>;
                client: {
                    id: PlacementId;
                    name: string | null;
                    phone: PhoneNumberE164 | null;
                    cellPhone: PhoneNumberE164 | null;
                } | null;
                community: {
                    id: CommunityId;
                    name: string | null;
                    mainPhone: PhoneNumberE164 | null;
                } | null;
                contact: {
                    id: ContactId | null;
                    name: string | null;
                    phone1: PhoneNumberE164 | null;
                    cellPhone: PhoneNumberE164 | null;
                } | null;
                clientContact: {
                    clientId: PlacementId;
                    contactId: ContactId;
                    relationship: Types.ClientContactRelationship;
                } | null;
                communityContact: {
                    communityId: CommunityId;
                    contactId: ContactId;
                    primary: boolean;
                } | null;
                referenceContact: {
                    id: ReferenceContactId;
                    contactEntity: {
                        id: ContactId | null;
                        name: string | null;
                        phone1: PhoneNumberE164 | null;
                        cellPhone: PhoneNumberE164 | null;
                    } | null;
                } | null;
                referenceBusiness: {
                    id: ReferenceBusinessId;
                    businessContactEntity: {
                        id: ContactId | null;
                        name: string | null;
                        phone1: PhoneNumberE164 | null;
                        cellPhone: PhoneNumberE164 | null;
                    } | null;
                } | null;
            }>;
        }>;
    };
};

export type ConversationFeedBasicsFragment = {
    threads: Array<{
        id: ConversationThreadId;
        type: Types.ConversationThreadType;
        latestTimestamp: IsoTimestamp;
        ownerUserId: UserId | null;
        smsThread: {
            contact: { id: TelecomContactId; phoneNumber: PhoneNumberE164 };
            phoneLine: {
                id: TelecomPhoneLineId;
                userId: UserId;
                phoneNumber: PhoneNumberE164;
            };
            messages: Array<{
                id: SmsMessageId;
                from: PhoneNumberE164;
                to: PhoneNumberE164;
                body: string;
                direction: Types.SmsDirection;
                dateAdded: IsoTimestamp;
                status: Types.SmsMessageStatus;
                createdBy: UserId;
                createdByUser: { id: UserId; displayName: string };
            }>;
        } | null;
        voiceCall: {
            id: VoiceCallId;
            direction: Types.VoiceCallDirection;
            from: PhoneNumberE164;
            to: PhoneNumberE164;
            dateAdded: IsoTimestamp;
            recordingSid: string | null;
            recordingStatus: string | null;
            recordingDurationSec: number | null;
            transcriptStatus: string | null;
            summary: string | null;
            createdByUser: { id: UserId; displayName: string };
        } | null;
        emailThread: {
            threadId: string | null;
            sentMessages: Array<{
                id: SentMessageId;
                messageId: string | null;
                userId: string | null;
                from: string | null;
                subject: string | null;
                bodyPreview: string | null;
                status: string;
                latestEventStatus: string | null;
                createdAt: any;
                to: Array<string> | null;
                cc: Array<string> | null;
                messageType: string | null;
                attachments: Array<string> | null;
                clientIdUuid: PlacementId | null;
                communityIdUuid: CommunityId | null;
                clickedLinkEvents: Array<{
                    link: string;
                    events: Array<string>;
                    redacted: boolean | null;
                    eventsV2: Array<{
                        timestamp: string;
                        recipient: string | null;
                    }> | null;
                }> | null;
                intakeCommunity: {
                    id: CommunityId;
                    name: string | null;
                    appLink: string;
                } | null;
                intakeClient: {
                    id: PlacementId;
                    name: string | null;
                    bestContactName: string | null;
                    appLink: string;
                } | null;
                events: Array<{
                    eventType: string;
                    eventTimestamp: any;
                    recipient: string | null;
                }> | null;
            }>;
            mailboxMessages: Array<{
                id: UserMailboxMessageId;
                externalId: string;
                subject: string;
                bodyPreview: string;
                receivedTimestamp: IsoTimestamp;
                attachmentCount: number;
                from: {
                    id: UserMailboxContactId;
                    name: string;
                    email: string;
                    resolvedName: string | null;
                } | null;
                sender: {
                    id: UserMailboxContactId;
                    name: string;
                    email: string;
                    resolvedName: string | null;
                } | null;
                to: Array<{
                    id: UserMailboxMessageRecipientId;
                    contact: {
                        id: UserMailboxContactId;
                        name: string;
                        email: string;
                        resolvedName: string | null;
                    };
                }>;
                cc: Array<{
                    id: UserMailboxMessageRecipientId;
                    contact: {
                        id: UserMailboxContactId;
                        name: string;
                        email: string;
                        resolvedName: string | null;
                    };
                }>;
                bcc: Array<{
                    id: UserMailboxMessageRecipientId;
                    contact: {
                        id: UserMailboxContactId;
                        name: string;
                        email: string;
                        resolvedName: string | null;
                    };
                }>;
                replyTo: Array<{
                    id: UserMailboxMessageRecipientId;
                    contact: {
                        id: UserMailboxContactId;
                        name: string;
                        email: string;
                        resolvedName: string | null;
                    };
                }>;
            }>;
        } | null;
        contactSearchHits: Array<{
            id: string;
            type: Types.ContactSearchHitType;
            appLink: string;
            fieldMatches: Array<{
                type: Types.ContactSearchFieldMatchType;
                phoneNumber: PhoneNumberE164 | null;
                email: string | null;
            }>;
            client: {
                id: PlacementId;
                name: string | null;
                phone: PhoneNumberE164 | null;
                cellPhone: PhoneNumberE164 | null;
            } | null;
            community: {
                id: CommunityId;
                name: string | null;
                mainPhone: PhoneNumberE164 | null;
            } | null;
            contact: {
                id: ContactId | null;
                name: string | null;
                phone1: PhoneNumberE164 | null;
                cellPhone: PhoneNumberE164 | null;
            } | null;
            clientContact: {
                clientId: PlacementId;
                contactId: ContactId;
                relationship: Types.ClientContactRelationship;
            } | null;
            communityContact: {
                communityId: CommunityId;
                contactId: ContactId;
                primary: boolean;
            } | null;
            referenceContact: {
                id: ReferenceContactId;
                contactEntity: {
                    id: ContactId | null;
                    name: string | null;
                    phone1: PhoneNumberE164 | null;
                    cellPhone: PhoneNumberE164 | null;
                } | null;
            } | null;
            referenceBusiness: {
                id: ReferenceBusinessId;
                businessContactEntity: {
                    id: ContactId | null;
                    name: string | null;
                    phone1: PhoneNumberE164 | null;
                    cellPhone: PhoneNumberE164 | null;
                } | null;
            } | null;
        }>;
    }>;
};

export type ContactSearchHitFragmentFragment = {
    id: string;
    type: Types.ContactSearchHitType;
    appLink: string;
    fieldMatches: Array<{
        type: Types.ContactSearchFieldMatchType;
        phoneNumber: PhoneNumberE164 | null;
        email: string | null;
    }>;
    client: {
        id: PlacementId;
        name: string | null;
        phone: PhoneNumberE164 | null;
        cellPhone: PhoneNumberE164 | null;
    } | null;
    community: {
        id: CommunityId;
        name: string | null;
        mainPhone: PhoneNumberE164 | null;
    } | null;
    contact: {
        id: ContactId | null;
        name: string | null;
        phone1: PhoneNumberE164 | null;
        cellPhone: PhoneNumberE164 | null;
    } | null;
    clientContact: {
        clientId: PlacementId;
        contactId: ContactId;
        relationship: Types.ClientContactRelationship;
    } | null;
    communityContact: {
        communityId: CommunityId;
        contactId: ContactId;
        primary: boolean;
    } | null;
    referenceContact: {
        id: ReferenceContactId;
        contactEntity: {
            id: ContactId | null;
            name: string | null;
            phone1: PhoneNumberE164 | null;
            cellPhone: PhoneNumberE164 | null;
        } | null;
    } | null;
    referenceBusiness: {
        id: ReferenceBusinessId;
        businessContactEntity: {
            id: ContactId | null;
            name: string | null;
            phone1: PhoneNumberE164 | null;
            cellPhone: PhoneNumberE164 | null;
        } | null;
    } | null;
};

export type GetCustomListQueryVariables = Exact<{
    key: Types.CustomListKey;
}>;

export type GetCustomListQuery = {
    getCustomList: {
        id: CustomListId;
        key: Types.CustomListKey;
        items: Array<{
            id: CustomListItemId;
            key: string;
            name: string;
            order: number;
            customListCategoryId: CustomListCategoryId | null;
            icon: string | null;
            pipelineVisibility: Types.PipelineVisibility | null;
            pipelineDateLabel: string | null;
            pipelineDateField: string | null;
            isArchived: boolean;
            updateBehavior: Types.CustomListItemUpdateBehavior;
        }>;
        categories: Array<{
            id: CustomListCategoryId;
            key: string;
            name: string;
            order: number;
        }>;
    };
};

export type GetCustomListsQueryVariables = Exact<{ [key: string]: never }>;

export type GetCustomListsQuery = {
    getCustomLists: Array<{
        id: CustomListId;
        key: Types.CustomListKey;
        items: Array<{
            id: CustomListItemId;
            key: string;
            name: string;
            order: number;
            customListCategoryId: CustomListCategoryId | null;
            icon: string | null;
            pipelineVisibility: Types.PipelineVisibility | null;
            pipelineDateLabel: string | null;
            pipelineDateField: string | null;
            isArchived: boolean;
            updateBehavior: Types.CustomListItemUpdateBehavior;
        }>;
        categories: Array<{
            id: CustomListCategoryId;
            key: string;
            name: string;
            order: number;
        }>;
    }>;
};

export type CreateCustomListItemMutationVariables = Exact<{
    params: Types.CreateCustomListItemRequest;
}>;

export type CreateCustomListItemMutation = {
    createCustomListItem: {
        id: CustomListItemId;
        key: string;
        name: string;
        order: number;
        customListCategoryId: CustomListCategoryId | null;
        icon: string | null;
        pipelineVisibility: Types.PipelineVisibility | null;
        pipelineDateLabel: string | null;
        pipelineDateField: string | null;
        isArchived: boolean;
        updateBehavior: Types.CustomListItemUpdateBehavior;
    };
};

export type PatchCustomListItemMutationVariables = Exact<{
    params: Types.PatchCustomListItemRequest;
}>;

export type PatchCustomListItemMutation = {
    patchCustomListItem: {
        id: CustomListItemId;
        key: string;
        name: string;
        order: number;
        customListCategoryId: CustomListCategoryId | null;
        icon: string | null;
        pipelineVisibility: Types.PipelineVisibility | null;
        pipelineDateLabel: string | null;
        pipelineDateField: string | null;
        isArchived: boolean;
        updateBehavior: Types.CustomListItemUpdateBehavior;
    };
};

export type PatchCustomListItemsMutationVariables = Exact<{
    params: Array<Types.PatchCustomListItemRequest> | Types.PatchCustomListItemRequest;
}>;

export type PatchCustomListItemsMutation = {
    patchCustomListItems: Array<{
        id: CustomListItemId;
        key: string;
        name: string;
        order: number;
        customListCategoryId: CustomListCategoryId | null;
        icon: string | null;
        pipelineVisibility: Types.PipelineVisibility | null;
        pipelineDateLabel: string | null;
        pipelineDateField: string | null;
        isArchived: boolean;
        updateBehavior: Types.CustomListItemUpdateBehavior;
    }>;
};

export type CustomListResponseFragment = {
    id: CustomListId;
    key: Types.CustomListKey;
    items: Array<{
        id: CustomListItemId;
        key: string;
        name: string;
        order: number;
        customListCategoryId: CustomListCategoryId | null;
        icon: string | null;
        pipelineVisibility: Types.PipelineVisibility | null;
        pipelineDateLabel: string | null;
        pipelineDateField: string | null;
        isArchived: boolean;
        updateBehavior: Types.CustomListItemUpdateBehavior;
    }>;
    categories: Array<{
        id: CustomListCategoryId;
        key: string;
        name: string;
        order: number;
    }>;
};

export type CustomListItemResponseFragment = {
    id: CustomListItemId;
    key: string;
    name: string;
    order: number;
    customListCategoryId: CustomListCategoryId | null;
    icon: string | null;
    pipelineVisibility: Types.PipelineVisibility | null;
    pipelineDateLabel: string | null;
    pipelineDateField: string | null;
    isArchived: boolean;
    updateBehavior: Types.CustomListItemUpdateBehavior;
};

export type GraphqlDebugQueryVariables = Exact<{
    expectedResult: string;
}>;

export type GraphqlDebugQuery = { graphqlDebug: { message: string } };

export type CreateDisclosureMutationVariables = Exact<{
    clientId: PlacementId;
}>;

export type CreateDisclosureMutation = {
    createDisclosure: {
        id: DisclosureId;
        dateAdded: any;
        publicUrl: string;
        acknowledgements: Array<{
            id: DisclosureAcknowledgementId;
            name: string;
            email: string;
            phone: string;
            agreed: boolean;
            response: Types.DisclosureAcknowledgementResponse | null;
            dateAdded: any;
            createdBy: UserId | null;
            ipAddress: string;
        }>;
    };
};

export type CreateDisclosureAcknowledgementMutationVariables = Exact<{
    disclosureId: DisclosureId;
    acknowledgement: Types.DisclosureAcknowledgementInput;
}>;

export type CreateDisclosureAcknowledgementMutation = {
    createDisclosureAcknowledgement: {
        id: DisclosureAcknowledgementId;
        name: string;
        email: string;
        phone: string;
        agreed: boolean;
        response: Types.DisclosureAcknowledgementResponse | null;
        dateAdded: any;
        createdBy: UserId | null;
        ipAddress: string;
    };
};

export type DisclosureFragmentFragment = {
    id: DisclosureId;
    dateAdded: any;
    publicUrl: string;
    acknowledgements: Array<{
        id: DisclosureAcknowledgementId;
        name: string;
        email: string;
        phone: string;
        agreed: boolean;
        response: Types.DisclosureAcknowledgementResponse | null;
        dateAdded: any;
        createdBy: UserId | null;
        ipAddress: string;
    }>;
};

export type DisclosureAcknowledgementFragmentFragment = {
    id: DisclosureAcknowledgementId;
    name: string;
    email: string;
    phone: string;
    agreed: boolean;
    response: Types.DisclosureAcknowledgementResponse | null;
    dateAdded: any;
    createdBy: UserId | null;
    ipAddress: string;
};

export type UpdateEvergreenDataSourceMutationVariables = Exact<{
    input: Types.EvergreenDataSourceInput;
}>;

export type UpdateEvergreenDataSourceMutation = {
    updateEvergreenDataSource: { id: InternalId };
};

export type UpdateEvergreenImportMutationVariables = Exact<{
    input: Types.EvergreenImportInput;
}>;

export type UpdateEvergreenImportMutation = { updateEvergreenImport: { id: InternalId } };

export type GetEvergreenDataSourcesQueryVariables = Exact<{ [key: string]: never }>;

export type GetEvergreenDataSourcesQuery = {
    getEvergreenDataSources: Array<{
        id: InternalId;
        name: string;
        lastSuccessfulRun: any;
        updateFrequencyInDays: number | null;
        updatedAt: any;
        dateAdded: any;
        updateRequested: any;
        warningsSilenced: any;
        warningsSnoozedUntil: any;
        warningsSnoozeReason: string | null;
        duplicatesDetectionWindowInDays: number | null;
    }>;
};

export type ImportFragmentFragment = {
    created: number | null;
    dateAdded: any;
    duplicates: number | null;
    endedAt: any;
    errors: number | null;
    id: InternalId;
    message: string | null;
    skips: number | null;
    delisted: number | null;
    sourceName: string;
    startedAt: any;
    updatedAt: any;
};

export type GetEvergreenDataSourceQueryVariables = Exact<{
    name: string;
}>;

export type GetEvergreenDataSourceQuery = {
    getEvergreenDataSource: {
        id: InternalId;
        name: string;
        lastSuccessfulRun: any;
        updateFrequencyInDays: number | null;
        warningsSilenced: any;
        warningsSnoozedUntil: any;
        warningsSnoozeReason: string | null;
        duplicatesDetectionWindowInDays: number | null;
        updatedAt: any;
        dateAdded: any;
        updateRequested: any;
        imports: Array<{
            created: number | null;
            dateAdded: any;
            duplicates: number | null;
            endedAt: any;
            errors: number | null;
            id: InternalId;
            message: string | null;
            skips: number | null;
            delisted: number | null;
            sourceName: string;
            startedAt: any;
            updatedAt: any;
        }> | null;
    };
};

export type GetEvergreenImportsQueryVariables = Exact<{
    skip: number;
    take: number;
}>;

export type GetEvergreenImportsQuery = {
    getEvergreenImports: Array<{
        created: number | null;
        dateAdded: any;
        duplicates: number | null;
        endedAt: any;
        errors: number | null;
        id: InternalId;
        message: string | null;
        skips: number | null;
        delisted: number | null;
        sourceName: string;
        startedAt: any;
        updatedAt: any;
    }>;
};

export type EvergreenCommunitySearchQueryVariables = Exact<{
    params: Types.EvergreenCommunitySearchRequest;
}>;

export type EvergreenCommunitySearchQuery = {
    evergreenCommunitySearch: {
        count: number;
        hits: Array<{
            id: EvergreenCommunityId;
            name: string;
            address: string;
            city: string;
            state: string;
            zip: string;
            county: string;
            status: Types.EvergreenCommunityStatus;
            licenseTypes: Array<string>;
            thumbnailUrl: string | null;
            aliases: Array<{
                id: InternalId;
                name: string;
                status: Types.EvergreenCommunityStatus;
                licenses: Array<{
                    id: InternalId;
                    jurisdiction: string;
                    licenseNumber: string;
                    status: string;
                    licenseTypes: Array<string>;
                }>;
            }>;
            listings: Array<{
                id: EvergreenCommunityListingId;
                visibility: Types.ListingVisibility;
                preferredName: string | null;
                website: string | null;
                summary: string | null;
                startingPrice: number | null;
                amenities: Array<string>;
                careTypes: Array<string>;
                careServices: Array<string>;
                evergreenCommunityId: EvergreenCommunityId;
                phoneNumbers: Array<{ number: string }>;
            }>;
        }>;
    };
};

export type GetPopularEvergreenCommunitiesQueryVariables = Exact<{
    params: Types.GetPopularEvergreenCommunitiesRequest;
}>;

export type GetPopularEvergreenCommunitiesQuery = {
    getPopularEvergreenCommunities: {
        count: number;
        hits: Array<{
            placementCount: number;
            community: {
                id: EvergreenCommunityId;
                name: string;
                address: string;
                city: string;
                state: string;
                zip: string;
                county: string;
                status: Types.EvergreenCommunityStatus;
                licenseTypes: Array<string>;
                thumbnailUrl: string | null;
                aliases: Array<{
                    id: InternalId;
                    name: string;
                    status: Types.EvergreenCommunityStatus;
                    licenses: Array<{
                        id: InternalId;
                        jurisdiction: string;
                        licenseNumber: string;
                        status: string;
                        licenseTypes: Array<string>;
                    }>;
                }>;
                listings: Array<{
                    id: EvergreenCommunityListingId;
                    visibility: Types.ListingVisibility;
                    preferredName: string | null;
                    website: string | null;
                    summary: string | null;
                    startingPrice: number | null;
                    amenities: Array<string>;
                    careTypes: Array<string>;
                    careServices: Array<string>;
                    evergreenCommunityId: EvergreenCommunityId;
                    phoneNumbers: Array<{ number: string }>;
                }>;
            };
        }>;
    };
};

export type EvergreenCommunitySearchHitFragmentFragment = {
    id: EvergreenCommunityId;
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    county: string;
    status: Types.EvergreenCommunityStatus;
    licenseTypes: Array<string>;
    thumbnailUrl: string | null;
    aliases: Array<{
        id: InternalId;
        name: string;
        status: Types.EvergreenCommunityStatus;
        licenses: Array<{
            id: InternalId;
            jurisdiction: string;
            licenseNumber: string;
            status: string;
            licenseTypes: Array<string>;
        }>;
    }>;
    listings: Array<{
        id: EvergreenCommunityListingId;
        visibility: Types.ListingVisibility;
        preferredName: string | null;
        website: string | null;
        summary: string | null;
        startingPrice: number | null;
        amenities: Array<string>;
        careTypes: Array<string>;
        careServices: Array<string>;
        evergreenCommunityId: EvergreenCommunityId;
        phoneNumbers: Array<{ number: string }>;
    }>;
};

export type CreateEvergreenCommunityListingMutationVariables = Exact<{
    params: Types.CreateEvergreenCommunityListingRequest;
}>;

export type CreateEvergreenCommunityListingMutation = {
    createEvergreenCommunityListing: boolean;
};

export type RegenerateEvergreenListingSlugsMutationVariables = Exact<{
    [key: string]: never;
}>;

export type RegenerateEvergreenListingSlugsMutation = {
    regenerateEvergreenListingSlugs: boolean;
};

export type GetListingsForEvergreenCommunityQueryVariables = Exact<{
    evergreenCommunityId: EvergreenCommunityId;
}>;

export type GetListingsForEvergreenCommunityQuery = {
    getListingsForEvergreenCommunity: Array<{
        id: EvergreenCommunityListingId;
        slug: string | null;
        evergreenCommunityId: EvergreenCommunityId;
        preferredName: string | null;
        website: string | null;
        summary: string | null;
        visibility: Types.ListingVisibility;
        startingPrice: number | null;
        amenities: Array<string>;
        careTypes: Array<string>;
        careServices: Array<string>;
        mostRecentDataRequestUrl: string | null;
        phoneNumbers: Array<{ number: string; label: string }>;
        reviews: Array<{
            id: EvergreenReviewId;
            text: string;
            webUri: string;
            rating: number;
            publishedAt: IsoTimestamp;
            authorDisplayName: string;
            authorUri: string | null;
            authorPhotoUri: string | null;
            source: Types.EvergreenReviewSource;
            externalId: string | null;
            createdAt: IsoTimestamp;
            updatedAt: IsoTimestamp;
        }>;
        googlePlace: {
            id: GooglePlaceId;
            externalId: string;
            displayName: string | null;
            googleMapsUrl: string | null;
        } | null;
    }>;
};

export type GetEvergreenCommunityQueryVariables = Exact<{
    id: EvergreenCommunityId;
}>;

export type GetEvergreenCommunityQuery = {
    getEvergreenCommunity: {
        id: EvergreenCommunityId;
        name: string;
        address: string;
        city: string;
        state: string;
        zip: string;
        county: string;
        status: Types.EvergreenCommunityStatus;
        thumbnailUrl: string | null;
        activeLicenseTypes: Array<string>;
        aliases: Array<{
            id: InternalId;
            name: string;
            status: Types.EvergreenCommunityStatus;
            licenses: Array<{
                id: InternalId;
                jurisdiction: string;
                licenseNumber: string;
                status: string;
                licenseTypes: Array<string>;
            }>;
        }>;
    } | null;
};

export type PatchEvergreenCommunityListingMutationVariables = Exact<{
    params: Types.PatchEvergreenCommunityListingRequest;
}>;

export type PatchEvergreenCommunityListingMutation = {
    patchEvergreenCommunityListing: {
        id: EvergreenCommunityListingId;
        slug: string | null;
        evergreenCommunityId: EvergreenCommunityId;
        preferredName: string | null;
        website: string | null;
        summary: string | null;
        visibility: Types.ListingVisibility;
        startingPrice: number | null;
        amenities: Array<string>;
        careTypes: Array<string>;
        careServices: Array<string>;
        phoneNumbers: Array<{ number: string; label: string }>;
    };
};

export type DeleteEvergreenCommunityListingMutationVariables = Exact<{
    id: EvergreenCommunityListingId;
}>;

export type DeleteEvergreenCommunityListingMutation = {
    deleteEvergreenCommunityListing: boolean;
};

export type SynchronizeEvergreenListingWithGooglePlaceMutationVariables = Exact<{
    id: EvergreenCommunityListingId;
}>;

export type SynchronizeEvergreenListingWithGooglePlaceMutation = {
    synchronizeEvergreenListingWithGooglePlace: boolean;
};

export type UpdateGooglePlaceMutationVariables = Exact<{
    id: EvergreenCommunityListingId;
    externalId: string;
}>;

export type UpdateGooglePlaceMutation = { updateGooglePlace: boolean };

export type GenerateEvergreenCommunityInsightsMutationVariables = Exact<{
    id: EvergreenCommunityId;
}>;

export type GenerateEvergreenCommunityInsightsMutation = {
    generateEvergreenCommunityInsights: boolean;
};

export type SearchEvergreenCommunityListingsQueryVariables = Exact<{
    params: Types.SearchEvergreenCommunityListingsRequest;
}>;

export type SearchEvergreenCommunityListingsQuery = {
    searchEvergreenCommunityListings: Array<{
        id: EvergreenCommunityListingId;
        slug: string | null;
        evergreenCommunityId: EvergreenCommunityId;
        preferredName: string | null;
        website: string | null;
        summary: string | null;
        startingPrice: number | null;
        visibility: Types.ListingVisibility;
        amenities: Array<string>;
        careTypes: Array<string>;
        careServices: Array<string>;
        phoneNumbers: Array<{ number: string; label: string }>;
        evergreenCommunity: {
            id: EvergreenCommunityId;
            address: string;
            city: string;
            state: string;
            zip: string;
            county: string;
            lat: string | null;
            lng: string | null;
        };
    }>;
};

export type UpdateFeatureFlagMutationVariables = Exact<{
    name: string | number;
    status: Types.FeatureStatus;
}>;

export type UpdateFeatureFlagMutation = {
    updateFeatureFlag: { name: string; status: Types.FeatureStatus };
};

export type GetGlobalFeaturesQueryVariables = Exact<{ [key: string]: never }>;

export type GetGlobalFeaturesQuery = {
    getGlobalFeatures: {
        skus: Array<string>;
        features: Array<{
            skus: Array<string> | null;
            key: string;
            friendlyTitle: string;
            description: string;
            createdOn: string;
            intent: string;
            packageEnabled: boolean | null;
            actual: { name: string; status: Types.FeatureStatus };
        }>;
    };
};

export type GetFeaturesForRegionQueryVariables = Exact<{
    params: Types.GetRegionFeaturesRequest;
}>;

export type GetFeaturesForRegionQuery = {
    getFeaturesForRegion: { features: Array<{ name: string; enabled: boolean }> };
};

export type FileFragmentFragment = {
    id: FileId;
    entityId: FileEntityId;
    originalFilename: string | null;
    mimeType: string | null;
    description: string | null;
    byteLength: number | null;
    shareWithClient: boolean;
    shareWithCommunity: boolean;
    shareWithReferral: boolean;
    shareWithHomeCareLead: boolean;
    createdAt: any;
    createdBy: UserId | null;
    updatedAt: any;
    updatedBy: UserId | null;
    hasSignatureTemplate: boolean;
    isGlobal: boolean;
    managementLevel: string;
    regionVisibility: {
        editable: boolean;
        regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
    } | null;
};

export type GetFilesQueryVariables = Exact<{
    entityId: FileEntityId;
}>;

export type GetFilesQuery = {
    getFiles: {
        files: Array<{
            id: FileId;
            entityId: FileEntityId;
            originalFilename: string | null;
            mimeType: string | null;
            description: string | null;
            byteLength: number | null;
            shareWithClient: boolean;
            shareWithCommunity: boolean;
            shareWithReferral: boolean;
            shareWithHomeCareLead: boolean;
            createdAt: any;
            createdBy: UserId | null;
            updatedAt: any;
            updatedBy: UserId | null;
            hasSignatureTemplate: boolean;
            isGlobal: boolean;
            managementLevel: string;
            regionVisibility: {
                editable: boolean;
                regions: Array<{
                    mode: Types.EntityRegionModeOutput;
                    regionId: RegionId;
                }>;
            } | null;
        }>;
    };
};

export type GetFileQueryVariables = Exact<{
    fileId: FileId;
}>;

export type GetFileQuery = {
    getFile: {
        outstandingSignatureRequestsCount: number;
        id: FileId;
        entityId: FileEntityId;
        originalFilename: string | null;
        mimeType: string | null;
        description: string | null;
        byteLength: number | null;
        shareWithClient: boolean;
        shareWithCommunity: boolean;
        shareWithReferral: boolean;
        shareWithHomeCareLead: boolean;
        createdAt: any;
        createdBy: UserId | null;
        updatedAt: any;
        updatedBy: UserId | null;
        hasSignatureTemplate: boolean;
        isGlobal: boolean;
        managementLevel: string;
        accessControls: Array<{ id: AccessControlId; groupId: UserGroupId }>;
        regionVisibility: {
            editable: boolean;
            regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
        } | null;
    };
};

export type UpdateFileMutationVariables = Exact<{
    fileId: FileId;
    details: Types.FileUpdateInput;
    accessControls?:
        | Array<Types.AccessControlInput>
        | Types.AccessControlInput
        | null
        | undefined;
    regionVisibility?: Types.EntityRegionVisibilityInput | null | undefined;
}>;

export type UpdateFileMutation = {
    updateFile: {
        outstandingSignatureRequestsCount: number;
        id: FileId;
        entityId: FileEntityId;
        originalFilename: string | null;
        mimeType: string | null;
        description: string | null;
        byteLength: number | null;
        shareWithClient: boolean;
        shareWithCommunity: boolean;
        shareWithReferral: boolean;
        shareWithHomeCareLead: boolean;
        createdAt: any;
        createdBy: UserId | null;
        updatedAt: any;
        updatedBy: UserId | null;
        hasSignatureTemplate: boolean;
        isGlobal: boolean;
        managementLevel: string;
        accessControls: Array<{ id: AccessControlId; groupId: UserGroupId }>;
        regionVisibility: {
            editable: boolean;
            regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
        } | null;
    };
};

export type DeleteFileMutationVariables = Exact<{
    fileId: FileId;
}>;

export type DeleteFileMutation = { deleteFile: boolean };

export type DeleteHostedFormMutationVariables = Exact<{
    hostedFormId: HostedFormId;
}>;

export type DeleteHostedFormMutation = { deleteHostedForm: boolean };

export type CreateHostedFormMutationVariables = Exact<{ [key: string]: never }>;

export type CreateHostedFormMutation = { createHostedForm: { id: HostedFormId } };

export type PatchHostedFormMutationVariables = Exact<{
    params: Types.PatchHostedFormRequest;
}>;

export type PatchHostedFormMutation = { patchHostedForm: { id: HostedFormId } };

export type GetHostedFormsQueryVariables = Exact<{
    regionId: RegionId;
}>;

export type GetHostedFormsQuery = {
    getHostedForms: Array<{
        id: HostedFormId;
        name: string | null;
        displayName: string;
        url: string;
    }>;
};

export type GetHostedFormByIdQueryVariables = Exact<{
    regionId: RegionId;
    id: HostedFormId;
}>;

export type GetHostedFormByIdQuery = {
    getHostedFormById: {
        id: HostedFormId;
        name: string | null;
        displayName: string;
        dateAdded: IsoTimestamp;
        customizable: boolean;
        url: string;
        configuration: {
            style: Types.HostedFormStyle | null;
            fileUploadEnabled: boolean | null;
            regions: Array<{
                regionId: RegionId;
                userId: UserId | null;
                recipientUserIds: Array<UserId> | null;
            }> | null;
        };
        layoutSections: Array<{
            id: LayoutSectionId;
            order: number;
            sectionKey: string;
            visible: boolean;
            layoutItems: Array<{
                id: LayoutItemId;
                order: number;
                questionId: QuestionId | null;
                entityFieldName: string | null;
                showIfBlank: boolean;
            }>;
        }>;
        slugs: Array<{
            slug: string;
            id: HostedFormSlugId;
            regionId: RegionId;
            referenceContactId: ReferenceContactId | null;
            referenceBusinessId: ReferenceBusinessId | null;
            hostedFormId: HostedFormId;
        }> | null;
    };
};

export type SendHostedFormWithTokenMutationVariables = Exact<{
    params: Types.SendHostedFormWithTokenRequest;
}>;

export type SendHostedFormWithTokenMutation = {
    sendHostedFormWithToken: {
        token: {
            id: HostedFormTokenId;
            expiresAt: any;
            updatedAt: any;
            dateAdded: any;
            createdBy: UserId;
            updatedBy: UserId;
        };
    };
};

export type SendHostedFormWithPermalinkMutationVariables = Exact<{
    params: Types.SendHostedFormWithPermalinkRequest;
}>;

export type SendHostedFormWithPermalinkMutation = {
    sendHostedFormWithPermalink: boolean;
};

export type GetHostedFormTokensForEntityQueryVariables = Exact<{
    entityId: HostedFormEntityId;
}>;

export type GetHostedFormTokensForEntityQuery = {
    getHostedFormTokensForEntity: Array<{
        id: HostedFormTokenId;
        expiresAt: any;
        completedAt: any;
        hostedFormId: HostedFormId;
        updatedAt: any;
        dateAdded: any;
        createdBy: UserId;
        updatedBy: UserId;
        status: Types.HostedFormTokenStatus;
        sentMessages: Array<{ to: Array<string> | null }> | null;
    }>;
};

export type DeleteHostedFormTokenMutationVariables = Exact<{
    tokenId: HostedFormTokenId;
}>;

export type DeleteHostedFormTokenMutation = { deleteHostedFormToken: boolean };

export type CheckHostedFormSlugQueryVariables = Exact<{
    slug: string;
}>;

export type CheckHostedFormSlugQuery = {
    checkHostedFormSlug: { available: boolean; errorMessage: string | null };
};

export type ClaimHostedFormSlugMutationVariables = Exact<{
    claim: Types.HostedFormSlugClaim;
}>;

export type ClaimHostedFormSlugMutation = { claimHostedFormSlug: boolean };

export type DeleteHostedFormSlugMutationVariables = Exact<{
    hostedFormSlugId: HostedFormSlugId;
}>;

export type DeleteHostedFormSlugMutation = { deleteHostedFormSlug: boolean };

export type RelocateImageMutationVariables = Exact<{
    payload: Types.RelocateImageInput;
}>;

export type RelocateImageMutation = { relocateImage: { id: string; src: string } };

export type CreateInvoiceMutationVariables = Exact<{
    clientId: PlacementId;
    serial?: string | null | undefined;
    amountCents: CurrencyInCents;
    status: string;
    sentDate?: string | null | undefined;
    dueDate?: string | null | undefined;
    generatePdf: boolean;
    notes?: string | null | undefined;
    message?: Types.OutboundMessage | null | undefined;
    lineItems?:
        | Array<Types.InvoiceLineItemInput>
        | Types.InvoiceLineItemInput
        | null
        | undefined;
    paymentSchedule?: Types.InvoicePaymentScheduleInput | null | undefined;
    externalReference?: string | null | undefined;
    commissions?:
        | Array<Types.InvoiceCommissionCreateInput>
        | Types.InvoiceCommissionCreateInput
        | null
        | undefined;
}>;

export type CreateInvoiceMutation = {
    createInvoice: {
        invoices: Array<{ id: InvoiceId; serial: string }> | null;
        errors: {
            message: string | null;
            fields: Array<{ name: string; message: string }>;
        } | null;
    };
};

export type ResendInvoiceMutationVariables = Exact<{
    invoiceId: InvoiceId;
    message: Types.OutboundMessage;
}>;

export type ResendInvoiceMutation = {
    resendInvoice: {
        invoices: Array<{ id: InvoiceId; serial: string }> | null;
        errors: {
            message: string | null;
            fields: Array<{ name: string; message: string }>;
        } | null;
    };
};

export type GenerateInvoiceMutationVariables = Exact<{
    id: InvoiceId;
}>;

export type GenerateInvoiceMutation = {
    generateInvoice: {
        invoices: Array<{ id: InvoiceId; serial: string }> | null;
        errors: {
            message: string | null;
            fields: Array<{ name: string; message: string }>;
        } | null;
    };
};

export type UpdateInvoiceMutationVariables = Exact<{
    invoiceId: InvoiceId;
    status: string;
    sentDate?: string | null | undefined;
    paidDate?: string | null | undefined;
    serial: string;
    amountCents: CurrencyInCents;
    notes?: string | null | undefined;
    dueDate?: string | null | undefined;
    lineItems?:
        | Array<Types.InvoiceLineItemInput>
        | Types.InvoiceLineItemInput
        | null
        | undefined;
    paymentSchedule?: Types.InvoicePaymentScheduleInput | null | undefined;
    externalReference?: string | null | undefined;
}>;

export type UpdateInvoiceMutation = {
    updateInvoice: {
        invoices: Array<{
            id: InvoiceId;
            placementId: PlacementId;
            communityId: CommunityId | null;
        }> | null;
        errors: {
            message: string | null;
            fields: Array<{ name: string; message: string }>;
        } | null;
    };
};

export type CreateExternalInvoicesMutationVariables = Exact<{
    params: Types.ExternalInvoicesCreateRequest;
}>;

export type CreateExternalInvoicesMutation = {
    createExternalInvoices: {
        invoices: Array<{ id: InvoiceId; serial: string }> | null;
        errors: {
            message: string | null;
            fields: Array<{ name: string; message: string }>;
        } | null;
    };
};

export type GetInvoiceCreateExperienceQueryVariables = Exact<{
    regionId: RegionId;
}>;

export type GetInvoiceCreateExperienceQuery = {
    getInvoiceCreateExperience: { experience: Types.InvoiceCreateExperience };
};

export type GetExternalInvoiceAccountSettingsQueryVariables = Exact<{
    params: Types.GetExternalInvoiceAccountSettingsRequest;
}>;

export type GetExternalInvoiceAccountSettingsQuery = {
    getExternalInvoiceAccountSettings: {
        paymentTerms: Array<{
            id: string;
            name: string;
            dueDays: number | null;
            default: boolean;
        }>;
    };
};

export type SearchExternalInvoicesQueryVariables = Exact<{
    params: Types.ExternalInvoiceSearchRequest;
}>;

export type SearchExternalInvoicesQuery = {
    searchExternalInvoices: {
        count: number;
        hits: Array<{
            externalId: string;
            serial: string;
            customer: string;
            invoiceDate: string;
            dueDate: string;
            amountInCents: number;
        }>;
    };
};

export type AddExistingExternalInvoiceMutationVariables = Exact<{
    params: Types.AddExternalInvoiceRequest;
}>;

export type AddExistingExternalInvoiceMutation = {
    addExistingExternalInvoice: { id: InvoiceId };
};

export type SearchExternalInvoiceCustomersQueryVariables = Exact<{
    params: Types.ExternalInvoiceCustomerSearchRequest;
}>;

export type SearchExternalInvoiceCustomersQuery = {
    searchExternalInvoiceCustomers: {
        count: number;
        hits: Array<{ id: string; name: string }>;
    };
};

export type GetExternalInvoiceInitialParametersQueryVariables = Exact<{
    params: Types.GetExternalInvoiceInitialParametersRequest;
}>;

export type GetExternalInvoiceInitialParametersQuery = {
    getExternalInvoiceInitialParameters: {
        createCustomerName: string | null;
        customer: { id: string; name: string } | null;
    };
};

export type CreateExternalInvoiceCustomerMutationVariables = Exact<{
    params: Types.CreateExternalInvoiceCustomerRequest;
}>;

export type CreateExternalInvoiceCustomerMutation = {
    createExternalInvoiceCustomer: { id: string; name: string };
};

export type DeleteInvoiceMutationVariables = Exact<{
    invoiceId: InvoiceId;
}>;

export type DeleteInvoiceMutation = { deleteInvoice: boolean };

export type GetLayoutSectionQueryVariables = Exact<{
    id: LayoutSectionId;
}>;

export type GetLayoutSectionQuery = {
    getLayoutSection: {
        id: LayoutSectionId;
        order: number;
        sectionKey: string;
        visible: boolean;
        entityType: string;
        title: string;
        icon: string | null;
        renderBehavior: Types.SectionRenderBehaviorType;
        questionCategory: string | null;
        legacyPerspective: Types.LayoutSectionLegacyPerspective | null;
        includeRelations: Array<string> | null;
        singlePropertyName: string | null;
        defaultStandardFields: Array<string> | null;
        settings: Array<{
            key: string;
            label: string;
            type: Types.LayoutSectionSettingType;
            order: number;
        }>;
        layoutItems: Array<{
            id: LayoutItemId;
            order: number;
            questionId: QuestionId | null;
            entityFieldName: string | null;
            showIfBlank: boolean;
            naryFormat: Types.NaryFormat | null;
            customTitle: string | null;
            isRequired: boolean;
        }>;
    };
};

export type CreateLayoutSectionMutationVariables = Exact<{
    params: Types.CreateLayoutSectionInput;
}>;

export type CreateLayoutSectionMutation = {
    createLayoutSection: {
        id: LayoutSectionId;
        order: number;
        sectionKey: string;
        visible: boolean;
        entityType: string;
        title: string;
        icon: string | null;
        renderBehavior: Types.SectionRenderBehaviorType;
        questionCategory: string | null;
        legacyPerspective: Types.LayoutSectionLegacyPerspective | null;
        includeRelations: Array<string> | null;
        singlePropertyName: string | null;
        defaultStandardFields: Array<string> | null;
        settings: Array<{
            key: string;
            label: string;
            type: Types.LayoutSectionSettingType;
            order: number;
        }>;
        layoutItems: Array<{
            id: LayoutItemId;
            order: number;
            questionId: QuestionId | null;
            entityFieldName: string | null;
            showIfBlank: boolean;
            naryFormat: Types.NaryFormat | null;
            customTitle: string | null;
            isRequired: boolean;
        }>;
    };
};

export type PatchLayoutSectionMutationVariables = Exact<{
    params: Types.PatchLayoutSectionInput;
}>;

export type PatchLayoutSectionMutation = {
    patchLayoutSection: {
        id: LayoutSectionId;
        order: number;
        sectionKey: string;
        visible: boolean;
        entityType: string;
        title: string;
        icon: string | null;
        renderBehavior: Types.SectionRenderBehaviorType;
        questionCategory: string | null;
        legacyPerspective: Types.LayoutSectionLegacyPerspective | null;
        includeRelations: Array<string> | null;
        singlePropertyName: string | null;
        defaultStandardFields: Array<string> | null;
        settings: Array<{
            key: string;
            label: string;
            type: Types.LayoutSectionSettingType;
            order: number;
        }>;
        layoutItems: Array<{
            id: LayoutItemId;
            order: number;
            questionId: QuestionId | null;
            entityFieldName: string | null;
            showIfBlank: boolean;
            naryFormat: Types.NaryFormat | null;
            customTitle: string | null;
            isRequired: boolean;
        }>;
    };
};

export type PatchLayoutSectionsMutationVariables = Exact<{
    params: Types.PatchLayoutSectionsInput;
}>;

export type PatchLayoutSectionsMutation = {
    patchLayoutSections: Array<{
        id: LayoutSectionId;
        order: number;
        sectionKey: string;
        visible: boolean;
        entityType: string;
        title: string;
        icon: string | null;
        renderBehavior: Types.SectionRenderBehaviorType;
        questionCategory: string | null;
        legacyPerspective: Types.LayoutSectionLegacyPerspective | null;
        includeRelations: Array<string> | null;
        singlePropertyName: string | null;
        defaultStandardFields: Array<string> | null;
        settings: Array<{
            key: string;
            label: string;
            type: Types.LayoutSectionSettingType;
            order: number;
        }>;
        layoutItems: Array<{
            id: LayoutItemId;
            order: number;
            questionId: QuestionId | null;
            entityFieldName: string | null;
            showIfBlank: boolean;
            naryFormat: Types.NaryFormat | null;
            customTitle: string | null;
            isRequired: boolean;
        }>;
    }>;
};

export type PatchLayoutItemMutationVariables = Exact<{
    params: Types.PatchLayoutItemInput;
}>;

export type PatchLayoutItemMutation = {
    patchLayoutItem: {
        id: LayoutItemId;
        order: number;
        questionId: QuestionId | null;
        entityFieldName: string | null;
        showIfBlank: boolean;
        naryFormat: Types.NaryFormat | null;
        customTitle: string | null;
        isRequired: boolean;
    };
};

export type DeleteLayoutSectionMutationVariables = Exact<{
    id: LayoutSectionId;
}>;

export type DeleteLayoutSectionMutation = { deleteLayoutSection: boolean };

export type GetLayoutSectionsQueryVariables = Exact<{
    parentKey: Types.LayoutSectionParentKey;
}>;

export type GetLayoutSectionsQuery = {
    getLayoutSections: {
        layoutSections: Array<{
            id: LayoutSectionId;
            order: number;
            sectionKey: string;
            visible: boolean;
            entityType: string;
            title: string;
            icon: string | null;
            renderBehavior: Types.SectionRenderBehaviorType;
            questionCategory: string | null;
            legacyPerspective: Types.LayoutSectionLegacyPerspective | null;
            includeRelations: Array<string> | null;
            singlePropertyName: string | null;
            defaultStandardFields: Array<string> | null;
            settings: Array<{
                key: string;
                label: string;
                type: Types.LayoutSectionSettingType;
                order: number;
            }>;
            layoutItems: Array<{
                id: LayoutItemId;
                order: number;
                questionId: QuestionId | null;
                entityFieldName: string | null;
                showIfBlank: boolean;
                naryFormat: Types.NaryFormat | null;
                customTitle: string | null;
                isRequired: boolean;
            }>;
        }>;
    };
};

export type GetLayoutContainerByKeyQueryVariables = Exact<{
    key: string;
}>;

export type GetLayoutContainerByKeyQuery = {
    getLayoutContainerByKey: {
        id: LayoutContainerId;
        title: string;
        key: string;
        layoutSections: Array<{
            id: LayoutSectionId;
            order: number;
            sectionKey: string;
            visible: boolean;
            entityType: string;
            title: string;
            icon: string | null;
            renderBehavior: Types.SectionRenderBehaviorType;
            questionCategory: string | null;
            legacyPerspective: Types.LayoutSectionLegacyPerspective | null;
            includeRelations: Array<string> | null;
            singlePropertyName: string | null;
            defaultStandardFields: Array<string> | null;
            settings: Array<{
                key: string;
                label: string;
                type: Types.LayoutSectionSettingType;
                order: number;
            }>;
            layoutItems: Array<{
                id: LayoutItemId;
                order: number;
                questionId: QuestionId | null;
                entityFieldName: string | null;
                showIfBlank: boolean;
                naryFormat: Types.NaryFormat | null;
                customTitle: string | null;
                isRequired: boolean;
            }>;
        }>;
    };
};

export type GetLayoutContainersByKeyQueryVariables = Exact<{
    keys: Array<Types.LayoutSectionParentKey> | Types.LayoutSectionParentKey;
}>;

export type GetLayoutContainersByKeyQuery = {
    getLayoutContainersByKey: Array<{
        id: LayoutContainerId;
        title: string;
        key: string;
        layoutSections: Array<{
            id: LayoutSectionId;
            order: number;
            sectionKey: string;
            visible: boolean;
            entityType: string;
            title: string;
            icon: string | null;
            renderBehavior: Types.SectionRenderBehaviorType;
            questionCategory: string | null;
            legacyPerspective: Types.LayoutSectionLegacyPerspective | null;
            includeRelations: Array<string> | null;
            singlePropertyName: string | null;
            defaultStandardFields: Array<string> | null;
            settings: Array<{
                key: string;
                label: string;
                type: Types.LayoutSectionSettingType;
                order: number;
            }>;
            layoutItems: Array<{
                id: LayoutItemId;
                order: number;
                questionId: QuestionId | null;
                entityFieldName: string | null;
                showIfBlank: boolean;
                naryFormat: Types.NaryFormat | null;
                customTitle: string | null;
                isRequired: boolean;
            }>;
        }>;
    }>;
};

export type LayoutSectionBasicsFragment = {
    id: LayoutSectionId;
    order: number;
    sectionKey: string;
    visible: boolean;
    entityType: string;
    title: string;
    icon: string | null;
    renderBehavior: Types.SectionRenderBehaviorType;
    questionCategory: string | null;
    legacyPerspective: Types.LayoutSectionLegacyPerspective | null;
    includeRelations: Array<string> | null;
    singlePropertyName: string | null;
    defaultStandardFields: Array<string> | null;
    settings: Array<{
        key: string;
        label: string;
        type: Types.LayoutSectionSettingType;
        order: number;
    }>;
    layoutItems: Array<{
        id: LayoutItemId;
        order: number;
        questionId: QuestionId | null;
        entityFieldName: string | null;
        showIfBlank: boolean;
        naryFormat: Types.NaryFormat | null;
        customTitle: string | null;
        isRequired: boolean;
    }>;
};

export type LayoutItemBasicsFragment = {
    id: LayoutItemId;
    order: number;
    questionId: QuestionId | null;
    entityFieldName: string | null;
    showIfBlank: boolean;
    naryFormat: Types.NaryFormat | null;
    customTitle: string | null;
    isRequired: boolean;
};

export type GetClientIntakeFaxRecipientsQueryVariables = Exact<{
    clientId: PlacementId;
}>;

export type GetClientIntakeFaxRecipientsQuery = {
    getClientIntakeFaxRecipients: Array<{
        faxNumber: string | null;
        communityName: string;
        communityId: CommunityId;
    }>;
};

export type SendClientIntakeFaxMutationVariables = Exact<{
    message: Types.OutboundClientIntakeFax;
}>;

export type SendClientIntakeFaxMutation = { sendClientIntakeFax: boolean };

export type ComposeMessageQueryVariables = Exact<{
    entityId: EmailEntityId;
    specialMessageExperience?: string | null | undefined;
}>;

export type ComposeMessageQuery = {
    getTemplates: {
        templates: Array<{
            isDefaultSignature: boolean;
            template: {
                id: EmailTemplateId;
                body: string | null;
                subject: string | null;
                description: string | null;
                type: string;
                migrationVersion: number;
                attachments: Array<FileId> | null;
            };
        }>;
    };
    composeMessage: {
        recipients: Array<string>;
        recipientSuggestions: Array<string>;
        specialCommunityComparisonDefaults: {
            showSummary: boolean;
            showRecords: boolean;
            showCoverPage: boolean;
            coverPageTemplateId: EmailTemplateId | null;
            liveLinkText: string;
            attachCommunityPDFByDefault: boolean;
            useLiveLinkByDefault: boolean;
        } | null;
        files: Array<{
            entityName: string;
            files: Array<{
                id: FileId;
                originalFilename: string | null;
                mimeType: string | null;
                byteLength: number | null;
            }>;
        }>;
    };
};

export type ComposeMessageBasicQueryVariables = Exact<{ [key: string]: never }>;

export type ComposeMessageBasicQuery = {
    getTemplates: {
        templates: Array<{
            isDefaultSignature: boolean;
            template: {
                id: EmailTemplateId;
                body: string | null;
                subject: string | null;
                description: string | null;
                type: string;
                migrationVersion: number;
                attachments: Array<FileId> | null;
            };
        }>;
    };
};

export type GetMessagesForEntityQueryVariables = Exact<{
    id: EmailEntityId;
}>;

export type GetMessagesForEntityQuery = {
    getMessagesForEntity: Array<{
        id: SentMessageId;
        messageId: string | null;
        userId: string | null;
        from: string | null;
        subject: string | null;
        bodyPreview: string | null;
        status: string;
        latestEventStatus: string | null;
        createdAt: any;
        to: Array<string> | null;
        cc: Array<string> | null;
        messageType: string | null;
        attachments: Array<string> | null;
        clientIdUuid: PlacementId | null;
        communityIdUuid: CommunityId | null;
        clickedLinkEvents: Array<{
            link: string;
            events: Array<string>;
            redacted: boolean | null;
            eventsV2: Array<{ timestamp: string; recipient: string | null }> | null;
        }> | null;
        intakeCommunity: { id: CommunityId; name: string | null; appLink: string } | null;
        intakeClient: {
            id: PlacementId;
            name: string | null;
            bestContactName: string | null;
            appLink: string;
        } | null;
        events: Array<{
            eventType: string;
            eventTimestamp: any;
            recipient: string | null;
        }> | null;
    }>;
};

export type SentMessageFragmentFragment = {
    id: SentMessageId;
    messageId: string | null;
    userId: string | null;
    from: string | null;
    subject: string | null;
    bodyPreview: string | null;
    status: string;
    latestEventStatus: string | null;
    createdAt: any;
    to: Array<string> | null;
    cc: Array<string> | null;
    messageType: string | null;
    attachments: Array<string> | null;
    clientIdUuid: PlacementId | null;
    communityIdUuid: CommunityId | null;
    clickedLinkEvents: Array<{
        link: string;
        events: Array<string>;
        redacted: boolean | null;
        eventsV2: Array<{ timestamp: string; recipient: string | null }> | null;
    }> | null;
    intakeCommunity: { id: CommunityId; name: string | null; appLink: string } | null;
    intakeClient: {
        id: PlacementId;
        name: string | null;
        bestContactName: string | null;
        appLink: string;
    } | null;
    events: Array<{
        eventType: string;
        eventTimestamp: any;
        recipient: string | null;
    }> | null;
};

export type GetMessageQueryVariables = Exact<{
    id: SentMessageId;
}>;

export type GetMessageQuery = {
    getMessage: {
        id: SentMessageId;
        subject: string | null;
        bouncedRecipients: Array<string>;
        status: string;
        createdAt: any;
        body: string | null;
        to: Array<string> | null;
        cc: Array<string> | null;
        messageType: string | null;
        attachments: Array<string> | null;
        intakeCommunity: { name: string | null } | null;
        invoice: { id: InvoiceId; serial: string } | null;
        clickedLinkEvents: Array<{
            link: string;
            events: Array<string>;
            redacted: boolean | null;
            eventsV2: Array<{ timestamp: string; recipient: string | null }> | null;
        }> | null;
        events: Array<{
            eventType: string;
            eventTimestamp: any;
            recipient: string | null;
        }> | null;
    };
};

export type SendMessageMutationVariables = Exact<{
    message: Types.OutboundMessage;
}>;

export type SendMessageMutation = {
    sendMessage: {
        status: string;
        accepted: Array<string>;
        rejected: Array<string>;
        code: string | null;
        sentMessage: { id: SentMessageId } | null;
    };
};

export type ValidateTemplateQueryVariables = Exact<{
    emailTemplateId: EmailTemplateId;
    messageType: string;
}>;

export type ValidateTemplateQuery = {
    validateTemplate: {
        isValid: boolean;
        message: string | null;
        missingTokens: Array<{
            tokenKey: string;
            description: string;
            humanFriendlyName: string;
        }>;
    };
};

export type GetMessagingConfigurationQueryVariables = Exact<{ [key: string]: never }>;

export type GetMessagingConfigurationQuery = {
    getMessagingConfiguration: {
        user: { directSmtp: boolean };
        tenant: {
            customDomains: Array<string>;
            trackingLevel: Types.MessageTrackingLevel;
        };
    };
};

export type CreateNoteMutationVariables = Exact<{
    entityType: string;
    entityId: NoteEntityId;
    params: Types.CreateNoteInput;
}>;

export type CreateNoteMutation = {
    createNote: {
        _id: NoteId;
        text: string;
        dateAdded: any;
        wasEdited: boolean;
        authorId: UserId;
        editorId: UserId;
        updatedAt: any;
        isDraft: boolean;
        isPinned: boolean;
    };
};

export type UpdateNoteMutationVariables = Exact<{
    entityId: NoteEntityId;
    noteId: NoteId;
    params: Types.UpdateNoteInput;
}>;

export type UpdateNoteMutation = {
    updateNote: {
        _id: NoteId;
        text: string;
        dateAdded: any;
        wasEdited: boolean;
        authorId: UserId;
        editorId: UserId;
        updatedAt: any;
        isDraft: boolean;
        isPinned: boolean;
    };
};

export type DeleteNoteMutationVariables = Exact<{
    entityType: string;
    entityId: NoteEntityId;
    noteId: NoteId;
}>;

export type DeleteNoteMutation = { deleteNote: { _id: NoteId } };

export type GetNotesForEntityQueryVariables = Exact<{
    entityId: NoteEntityId;
    filter?: Types.GetNotesForEntityFilter | null | undefined;
}>;

export type GetNotesForEntityQuery = {
    getNotesForEntity: Array<{
        _id: NoteId;
        text: string;
        dateAdded: any;
        wasEdited: boolean;
        authorId: UserId;
        editorId: UserId;
        updatedAt: any;
        isDraft: boolean;
        isPinned: boolean;
    }>;
};

export type NoteResponseFragment = {
    _id: NoteId;
    text: string;
    dateAdded: any;
    wasEdited: boolean;
    authorId: UserId;
    editorId: UserId;
    updatedAt: any;
    isDraft: boolean;
    isPinned: boolean;
};

export type GetPhotosForEntityQueryVariables = Exact<{
    entityId: FileEntityId;
}>;

export type GetPhotosForEntityQuery = {
    getPhotosForEntity: Array<{
        id: PhotoId;
        entityId: FileEntityId;
        order: number;
        dateAdded: any;
        createdBy: UserId;
        originalFile: {
            id: FileId;
            entityId: FileEntityId;
            originalFilename: string | null;
            mimeType: string | null;
            description: string | null;
            byteLength: number | null;
            shareWithClient: boolean;
            shareWithCommunity: boolean;
            shareWithReferral: boolean;
            shareWithHomeCareLead: boolean;
            createdAt: any;
            createdBy: UserId | null;
            updatedAt: any;
            updatedBy: UserId | null;
            hasSignatureTemplate: boolean;
            isGlobal: boolean;
            managementLevel: string;
            regionVisibility: {
                editable: boolean;
                regions: Array<{
                    mode: Types.EntityRegionModeOutput;
                    regionId: RegionId;
                }>;
            } | null;
        };
    }>;
};

export type PatchPhotoMutationVariables = Exact<{
    params: Types.PatchPhotoInput;
}>;

export type PatchPhotoMutation = {
    patchPhoto: {
        id: PhotoId;
        entityId: FileEntityId;
        order: number;
        dateAdded: any;
        createdBy: UserId;
        originalFile: {
            id: FileId;
            entityId: FileEntityId;
            originalFilename: string | null;
            mimeType: string | null;
            description: string | null;
            byteLength: number | null;
            shareWithClient: boolean;
            shareWithCommunity: boolean;
            shareWithReferral: boolean;
            shareWithHomeCareLead: boolean;
            createdAt: any;
            createdBy: UserId | null;
            updatedAt: any;
            updatedBy: UserId | null;
            hasSignatureTemplate: boolean;
            isGlobal: boolean;
            managementLevel: string;
            regionVisibility: {
                editable: boolean;
                regions: Array<{
                    mode: Types.EntityRegionModeOutput;
                    regionId: RegionId;
                }>;
            } | null;
        };
    };
};

export type DeletePhotoMutationVariables = Exact<{
    params: Types.DeletePhotoInput;
}>;

export type DeletePhotoMutation = { deletePhoto: boolean };

export type PatchPhotosMutationVariables = Exact<{
    params: Array<Types.PatchPhotoInput> | Types.PatchPhotoInput;
}>;

export type PatchPhotosMutation = {
    patchPhotos: Array<{
        id: PhotoId;
        entityId: FileEntityId;
        order: number;
        dateAdded: any;
        createdBy: UserId;
        originalFile: {
            id: FileId;
            entityId: FileEntityId;
            originalFilename: string | null;
            mimeType: string | null;
            description: string | null;
            byteLength: number | null;
            shareWithClient: boolean;
            shareWithCommunity: boolean;
            shareWithReferral: boolean;
            shareWithHomeCareLead: boolean;
            createdAt: any;
            createdBy: UserId | null;
            updatedAt: any;
            updatedBy: UserId | null;
            hasSignatureTemplate: boolean;
            isGlobal: boolean;
            managementLevel: string;
            regionVisibility: {
                editable: boolean;
                regions: Array<{
                    mode: Types.EntityRegionModeOutput;
                    regionId: RegionId;
                }>;
            } | null;
        };
    }>;
};

export type PhotoBasicsFragment = {
    id: PhotoId;
    entityId: FileEntityId;
    order: number;
    dateAdded: any;
    createdBy: UserId;
    originalFile: {
        id: FileId;
        entityId: FileEntityId;
        originalFilename: string | null;
        mimeType: string | null;
        description: string | null;
        byteLength: number | null;
        shareWithClient: boolean;
        shareWithCommunity: boolean;
        shareWithReferral: boolean;
        shareWithHomeCareLead: boolean;
        createdAt: any;
        createdBy: UserId | null;
        updatedAt: any;
        updatedBy: UserId | null;
        hasSignatureTemplate: boolean;
        isGlobal: boolean;
        managementLevel: string;
        regionVisibility: {
            editable: boolean;
            regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
        } | null;
    };
};

export type BulkCreateLegacyQuestionsMutationVariables = Exact<{
    payload: Types.QuestionBulkCreatePayload;
}>;

export type BulkCreateLegacyQuestionsMutation = {
    bulkCreateLegacyQuestions: Array<{ id: QuestionId }>;
};

export type GetLegacyQuestionsForTenantQueryVariables = Exact<{
    tenantId: TenantId;
}>;

export type GetLegacyQuestionsForTenantQuery = {
    getLegacyQuestionsForTenant: Array<{
        id: QuestionId;
        key: string;
        order: number | null;
        category: string | null;
        clientTitle: string | null;
        communityTitle: string | null;
        questionType: string | null;
        clientInteraction: string | null;
        clientPdfBehavior: string | null;
        clientSearchBehavior: string | null;
        clientAnnotation: string | null;
        communityInteraction: string | null;
        communityPdfBehavior: string | null;
        communitySearchBehavior: string | null;
        communityAnnotation: string | null;
        clientRulesString: string | null;
        communityRulesString: string | null;
        prompts: Array<{
            id: PromptId;
            client: string | null;
            community: string | null;
            key: string;
            order: number | null;
        }> | null;
    }>;
};

export type ReorderLegacyQuestionsMutationVariables = Exact<{
    questionReorderPayload: Types.QuestionReorderPayload;
}>;

export type ReorderLegacyQuestionsMutation = { reorderLegacyQuestions: boolean };

export type GetReferenceContactQueryVariables = Exact<{
    id: ReferenceContactId;
}>;

export type GetReferenceContactQuery = {
    getReferenceContact: {
        id: ReferenceContactId;
        ownerId: UserId | null;
        deleted: boolean;
        summary: string | null;
        businessId: ReferenceBusinessId | null;
        communityId: CommunityId | null;
        regionId: RegionId;
        dateAdded: any;
        updatedAt: any;
        createdBy: UserId;
        updatedBy: UserId;
        draftNote: string | null;
        bestDaysToVisit: Array<string> | null;
        bestTimeToContact: Array<string> | null;
        contactEntity: {
            id: ContactId | null;
            name: string | null;
            preferredName: string | null;
            address1: string | null;
            address2: string | null;
            city: string | null;
            state: string | null;
            zip: string | null;
            relationship: string | null;
            role: string | null;
            cellPhone: PhoneNumberE164 | null;
            phone1: PhoneNumberE164 | null;
            email1: string | null;
            email1OptOut: boolean;
            website: string | null;
            contactType: string | null;
            type: string | null;
            fax: PhoneNumberE164 | null;
            contactNotes: string | null;
            dateAdded: any;
        } | null;
        business: {
            id: ReferenceBusinessId;
            ownerId: UserId | null;
            deleted: boolean;
            regionId: RegionId;
            latitude: string | null;
            longitude: string | null;
            businessContactEntity: {
                id: ContactId | null;
                name: string | null;
                preferredName: string | null;
                address1: string | null;
                address2: string | null;
                city: string | null;
                state: string | null;
                zip: string | null;
                relationship: string | null;
                role: string | null;
                cellPhone: PhoneNumberE164 | null;
                phone1: PhoneNumberE164 | null;
                email1: string | null;
                email1OptOut: boolean;
                website: string | null;
                contactType: string | null;
                type: string | null;
                fax: PhoneNumberE164 | null;
                contactNotes: string | null;
                dateAdded: any;
            } | null;
        } | null;
        community: {
            id: CommunityId;
            name: string | null;
            address: string | null;
            city: string | null;
            state: string | null;
            zip: string | null;
            mainPhone: PhoneNumberE164 | null;
            website: string | null;
            email: string | null;
            latitude: string | null;
            longitude: string | null;
            contractStatus: string | null;
        } | null;
        answers: Array<{
            questionId: QuestionId | null;
            booleanAnswer: boolean | null;
            numberAnswer: number | null;
            numberAnswerHigh: number | null;
            textAnswer: string | null;
            dateAnswer: string | null;
            calendarDateAnswer: string | null;
            selections: Array<string> | null;
            idReferenceAnswer: UserId | null;
            selectionAnnotations: Array<{
                text: string | null;
                selectionId: string | null;
            }> | null;
        }>;
        notes: Array<{
            _id: NoteId;
            text: string;
            dateAdded: any;
            wasEdited: boolean;
            authorId: UserId;
            editorId: UserId;
            updatedAt: any;
            isDraft: boolean;
            isPinned: boolean;
        }>;
    };
};

export type GetReferenceBusinessQueryVariables = Exact<{
    id: ReferenceBusinessId;
}>;

export type GetReferenceBusinessQuery = {
    getReferenceBusiness: {
        id: ReferenceBusinessId;
        ownerId: UserId | null;
        deleted: boolean;
        summary: string | null;
        email: string | null;
        dateAdded: any;
        updatedAt: any;
        createdBy: UserId | null;
        updatedBy: UserId | null;
        draftNote: string | null;
        bestDaysToVisit: Array<string> | null;
        bestTimeToContact: Array<string> | null;
        regionId: RegionId;
        latitude: string | null;
        longitude: string | null;
        businessContactEntity: {
            id: ContactId | null;
            name: string | null;
            preferredName: string | null;
            address1: string | null;
            address2: string | null;
            city: string | null;
            state: string | null;
            zip: string | null;
            relationship: string | null;
            role: string | null;
            cellPhone: PhoneNumberE164 | null;
            phone1: PhoneNumberE164 | null;
            email1: string | null;
            email1OptOut: boolean;
            website: string | null;
            contactType: string | null;
            type: string | null;
            fax: PhoneNumberE164 | null;
            contactNotes: string | null;
            dateAdded: any;
        } | null;
        answers: Array<{
            questionId: QuestionId | null;
            booleanAnswer: boolean | null;
            numberAnswer: number | null;
            numberAnswerHigh: number | null;
            textAnswer: string | null;
            dateAnswer: string | null;
            calendarDateAnswer: string | null;
            selections: Array<string> | null;
            idReferenceAnswer: UserId | null;
            selectionAnnotations: Array<{
                text: string | null;
                selectionId: string | null;
            }> | null;
        }>;
        notes: Array<{
            _id: NoteId;
            text: string;
            dateAdded: any;
            wasEdited: boolean;
            authorId: UserId;
            editorId: UserId;
            updatedAt: any;
            isDraft: boolean;
            isPinned: boolean;
        }>;
    };
};

export type ReferenceContactSearchQueryVariables = Exact<{
    search: Types.ReferenceContactSearchRequest;
}>;

export type ReferenceContactSearchQuery = {
    referenceContactSearch: {
        total: number;
        coordinates: { lat: string; lng: string } | null;
        hits: Array<{
            id: ReferenceContactId;
            name: string | null;
            role: string | null;
            updatedAt: any;
            organizationName: string | null;
            organizationType: string | null;
            organizationPhone: string | null;
            organizationFax: string | null;
            address: string | null;
            city: string | null;
            state: string | null;
            zip: string | null;
            email: string | null;
            cellPhone: string | null;
            phone: string | null;
            numberOfReferrals: number;
            mostRecentReferral: any;
            assignedTo: string | null;
            businessId: ReferenceBusinessId | null;
            communityId: CommunityId | null;
            summary: string | null;
            latitude: string | null;
            longitude: string | null;
            appLink: string;
            answers: Array<{
                questionId: QuestionId | null;
                booleanAnswer: boolean | null;
                numberAnswer: number | null;
                numberAnswerHigh: number | null;
                textAnswer: string | null;
                dateAnswer: string | null;
                calendarDateAnswer: string | null;
                selections: Array<string> | null;
                idReferenceAnswer: UserId | null;
                selectionAnnotations: Array<{
                    text: string | null;
                    selectionId: string | null;
                }> | null;
            }>;
            organizationAnswers: Array<{
                questionId: QuestionId | null;
                booleanAnswer: boolean | null;
                numberAnswer: number | null;
                numberAnswerHigh: number | null;
                textAnswer: string | null;
                dateAnswer: string | null;
                calendarDateAnswer: string | null;
                selections: Array<string> | null;
                idReferenceAnswer: UserId | null;
                selectionAnnotations: Array<{
                    text: string | null;
                    selectionId: string | null;
                }> | null;
            }>;
        }>;
    };
};

export type ReferenceContactMapSearchQueryVariables = Exact<{
    searchParams: Types.ReferenceContactSearchRequest;
    mapView: Types.CommunitySearchRequestMapComponent;
}>;

export type ReferenceContactMapSearchQuery = {
    referenceContactMapSearch: {
        recommendedZoom: number;
        referenceContactCoordinates: Array<{
            businessId: ReferenceBusinessId | null;
            communityId: CommunityId | null;
            name: string | null;
            lat: number;
            lng: number;
            appLink: string | null;
            contacts: Array<{
                id: ReferenceContactId;
                name: string | null;
                appLink: string;
            }>;
        }>;
        referenceContactClusters: Array<{ lat: number; lng: number; count: number }>;
        coordinates: { lat: string; lng: string } | null;
    };
};

export type ReferenceBusinessSearchQueryVariables = Exact<{
    search: Types.ReferenceBusinessSearchRequest;
}>;

export type ReferenceBusinessSearchQuery = {
    referenceBusinessSearch: {
        total: number;
        coordinates: { lat: string; lng: string } | null;
        hits: Array<{
            id: ReferenceBusinessId;
            name: string | null;
            updatedAt: any;
            summary: string | null;
            organizationType: string | null;
            city: string | null;
            state: string | null;
            zip: string | null;
            address: string | null;
            phone: string | null;
            fax: string | null;
            email: string | null;
            numberOfReferrals: number;
            mostRecentReferral: any;
            assignedTo: string | null;
            latitude: string | null;
            longitude: string | null;
        }>;
    };
};

export type ReferenceBusinessSearchHitDefaultsFragment = {
    id: ReferenceBusinessId;
    name: string | null;
    updatedAt: any;
    summary: string | null;
    organizationType: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    address: string | null;
    phone: string | null;
    fax: string | null;
    email: string | null;
    numberOfReferrals: number;
    mostRecentReferral: any;
    assignedTo: string | null;
    latitude: string | null;
    longitude: string | null;
};

export type ReferenceBusinessMapSearchQueryVariables = Exact<{
    searchParams: Types.ReferenceBusinessSearchRequest;
    mapView: Types.CommunitySearchRequestMapComponent;
}>;

export type ReferenceBusinessMapSearchQuery = {
    referenceBusinessMapSearch: {
        recommendedZoom: number;
        referenceBusinessCoordinates: Array<{
            id: ReferenceBusinessId;
            name: string | null;
            lat: number;
            lng: number;
            appLink: string | null;
        }>;
        referenceBusinessClusters: Array<{ lat: number; lng: number; count: number }>;
        coordinates: { lat: string; lng: string } | null;
    };
};

export type ReferenceCommunitySearchQueryVariables = Exact<{
    search: Types.ReferenceCommunitySearchRequest;
}>;

export type ReferenceCommunitySearchQuery = {
    referenceCommunitySearch: {
        total: number;
        hits: Array<{
            id: CommunityId;
            name: string | null;
            updatedAt: any;
            city: string | null;
            state: string | null;
            zip: string | null;
            address: string | null;
            phone: string | null;
            fax: string | null;
            summary: string | null;
            numberOfReferrals: number;
            mostRecentReferral: any;
            latitude: string | null;
            longitude: string | null;
        }>;
    };
};

export type ReferenceCommunitySearchHitDefaultsFragment = {
    id: CommunityId;
    name: string | null;
    updatedAt: any;
    city: string | null;
    state: string | null;
    zip: string | null;
    address: string | null;
    phone: string | null;
    fax: string | null;
    summary: string | null;
    numberOfReferrals: number;
    mostRecentReferral: any;
    latitude: string | null;
    longitude: string | null;
};

export type ReferenceCommunityMapSearchQueryVariables = Exact<{
    searchParams: Types.ReferenceCommunitySearchRequest;
    mapView: Types.CommunitySearchRequestMapComponent;
}>;

export type ReferenceCommunityMapSearchQuery = {
    referenceCommunityMapSearch: {
        recommendedZoom: number;
        communityCoordinates: Array<{
            id: CommunityId;
            name: string | null;
            lat: number;
            lng: number;
            appLink: string;
        }>;
        communityClusters: Array<{ lat: number; lng: number; count: number }>;
        coordinates: { lat: string; lng: string } | null;
    };
};

export type ReferenceOrganizationSearchQueryVariables = Exact<{
    search: Types.ReferenceOrganizationSearchRequest;
}>;

export type ReferenceOrganizationSearchQuery = {
    referenceOrganizationSearch: {
        total: number;
        hits: Array<{
            id: OrganizationId;
            name: string | null;
            updatedAt: any;
            summary: string | null;
            organizationType: string | null;
            city: string | null;
            state: string | null;
            zip: string | null;
            address: string | null;
            phone: string | null;
            fax: string | null;
            email: string | null;
            numberOfReferrals: number;
            mostRecentReferral: any;
            assignedTo: string | null;
            latitude: string | null;
            longitude: string | null;
            appLink: string | null;
            entityType: Types.AdvancedSearchEntityType;
            contacts: Array<{
                cellPhone: string | null;
                name: string | null;
                phone: string | null;
                email: string | null;
                role: string | null;
                appLink: string;
            }>;
            answers: Array<{
                questionId: QuestionId | null;
                booleanAnswer: boolean | null;
                numberAnswer: number | null;
                numberAnswerHigh: number | null;
                textAnswer: string | null;
                dateAnswer: string | null;
                calendarDateAnswer: string | null;
                selections: Array<string> | null;
                idReferenceAnswer: UserId | null;
                selectionAnnotations: Array<{
                    text: string | null;
                    selectionId: string | null;
                }> | null;
            }>;
        }>;
    };
};

export type ReferenceOrganizationMapSearchQueryVariables = Exact<{
    searchParams: Types.ReferenceOrganizationSearchRequest;
    mapView: Types.CommunitySearchRequestMapComponent;
}>;

export type ReferenceOrganizationMapSearchQuery = {
    referenceOrganizationMapSearch: {
        recommendedZoom: number;
        organizationCoordinates: Array<{
            id: OrganizationId;
            name: string | null;
            lat: number;
            lng: number;
            appLink: string | null;
        }>;
        organizationClusters: Array<{ lat: number; lng: number; count: number }>;
        coordinates: { lat: string; lng: string } | null;
    };
};

export type GetReferralsToContactQueryVariables = Exact<{
    referenceContactId: ReferenceContactId;
}>;

export type GetReferralsToContactQuery = {
    getReferralsToContact: Array<{
        id: ReferralId;
        placementId: PlacementId;
        placement: {
            id: PlacementId;
            name: string | null;
            bestContactName: string | null;
            statusListItemId: CustomListItemId;
            dateAdded: any;
            serial: number | null;
            assignedUserId: UserId | null;
            updatedAt: any;
            moveTimeline: string | null;
            moveTimelineActual: string | null;
            nextTask: {
                id: TaskId;
                text: string | null;
                dueDate: string | null;
                dueDateTime: any;
                compositeDueDateTime: any;
                status: string;
            } | null;
        } | null;
    }>;
};

export type ReferralBasicsFragment = {
    id: ReferralId;
    placementId: PlacementId;
    placement: {
        id: PlacementId;
        name: string | null;
        bestContactName: string | null;
        statusListItemId: CustomListItemId;
        dateAdded: any;
        serial: number | null;
        assignedUserId: UserId | null;
        updatedAt: any;
        moveTimeline: string | null;
        moveTimelineActual: string | null;
        nextTask: {
            id: TaskId;
            text: string | null;
            dueDate: string | null;
            dueDateTime: any;
            compositeDueDateTime: any;
            status: string;
        } | null;
    } | null;
};

export type ReferenceContactBasicsFragment = {
    id: ReferenceContactId;
    ownerId: UserId | null;
    deleted: boolean;
    summary: string | null;
    businessId: ReferenceBusinessId | null;
    communityId: CommunityId | null;
    regionId: RegionId;
    dateAdded: any;
    updatedAt: any;
    createdBy: UserId;
    updatedBy: UserId;
    draftNote: string | null;
    bestDaysToVisit: Array<string> | null;
    bestTimeToContact: Array<string> | null;
    contactEntity: {
        id: ContactId | null;
        name: string | null;
        preferredName: string | null;
        address1: string | null;
        address2: string | null;
        city: string | null;
        state: string | null;
        zip: string | null;
        relationship: string | null;
        role: string | null;
        cellPhone: PhoneNumberE164 | null;
        phone1: PhoneNumberE164 | null;
        email1: string | null;
        email1OptOut: boolean;
        website: string | null;
        contactType: string | null;
        type: string | null;
        fax: PhoneNumberE164 | null;
        contactNotes: string | null;
        dateAdded: any;
    } | null;
    business: {
        id: ReferenceBusinessId;
        ownerId: UserId | null;
        deleted: boolean;
        regionId: RegionId;
        latitude: string | null;
        longitude: string | null;
        businessContactEntity: {
            id: ContactId | null;
            name: string | null;
            preferredName: string | null;
            address1: string | null;
            address2: string | null;
            city: string | null;
            state: string | null;
            zip: string | null;
            relationship: string | null;
            role: string | null;
            cellPhone: PhoneNumberE164 | null;
            phone1: PhoneNumberE164 | null;
            email1: string | null;
            email1OptOut: boolean;
            website: string | null;
            contactType: string | null;
            type: string | null;
            fax: PhoneNumberE164 | null;
            contactNotes: string | null;
            dateAdded: any;
        } | null;
    } | null;
    community: {
        id: CommunityId;
        name: string | null;
        address: string | null;
        city: string | null;
        state: string | null;
        zip: string | null;
        mainPhone: PhoneNumberE164 | null;
        website: string | null;
        email: string | null;
        latitude: string | null;
        longitude: string | null;
        contractStatus: string | null;
    } | null;
    answers: Array<{
        questionId: QuestionId | null;
        booleanAnswer: boolean | null;
        numberAnswer: number | null;
        numberAnswerHigh: number | null;
        textAnswer: string | null;
        dateAnswer: string | null;
        calendarDateAnswer: string | null;
        selections: Array<string> | null;
        idReferenceAnswer: UserId | null;
        selectionAnnotations: Array<{
            text: string | null;
            selectionId: string | null;
        }> | null;
    }>;
    notes: Array<{
        _id: NoteId;
        text: string;
        dateAdded: any;
        wasEdited: boolean;
        authorId: UserId;
        editorId: UserId;
        updatedAt: any;
        isDraft: boolean;
        isPinned: boolean;
    }>;
};

export type CreateReferenceContactMutationVariables = Exact<{
    regionId: RegionId;
}>;

export type CreateReferenceContactMutation = {
    createReferenceContact: { id: ReferenceContactId };
};

export type PatchReferenceContactMutationVariables = Exact<{
    id: ReferenceContactId;
    summary?: string | null | undefined;
    ownerId?: UserId | null | undefined;
    businessId?: ReferenceBusinessId | null | undefined;
    communityId?: CommunityId | null | undefined;
    contactEntity?: Types.ContactInput | null | undefined;
    deleted?: boolean | null | undefined;
    draftNote?: string | null | undefined;
    bestDaysToVisit?: Array<string> | string | null | undefined;
    bestTimeToContact?: Array<string> | string | null | undefined;
}>;

export type PatchReferenceContactMutation = {
    patchReferenceContact: {
        id: ReferenceContactId;
        ownerId: UserId | null;
        deleted: boolean;
        summary: string | null;
        businessId: ReferenceBusinessId | null;
        communityId: CommunityId | null;
        regionId: RegionId;
        dateAdded: any;
        updatedAt: any;
        createdBy: UserId;
        updatedBy: UserId;
        draftNote: string | null;
        bestDaysToVisit: Array<string> | null;
        bestTimeToContact: Array<string> | null;
        contactEntity: {
            id: ContactId | null;
            name: string | null;
            preferredName: string | null;
            address1: string | null;
            address2: string | null;
            city: string | null;
            state: string | null;
            zip: string | null;
            relationship: string | null;
            role: string | null;
            cellPhone: PhoneNumberE164 | null;
            phone1: PhoneNumberE164 | null;
            email1: string | null;
            email1OptOut: boolean;
            website: string | null;
            contactType: string | null;
            type: string | null;
            fax: PhoneNumberE164 | null;
            contactNotes: string | null;
            dateAdded: any;
        } | null;
        business: {
            id: ReferenceBusinessId;
            ownerId: UserId | null;
            deleted: boolean;
            regionId: RegionId;
            latitude: string | null;
            longitude: string | null;
            businessContactEntity: {
                id: ContactId | null;
                name: string | null;
                preferredName: string | null;
                address1: string | null;
                address2: string | null;
                city: string | null;
                state: string | null;
                zip: string | null;
                relationship: string | null;
                role: string | null;
                cellPhone: PhoneNumberE164 | null;
                phone1: PhoneNumberE164 | null;
                email1: string | null;
                email1OptOut: boolean;
                website: string | null;
                contactType: string | null;
                type: string | null;
                fax: PhoneNumberE164 | null;
                contactNotes: string | null;
                dateAdded: any;
            } | null;
        } | null;
        community: {
            id: CommunityId;
            name: string | null;
            address: string | null;
            city: string | null;
            state: string | null;
            zip: string | null;
            mainPhone: PhoneNumberE164 | null;
            website: string | null;
            email: string | null;
            latitude: string | null;
            longitude: string | null;
            contractStatus: string | null;
        } | null;
        answers: Array<{
            questionId: QuestionId | null;
            booleanAnswer: boolean | null;
            numberAnswer: number | null;
            numberAnswerHigh: number | null;
            textAnswer: string | null;
            dateAnswer: string | null;
            calendarDateAnswer: string | null;
            selections: Array<string> | null;
            idReferenceAnswer: UserId | null;
            selectionAnnotations: Array<{
                text: string | null;
                selectionId: string | null;
            }> | null;
        }>;
        notes: Array<{
            _id: NoteId;
            text: string;
            dateAdded: any;
            wasEdited: boolean;
            authorId: UserId;
            editorId: UserId;
            updatedAt: any;
            isDraft: boolean;
            isPinned: boolean;
        }>;
    };
};

export type CreateReferenceBusinessMutationVariables = Exact<{
    regionId: RegionId;
}>;

export type CreateReferenceBusinessMutation = {
    createReferenceBusiness: { id: ReferenceBusinessId };
};

export type PatchReferenceBusinessMutationVariables = Exact<{
    id: ReferenceBusinessId;
    summary?: string | null | undefined;
    email?: string | null | undefined;
    ownerId?: UserId | null | undefined;
    businessContactEntity?: Types.ContactInput | null | undefined;
    deleted?: boolean | null | undefined;
    draftNote?: string | null | undefined;
    bestDaysToVisit?: Array<string> | string | null | undefined;
    bestTimeToContact?: Array<string> | string | null | undefined;
}>;

export type PatchReferenceBusinessMutation = {
    patchReferenceBusiness: {
        id: ReferenceBusinessId;
        ownerId: UserId | null;
        deleted: boolean;
        summary: string | null;
        email: string | null;
        dateAdded: any;
        updatedAt: any;
        createdBy: UserId | null;
        updatedBy: UserId | null;
        draftNote: string | null;
        bestDaysToVisit: Array<string> | null;
        bestTimeToContact: Array<string> | null;
        regionId: RegionId;
        latitude: string | null;
        longitude: string | null;
        businessContactEntity: {
            id: ContactId | null;
            name: string | null;
            preferredName: string | null;
            address1: string | null;
            address2: string | null;
            city: string | null;
            state: string | null;
            zip: string | null;
            relationship: string | null;
            role: string | null;
            cellPhone: PhoneNumberE164 | null;
            phone1: PhoneNumberE164 | null;
            email1: string | null;
            email1OptOut: boolean;
            website: string | null;
            contactType: string | null;
            type: string | null;
            fax: PhoneNumberE164 | null;
            contactNotes: string | null;
            dateAdded: any;
        } | null;
        answers: Array<{
            questionId: QuestionId | null;
            booleanAnswer: boolean | null;
            numberAnswer: number | null;
            numberAnswerHigh: number | null;
            textAnswer: string | null;
            dateAnswer: string | null;
            calendarDateAnswer: string | null;
            selections: Array<string> | null;
            idReferenceAnswer: UserId | null;
            selectionAnnotations: Array<{
                text: string | null;
                selectionId: string | null;
            }> | null;
        }>;
        notes: Array<{
            _id: NoteId;
            text: string;
            dateAdded: any;
            wasEdited: boolean;
            authorId: UserId;
            editorId: UserId;
            updatedAt: any;
            isDraft: boolean;
            isPinned: boolean;
        }>;
    };
};

export type GetReferralsToBusinessQueryVariables = Exact<{
    referenceBusinessId: ReferenceBusinessId;
}>;

export type GetReferralsToBusinessQuery = {
    getReferralsToBusiness: Array<{
        id: ReferralId;
        placementId: PlacementId;
        placement: {
            id: PlacementId;
            name: string | null;
            bestContactName: string | null;
            statusListItemId: CustomListItemId;
            dateAdded: any;
            serial: number | null;
            assignedUserId: UserId | null;
            updatedAt: any;
            moveTimeline: string | null;
            moveTimelineActual: string | null;
            nextTask: {
                id: TaskId;
                text: string | null;
                dueDate: string | null;
                dueDateTime: any;
                compositeDueDateTime: any;
                status: string;
            } | null;
        } | null;
    }>;
};

export type ReferenceBusinessBasicsFragment = {
    id: ReferenceBusinessId;
    ownerId: UserId | null;
    deleted: boolean;
    summary: string | null;
    email: string | null;
    dateAdded: any;
    updatedAt: any;
    createdBy: UserId | null;
    updatedBy: UserId | null;
    draftNote: string | null;
    bestDaysToVisit: Array<string> | null;
    bestTimeToContact: Array<string> | null;
    regionId: RegionId;
    latitude: string | null;
    longitude: string | null;
    businessContactEntity: {
        id: ContactId | null;
        name: string | null;
        preferredName: string | null;
        address1: string | null;
        address2: string | null;
        city: string | null;
        state: string | null;
        zip: string | null;
        relationship: string | null;
        role: string | null;
        cellPhone: PhoneNumberE164 | null;
        phone1: PhoneNumberE164 | null;
        email1: string | null;
        email1OptOut: boolean;
        website: string | null;
        contactType: string | null;
        type: string | null;
        fax: PhoneNumberE164 | null;
        contactNotes: string | null;
        dateAdded: any;
    } | null;
    answers: Array<{
        questionId: QuestionId | null;
        booleanAnswer: boolean | null;
        numberAnswer: number | null;
        numberAnswerHigh: number | null;
        textAnswer: string | null;
        dateAnswer: string | null;
        calendarDateAnswer: string | null;
        selections: Array<string> | null;
        idReferenceAnswer: UserId | null;
        selectionAnnotations: Array<{
            text: string | null;
            selectionId: string | null;
        }> | null;
    }>;
    notes: Array<{
        _id: NoteId;
        text: string;
        dateAdded: any;
        wasEdited: boolean;
        authorId: UserId;
        editorId: UserId;
        updatedAt: any;
        isDraft: boolean;
        isPinned: boolean;
    }>;
};

export type GeocodeReferenceBusinessesMutationVariables = Exact<{ [key: string]: never }>;

export type GeocodeReferenceBusinessesMutation = { geocodeReferenceBusinesses: boolean };

export type AddReferenceContactFromAgentDirectoryMutationVariables = Exact<{
    userProfileId: UserProfileId;
    regionId: RegionId;
}>;

export type AddReferenceContactFromAgentDirectoryMutation = {
    addReferenceContactFromAgentDirectory: {
        id: ReferenceContactId;
        userProfileId: UserProfileId | null;
    };
};

export type GetRegionQueryVariables = Exact<{
    id: RegionId;
    tenantId: TenantId;
}>;

export type GetRegionQuery = {
    getRegion: {
        id: RegionId;
        key: string;
        name: string;
        parentRegionId: RegionId | null;
        billableEntityId: BillableEntityId | null;
        regionLicenseMaps: Array<{
            id: RegionLicenseMapId;
            autoPropagateEnabled: boolean;
            identityFilterObjects: Array<{
                state: string;
                zip: string | null;
                county: string | null;
            }>;
            licenseMappingObjects: Array<{
                licenseIdentifier: string;
                promptId: PromptId | null;
                questionId: QuestionId | null;
                annotationOnly: boolean | null;
            }>;
            settings: {
                mode: Types.RegionLicenseMapSettingsMode | null;
                updateAttributes: Types.RegionLicenseMapSettingsUpdateAttributes | null;
                closedHandling: Types.RegionLicenseMapSettingsClosedHandling | null;
            } | null;
        }>;
    };
};

export type GetStateReportQueryVariables = Exact<{ [key: string]: never }>;

export type GetStateReportQuery = {
    getStateReport: Array<{
        thecount: number;
        totalrlmcount: number;
        state: string;
        regionid: RegionId;
        regionkey: string;
        regionname: string;
        tenantname: string;
        tenantid: TenantId;
    }>;
};

export type PropagateChangesMutationVariables = Exact<{
    tenantId: TenantId;
    regionId: RegionId;
}>;

export type PropagateChangesMutation = {
    createCommunitiesInRegion: {
        propagations: Array<{
            regionLicenseMapId: RegionLicenseMapId;
            communitiesCreated: number;
            communitiesMatched: number;
            communitiesWithExistingLinks: number;
        }>;
    };
};

export type CreateOrUpdateRegionLicenseMapMutationVariables = Exact<{
    regionLicenseMap: Types.RegionLicenseMapUpdateRequest;
}>;

export type CreateOrUpdateRegionLicenseMapMutation = {
    createOrUpdateRegionLicenseMap: { id: RegionLicenseMapId };
};

export type DeleteRegionLicenseMapMutationVariables = Exact<{
    id: RegionLicenseMapId;
}>;

export type DeleteRegionLicenseMapMutation = { deleteRegionLicenseMap: boolean };

export type GetEntityRegionPermissionsQueryVariables = Exact<{
    params: Types.GetEntityRegionPermissionsParams;
}>;

export type GetEntityRegionPermissionsQuery = {
    getEntityRegionPermissions: {
        editableRegions: Array<{
            id: RegionId;
            key: string;
            name: string;
            type: Types.EntityRegionPermissionType;
        }>;
    };
};

export type ClientPlacementRevenueQueryVariables = Exact<{
    startDate: string;
    endDate: string;
    region: string;
    revenueBasis: Types.RevenueBasis;
    amountBasis: Types.RevenueAmountBasis;
}>;

export type ClientPlacementRevenueQuery = {
    reportClientPlacementRevenue: {
        segments: Array<{
            startDate: string;
            revenue: number;
            moves: number;
            users: Array<{
                revenue: number;
                moves: number;
                user: { id: UserId; displayName: string } | null;
            }>;
        }>;
    };
};

export type GetRegionSettingsQueryVariables = Exact<{
    params: Types.GetRegionSettingsRequest;
}>;

export type GetRegionSettingsQuery = {
    getRegionSettings: {
        regionId: RegionId;
        settings: Array<{
            key: string;
            source: Types.RegionSettingSource;
            value: {
                textValue: string | null;
                intValue: number | null;
                booleanValue: boolean | null;
                jsonValue: string | null;
            };
        }>;
    };
};

export type PutRegionSettingMutationVariables = Exact<{
    params: Types.PutRegionSettingRequest;
}>;

export type PutRegionSettingMutation = { putRegionSetting: boolean };

export type PutRegionSettingsMutationVariables = Exact<{
    params: Types.PutRegionSettingsRequest;
}>;

export type PutRegionSettingsMutation = { putRegionSettings: boolean };

export type DeleteRegionSettingMutationVariables = Exact<{
    params: Types.DeleteRegionSettingRequest;
}>;

export type DeleteRegionSettingMutation = { deleteRegionSetting: boolean };

export type DeleteRegionSettingsMutationVariables = Exact<{
    params: Types.DeleteRegionSettingsRequest;
}>;

export type DeleteRegionSettingsMutation = { deleteRegionSettings: boolean };

export type PutTenantSettingMutationVariables = Exact<{
    params: Types.PutTenantSettingRequest;
}>;

export type PutTenantSettingMutation = { putTenantSetting: boolean };

export type GetTenantSettingsQueryVariables = Exact<{ [key: string]: never }>;

export type GetTenantSettingsQuery = {
    getTenantSettings: {
        tenantId: TenantId;
        settings: Array<{
            key: string;
            value: {
                textValue: string | null;
                intValue: number | null;
                booleanValue: boolean | null;
                jsonValue: string | null;
            };
        }>;
    };
};

export type GetSignatureTemplateQueryVariables = Exact<{
    fileId: FileId;
}>;

export type GetSignatureTemplateQuery = {
    getFile: { originalFilename: string | null };
    getSignatureTemplate: {
        id: SignatureTemplateId;
        recipientCount: number;
        emailTemplate: {
            subject: string | null;
            body: string | null;
            attachments: Array<FileId> | null;
        } | null;
        completedEmailTemplate: {
            subject: string | null;
            body: string | null;
            attachments: Array<FileId> | null;
        } | null;
        signatureTemplateFields: Array<{
            defaultTextValue: string | null;
            defaultBooleanValue: boolean | null;
            isRequired: boolean;
            placeholder: string | null;
            fieldType: Types.SignatureFieldType;
            font: string;
            fontSize: number;
            height: number;
            id: SignatureTemplateFieldId;
            order: number;
            page: number;
            recipientIndex: number;
            width: number;
            x: number;
            y: number;
        }>;
    } | null;
};

export type SetSignatureTemplateMutationVariables = Exact<{
    fileId: FileId;
    signatureTemplate: Types.SignatureTemplateInput;
}>;

export type SetSignatureTemplateMutation = {
    setSignatureTemplate: { id: SignatureTemplateId };
};

export type GetAllSignatureRequestsQueryVariables = Exact<{
    signatureRequestFilter: Types.SignatureRequestFilter;
}>;

export type GetAllSignatureRequestsQuery = {
    getAllSignatureRequests: {
        total: number;
        page: number;
        signatureRequests: Array<{
            entity:
                | {
                      displayName: string | null;
                      kind: string;
                      kindTitle: string;
                      path: string;
                  }
                | { displayName: string; kind: string; kindTitle: string; path: string }
                | {
                      displayName: string | null;
                      kind: string;
                      kindTitle: string;
                      path: string;
                  }
                | { displayName: string; kind: string; kindTitle: string; path: string }
                | {
                      displayName: string | null;
                      kind: string;
                      kindTitle: string;
                      path: string;
                  }
                | {
                      displayName: string | null;
                      kind: string;
                      kindTitle: string;
                      path: string;
                  };
            signatureRequest: {
                id: SignatureRequestId;
                entityId: FileEntityId;
                createdAt: any;
                updatedAt: any;
                filename: string;
                sourceFileId: FileId;
                signatureTemplateFileId: FileId | null;
                state: Types.SignatureRequestState;
                signatureRequestRecipients: Array<{
                    email: string;
                    isComplete: boolean;
                    id: SignatureRequestRecipientId;
                    order: number;
                    remindAfter: any;
                }>;
            };
        }>;
    };
};

export type GetSignableTemplatesQueryVariables = Exact<{
    entityType: Types.FileEntityType;
}>;

export type GetSignableTemplatesQuery = {
    getSignableTemplates: Array<{ originalFilename: string | null; id: FileId }>;
};

export type GetSignatureRequestsForEntityQueryVariables = Exact<{
    entityId: FileEntityId;
}>;

export type GetSignatureRequestsForEntityQuery = {
    getSignatureRequestsForEntity: Array<{
        id: SignatureRequestId;
        entityId: FileEntityId;
        createdAt: any;
        updatedAt: any;
        filename: string;
        sourceFileId: FileId;
        signatureTemplateFileId: FileId | null;
        state: Types.SignatureRequestState;
        signatureRequestRecipients: Array<{
            email: string;
            isComplete: boolean;
            id: SignatureRequestRecipientId;
            order: number;
            remindAfter: any;
        }>;
    }>;
};

export type GetSignatureRequestQueryVariables = Exact<{
    signatureRequestId: SignatureRequestId;
}>;

export type GetSignatureRequestQuery = {
    getSignatureRequest: {
        id: SignatureRequestId;
        createdAt: any;
        updatedAt: any;
        entityId: FileEntityId;
        filename: string;
        sourceFileId: FileId;
        signatureTemplateFileId: FileId | null;
        state: Types.SignatureRequestState;
        signatureRequestFields: Array<{
            id: SignatureRequestFieldId;
            page: number;
            recipientIndex: number;
            order: number;
            x: number;
            y: number;
            width: number;
            height: number;
            font: string;
            fontSize: number;
            fieldType: Types.SignatureFieldType;
            textValue: string | null;
            booleanValue: boolean | null;
            isRequired: boolean;
            placeholder: string | null;
            filledBy: SignatureRequestRecipientId | null;
        }>;
        signatureRequestRecipients: Array<{
            id: SignatureRequestRecipientId;
            order: number;
            remindAfter: any;
            remindersSent: number;
            createdAt: any;
            updatedAt: any;
            completedAt: any;
            email: string;
            name: string;
            isComplete: boolean;
        }>;
        ccRecipients: Array<{ email: string; name: string | null }>;
    };
};

export type PlaceholderPerspectiveQueryVariables = Exact<{ [key: string]: never }>;

export type PlaceholderPerspectiveQuery = {
    placeholderPerspective: { recipientIndex: number };
};

export type CreateSignatureRequestMutationVariables = Exact<{
    signatureRequestPayload: Types.SignatureRequestPayload;
}>;

export type CreateSignatureRequestMutation = {
    createSignatureRequest: {
        id: SignatureRequestId;
        signatureRequestRecipients: Array<{ id: SignatureRequestRecipientId }>;
    };
};

export type GetDraftSignatureRequestQueryVariables = Exact<{
    entityId: FileEntityId;
    fileId: FileId;
    signatureRequestId?: SignatureRequestId | null | undefined;
}>;

export type GetDraftSignatureRequestQuery = {
    getFile: { originalFilename: string | null };
    getDraftSignatureRequest: {
        entityType: Types.AdvancedSearchEntityType | null;
        potentialRecipients: Array<{ name: string; email: string; source: string }>;
        signatureTemplate: {
            id: SignatureTemplateId;
            recipientCount: number;
            signatureTemplateFields: Array<{
                isRequired: boolean;
                defaultTextValue: string | null;
                defaultBooleanValue: boolean | null;
                placeholder: string | null;
                fieldType: Types.SignatureFieldType;
                font: string;
                fontSize: number;
                height: number;
                id: SignatureTemplateFieldId;
                order: number;
                page: number;
                recipientIndex: number;
                width: number;
                x: number;
                y: number;
            }>;
        };
        actualRecipients: Array<{ name: string; email: string }>;
        draftFields: Array<{
            signatureTemplateFieldId: SignatureTemplateFieldId;
            textValue: string | null;
            booleanValue: boolean | null;
            placeholder: string | null;
            isRequired: boolean | null;
        }> | null;
        emailTemplate: { subject: string; body: string; attachments: Array<FileId> };
        completedEmailTemplate: {
            subject: string;
            body: string;
            attachments: Array<FileId>;
        } | null;
        ccRecipients: Array<{ name: string | null; email: string }>;
    };
};

export type ResendSignatureRequestMutationVariables = Exact<{
    signatureRequestId: SignatureRequestId;
}>;

export type ResendSignatureRequestMutation = { resendSignatureRequest: boolean };

export type AbandonSignatureRequestMutationVariables = Exact<{
    signatureRequestId: SignatureRequestId;
}>;

export type AbandonSignatureRequestMutation = { abandonSignatureRequest: boolean };

export type SimpleSearchQueryVariables = Exact<{
    query: Types.SimpleSearchRequest;
}>;

export type SimpleSearchQuery = {
    simpleSearch: {
        hits: Array<{ type: string; excerpt: string; entityId: SimpleSearchEntityId }>;
    };
};

export type CreateSmtpCredentialMutationVariables = Exact<{
    params: Types.CreateSmtpCredentialRequest;
}>;

export type CreateSmtpCredentialMutation = {
    createSmtpCredential: { success: boolean; message: string | null };
};

export type GetSmtpCredentialForUserQueryVariables = Exact<{ [key: string]: never }>;

export type GetSmtpCredentialForUserQuery = {
    getSmtpCredentialForUser: {
        id: SmtpCredentialId;
        host: string;
        port: number;
        username: string;
        status: Types.SmtpCredentialStatus;
        authenticationType: Types.SmtpAuthenticationType;
        provider: Types.UserMailboxProvider | null;
    } | null;
};

export type DeleteSmtpCredentialForUserMutationVariables = Exact<{
    [key: string]: never;
}>;

export type DeleteSmtpCredentialForUserMutation = {
    deleteSmtpCredentialForUser: boolean;
};

export type SmtpCredentialBasicsFragment = {
    id: SmtpCredentialId;
    host: string;
    port: number;
    username: string;
    status: Types.SmtpCredentialStatus;
    authenticationType: Types.SmtpAuthenticationType;
    provider: Types.UserMailboxProvider | null;
};

export type SystemQueryVariables = Exact<{ [key: string]: never }>;

export type SystemQuery = {
    jobStats: {
        complete: number;
        pending: number;
        available: number;
        started: number;
        failed: number;
    };
    systemConfiguration: { fileStorage: string };
};

export type GetTaskQueryVariables = Exact<{
    regionId: RegionId;
    taskId: TaskId;
}>;

export type GetTaskQuery = {
    getTask: {
        populatedTask: {
            task: {
                id: TaskId;
                completedAt: any;
                dueDate: string | null;
                dueDateTime: any;
                notes: string | null;
                ownerId: UserId | null;
                parentId: TaskParentId;
                status: string;
                text: string | null;
                done: boolean;
                taskTypeId: TaskTypeId | null;
                recurrencePattern: Types.RecurrencePattern | null;
                recurrenceInterval: number | null;
                recurrenceDaysOfWeek: Array<Types.DayOfWeek> | null;
                recurrenceDayOfMonth: number | null;
                taskType: { id: TaskTypeId; name: string } | null;
            };
            entity:
                | { displayName: string | null; path: string; kind: string }
                | { displayName: string; path: string; kind: string }
                | { displayName: string | null; path: string; kind: string }
                | { displayName: string; path: string; kind: string }
                | { displayName: string | null; path: string; kind: string }
                | { displayName: string | null; path: string; kind: string };
        } | null;
    };
};

export type GetTasksQueryVariables = Exact<{
    activeOnly: boolean;
    regionId: string | number;
    page: number;
}>;

export type GetTasksQuery = {
    getTasks: {
        page: number;
        totalPages: number;
        tasks: Array<{
            task: {
                id: TaskId;
                completedAt: any;
                dueDate: string | null;
                dueDateTime: any;
                notes: string | null;
                ownerId: UserId | null;
                parentId: TaskParentId;
                status: string;
                text: string | null;
                done: boolean;
                taskTypeId: TaskTypeId | null;
                recurrencePattern: Types.RecurrencePattern | null;
                recurrenceInterval: number | null;
                recurrenceDaysOfWeek: Array<Types.DayOfWeek> | null;
                recurrenceDayOfMonth: number | null;
                taskType: { id: TaskTypeId; name: string } | null;
            };
            entity:
                | { displayName: string | null; path: string; kind: string }
                | { displayName: string; path: string; kind: string }
                | { displayName: string | null; path: string; kind: string }
                | { displayName: string; path: string; kind: string }
                | { displayName: string | null; path: string; kind: string }
                | { displayName: string | null; path: string; kind: string };
        }>;
    };
};

export type GetTasksv2QueryVariables = Exact<{
    params: Types.GetTasksRequest;
}>;

export type GetTasksv2Query = {
    getTasksv2: Array<{
        task: {
            id: TaskId;
            completedAt: any;
            dueDate: string | null;
            dueDateTime: any;
            notes: string | null;
            ownerId: UserId | null;
            parentId: TaskParentId;
            status: string;
            text: string | null;
            done: boolean;
            taskTypeId: TaskTypeId | null;
            recurrencePattern: Types.RecurrencePattern | null;
            recurrenceInterval: number | null;
            recurrenceDaysOfWeek: Array<Types.DayOfWeek> | null;
            recurrenceDayOfMonth: number | null;
            taskType: { id: TaskTypeId; name: string } | null;
        };
        entity:
            | {
                  displayName: string | null;
                  path: string;
                  kind: string;
                  contact: {
                      name: string | null;
                      phoneNumber: string | null;
                      contactType: Types.CrmEntityContactType;
                  } | null;
              }
            | {
                  displayName: string;
                  path: string;
                  kind: string;
                  contact: {
                      name: string | null;
                      phoneNumber: string | null;
                      contactType: Types.CrmEntityContactType;
                  } | null;
              }
            | {
                  displayName: string | null;
                  path: string;
                  kind: string;
                  contact: {
                      name: string | null;
                      phoneNumber: string | null;
                      contactType: Types.CrmEntityContactType;
                  } | null;
              }
            | {
                  displayName: string;
                  path: string;
                  kind: string;
                  contact: {
                      name: string | null;
                      phoneNumber: string | null;
                      contactType: Types.CrmEntityContactType;
                  } | null;
              }
            | {
                  displayName: string | null;
                  path: string;
                  kind: string;
                  contact: {
                      name: string | null;
                      phoneNumber: string | null;
                      contactType: Types.CrmEntityContactType;
                  } | null;
              }
            | {
                  displayName: string | null;
                  path: string;
                  kind: string;
                  contact: {
                      name: string | null;
                      phoneNumber: string | null;
                      contactType: Types.CrmEntityContactType;
                  } | null;
              };
    }>;
};

export type GetPopulatedTasksForParentQueryVariables = Exact<{
    regionId: RegionId;
    parentId: TaskParentId;
}>;

export type GetPopulatedTasksForParentQuery = {
    getPopulatedTasksForParent: Array<{
        task: {
            id: TaskId;
            completedAt: any;
            dueDate: string | null;
            dueDateTime: any;
            notes: string | null;
            ownerId: UserId | null;
            parentId: TaskParentId;
            status: string;
            text: string | null;
            done: boolean;
            taskTypeId: TaskTypeId | null;
            recurrencePattern: Types.RecurrencePattern | null;
            recurrenceInterval: number | null;
            recurrenceDaysOfWeek: Array<Types.DayOfWeek> | null;
            recurrenceDayOfMonth: number | null;
            taskType: { id: TaskTypeId; name: string } | null;
        };
        entity:
            | { displayName: string | null; path: string; kind: string }
            | { displayName: string; path: string; kind: string }
            | { displayName: string | null; path: string; kind: string }
            | { displayName: string; path: string; kind: string }
            | { displayName: string | null; path: string; kind: string }
            | { displayName: string | null; path: string; kind: string };
    }>;
};

export type GetNextTasksForParentIdsQueryVariables = Exact<{
    parentIds: Array<TaskParentId> | TaskParentId;
}>;

export type GetNextTasksForParentIdsQuery = {
    getNextTasksForParentIds: Array<{
        id: TaskId;
        completedAt: any;
        dueDate: string | null;
        dueDateTime: any;
        notes: string | null;
        ownerId: UserId | null;
        parentId: TaskParentId;
        status: string;
        text: string | null;
        done: boolean;
        taskTypeId: TaskTypeId | null;
        recurrencePattern: Types.RecurrencePattern | null;
        recurrenceInterval: number | null;
        recurrenceDaysOfWeek: Array<Types.DayOfWeek> | null;
        recurrenceDayOfMonth: number | null;
        taskType: { id: TaskTypeId; name: string } | null;
    }>;
};

export type GetTasksForEntityQueryVariables = Exact<{
    parentId: TaskParentId;
}>;

export type GetTasksForEntityQuery = {
    getTasksForEntity: Array<{
        notes: string | null;
        id: TaskId;
        completedAt: any;
        dueDate: string | null;
        dueDateTime: any;
        ownerId: UserId | null;
        parentId: TaskParentId;
        status: string;
        text: string | null;
        done: boolean;
        taskTypeId: TaskTypeId | null;
        recurrencePattern: Types.RecurrencePattern | null;
        recurrenceInterval: number | null;
        recurrenceDaysOfWeek: Array<Types.DayOfWeek> | null;
        recurrenceDayOfMonth: number | null;
        taskType: { id: TaskTypeId; name: string } | null;
    }>;
};

export type CreateTaskMutationVariables = Exact<{
    parentId: TaskParentId;
    params: Types.CreateTaskPayload;
}>;

export type CreateTaskMutation = {
    createTask: {
        notes: string | null;
        id: TaskId;
        completedAt: any;
        dueDate: string | null;
        dueDateTime: any;
        ownerId: UserId | null;
        parentId: TaskParentId;
        status: string;
        text: string | null;
        done: boolean;
        taskTypeId: TaskTypeId | null;
        recurrencePattern: Types.RecurrencePattern | null;
        recurrenceInterval: number | null;
        recurrenceDaysOfWeek: Array<Types.DayOfWeek> | null;
        recurrenceDayOfMonth: number | null;
        taskType: { id: TaskTypeId; name: string } | null;
    };
};

export type UpdateTaskMutationVariables = Exact<{
    taskId: TaskId;
    params: Types.UpdateTaskPayload;
}>;

export type UpdateTaskMutation = {
    updateTask: {
        notes: string | null;
        id: TaskId;
        completedAt: any;
        dueDate: string | null;
        dueDateTime: any;
        ownerId: UserId | null;
        parentId: TaskParentId;
        status: string;
        text: string | null;
        done: boolean;
        taskTypeId: TaskTypeId | null;
        recurrencePattern: Types.RecurrencePattern | null;
        recurrenceInterval: number | null;
        recurrenceDaysOfWeek: Array<Types.DayOfWeek> | null;
        recurrenceDayOfMonth: number | null;
        taskType: { id: TaskTypeId; name: string } | null;
    };
};

export type DeleteTaskMutationVariables = Exact<{
    taskId: TaskId;
}>;

export type DeleteTaskMutation = { deleteTask: boolean };

export type TaskBasicsFragment = {
    id: TaskId;
    completedAt: any;
    dueDate: string | null;
    dueDateTime: any;
    notes: string | null;
    ownerId: UserId | null;
    parentId: TaskParentId;
    status: string;
    text: string | null;
    done: boolean;
    taskTypeId: TaskTypeId | null;
    recurrencePattern: Types.RecurrencePattern | null;
    recurrenceInterval: number | null;
    recurrenceDaysOfWeek: Array<Types.DayOfWeek> | null;
    recurrenceDayOfMonth: number | null;
    taskType: { id: TaskTypeId; name: string } | null;
};

export type TaskDetailsFragment = {
    notes: string | null;
    id: TaskId;
    completedAt: any;
    dueDate: string | null;
    dueDateTime: any;
    ownerId: UserId | null;
    parentId: TaskParentId;
    status: string;
    text: string | null;
    done: boolean;
    taskTypeId: TaskTypeId | null;
    recurrencePattern: Types.RecurrencePattern | null;
    recurrenceInterval: number | null;
    recurrenceDaysOfWeek: Array<Types.DayOfWeek> | null;
    recurrenceDayOfMonth: number | null;
    taskType: { id: TaskTypeId; name: string } | null;
};

export type GetTaskTypesQueryVariables = Exact<{ [key: string]: never }>;

export type GetTaskTypesQuery = {
    getTaskTypes: Array<{
        id: TaskTypeId;
        name: string;
        isArchived: boolean;
        category: string;
        recordTypes: Array<string>;
    }>;
};

export type CreateTaskTypeMutationVariables = Exact<{
    payload: Types.CreateTaskTypePayload;
}>;

export type CreateTaskTypeMutation = { createTaskType: { id: TaskTypeId } };

export type UpdateTaskTypeMutationVariables = Exact<{
    payload: Types.UpdateTaskTypePayload;
}>;

export type UpdateTaskTypeMutation = { updateTaskType: { id: TaskTypeId } };

export type GetEntitiesWithoutActiveTasksPaginatedQueryVariables = Exact<{
    regionId: RegionId;
    entityType: Types.AdvancedSearchEntityType;
    page: number;
    pageSize?: number | null | undefined;
    assignedUserId?: string | null | undefined;
}>;

export type GetEntitiesWithoutActiveTasksPaginatedQuery = {
    getEntitiesWithoutActiveTasksPaginated: {
        page: number;
        totalPages: number;
        totalCount: number;
        entities: Array<
            | {
                  id: string;
                  displayName: string | null;
                  path: string;
                  kind: string;
                  kindTitle: string;
                  contact: {
                      name: string | null;
                      phoneNumber: string | null;
                      contactType: Types.CrmEntityContactType;
                  } | null;
              }
            | {
                  id: string;
                  displayName: string;
                  path: string;
                  kind: string;
                  kindTitle: string;
                  contact: {
                      name: string | null;
                      phoneNumber: string | null;
                      contactType: Types.CrmEntityContactType;
                  } | null;
              }
            | {
                  id: string;
                  displayName: string | null;
                  path: string;
                  kind: string;
                  kindTitle: string;
                  contact: {
                      name: string | null;
                      phoneNumber: string | null;
                      contactType: Types.CrmEntityContactType;
                  } | null;
              }
            | {
                  id: string;
                  displayName: string;
                  path: string;
                  kind: string;
                  kindTitle: string;
                  contact: {
                      name: string | null;
                      phoneNumber: string | null;
                      contactType: Types.CrmEntityContactType;
                  } | null;
              }
            | {
                  id: string;
                  displayName: string | null;
                  path: string;
                  kind: string;
                  kindTitle: string;
                  contact: {
                      name: string | null;
                      phoneNumber: string | null;
                      contactType: Types.CrmEntityContactType;
                  } | null;
              }
            | {
                  id: string;
                  displayName: string | null;
                  path: string;
                  kind: string;
                  kindTitle: string;
                  contact: {
                      name: string | null;
                      phoneNumber: string | null;
                      contactType: Types.CrmEntityContactType;
                  } | null;
              }
        >;
    };
};

export type SendSmsMutationVariables = Exact<{
    request: Types.SendSmsRequest;
}>;

export type SendSmsMutation = { sendSms: boolean };

export type ListRentablePhoneNumbersQueryVariables = Exact<{
    request: Types.ListRentablePhoneNumbersRequest;
}>;

export type ListRentablePhoneNumbersQuery = {
    listRentablePhoneNumbers: {
        phoneNumbers: Array<{
            phoneNumber: PhoneNumberE164;
            friendlyName: string;
            locality: string | null;
            region: string | null;
            postalCode: string | null;
            friendlyLocation: string | null;
        }>;
        suggestedAreaCodes: Array<{ areaCode: AreaCode; metroName: string }> | null;
    };
};

export type GetPersonalPhoneNumbersQueryVariables = Exact<{ [key: string]: never }>;

export type GetPersonalPhoneNumbersQuery = {
    getPersonalPhoneNumbers: Array<{
        id: TelecomPhoneLineId;
        userId: UserId;
        phoneNumber: PhoneNumberE164;
        createdAt: IsoTimestamp;
        updatedAt: IsoTimestamp;
        status: Types.TelecomPhoneLineStatus;
        credentialType: Types.TelecomPhoneLineCredentialType;
    }>;
};

export type ClaimPhoneNumberMutationVariables = Exact<{
    request: Types.ClaimPhoneNumberRequest;
}>;

export type ClaimPhoneNumberMutation = {
    claimPhoneNumber: {
        id: TelecomPhoneLineId;
        userId: UserId;
        phoneNumber: PhoneNumberE164;
        createdAt: IsoTimestamp;
        updatedAt: IsoTimestamp;
        status: Types.TelecomPhoneLineStatus;
        credentialType: Types.TelecomPhoneLineCredentialType;
    };
};

export type TelecomPhoneLineBasicsFragment = {
    id: TelecomPhoneLineId;
    userId: UserId;
    phoneNumber: PhoneNumberE164;
    createdAt: IsoTimestamp;
    updatedAt: IsoTimestamp;
    status: Types.TelecomPhoneLineStatus;
    credentialType: Types.TelecomPhoneLineCredentialType;
};

export type ReleasePhoneNumberMutationVariables = Exact<{
    id: TelecomPhoneLineId;
}>;

export type ReleasePhoneNumberMutation = { releasePhoneNumber: boolean };

export type SmsMessageBasicsFragment = {
    id: SmsMessageId;
    from: PhoneNumberE164;
    to: PhoneNumberE164;
    body: string;
    direction: Types.SmsDirection;
    dateAdded: IsoTimestamp;
    status: Types.SmsMessageStatus;
    createdBy: UserId;
    createdByUser: { id: UserId; displayName: string };
};

export type GetSmsConversationQueryVariables = Exact<{
    request: Types.GetSmsConversationRequest;
}>;

export type GetSmsConversationQuery = {
    getSmsConversation: Array<{
        id: SmsMessageId;
        from: PhoneNumberE164;
        to: PhoneNumberE164;
        body: string;
        direction: Types.SmsDirection;
        dateAdded: IsoTimestamp;
        status: Types.SmsMessageStatus;
        createdBy: UserId;
        createdByUser: { id: UserId; displayName: string };
    }>;
};

export type GetSmsConversationByPhoneLineQueryVariables = Exact<{
    request: Types.GetSmsConversationByPhoneLineRequest;
}>;

export type GetSmsConversationByPhoneLineQuery = {
    getSmsConversationByPhoneLine: Array<{
        id: SmsMessageId;
        from: PhoneNumberE164;
        to: PhoneNumberE164;
        body: string;
        direction: Types.SmsDirection;
        dateAdded: IsoTimestamp;
        status: Types.SmsMessageStatus;
        createdBy: UserId;
        createdByUser: { id: UserId; displayName: string };
    }>;
};

export type MarkPriorSmsConsentMutationVariables = Exact<{
    request: Types.MarkPriorSmsConsentRequest;
}>;

export type MarkPriorSmsConsentMutation = { markPriorSmsConsent: boolean };

export type GetTelecomContactStatusQueryVariables = Exact<{
    request: Types.GetTelecomContactStatusRequest;
}>;

export type GetTelecomContactStatusQuery = {
    getTelecomContactStatus: {
        phoneNumber: PhoneNumberE164;
        consentStatus: Types.SmsConsentStatus;
        canSend: boolean;
    };
};

export type RequestVoiceTokenMutationVariables = Exact<{
    phoneLineId: TelecomPhoneLineId;
}>;

export type RequestVoiceTokenMutation = {
    requestVoiceToken: { token: string; identity: string };
};

export type LogVoiceCallMutationVariables = Exact<{
    entityId: ActivityParentEntityId;
    phoneNumber: string;
    phoneLineId: TelecomPhoneLineId;
    activityTypeId?: TaskTypeId | null | undefined;
}>;

export type LogVoiceCallMutation = { logVoiceCall: VoiceCallId };

export type GetTemplateQueryVariables = Exact<{
    id: EmailTemplateId;
}>;

export type GetTemplateQuery = {
    getTemplate: {
        isEditable: boolean;
        isDefaultSignature: boolean;
        template: {
            id: EmailTemplateId;
            body: string | null;
            description: string | null;
            isShared: boolean;
            type: string;
            subject: string | null;
            migrationVersion: number;
            attachments: Array<FileId> | null;
            regionVisibility: {
                editable: boolean;
                regions: Array<{
                    mode: Types.EntityRegionModeOutput;
                    regionId: RegionId;
                }>;
            } | null;
        };
        author: { name: string | null; email: string };
    };
};

export type GetTemplatesQueryVariables = Exact<{ [key: string]: never }>;

export type GetTemplatesQuery = {
    getTemplates: {
        templates: Array<{
            isEditable: boolean;
            isDefaultSignature: boolean;
            template: {
                id: EmailTemplateId;
                body: string | null;
                description: string | null;
                isShared: boolean;
                type: string;
                subject: string | null;
                migrationVersion: number;
                attachments: Array<FileId> | null;
                regionVisibility: {
                    editable: boolean;
                    regions: Array<{
                        mode: Types.EntityRegionModeOutput;
                        regionId: RegionId;
                    }>;
                } | null;
            };
            author: { name: string | null; email: string };
        }>;
    };
};

export type CanCreateSignatureTemplateQueryVariables = Exact<{
    fileId: FileId;
}>;

export type CanCreateSignatureTemplateQuery = {
    canCreateSignatureTemplate: Types.SignatureCreatePermission;
};

export type DeleteSignatureTemplateMutationVariables = Exact<{
    signatureTemplateId: SignatureTemplateId;
}>;

export type DeleteSignatureTemplateMutation = {
    deleteSignatureTemplate: { success: boolean; message: string | null };
};

export type CreateOrUpdateTemplateMutationVariables = Exact<{
    template: Types.TemplateUpdate;
}>;

export type CreateOrUpdateTemplateMutation = {
    createOrUpdateTemplate: { template: { id: EmailTemplateId } };
};

export type DeleteTemplateMutationVariables = Exact<{
    id: EmailTemplateId;
}>;

export type DeleteTemplateMutation = { deleteTemplate: boolean };

export type GetTenantCredentialsQueryVariables = Exact<{ [key: string]: never }>;

export type GetTenantCredentialsQuery = {
    getTenantCredentials: Array<{
        id: TenantCredentialId;
        type: Types.TenantCredentialType;
        status: Types.TenantCredentialStatus;
        quickBooksDetails: { realmId: string } | null;
    }>;
};

export type DeleteTenantCredentialMutationVariables = Exact<{
    id: TenantCredentialId;
}>;

export type DeleteTenantCredentialMutation = { deleteTenantCredential: boolean };

export type CreateTenantRegionMutationVariables = Exact<{
    tenantId: TenantId;
    name: string;
    key: string;
}>;

export type CreateTenantRegionMutation = { createTenantRegion: { id: TenantId } };

export type UpdateTenantMutationVariables = Exact<{
    id: TenantId;
    name?: string | null | undefined;
    dateAdded?: string | null | undefined;
    features?: Array<string> | string | null | undefined;
    sku?: string | null | undefined;
    customDomains?: Array<string> | string | null | undefined;
    emailProvider?: Types.ExternalEmailProvider | null | undefined;
    internalEmailSender?: string | null | undefined;
    supportEmailAddress?: string | null | undefined;
    customProductName?: string | null | undefined;
    customAppDomain?: string | null | undefined;
    whiteLabelContentMode?: Types.WhiteLabelContentMode | null | undefined;
    baaEnforced?: boolean | null | undefined;
    baaDocumentUrl?: string | null | undefined;
    communicationAcknowledgmentUrl?: string | null | undefined;
    franchiseManagementEnabled?: boolean | null | undefined;
}>;

export type UpdateTenantMutation = { updateTenant: { id: TenantId } };

export type CreateTenantHistoryEntryMutationVariables = Exact<{
    tenantId: TenantId;
    message: string;
}>;

export type CreateTenantHistoryEntryMutation = {
    createTenantHistoryEntry: {
        id: InternalId;
        message: string;
        dateAdded: any;
        author: { preferredName: string };
    };
};

export type UpdateRegionNameMutationVariables = Exact<{
    tenantId: TenantId;
    regionId: RegionId;
    name: string;
}>;

export type UpdateRegionNameMutation = { updateRegion: { id: RegionId } };

export type UpdateRegionMutationVariables = Exact<{
    tenantId: TenantId;
    regionId: RegionId;
    name?: string | null | undefined;
    parentRegionId?: RegionId | null | undefined;
}>;

export type UpdateRegionMutation = { updateRegion: { id: RegionId } };

export type ModifyUserRoleMutationVariables = Exact<{
    tenantId: TenantId;
    userId: UserId;
    regionKey: string | number;
    role: string;
}>;

export type ModifyUserRoleMutation = { modifyUserRole: boolean };

export type DestroyPermissionMutationVariables = Exact<{
    id: InternalId;
}>;

export type DestroyPermissionMutation = { destroyPermission: boolean };

export type GetTenantsQueryVariables = Exact<{ [key: string]: never }>;

export type GetTenantsQuery = {
    getTenants: Array<{
        id: TenantId;
        name: string;
        activeUsers: number;
        activeHumanUsers: number;
        sku: string;
    }>;
};

export type GetTenantQueryVariables = Exact<{
    id: TenantId;
}>;

export type GetTenantQuery = {
    getTenant: {
        id: TenantId;
        name: string;
        dateAdded: string;
        sku: string;
        customDomains: Array<string>;
        emailProvider: Types.ExternalEmailProvider;
        internalEmailSender: string | null;
        supportEmailAddress: string | null;
        customProductName: string | null;
        customAppDomain: string | null;
        whiteLabelContentMode: Types.WhiteLabelContentMode;
        billableEntityId: BillableEntityId;
        baaEnforced: boolean;
        baaDocumentUrl: string | null;
        communicationAcknowledgmentUrl: string | null;
        franchiseManagementEnabled: boolean;
        users: Array<{
            id: UserId;
            name: string | null;
            email: string;
            disabled: boolean;
            hidden: boolean;
        }>;
        features: Array<{ name: string; enabled: boolean; enabledUpstream: boolean }>;
        regions: Array<{
            id: RegionId;
            name: string;
            key: string;
            parentRegionId: RegionId | null;
            billableEntityId: BillableEntityId | null;
            permissions: Array<{ role: string; userId: UserId }>;
        }>;
    };
    getGlobalFeatures: {
        skus: Array<string>;
        features: Array<{
            skus: Array<string> | null;
            key: string;
            friendlyTitle: string;
            description: string;
            createdOn: string;
            intent: string;
            actual: { name: string; status: Types.FeatureStatus };
        }>;
    };
};

export type GetTenantHistoryQueryVariables = Exact<{
    tenantId: TenantId;
}>;

export type GetTenantHistoryQuery = {
    getTenantHistory: Array<{
        id: InternalId;
        message: string;
        dateAdded: any;
        author: { preferredName: string };
    }>;
};

export type GetImportsQueryVariables = Exact<{
    tenantId: TenantId;
}>;

export type GetImportsQuery = {
    getImports: Array<{
        id: ImportId;
        name: string;
        status: Types.ImportStatus;
        dateAdded: any;
        runAs: UserId;
        regionId: RegionId | null;
    }>;
};

export type GetImportQueryVariables = Exact<{
    tenantId: TenantId;
    importId: ImportId;
}>;

export type GetImportQuery = {
    getImport: {
        id: ImportId;
        name: string;
        status: Types.ImportStatus;
        dateAdded: any;
        runAs: UserId;
        regionId: RegionId | null;
        plan: {
            inserts: Array<{ entityType: string; records: Array<string> }>;
            errors: Array<{
                rowNumber: number | null;
                message: string;
                sheetName: string;
            }> | null;
        } | null;
        result: {
            error: string | null;
            results: Array<{
                entityType: string;
                inserts: Array<{
                    rowNumber: number;
                    insertedId: string | null;
                    warnings: Array<string> | null;
                }>;
                errors: Array<{ rowNumber: number; message: string; sheetName: string }>;
            }>;
        } | null;
    };
};

export type ScheduleImportMutationVariables = Exact<{
    tenantId: TenantId;
    importId: ImportId;
}>;

export type ScheduleImportMutation = {
    scheduleImport: { id: ImportId; status: Types.ImportStatus };
};

export type ResetTenantMutationVariables = Exact<{
    options: Types.ResetTenantOptions;
}>;

export type ResetTenantMutation = { resetTenant: boolean };

export type GetTokenForClientQueryVariables = Exact<{
    id: PlacementId;
}>;

export type GetTokenForClientQuery = {
    getTokenForClient: {
        id: TokenId;
        clientId: PlacementId;
        url: string;
        includeContacts: boolean;
        includeFinancial: boolean;
        includeLicensing: boolean;
        sectionKeys: Array<Types.LayoutSectionKey> | null;
    };
};

export type UpdateTokenMutationVariables = Exact<{
    updateRequest: Types.UpdateTokenRequest;
}>;

export type UpdateTokenMutation = {
    updateToken: {
        id: TokenId;
        clientId: PlacementId;
        url: string;
        includeContacts: boolean;
        includeFinancial: boolean;
        includeLicensing: boolean;
        sectionKeys: Array<Types.LayoutSectionKey> | null;
    };
};

export type SetupTwoFactorMutationVariables = Exact<{
    input: Types.TwoFactorSetupInput;
}>;

export type SetupTwoFactorMutation = { setupTwoFactor: { success: boolean } };

export type VerifySetupCodeMutationVariables = Exact<{
    input: Types.TwoFactorSetupCodeInput;
}>;

export type VerifySetupCodeMutation = {
    verifySetupCode: { success: boolean; message: string | null };
};

export type DisableSpecificTwoFactorMethodMutationVariables = Exact<{
    method: Types.TwoFactorMethod;
    password: string;
}>;

export type DisableSpecificTwoFactorMethodMutation = {
    disableSpecificTwoFactorMethod: boolean;
};

export type SendTwoFactorCodeMutationVariables = Exact<{
    input: Types.TwoFactorSendCodeInput;
}>;

export type SendTwoFactorCodeMutation = { sendTwoFactorCode: boolean };

export type GetUserGroupsQueryVariables = Exact<{ [key: string]: never }>;

export type GetUserGroupsQuery = {
    getUserGroups: Array<{ id: UserGroupId; name: string }>;
};

export type GetUserMailboxesQueryVariables = Exact<{ [key: string]: never }>;

export type GetUserMailboxesQuery = {
    getUserMailboxes: Array<{
        id: UserMailboxId;
        status: Types.UserMailboxStatus;
        email: string | null;
        provider: Types.UserMailboxProvider;
        isHistoricalSyncRunning: boolean;
    }>;
};

export type DeleteUserMailboxMutationVariables = Exact<{
    id: UserMailboxId;
}>;

export type DeleteUserMailboxMutation = { deleteUserMailbox: boolean };

export type UserManagementGetRolesQueryVariables = Exact<{ [key: string]: never }>;

export type UserManagementGetRolesQuery = {
    userManagementGetRoles: Array<{ key: string; humanReadableName: string }>;
};

export type UserManagementGetUsersQueryVariables = Exact<{ [key: string]: never }>;

export type UserManagementGetUsersQuery = {
    userManagementGetUsers: Array<{
        user: {
            id: UserId;
            email: string;
            name: string | null;
            type: Types.UserType;
            hidden: boolean;
            disabled: boolean;
            twoFactorEmailEnabled: boolean;
            twoFactorSmsEnabled: boolean;
            permissions: Array<{
                role: string;
                region: {
                    id: RegionId;
                    name: string;
                    key: string;
                    membershipType: Types.EntityRegionPermissionType;
                };
            }>;
        };
    }>;
};

export type UserManagementGetIntegrationUsersQueryVariables = Exact<{
    [key: string]: never;
}>;

export type UserManagementGetIntegrationUsersQuery = {
    userManagementGetUsers: Array<{
        user: {
            id: UserId;
            email: string;
            name: string | null;
            type: Types.UserType;
            hidden: boolean;
            disabled: boolean;
            twoFactorEmailEnabled: boolean;
            twoFactorSmsEnabled: boolean;
            permissions: Array<{
                role: string;
                region: {
                    id: RegionId;
                    name: string;
                    key: string;
                    membershipType: Types.EntityRegionPermissionType;
                };
            }>;
        };
    }>;
};

export type UserManagementGetUserQueryVariables = Exact<{
    userId: UserId;
}>;

export type UserManagementGetUserQuery = {
    userManagementGetUser: {
        canDisable: boolean;
        user: {
            id: UserId;
            email: string;
            name: string | null;
            type: Types.UserType;
            hidden: boolean;
            disabled: boolean;
            twoFactorEmailEnabled: boolean;
            twoFactorSmsEnabled: boolean;
            permissions: Array<{
                role: string;
                region: {
                    id: RegionId;
                    name: string;
                    key: string;
                    membershipType: Types.EntityRegionPermissionType;
                };
            }>;
        };
    };
};

export type UserManagementGetImpliedUsersQueryVariables = Exact<{ [key: string]: never }>;

export type UserManagementGetImpliedUsersQuery = {
    userManagementGetImpliedUsers: Array<{
        user: {
            id: UserId;
            email: string;
            name: string | null;
            type: Types.UserType;
            hidden: boolean;
            disabled: boolean;
            twoFactorEmailEnabled: boolean;
            twoFactorSmsEnabled: boolean;
            permissions: Array<{
                role: string;
                region: {
                    id: RegionId;
                    name: string;
                    key: string;
                    membershipType: Types.EntityRegionPermissionType;
                };
            }>;
        };
    }>;
};

export type UserManagementUserFragment = {
    id: UserId;
    email: string;
    name: string | null;
    type: Types.UserType;
    hidden: boolean;
    disabled: boolean;
    twoFactorEmailEnabled: boolean;
    twoFactorSmsEnabled: boolean;
    permissions: Array<{
        role: string;
        region: {
            id: RegionId;
            name: string;
            key: string;
            membershipType: Types.EntityRegionPermissionType;
        };
    }>;
};

export type UserManagementGetApiKeyQueryVariables = Exact<{
    userId: UserId;
}>;

export type UserManagementGetApiKeyQuery = {
    userManagementGetApiKey: { apiKey: string };
};

export type UserManagementCreateUserMutationVariables = Exact<{
    userEmail: string;
    roleKey: string;
    regionId: RegionId;
}>;

export type UserManagementCreateUserMutation = { userManagementCreateUser: boolean };

export type UserManagementCreateSystemUserMutationVariables = Exact<{
    name: string;
    roleKey: string;
    regionId: RegionId;
}>;

export type UserManagementCreateSystemUserMutation = {
    userManagementCreateSystemUser: boolean;
};

export type UserManagementUpdatePermissionMutationVariables = Exact<{
    userId: UserId;
    regionId: RegionId;
    roleKey?: string | null | undefined;
}>;

export type UserManagementUpdatePermissionMutation = {
    userManagementUpdatePermission: boolean;
};

export type UserManagementDisableUserMutationVariables = Exact<{
    userId: UserId;
}>;

export type UserManagementDisableUserMutation = { userManagementDisableUser: boolean };

export type UserManagementEnableUserMutationVariables = Exact<{
    userId: UserId;
}>;

export type UserManagementEnableUserMutation = { userManagementEnableUser: boolean };

export type UserManagementHideUserMutationVariables = Exact<{
    userId: UserId;
}>;

export type UserManagementHideUserMutation = { userManagementHideUser: boolean };

export type UserManagementShowUserMutationVariables = Exact<{
    userId: UserId;
}>;

export type UserManagementShowUserMutation = { userManagementShowUser: boolean };

export type SuperuserAccountRecoveryMutationVariables = Exact<{
    userId: UserId;
}>;

export type SuperuserAccountRecoveryMutation = { superuserAccountRecovery: boolean };

export type SuperuserResetPasswordMutationVariables = Exact<{
    userId: UserId;
}>;

export type SuperuserResetPasswordMutation = {
    superuserResetPassword: { userId: string; password: string };
};

export type GetUsersQueryVariables = Exact<{ [key: string]: never }>;

export type GetUsersQuery = {
    getUsers: Array<{
        id: UserId;
        email: string;
        type: Types.UserType;
        name: string | null;
        firstName: string | null;
        disabled: boolean;
        hidden: boolean;
    }>;
};

export type CreateUserMutationVariables = Exact<{
    tenantId: TenantId;
    email: string;
    password: string;
}>;

export type CreateUserMutation = { createUser: { id: UserId; email: string } };

export type CloneUserMutationVariables = Exact<{
    tenantId: TenantId;
    userId: UserId;
}>;

export type CloneUserMutation = { cloneUser: boolean };

export type SuperuserUpdateUserMutationVariables = Exact<{
    userId: UserId;
    disabled: boolean;
    hidden: boolean;
    hideSmtpWarning: boolean;
    email: string;
    firstName?: string | null | undefined;
    name?: string | null | undefined;
}>;

export type SuperuserUpdateUserMutation = { superuserUpdateUser: { id: UserId } };

export type AdminUpdateUserMutationVariables = Exact<{
    userId: UserId;
    name: string;
}>;

export type AdminUpdateUserMutation = { adminUpdateUser: { id: UserId } };

export type GetUserQueryVariables = Exact<{
    id: UserId;
}>;

export type GetUserQuery = {
    getUser: {
        id: UserId;
        name: string | null;
        firstName: string | null;
        type: Types.UserType;
        email: string;
        disabled: boolean;
        hidden: boolean;
        hideSmtpWarning: boolean;
        token: string | null;
        twoFactorEmailEnabled: boolean;
        twoFactorSmsEnabled: boolean;
        tenant: {
            id: TenantId;
            name: string;
            regions: Array<{
                id: RegionId;
                key: string;
                name: string;
                permissions: Array<{
                    id: InternalId;
                    role: string;
                    userId: UserId;
                    deletedAt: IsoTimestamp | null;
                }>;
            }>;
            features: Array<{ name: string; enabled: boolean; enabledUpstream: boolean }>;
        };
    };
};

export type GetUserApiTokenQueryVariables = Exact<{
    id: UserId;
}>;

export type GetUserApiTokenQuery = { getUser: { id: UserId; apiToken: string } };

export type GetRolesWithActionsQueryVariables = Exact<{
    regionId: RegionId;
}>;

export type GetRolesWithActionsQuery = {
    getRolesWithActions: Array<{
        key: string;
        humanReadableName: string;
        userAssignable: boolean;
        actions: Array<Types.ActionType>;
    }>;
};

export type GetCurrentUserQueryVariables = Exact<{ [key: string]: never }>;

export type GetCurrentUserQuery = {
    getCurrentUser: {
        id: UserId;
        name: string | null;
        firstName: string | null;
        type: Types.UserType;
        email: string;
        disabled: boolean;
        hidden: boolean;
        hideSmtpWarning: boolean;
        token: string | null;
        phone: PhoneNumberE164 | null;
        twoFactorEmailEnabled: boolean;
        twoFactorSmsEnabled: boolean;
        tenant: { id: TenantId; name: string };
    };
};

export type GetUserProfileQueryVariables = Exact<{ [key: string]: never }>;

export type GetUserProfileQuery = {
    getUserProfile: {
        id: UserProfileId;
        name: string;
        businessName: string;
        website: string | null;
        email: string;
        phone: PhoneNumberE164;
        summary: string;
        approach: string;
        certifications: Array<string>;
        pictureUrl: string | null;
        status: Types.UserProfileStatus;
        latitude: string | null;
        longitude: string | null;
        isComplete: boolean;
        territories: Array<{
            id: UserTerritoryId;
            type: Types.UserTerritoryType;
            distance: number | null;
            address: string | null;
            city: string | null;
            county: string | null;
            state: string | null;
            zip: string | null;
            latitude: string | null;
            longitude: string | null;
        }>;
    };
};

export type PatchUserProfileMutationVariables = Exact<{
    params: Types.PatchUserProfileRequest;
}>;

export type PatchUserProfileMutation = {
    patchUserProfile: {
        id: UserProfileId;
        name: string;
        businessName: string;
        website: string | null;
        email: string;
        phone: PhoneNumberE164;
        summary: string;
        approach: string;
        certifications: Array<string>;
        pictureUrl: string | null;
        status: Types.UserProfileStatus;
        latitude: string | null;
        longitude: string | null;
        isComplete: boolean;
        territories: Array<{
            id: UserTerritoryId;
            type: Types.UserTerritoryType;
            distance: number | null;
            address: string | null;
            city: string | null;
            county: string | null;
            state: string | null;
            zip: string | null;
            latitude: string | null;
            longitude: string | null;
        }>;
    };
};

export type UserProfileBasicsFragment = {
    id: UserProfileId;
    name: string;
    businessName: string;
    website: string | null;
    email: string;
    phone: PhoneNumberE164;
    summary: string;
    approach: string;
    certifications: Array<string>;
    pictureUrl: string | null;
    status: Types.UserProfileStatus;
    latitude: string | null;
    longitude: string | null;
    isComplete: boolean;
    territories: Array<{
        id: UserTerritoryId;
        type: Types.UserTerritoryType;
        distance: number | null;
        address: string | null;
        city: string | null;
        county: string | null;
        state: string | null;
        zip: string | null;
        latitude: string | null;
        longitude: string | null;
    }>;
};

export type UpsertUserTerritoryMutationVariables = Exact<{
    params: Types.UpsertUserTerritoryRequest;
}>;

export type UpsertUserTerritoryMutation = {
    upsertUserTerritory: {
        id: UserTerritoryId;
        type: Types.UserTerritoryType;
        distance: number | null;
        address: string | null;
        city: string | null;
        county: string | null;
        state: string | null;
        zip: string | null;
        latitude: string | null;
        longitude: string | null;
    };
};

export type UserTerritoryBasicsFragment = {
    id: UserTerritoryId;
    type: Types.UserTerritoryType;
    distance: number | null;
    address: string | null;
    city: string | null;
    county: string | null;
    state: string | null;
    zip: string | null;
    latitude: string | null;
    longitude: string | null;
};

export type DeleteUserTerritoryMutationVariables = Exact<{
    id: UserTerritoryId;
}>;

export type DeleteUserTerritoryMutation = { deleteUserTerritory: boolean };

export type SearchUserProfilesQueryVariables = Exact<{
    params: Types.SearchUserProfileRequest;
}>;

export type SearchUserProfilesQuery = {
    searchUserProfiles: {
        total: number;
        hits: Array<{
            verticals: Array<string>;
            id: UserProfileId;
            name: string;
            businessName: string;
            website: string | null;
            email: string;
            phone: PhoneNumberE164;
            summary: string;
            approach: string;
            certifications: Array<string>;
            pictureUrl: string | null;
            status: Types.UserProfileStatus;
            latitude: string | null;
            longitude: string | null;
            isComplete: boolean;
            territories: Array<{
                id: UserTerritoryId;
                type: Types.UserTerritoryType;
                distance: number | null;
                address: string | null;
                city: string | null;
                county: string | null;
                state: string | null;
                zip: string | null;
                latitude: string | null;
                longitude: string | null;
            }>;
        }>;
        referenceContacts: Array<{
            id: ReferenceContactId;
            userProfileId: UserProfileId | null;
            appLink: string;
        }>;
        coordinates: { lat: string; lng: string } | null;
    };
};

export type SearchUserProfilesMapViewQueryVariables = Exact<{
    params: Types.SearchUserProfileRequest;
    mapView: Types.CommunitySearchRequestMapComponent;
}>;

export type SearchUserProfilesMapViewQuery = {
    searchUserProfilesMapView: {
        recommendedZoom: number;
        userProfileCoordinates: Array<{
            id: UserProfileId;
            name: string | null;
            lat: number;
            lng: number;
        }>;
        userProfileClusters: Array<{ lat: number; lng: number; count: number }>;
        coordinates: { lat: string; lng: string } | null;
    };
};

export type GetCalendarSubscriptionQueryVariables = Exact<{ [key: string]: never }>;

export type GetCalendarSubscriptionQuery = {
    getCalendarSubscription: { calendarUrl: string };
};

export type GetUserTenantRegionOptionsQueryVariables = Exact<{ [key: string]: never }>;

export type GetUserTenantRegionOptionsQuery = {
    getUserTenantRegionOptions: {
        preferredRegionId: RegionId | null;
        options: Array<{
            regionId: RegionId;
            regionName: string;
            regionKey: string;
            tenantId: TenantId;
            tenantName: string;
            userId: UserId;
        }>;
    };
};

export type GetUsersForRegionQueryVariables = Exact<{
    regionId: RegionId;
}>;

export type GetUsersForRegionQuery = {
    getUsersForRegion: Array<{
        id: UserId;
        disabled: boolean;
        hidden: boolean;
        preferredName: string;
        email: string;
        type: Types.UserType;
    }>;
};

export type GetVisibleUsersAcrossRegionsQueryVariables = Exact<{ [key: string]: never }>;

export type GetVisibleUsersAcrossRegionsQuery = {
    getVisibleUsersAcrossRegions: Array<{
        id: UserId;
        disabled: boolean;
        hidden: boolean;
        preferredName: string;
        email: string;
    }>;
};

export type WorkflowDefaultsFragment = {
    attachTo: Types.AdvancedSearchEntityType;
    createdBy: UserId;
    dateAdded: any;
    delay: number;
    delayRecipe: string | null;
    description: string;
    id: WorkflowId;
    name: string;
    status: Types.WorkflowStatus;
    defaultActorId: UserId;
    actorMode: Types.WorkflowActorMode;
    updatedAt: any;
    updatedBy: UserId;
    actions: Array<
        | {
              __typename: "WorkflowActionEmailEntity";
              body: string;
              subject: string;
              to: string;
              discreteTo: string | null;
              attachments: string | null;
              bccMyself: string | null;
          }
        | {
              __typename: "WorkflowActionTaskEntity";
              dueDate: string;
              text: string;
              assignedTo: string | null;
              taskTypeId: string | null;
              notifyOwner: boolean | null;
              recurrencePattern: string | null;
              recurrenceInterval: string | null;
              recurrenceDaysOfWeek: string | null;
              recurrenceDayOfMonth: string | null;
              notification: { subject: string | null } | null;
          }
    >;
    settings: Array<{
        defaultValue: string;
        description: string;
        key: string;
        label: string;
        type: Types.WorkflowSettingType;
        scope: string;
    }>;
    trigger: {
        eventType: Types.WorkflowEventType;
        entityUpdated: {
            fieldMatchers: Array<{
                fieldName: string;
                previous: {
                    values: Array<string> | null;
                    specialValue: string | null;
                } | null;
                current: {
                    values: Array<string> | null;
                    specialValue: string | null;
                } | null;
            }>;
        } | null;
        noteAdded: {
            entityFieldMatchers: Array<{
                fieldName: string;
                current: {
                    values: Array<string> | null;
                    specialValue: string | null;
                } | null;
            }>;
        } | null;
        emailOpened: {
            activityThresholdDays: number;
            entityFieldMatchers: Array<{
                fieldName: string;
                current: {
                    values: Array<string> | null;
                    specialValue: string | null;
                } | null;
            }>;
        } | null;
        pipelineStageChanged: {
            pipelineId: PipelineId;
            toStageIds: Array<PipelineStageId>;
        } | null;
        schedule: {
            cron: string;
            entityCondition: {
                nodeType: Types.AdvancedSearchConditionNodeType;
                fieldName: string | null;
                operator: Types.FieldConditionOperator | null;
                textValue: string | null;
                dateValue: any;
                calendarDateValue: string | null;
                numberValue: number | null;
                questionId: QuestionId | null;
                promptId: PromptId | null;
                children: Array<{
                    nodeType: Types.AdvancedSearchConditionNodeType;
                    fieldName: string | null;
                    operator: Types.FieldConditionOperator | null;
                    textValue: string | null;
                    dateValue: any;
                    calendarDateValue: string | null;
                    numberValue: number | null;
                    questionId: QuestionId | null;
                    promptId: PromptId | null;
                    children: Array<{
                        nodeType: Types.AdvancedSearchConditionNodeType;
                        fieldName: string | null;
                        operator: Types.FieldConditionOperator | null;
                        textValue: string | null;
                        dateValue: any;
                        calendarDateValue: string | null;
                        numberValue: number | null;
                        questionId: QuestionId | null;
                        promptId: PromptId | null;
                        children: Array<{
                            nodeType: Types.AdvancedSearchConditionNodeType;
                            fieldName: string | null;
                            operator: Types.FieldConditionOperator | null;
                            textValue: string | null;
                            dateValue: any;
                            calendarDateValue: string | null;
                            numberValue: number | null;
                            questionId: QuestionId | null;
                            promptId: PromptId | null;
                            children: Array<{
                                nodeType: Types.AdvancedSearchConditionNodeType;
                                fieldName: string | null;
                                operator: Types.FieldConditionOperator | null;
                                textValue: string | null;
                                dateValue: any;
                                calendarDateValue: string | null;
                                numberValue: number | null;
                                questionId: QuestionId | null;
                                promptId: PromptId | null;
                                children: Array<{
                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                    fieldName: string | null;
                                    operator: Types.FieldConditionOperator | null;
                                    textValue: string | null;
                                    dateValue: any;
                                    calendarDateValue: string | null;
                                    numberValue: number | null;
                                    questionId: QuestionId | null;
                                    promptId: PromptId | null;
                                    children: Array<{
                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                        fieldName: string | null;
                                        operator: Types.FieldConditionOperator | null;
                                        textValue: string | null;
                                        dateValue: any;
                                        calendarDateValue: string | null;
                                        numberValue: number | null;
                                        questionId: QuestionId | null;
                                        promptId: PromptId | null;
                                        children: Array<{
                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                            fieldName: string | null;
                                            operator: Types.FieldConditionOperator | null;
                                            textValue: string | null;
                                            dateValue: any;
                                            calendarDateValue: string | null;
                                            numberValue: number | null;
                                            questionId: QuestionId | null;
                                            promptId: PromptId | null;
                                            children: Array<{
                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                fieldName: string | null;
                                                operator: Types.FieldConditionOperator | null;
                                                textValue: string | null;
                                                dateValue: any;
                                                calendarDateValue: string | null;
                                                numberValue: number | null;
                                                questionId: QuestionId | null;
                                                promptId: PromptId | null;
                                                children: Array<{
                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                    fieldName: string | null;
                                                    operator: Types.FieldConditionOperator | null;
                                                    textValue: string | null;
                                                    dateValue: any;
                                                    calendarDateValue: string | null;
                                                    numberValue: number | null;
                                                    questionId: QuestionId | null;
                                                    promptId: PromptId | null;
                                                    children: Array<{
                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                        fieldName: string | null;
                                                        operator: Types.FieldConditionOperator | null;
                                                        textValue: string | null;
                                                        dateValue: any;
                                                        calendarDateValue: string | null;
                                                        numberValue: number | null;
                                                        questionId: QuestionId | null;
                                                        promptId: PromptId | null;
                                                        relativeDate: {
                                                            value: number;
                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                        } | null;
                                                    }> | null;
                                                    relativeDate: {
                                                        value: number;
                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                    } | null;
                                                }> | null;
                                                relativeDate: {
                                                    value: number;
                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                } | null;
                                            }> | null;
                                            relativeDate: {
                                                value: number;
                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                            } | null;
                                        }> | null;
                                        relativeDate: {
                                            value: number;
                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                        } | null;
                                    }> | null;
                                    relativeDate: {
                                        value: number;
                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                    } | null;
                                }> | null;
                                relativeDate: {
                                    value: number;
                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                } | null;
                            }> | null;
                            relativeDate: {
                                value: number;
                                interval: Types.AdvancedSearchRelativeDateInterval;
                            } | null;
                        }> | null;
                        relativeDate: {
                            value: number;
                            interval: Types.AdvancedSearchRelativeDateInterval;
                        } | null;
                    }> | null;
                    relativeDate: {
                        value: number;
                        interval: Types.AdvancedSearchRelativeDateInterval;
                    } | null;
                }> | null;
                relativeDate: {
                    value: number;
                    interval: Types.AdvancedSearchRelativeDateInterval;
                } | null;
            };
        } | null;
    };
    regionVisibility: {
        editable: boolean;
        regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
    } | null;
};

export type GetWorkflowsForTenantQueryVariables = Exact<{
    tenantId: TenantId;
}>;

export type GetWorkflowsForTenantQuery = {
    getWorkflowsForTenant: Array<{
        attachTo: Types.AdvancedSearchEntityType;
        createdBy: UserId;
        dateAdded: any;
        delay: number;
        delayRecipe: string | null;
        description: string;
        id: WorkflowId;
        name: string;
        status: Types.WorkflowStatus;
        defaultActorId: UserId;
        actorMode: Types.WorkflowActorMode;
        updatedAt: any;
        updatedBy: UserId;
        actions: Array<
            | {
                  __typename: "WorkflowActionEmailEntity";
                  body: string;
                  subject: string;
                  to: string;
                  discreteTo: string | null;
                  attachments: string | null;
                  bccMyself: string | null;
              }
            | {
                  __typename: "WorkflowActionTaskEntity";
                  dueDate: string;
                  text: string;
                  assignedTo: string | null;
                  taskTypeId: string | null;
                  notifyOwner: boolean | null;
                  recurrencePattern: string | null;
                  recurrenceInterval: string | null;
                  recurrenceDaysOfWeek: string | null;
                  recurrenceDayOfMonth: string | null;
                  notification: { subject: string | null } | null;
              }
        >;
        settings: Array<{
            defaultValue: string;
            description: string;
            key: string;
            label: string;
            type: Types.WorkflowSettingType;
            scope: string;
        }>;
        trigger: {
            eventType: Types.WorkflowEventType;
            entityUpdated: {
                fieldMatchers: Array<{
                    fieldName: string;
                    previous: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            noteAdded: {
                entityFieldMatchers: Array<{
                    fieldName: string;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            emailOpened: {
                activityThresholdDays: number;
                entityFieldMatchers: Array<{
                    fieldName: string;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            pipelineStageChanged: {
                pipelineId: PipelineId;
                toStageIds: Array<PipelineStageId>;
            } | null;
            schedule: {
                cron: string;
                entityCondition: {
                    nodeType: Types.AdvancedSearchConditionNodeType;
                    fieldName: string | null;
                    operator: Types.FieldConditionOperator | null;
                    textValue: string | null;
                    dateValue: any;
                    calendarDateValue: string | null;
                    numberValue: number | null;
                    questionId: QuestionId | null;
                    promptId: PromptId | null;
                    children: Array<{
                        nodeType: Types.AdvancedSearchConditionNodeType;
                        fieldName: string | null;
                        operator: Types.FieldConditionOperator | null;
                        textValue: string | null;
                        dateValue: any;
                        calendarDateValue: string | null;
                        numberValue: number | null;
                        questionId: QuestionId | null;
                        promptId: PromptId | null;
                        children: Array<{
                            nodeType: Types.AdvancedSearchConditionNodeType;
                            fieldName: string | null;
                            operator: Types.FieldConditionOperator | null;
                            textValue: string | null;
                            dateValue: any;
                            calendarDateValue: string | null;
                            numberValue: number | null;
                            questionId: QuestionId | null;
                            promptId: PromptId | null;
                            children: Array<{
                                nodeType: Types.AdvancedSearchConditionNodeType;
                                fieldName: string | null;
                                operator: Types.FieldConditionOperator | null;
                                textValue: string | null;
                                dateValue: any;
                                calendarDateValue: string | null;
                                numberValue: number | null;
                                questionId: QuestionId | null;
                                promptId: PromptId | null;
                                children: Array<{
                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                    fieldName: string | null;
                                    operator: Types.FieldConditionOperator | null;
                                    textValue: string | null;
                                    dateValue: any;
                                    calendarDateValue: string | null;
                                    numberValue: number | null;
                                    questionId: QuestionId | null;
                                    promptId: PromptId | null;
                                    children: Array<{
                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                        fieldName: string | null;
                                        operator: Types.FieldConditionOperator | null;
                                        textValue: string | null;
                                        dateValue: any;
                                        calendarDateValue: string | null;
                                        numberValue: number | null;
                                        questionId: QuestionId | null;
                                        promptId: PromptId | null;
                                        children: Array<{
                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                            fieldName: string | null;
                                            operator: Types.FieldConditionOperator | null;
                                            textValue: string | null;
                                            dateValue: any;
                                            calendarDateValue: string | null;
                                            numberValue: number | null;
                                            questionId: QuestionId | null;
                                            promptId: PromptId | null;
                                            children: Array<{
                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                fieldName: string | null;
                                                operator: Types.FieldConditionOperator | null;
                                                textValue: string | null;
                                                dateValue: any;
                                                calendarDateValue: string | null;
                                                numberValue: number | null;
                                                questionId: QuestionId | null;
                                                promptId: PromptId | null;
                                                children: Array<{
                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                    fieldName: string | null;
                                                    operator: Types.FieldConditionOperator | null;
                                                    textValue: string | null;
                                                    dateValue: any;
                                                    calendarDateValue: string | null;
                                                    numberValue: number | null;
                                                    questionId: QuestionId | null;
                                                    promptId: PromptId | null;
                                                    children: Array<{
                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                        fieldName: string | null;
                                                        operator: Types.FieldConditionOperator | null;
                                                        textValue: string | null;
                                                        dateValue: any;
                                                        calendarDateValue: string | null;
                                                        numberValue: number | null;
                                                        questionId: QuestionId | null;
                                                        promptId: PromptId | null;
                                                        children: Array<{
                                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                                            fieldName: string | null;
                                                            operator: Types.FieldConditionOperator | null;
                                                            textValue: string | null;
                                                            dateValue: any;
                                                            calendarDateValue:
                                                                | string
                                                                | null;
                                                            numberValue: number | null;
                                                            questionId: QuestionId | null;
                                                            promptId: PromptId | null;
                                                            relativeDate: {
                                                                value: number;
                                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                                            } | null;
                                                        }> | null;
                                                        relativeDate: {
                                                            value: number;
                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                        } | null;
                                                    }> | null;
                                                    relativeDate: {
                                                        value: number;
                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                    } | null;
                                                }> | null;
                                                relativeDate: {
                                                    value: number;
                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                } | null;
                                            }> | null;
                                            relativeDate: {
                                                value: number;
                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                            } | null;
                                        }> | null;
                                        relativeDate: {
                                            value: number;
                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                        } | null;
                                    }> | null;
                                    relativeDate: {
                                        value: number;
                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                    } | null;
                                }> | null;
                                relativeDate: {
                                    value: number;
                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                } | null;
                            }> | null;
                            relativeDate: {
                                value: number;
                                interval: Types.AdvancedSearchRelativeDateInterval;
                            } | null;
                        }> | null;
                        relativeDate: {
                            value: number;
                            interval: Types.AdvancedSearchRelativeDateInterval;
                        } | null;
                    }> | null;
                    relativeDate: {
                        value: number;
                        interval: Types.AdvancedSearchRelativeDateInterval;
                    } | null;
                };
            } | null;
        };
        regionVisibility: {
            editable: boolean;
            regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
        } | null;
    }>;
};

export type GetWorkflowsQueryVariables = Exact<{ [key: string]: never }>;

export type GetWorkflowsQuery = {
    getWorkflows: Array<{
        attachTo: Types.AdvancedSearchEntityType;
        createdBy: UserId;
        dateAdded: any;
        delay: number;
        delayRecipe: string | null;
        description: string;
        id: WorkflowId;
        name: string;
        status: Types.WorkflowStatus;
        defaultActorId: UserId;
        actorMode: Types.WorkflowActorMode;
        updatedAt: any;
        updatedBy: UserId;
        actions: Array<
            | {
                  __typename: "WorkflowActionEmailEntity";
                  body: string;
                  subject: string;
                  to: string;
                  discreteTo: string | null;
                  attachments: string | null;
                  bccMyself: string | null;
              }
            | {
                  __typename: "WorkflowActionTaskEntity";
                  dueDate: string;
                  text: string;
                  assignedTo: string | null;
                  taskTypeId: string | null;
                  notifyOwner: boolean | null;
                  recurrencePattern: string | null;
                  recurrenceInterval: string | null;
                  recurrenceDaysOfWeek: string | null;
                  recurrenceDayOfMonth: string | null;
                  notification: { subject: string | null } | null;
              }
        >;
        settings: Array<{
            defaultValue: string;
            description: string;
            key: string;
            label: string;
            type: Types.WorkflowSettingType;
            scope: string;
        }>;
        trigger: {
            eventType: Types.WorkflowEventType;
            entityUpdated: {
                fieldMatchers: Array<{
                    fieldName: string;
                    previous: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            noteAdded: {
                entityFieldMatchers: Array<{
                    fieldName: string;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            emailOpened: {
                activityThresholdDays: number;
                entityFieldMatchers: Array<{
                    fieldName: string;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            pipelineStageChanged: {
                pipelineId: PipelineId;
                toStageIds: Array<PipelineStageId>;
            } | null;
            schedule: {
                cron: string;
                entityCondition: {
                    nodeType: Types.AdvancedSearchConditionNodeType;
                    fieldName: string | null;
                    operator: Types.FieldConditionOperator | null;
                    textValue: string | null;
                    dateValue: any;
                    calendarDateValue: string | null;
                    numberValue: number | null;
                    questionId: QuestionId | null;
                    promptId: PromptId | null;
                    children: Array<{
                        nodeType: Types.AdvancedSearchConditionNodeType;
                        fieldName: string | null;
                        operator: Types.FieldConditionOperator | null;
                        textValue: string | null;
                        dateValue: any;
                        calendarDateValue: string | null;
                        numberValue: number | null;
                        questionId: QuestionId | null;
                        promptId: PromptId | null;
                        children: Array<{
                            nodeType: Types.AdvancedSearchConditionNodeType;
                            fieldName: string | null;
                            operator: Types.FieldConditionOperator | null;
                            textValue: string | null;
                            dateValue: any;
                            calendarDateValue: string | null;
                            numberValue: number | null;
                            questionId: QuestionId | null;
                            promptId: PromptId | null;
                            children: Array<{
                                nodeType: Types.AdvancedSearchConditionNodeType;
                                fieldName: string | null;
                                operator: Types.FieldConditionOperator | null;
                                textValue: string | null;
                                dateValue: any;
                                calendarDateValue: string | null;
                                numberValue: number | null;
                                questionId: QuestionId | null;
                                promptId: PromptId | null;
                                children: Array<{
                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                    fieldName: string | null;
                                    operator: Types.FieldConditionOperator | null;
                                    textValue: string | null;
                                    dateValue: any;
                                    calendarDateValue: string | null;
                                    numberValue: number | null;
                                    questionId: QuestionId | null;
                                    promptId: PromptId | null;
                                    children: Array<{
                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                        fieldName: string | null;
                                        operator: Types.FieldConditionOperator | null;
                                        textValue: string | null;
                                        dateValue: any;
                                        calendarDateValue: string | null;
                                        numberValue: number | null;
                                        questionId: QuestionId | null;
                                        promptId: PromptId | null;
                                        children: Array<{
                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                            fieldName: string | null;
                                            operator: Types.FieldConditionOperator | null;
                                            textValue: string | null;
                                            dateValue: any;
                                            calendarDateValue: string | null;
                                            numberValue: number | null;
                                            questionId: QuestionId | null;
                                            promptId: PromptId | null;
                                            children: Array<{
                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                fieldName: string | null;
                                                operator: Types.FieldConditionOperator | null;
                                                textValue: string | null;
                                                dateValue: any;
                                                calendarDateValue: string | null;
                                                numberValue: number | null;
                                                questionId: QuestionId | null;
                                                promptId: PromptId | null;
                                                children: Array<{
                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                    fieldName: string | null;
                                                    operator: Types.FieldConditionOperator | null;
                                                    textValue: string | null;
                                                    dateValue: any;
                                                    calendarDateValue: string | null;
                                                    numberValue: number | null;
                                                    questionId: QuestionId | null;
                                                    promptId: PromptId | null;
                                                    children: Array<{
                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                        fieldName: string | null;
                                                        operator: Types.FieldConditionOperator | null;
                                                        textValue: string | null;
                                                        dateValue: any;
                                                        calendarDateValue: string | null;
                                                        numberValue: number | null;
                                                        questionId: QuestionId | null;
                                                        promptId: PromptId | null;
                                                        children: Array<{
                                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                                            fieldName: string | null;
                                                            operator: Types.FieldConditionOperator | null;
                                                            textValue: string | null;
                                                            dateValue: any;
                                                            calendarDateValue:
                                                                | string
                                                                | null;
                                                            numberValue: number | null;
                                                            questionId: QuestionId | null;
                                                            promptId: PromptId | null;
                                                            relativeDate: {
                                                                value: number;
                                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                                            } | null;
                                                        }> | null;
                                                        relativeDate: {
                                                            value: number;
                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                        } | null;
                                                    }> | null;
                                                    relativeDate: {
                                                        value: number;
                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                    } | null;
                                                }> | null;
                                                relativeDate: {
                                                    value: number;
                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                } | null;
                                            }> | null;
                                            relativeDate: {
                                                value: number;
                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                            } | null;
                                        }> | null;
                                        relativeDate: {
                                            value: number;
                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                        } | null;
                                    }> | null;
                                    relativeDate: {
                                        value: number;
                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                    } | null;
                                }> | null;
                                relativeDate: {
                                    value: number;
                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                } | null;
                            }> | null;
                            relativeDate: {
                                value: number;
                                interval: Types.AdvancedSearchRelativeDateInterval;
                            } | null;
                        }> | null;
                        relativeDate: {
                            value: number;
                            interval: Types.AdvancedSearchRelativeDateInterval;
                        } | null;
                    }> | null;
                    relativeDate: {
                        value: number;
                        interval: Types.AdvancedSearchRelativeDateInterval;
                    } | null;
                };
            } | null;
        };
        regionVisibility: {
            editable: boolean;
            regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
        } | null;
    }>;
    getWorkflowSettings: {
        user: Array<{ key: string; workflowId: WorkflowId; textValue: string | null }>;
        tenant: Array<{ key: string; workflowId: WorkflowId; textValue: string | null }>;
    };
};

export type GetWorkflowQueryVariables = Exact<{
    tenantId: TenantId;
    workflowId: WorkflowId;
}>;

export type GetWorkflowQuery = {
    getWorkflow: {
        attachTo: Types.AdvancedSearchEntityType;
        createdBy: UserId;
        dateAdded: any;
        delay: number;
        delayRecipe: string | null;
        description: string;
        id: WorkflowId;
        name: string;
        status: Types.WorkflowStatus;
        defaultActorId: UserId;
        actorMode: Types.WorkflowActorMode;
        updatedAt: any;
        updatedBy: UserId;
        actions: Array<
            | {
                  __typename: "WorkflowActionEmailEntity";
                  body: string;
                  subject: string;
                  to: string;
                  discreteTo: string | null;
                  attachments: string | null;
                  bccMyself: string | null;
              }
            | {
                  __typename: "WorkflowActionTaskEntity";
                  dueDate: string;
                  text: string;
                  assignedTo: string | null;
                  taskTypeId: string | null;
                  notifyOwner: boolean | null;
                  recurrencePattern: string | null;
                  recurrenceInterval: string | null;
                  recurrenceDaysOfWeek: string | null;
                  recurrenceDayOfMonth: string | null;
                  notification: { subject: string | null } | null;
              }
        >;
        settings: Array<{
            defaultValue: string;
            description: string;
            key: string;
            label: string;
            type: Types.WorkflowSettingType;
            scope: string;
        }>;
        trigger: {
            eventType: Types.WorkflowEventType;
            entityUpdated: {
                fieldMatchers: Array<{
                    fieldName: string;
                    previous: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            noteAdded: {
                entityFieldMatchers: Array<{
                    fieldName: string;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            emailOpened: {
                activityThresholdDays: number;
                entityFieldMatchers: Array<{
                    fieldName: string;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            pipelineStageChanged: {
                pipelineId: PipelineId;
                toStageIds: Array<PipelineStageId>;
            } | null;
            schedule: {
                cron: string;
                entityCondition: {
                    nodeType: Types.AdvancedSearchConditionNodeType;
                    fieldName: string | null;
                    operator: Types.FieldConditionOperator | null;
                    textValue: string | null;
                    dateValue: any;
                    calendarDateValue: string | null;
                    numberValue: number | null;
                    questionId: QuestionId | null;
                    promptId: PromptId | null;
                    children: Array<{
                        nodeType: Types.AdvancedSearchConditionNodeType;
                        fieldName: string | null;
                        operator: Types.FieldConditionOperator | null;
                        textValue: string | null;
                        dateValue: any;
                        calendarDateValue: string | null;
                        numberValue: number | null;
                        questionId: QuestionId | null;
                        promptId: PromptId | null;
                        children: Array<{
                            nodeType: Types.AdvancedSearchConditionNodeType;
                            fieldName: string | null;
                            operator: Types.FieldConditionOperator | null;
                            textValue: string | null;
                            dateValue: any;
                            calendarDateValue: string | null;
                            numberValue: number | null;
                            questionId: QuestionId | null;
                            promptId: PromptId | null;
                            children: Array<{
                                nodeType: Types.AdvancedSearchConditionNodeType;
                                fieldName: string | null;
                                operator: Types.FieldConditionOperator | null;
                                textValue: string | null;
                                dateValue: any;
                                calendarDateValue: string | null;
                                numberValue: number | null;
                                questionId: QuestionId | null;
                                promptId: PromptId | null;
                                children: Array<{
                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                    fieldName: string | null;
                                    operator: Types.FieldConditionOperator | null;
                                    textValue: string | null;
                                    dateValue: any;
                                    calendarDateValue: string | null;
                                    numberValue: number | null;
                                    questionId: QuestionId | null;
                                    promptId: PromptId | null;
                                    children: Array<{
                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                        fieldName: string | null;
                                        operator: Types.FieldConditionOperator | null;
                                        textValue: string | null;
                                        dateValue: any;
                                        calendarDateValue: string | null;
                                        numberValue: number | null;
                                        questionId: QuestionId | null;
                                        promptId: PromptId | null;
                                        children: Array<{
                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                            fieldName: string | null;
                                            operator: Types.FieldConditionOperator | null;
                                            textValue: string | null;
                                            dateValue: any;
                                            calendarDateValue: string | null;
                                            numberValue: number | null;
                                            questionId: QuestionId | null;
                                            promptId: PromptId | null;
                                            children: Array<{
                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                fieldName: string | null;
                                                operator: Types.FieldConditionOperator | null;
                                                textValue: string | null;
                                                dateValue: any;
                                                calendarDateValue: string | null;
                                                numberValue: number | null;
                                                questionId: QuestionId | null;
                                                promptId: PromptId | null;
                                                children: Array<{
                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                    fieldName: string | null;
                                                    operator: Types.FieldConditionOperator | null;
                                                    textValue: string | null;
                                                    dateValue: any;
                                                    calendarDateValue: string | null;
                                                    numberValue: number | null;
                                                    questionId: QuestionId | null;
                                                    promptId: PromptId | null;
                                                    children: Array<{
                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                        fieldName: string | null;
                                                        operator: Types.FieldConditionOperator | null;
                                                        textValue: string | null;
                                                        dateValue: any;
                                                        calendarDateValue: string | null;
                                                        numberValue: number | null;
                                                        questionId: QuestionId | null;
                                                        promptId: PromptId | null;
                                                        children: Array<{
                                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                                            fieldName: string | null;
                                                            operator: Types.FieldConditionOperator | null;
                                                            textValue: string | null;
                                                            dateValue: any;
                                                            calendarDateValue:
                                                                | string
                                                                | null;
                                                            numberValue: number | null;
                                                            questionId: QuestionId | null;
                                                            promptId: PromptId | null;
                                                            relativeDate: {
                                                                value: number;
                                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                                            } | null;
                                                        }> | null;
                                                        relativeDate: {
                                                            value: number;
                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                        } | null;
                                                    }> | null;
                                                    relativeDate: {
                                                        value: number;
                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                    } | null;
                                                }> | null;
                                                relativeDate: {
                                                    value: number;
                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                } | null;
                                            }> | null;
                                            relativeDate: {
                                                value: number;
                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                            } | null;
                                        }> | null;
                                        relativeDate: {
                                            value: number;
                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                        } | null;
                                    }> | null;
                                    relativeDate: {
                                        value: number;
                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                    } | null;
                                }> | null;
                                relativeDate: {
                                    value: number;
                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                } | null;
                            }> | null;
                            relativeDate: {
                                value: number;
                                interval: Types.AdvancedSearchRelativeDateInterval;
                            } | null;
                        }> | null;
                        relativeDate: {
                            value: number;
                            interval: Types.AdvancedSearchRelativeDateInterval;
                        } | null;
                    }> | null;
                    relativeDate: {
                        value: number;
                        interval: Types.AdvancedSearchRelativeDateInterval;
                    } | null;
                };
            } | null;
        };
        regionVisibility: {
            editable: boolean;
            regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
        } | null;
    };
};

export type CreateWorkflowForTenantMutationVariables = Exact<{
    tenantId: TenantId;
    workflow: Types.WorkflowPayload;
}>;

export type CreateWorkflowForTenantMutation = {
    createWorkflowForTenant: {
        attachTo: Types.AdvancedSearchEntityType;
        createdBy: UserId;
        dateAdded: any;
        delay: number;
        delayRecipe: string | null;
        description: string;
        id: WorkflowId;
        name: string;
        status: Types.WorkflowStatus;
        defaultActorId: UserId;
        actorMode: Types.WorkflowActorMode;
        updatedAt: any;
        updatedBy: UserId;
        actions: Array<
            | {
                  __typename: "WorkflowActionEmailEntity";
                  body: string;
                  subject: string;
                  to: string;
                  discreteTo: string | null;
                  attachments: string | null;
                  bccMyself: string | null;
              }
            | {
                  __typename: "WorkflowActionTaskEntity";
                  dueDate: string;
                  text: string;
                  assignedTo: string | null;
                  taskTypeId: string | null;
                  notifyOwner: boolean | null;
                  recurrencePattern: string | null;
                  recurrenceInterval: string | null;
                  recurrenceDaysOfWeek: string | null;
                  recurrenceDayOfMonth: string | null;
                  notification: { subject: string | null } | null;
              }
        >;
        settings: Array<{
            defaultValue: string;
            description: string;
            key: string;
            label: string;
            type: Types.WorkflowSettingType;
            scope: string;
        }>;
        trigger: {
            eventType: Types.WorkflowEventType;
            entityUpdated: {
                fieldMatchers: Array<{
                    fieldName: string;
                    previous: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            noteAdded: {
                entityFieldMatchers: Array<{
                    fieldName: string;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            emailOpened: {
                activityThresholdDays: number;
                entityFieldMatchers: Array<{
                    fieldName: string;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            pipelineStageChanged: {
                pipelineId: PipelineId;
                toStageIds: Array<PipelineStageId>;
            } | null;
            schedule: {
                cron: string;
                entityCondition: {
                    nodeType: Types.AdvancedSearchConditionNodeType;
                    fieldName: string | null;
                    operator: Types.FieldConditionOperator | null;
                    textValue: string | null;
                    dateValue: any;
                    calendarDateValue: string | null;
                    numberValue: number | null;
                    questionId: QuestionId | null;
                    promptId: PromptId | null;
                    children: Array<{
                        nodeType: Types.AdvancedSearchConditionNodeType;
                        fieldName: string | null;
                        operator: Types.FieldConditionOperator | null;
                        textValue: string | null;
                        dateValue: any;
                        calendarDateValue: string | null;
                        numberValue: number | null;
                        questionId: QuestionId | null;
                        promptId: PromptId | null;
                        children: Array<{
                            nodeType: Types.AdvancedSearchConditionNodeType;
                            fieldName: string | null;
                            operator: Types.FieldConditionOperator | null;
                            textValue: string | null;
                            dateValue: any;
                            calendarDateValue: string | null;
                            numberValue: number | null;
                            questionId: QuestionId | null;
                            promptId: PromptId | null;
                            children: Array<{
                                nodeType: Types.AdvancedSearchConditionNodeType;
                                fieldName: string | null;
                                operator: Types.FieldConditionOperator | null;
                                textValue: string | null;
                                dateValue: any;
                                calendarDateValue: string | null;
                                numberValue: number | null;
                                questionId: QuestionId | null;
                                promptId: PromptId | null;
                                children: Array<{
                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                    fieldName: string | null;
                                    operator: Types.FieldConditionOperator | null;
                                    textValue: string | null;
                                    dateValue: any;
                                    calendarDateValue: string | null;
                                    numberValue: number | null;
                                    questionId: QuestionId | null;
                                    promptId: PromptId | null;
                                    children: Array<{
                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                        fieldName: string | null;
                                        operator: Types.FieldConditionOperator | null;
                                        textValue: string | null;
                                        dateValue: any;
                                        calendarDateValue: string | null;
                                        numberValue: number | null;
                                        questionId: QuestionId | null;
                                        promptId: PromptId | null;
                                        children: Array<{
                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                            fieldName: string | null;
                                            operator: Types.FieldConditionOperator | null;
                                            textValue: string | null;
                                            dateValue: any;
                                            calendarDateValue: string | null;
                                            numberValue: number | null;
                                            questionId: QuestionId | null;
                                            promptId: PromptId | null;
                                            children: Array<{
                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                fieldName: string | null;
                                                operator: Types.FieldConditionOperator | null;
                                                textValue: string | null;
                                                dateValue: any;
                                                calendarDateValue: string | null;
                                                numberValue: number | null;
                                                questionId: QuestionId | null;
                                                promptId: PromptId | null;
                                                children: Array<{
                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                    fieldName: string | null;
                                                    operator: Types.FieldConditionOperator | null;
                                                    textValue: string | null;
                                                    dateValue: any;
                                                    calendarDateValue: string | null;
                                                    numberValue: number | null;
                                                    questionId: QuestionId | null;
                                                    promptId: PromptId | null;
                                                    children: Array<{
                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                        fieldName: string | null;
                                                        operator: Types.FieldConditionOperator | null;
                                                        textValue: string | null;
                                                        dateValue: any;
                                                        calendarDateValue: string | null;
                                                        numberValue: number | null;
                                                        questionId: QuestionId | null;
                                                        promptId: PromptId | null;
                                                        children: Array<{
                                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                                            fieldName: string | null;
                                                            operator: Types.FieldConditionOperator | null;
                                                            textValue: string | null;
                                                            dateValue: any;
                                                            calendarDateValue:
                                                                | string
                                                                | null;
                                                            numberValue: number | null;
                                                            questionId: QuestionId | null;
                                                            promptId: PromptId | null;
                                                            relativeDate: {
                                                                value: number;
                                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                                            } | null;
                                                        }> | null;
                                                        relativeDate: {
                                                            value: number;
                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                        } | null;
                                                    }> | null;
                                                    relativeDate: {
                                                        value: number;
                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                    } | null;
                                                }> | null;
                                                relativeDate: {
                                                    value: number;
                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                } | null;
                                            }> | null;
                                            relativeDate: {
                                                value: number;
                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                            } | null;
                                        }> | null;
                                        relativeDate: {
                                            value: number;
                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                        } | null;
                                    }> | null;
                                    relativeDate: {
                                        value: number;
                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                    } | null;
                                }> | null;
                                relativeDate: {
                                    value: number;
                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                } | null;
                            }> | null;
                            relativeDate: {
                                value: number;
                                interval: Types.AdvancedSearchRelativeDateInterval;
                            } | null;
                        }> | null;
                        relativeDate: {
                            value: number;
                            interval: Types.AdvancedSearchRelativeDateInterval;
                        } | null;
                    }> | null;
                    relativeDate: {
                        value: number;
                        interval: Types.AdvancedSearchRelativeDateInterval;
                    } | null;
                };
            } | null;
        };
        regionVisibility: {
            editable: boolean;
            regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
        } | null;
    };
};

export type CreateWorkflowMutationVariables = Exact<{
    workflow: Types.WorkflowPayload;
}>;

export type CreateWorkflowMutation = {
    createWorkflow: {
        attachTo: Types.AdvancedSearchEntityType;
        createdBy: UserId;
        dateAdded: any;
        delay: number;
        delayRecipe: string | null;
        description: string;
        id: WorkflowId;
        name: string;
        status: Types.WorkflowStatus;
        defaultActorId: UserId;
        actorMode: Types.WorkflowActorMode;
        updatedAt: any;
        updatedBy: UserId;
        actions: Array<
            | {
                  __typename: "WorkflowActionEmailEntity";
                  body: string;
                  subject: string;
                  to: string;
                  discreteTo: string | null;
                  attachments: string | null;
                  bccMyself: string | null;
              }
            | {
                  __typename: "WorkflowActionTaskEntity";
                  dueDate: string;
                  text: string;
                  assignedTo: string | null;
                  taskTypeId: string | null;
                  notifyOwner: boolean | null;
                  recurrencePattern: string | null;
                  recurrenceInterval: string | null;
                  recurrenceDaysOfWeek: string | null;
                  recurrenceDayOfMonth: string | null;
                  notification: { subject: string | null } | null;
              }
        >;
        settings: Array<{
            defaultValue: string;
            description: string;
            key: string;
            label: string;
            type: Types.WorkflowSettingType;
            scope: string;
        }>;
        trigger: {
            eventType: Types.WorkflowEventType;
            entityUpdated: {
                fieldMatchers: Array<{
                    fieldName: string;
                    previous: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            noteAdded: {
                entityFieldMatchers: Array<{
                    fieldName: string;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            emailOpened: {
                activityThresholdDays: number;
                entityFieldMatchers: Array<{
                    fieldName: string;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            pipelineStageChanged: {
                pipelineId: PipelineId;
                toStageIds: Array<PipelineStageId>;
            } | null;
            schedule: {
                cron: string;
                entityCondition: {
                    nodeType: Types.AdvancedSearchConditionNodeType;
                    fieldName: string | null;
                    operator: Types.FieldConditionOperator | null;
                    textValue: string | null;
                    dateValue: any;
                    calendarDateValue: string | null;
                    numberValue: number | null;
                    questionId: QuestionId | null;
                    promptId: PromptId | null;
                    children: Array<{
                        nodeType: Types.AdvancedSearchConditionNodeType;
                        fieldName: string | null;
                        operator: Types.FieldConditionOperator | null;
                        textValue: string | null;
                        dateValue: any;
                        calendarDateValue: string | null;
                        numberValue: number | null;
                        questionId: QuestionId | null;
                        promptId: PromptId | null;
                        children: Array<{
                            nodeType: Types.AdvancedSearchConditionNodeType;
                            fieldName: string | null;
                            operator: Types.FieldConditionOperator | null;
                            textValue: string | null;
                            dateValue: any;
                            calendarDateValue: string | null;
                            numberValue: number | null;
                            questionId: QuestionId | null;
                            promptId: PromptId | null;
                            children: Array<{
                                nodeType: Types.AdvancedSearchConditionNodeType;
                                fieldName: string | null;
                                operator: Types.FieldConditionOperator | null;
                                textValue: string | null;
                                dateValue: any;
                                calendarDateValue: string | null;
                                numberValue: number | null;
                                questionId: QuestionId | null;
                                promptId: PromptId | null;
                                children: Array<{
                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                    fieldName: string | null;
                                    operator: Types.FieldConditionOperator | null;
                                    textValue: string | null;
                                    dateValue: any;
                                    calendarDateValue: string | null;
                                    numberValue: number | null;
                                    questionId: QuestionId | null;
                                    promptId: PromptId | null;
                                    children: Array<{
                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                        fieldName: string | null;
                                        operator: Types.FieldConditionOperator | null;
                                        textValue: string | null;
                                        dateValue: any;
                                        calendarDateValue: string | null;
                                        numberValue: number | null;
                                        questionId: QuestionId | null;
                                        promptId: PromptId | null;
                                        children: Array<{
                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                            fieldName: string | null;
                                            operator: Types.FieldConditionOperator | null;
                                            textValue: string | null;
                                            dateValue: any;
                                            calendarDateValue: string | null;
                                            numberValue: number | null;
                                            questionId: QuestionId | null;
                                            promptId: PromptId | null;
                                            children: Array<{
                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                fieldName: string | null;
                                                operator: Types.FieldConditionOperator | null;
                                                textValue: string | null;
                                                dateValue: any;
                                                calendarDateValue: string | null;
                                                numberValue: number | null;
                                                questionId: QuestionId | null;
                                                promptId: PromptId | null;
                                                children: Array<{
                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                    fieldName: string | null;
                                                    operator: Types.FieldConditionOperator | null;
                                                    textValue: string | null;
                                                    dateValue: any;
                                                    calendarDateValue: string | null;
                                                    numberValue: number | null;
                                                    questionId: QuestionId | null;
                                                    promptId: PromptId | null;
                                                    children: Array<{
                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                        fieldName: string | null;
                                                        operator: Types.FieldConditionOperator | null;
                                                        textValue: string | null;
                                                        dateValue: any;
                                                        calendarDateValue: string | null;
                                                        numberValue: number | null;
                                                        questionId: QuestionId | null;
                                                        promptId: PromptId | null;
                                                        children: Array<{
                                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                                            fieldName: string | null;
                                                            operator: Types.FieldConditionOperator | null;
                                                            textValue: string | null;
                                                            dateValue: any;
                                                            calendarDateValue:
                                                                | string
                                                                | null;
                                                            numberValue: number | null;
                                                            questionId: QuestionId | null;
                                                            promptId: PromptId | null;
                                                            relativeDate: {
                                                                value: number;
                                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                                            } | null;
                                                        }> | null;
                                                        relativeDate: {
                                                            value: number;
                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                        } | null;
                                                    }> | null;
                                                    relativeDate: {
                                                        value: number;
                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                    } | null;
                                                }> | null;
                                                relativeDate: {
                                                    value: number;
                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                } | null;
                                            }> | null;
                                            relativeDate: {
                                                value: number;
                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                            } | null;
                                        }> | null;
                                        relativeDate: {
                                            value: number;
                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                        } | null;
                                    }> | null;
                                    relativeDate: {
                                        value: number;
                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                    } | null;
                                }> | null;
                                relativeDate: {
                                    value: number;
                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                } | null;
                            }> | null;
                            relativeDate: {
                                value: number;
                                interval: Types.AdvancedSearchRelativeDateInterval;
                            } | null;
                        }> | null;
                        relativeDate: {
                            value: number;
                            interval: Types.AdvancedSearchRelativeDateInterval;
                        } | null;
                    }> | null;
                    relativeDate: {
                        value: number;
                        interval: Types.AdvancedSearchRelativeDateInterval;
                    } | null;
                };
            } | null;
        };
        regionVisibility: {
            editable: boolean;
            regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
        } | null;
    };
};

export type UpdateWorkflowForTenantMutationVariables = Exact<{
    tenantId: TenantId;
    workflowId: WorkflowId;
    workflow: Types.WorkflowPayload;
}>;

export type UpdateWorkflowForTenantMutation = {
    updateWorkflowForTenant: {
        attachTo: Types.AdvancedSearchEntityType;
        createdBy: UserId;
        dateAdded: any;
        delay: number;
        delayRecipe: string | null;
        description: string;
        id: WorkflowId;
        name: string;
        status: Types.WorkflowStatus;
        defaultActorId: UserId;
        actorMode: Types.WorkflowActorMode;
        updatedAt: any;
        updatedBy: UserId;
        actions: Array<
            | {
                  __typename: "WorkflowActionEmailEntity";
                  body: string;
                  subject: string;
                  to: string;
                  discreteTo: string | null;
                  attachments: string | null;
                  bccMyself: string | null;
              }
            | {
                  __typename: "WorkflowActionTaskEntity";
                  dueDate: string;
                  text: string;
                  assignedTo: string | null;
                  taskTypeId: string | null;
                  notifyOwner: boolean | null;
                  recurrencePattern: string | null;
                  recurrenceInterval: string | null;
                  recurrenceDaysOfWeek: string | null;
                  recurrenceDayOfMonth: string | null;
                  notification: { subject: string | null } | null;
              }
        >;
        settings: Array<{
            defaultValue: string;
            description: string;
            key: string;
            label: string;
            type: Types.WorkflowSettingType;
            scope: string;
        }>;
        trigger: {
            eventType: Types.WorkflowEventType;
            entityUpdated: {
                fieldMatchers: Array<{
                    fieldName: string;
                    previous: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            noteAdded: {
                entityFieldMatchers: Array<{
                    fieldName: string;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            emailOpened: {
                activityThresholdDays: number;
                entityFieldMatchers: Array<{
                    fieldName: string;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            pipelineStageChanged: {
                pipelineId: PipelineId;
                toStageIds: Array<PipelineStageId>;
            } | null;
            schedule: {
                cron: string;
                entityCondition: {
                    nodeType: Types.AdvancedSearchConditionNodeType;
                    fieldName: string | null;
                    operator: Types.FieldConditionOperator | null;
                    textValue: string | null;
                    dateValue: any;
                    calendarDateValue: string | null;
                    numberValue: number | null;
                    questionId: QuestionId | null;
                    promptId: PromptId | null;
                    children: Array<{
                        nodeType: Types.AdvancedSearchConditionNodeType;
                        fieldName: string | null;
                        operator: Types.FieldConditionOperator | null;
                        textValue: string | null;
                        dateValue: any;
                        calendarDateValue: string | null;
                        numberValue: number | null;
                        questionId: QuestionId | null;
                        promptId: PromptId | null;
                        children: Array<{
                            nodeType: Types.AdvancedSearchConditionNodeType;
                            fieldName: string | null;
                            operator: Types.FieldConditionOperator | null;
                            textValue: string | null;
                            dateValue: any;
                            calendarDateValue: string | null;
                            numberValue: number | null;
                            questionId: QuestionId | null;
                            promptId: PromptId | null;
                            children: Array<{
                                nodeType: Types.AdvancedSearchConditionNodeType;
                                fieldName: string | null;
                                operator: Types.FieldConditionOperator | null;
                                textValue: string | null;
                                dateValue: any;
                                calendarDateValue: string | null;
                                numberValue: number | null;
                                questionId: QuestionId | null;
                                promptId: PromptId | null;
                                children: Array<{
                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                    fieldName: string | null;
                                    operator: Types.FieldConditionOperator | null;
                                    textValue: string | null;
                                    dateValue: any;
                                    calendarDateValue: string | null;
                                    numberValue: number | null;
                                    questionId: QuestionId | null;
                                    promptId: PromptId | null;
                                    children: Array<{
                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                        fieldName: string | null;
                                        operator: Types.FieldConditionOperator | null;
                                        textValue: string | null;
                                        dateValue: any;
                                        calendarDateValue: string | null;
                                        numberValue: number | null;
                                        questionId: QuestionId | null;
                                        promptId: PromptId | null;
                                        children: Array<{
                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                            fieldName: string | null;
                                            operator: Types.FieldConditionOperator | null;
                                            textValue: string | null;
                                            dateValue: any;
                                            calendarDateValue: string | null;
                                            numberValue: number | null;
                                            questionId: QuestionId | null;
                                            promptId: PromptId | null;
                                            children: Array<{
                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                fieldName: string | null;
                                                operator: Types.FieldConditionOperator | null;
                                                textValue: string | null;
                                                dateValue: any;
                                                calendarDateValue: string | null;
                                                numberValue: number | null;
                                                questionId: QuestionId | null;
                                                promptId: PromptId | null;
                                                children: Array<{
                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                    fieldName: string | null;
                                                    operator: Types.FieldConditionOperator | null;
                                                    textValue: string | null;
                                                    dateValue: any;
                                                    calendarDateValue: string | null;
                                                    numberValue: number | null;
                                                    questionId: QuestionId | null;
                                                    promptId: PromptId | null;
                                                    children: Array<{
                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                        fieldName: string | null;
                                                        operator: Types.FieldConditionOperator | null;
                                                        textValue: string | null;
                                                        dateValue: any;
                                                        calendarDateValue: string | null;
                                                        numberValue: number | null;
                                                        questionId: QuestionId | null;
                                                        promptId: PromptId | null;
                                                        children: Array<{
                                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                                            fieldName: string | null;
                                                            operator: Types.FieldConditionOperator | null;
                                                            textValue: string | null;
                                                            dateValue: any;
                                                            calendarDateValue:
                                                                | string
                                                                | null;
                                                            numberValue: number | null;
                                                            questionId: QuestionId | null;
                                                            promptId: PromptId | null;
                                                            relativeDate: {
                                                                value: number;
                                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                                            } | null;
                                                        }> | null;
                                                        relativeDate: {
                                                            value: number;
                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                        } | null;
                                                    }> | null;
                                                    relativeDate: {
                                                        value: number;
                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                    } | null;
                                                }> | null;
                                                relativeDate: {
                                                    value: number;
                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                } | null;
                                            }> | null;
                                            relativeDate: {
                                                value: number;
                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                            } | null;
                                        }> | null;
                                        relativeDate: {
                                            value: number;
                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                        } | null;
                                    }> | null;
                                    relativeDate: {
                                        value: number;
                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                    } | null;
                                }> | null;
                                relativeDate: {
                                    value: number;
                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                } | null;
                            }> | null;
                            relativeDate: {
                                value: number;
                                interval: Types.AdvancedSearchRelativeDateInterval;
                            } | null;
                        }> | null;
                        relativeDate: {
                            value: number;
                            interval: Types.AdvancedSearchRelativeDateInterval;
                        } | null;
                    }> | null;
                    relativeDate: {
                        value: number;
                        interval: Types.AdvancedSearchRelativeDateInterval;
                    } | null;
                };
            } | null;
        };
        regionVisibility: {
            editable: boolean;
            regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
        } | null;
    };
};

export type UpdateWorkflowMutationVariables = Exact<{
    workflowId: WorkflowId;
    workflow: Types.WorkflowPayload;
}>;

export type UpdateWorkflowMutation = {
    updateWorkflow: {
        attachTo: Types.AdvancedSearchEntityType;
        createdBy: UserId;
        dateAdded: any;
        delay: number;
        delayRecipe: string | null;
        description: string;
        id: WorkflowId;
        name: string;
        status: Types.WorkflowStatus;
        defaultActorId: UserId;
        actorMode: Types.WorkflowActorMode;
        updatedAt: any;
        updatedBy: UserId;
        actions: Array<
            | {
                  __typename: "WorkflowActionEmailEntity";
                  body: string;
                  subject: string;
                  to: string;
                  discreteTo: string | null;
                  attachments: string | null;
                  bccMyself: string | null;
              }
            | {
                  __typename: "WorkflowActionTaskEntity";
                  dueDate: string;
                  text: string;
                  assignedTo: string | null;
                  taskTypeId: string | null;
                  notifyOwner: boolean | null;
                  recurrencePattern: string | null;
                  recurrenceInterval: string | null;
                  recurrenceDaysOfWeek: string | null;
                  recurrenceDayOfMonth: string | null;
                  notification: { subject: string | null } | null;
              }
        >;
        settings: Array<{
            defaultValue: string;
            description: string;
            key: string;
            label: string;
            type: Types.WorkflowSettingType;
            scope: string;
        }>;
        trigger: {
            eventType: Types.WorkflowEventType;
            entityUpdated: {
                fieldMatchers: Array<{
                    fieldName: string;
                    previous: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            noteAdded: {
                entityFieldMatchers: Array<{
                    fieldName: string;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            emailOpened: {
                activityThresholdDays: number;
                entityFieldMatchers: Array<{
                    fieldName: string;
                    current: {
                        values: Array<string> | null;
                        specialValue: string | null;
                    } | null;
                }>;
            } | null;
            pipelineStageChanged: {
                pipelineId: PipelineId;
                toStageIds: Array<PipelineStageId>;
            } | null;
            schedule: {
                cron: string;
                entityCondition: {
                    nodeType: Types.AdvancedSearchConditionNodeType;
                    fieldName: string | null;
                    operator: Types.FieldConditionOperator | null;
                    textValue: string | null;
                    dateValue: any;
                    calendarDateValue: string | null;
                    numberValue: number | null;
                    questionId: QuestionId | null;
                    promptId: PromptId | null;
                    children: Array<{
                        nodeType: Types.AdvancedSearchConditionNodeType;
                        fieldName: string | null;
                        operator: Types.FieldConditionOperator | null;
                        textValue: string | null;
                        dateValue: any;
                        calendarDateValue: string | null;
                        numberValue: number | null;
                        questionId: QuestionId | null;
                        promptId: PromptId | null;
                        children: Array<{
                            nodeType: Types.AdvancedSearchConditionNodeType;
                            fieldName: string | null;
                            operator: Types.FieldConditionOperator | null;
                            textValue: string | null;
                            dateValue: any;
                            calendarDateValue: string | null;
                            numberValue: number | null;
                            questionId: QuestionId | null;
                            promptId: PromptId | null;
                            children: Array<{
                                nodeType: Types.AdvancedSearchConditionNodeType;
                                fieldName: string | null;
                                operator: Types.FieldConditionOperator | null;
                                textValue: string | null;
                                dateValue: any;
                                calendarDateValue: string | null;
                                numberValue: number | null;
                                questionId: QuestionId | null;
                                promptId: PromptId | null;
                                children: Array<{
                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                    fieldName: string | null;
                                    operator: Types.FieldConditionOperator | null;
                                    textValue: string | null;
                                    dateValue: any;
                                    calendarDateValue: string | null;
                                    numberValue: number | null;
                                    questionId: QuestionId | null;
                                    promptId: PromptId | null;
                                    children: Array<{
                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                        fieldName: string | null;
                                        operator: Types.FieldConditionOperator | null;
                                        textValue: string | null;
                                        dateValue: any;
                                        calendarDateValue: string | null;
                                        numberValue: number | null;
                                        questionId: QuestionId | null;
                                        promptId: PromptId | null;
                                        children: Array<{
                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                            fieldName: string | null;
                                            operator: Types.FieldConditionOperator | null;
                                            textValue: string | null;
                                            dateValue: any;
                                            calendarDateValue: string | null;
                                            numberValue: number | null;
                                            questionId: QuestionId | null;
                                            promptId: PromptId | null;
                                            children: Array<{
                                                nodeType: Types.AdvancedSearchConditionNodeType;
                                                fieldName: string | null;
                                                operator: Types.FieldConditionOperator | null;
                                                textValue: string | null;
                                                dateValue: any;
                                                calendarDateValue: string | null;
                                                numberValue: number | null;
                                                questionId: QuestionId | null;
                                                promptId: PromptId | null;
                                                children: Array<{
                                                    nodeType: Types.AdvancedSearchConditionNodeType;
                                                    fieldName: string | null;
                                                    operator: Types.FieldConditionOperator | null;
                                                    textValue: string | null;
                                                    dateValue: any;
                                                    calendarDateValue: string | null;
                                                    numberValue: number | null;
                                                    questionId: QuestionId | null;
                                                    promptId: PromptId | null;
                                                    children: Array<{
                                                        nodeType: Types.AdvancedSearchConditionNodeType;
                                                        fieldName: string | null;
                                                        operator: Types.FieldConditionOperator | null;
                                                        textValue: string | null;
                                                        dateValue: any;
                                                        calendarDateValue: string | null;
                                                        numberValue: number | null;
                                                        questionId: QuestionId | null;
                                                        promptId: PromptId | null;
                                                        children: Array<{
                                                            nodeType: Types.AdvancedSearchConditionNodeType;
                                                            fieldName: string | null;
                                                            operator: Types.FieldConditionOperator | null;
                                                            textValue: string | null;
                                                            dateValue: any;
                                                            calendarDateValue:
                                                                | string
                                                                | null;
                                                            numberValue: number | null;
                                                            questionId: QuestionId | null;
                                                            promptId: PromptId | null;
                                                            relativeDate: {
                                                                value: number;
                                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                                            } | null;
                                                        }> | null;
                                                        relativeDate: {
                                                            value: number;
                                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                                        } | null;
                                                    }> | null;
                                                    relativeDate: {
                                                        value: number;
                                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                                    } | null;
                                                }> | null;
                                                relativeDate: {
                                                    value: number;
                                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                                } | null;
                                            }> | null;
                                            relativeDate: {
                                                value: number;
                                                interval: Types.AdvancedSearchRelativeDateInterval;
                                            } | null;
                                        }> | null;
                                        relativeDate: {
                                            value: number;
                                            interval: Types.AdvancedSearchRelativeDateInterval;
                                        } | null;
                                    }> | null;
                                    relativeDate: {
                                        value: number;
                                        interval: Types.AdvancedSearchRelativeDateInterval;
                                    } | null;
                                }> | null;
                                relativeDate: {
                                    value: number;
                                    interval: Types.AdvancedSearchRelativeDateInterval;
                                } | null;
                            }> | null;
                            relativeDate: {
                                value: number;
                                interval: Types.AdvancedSearchRelativeDateInterval;
                            } | null;
                        }> | null;
                        relativeDate: {
                            value: number;
                            interval: Types.AdvancedSearchRelativeDateInterval;
                        } | null;
                    }> | null;
                    relativeDate: {
                        value: number;
                        interval: Types.AdvancedSearchRelativeDateInterval;
                    } | null;
                };
            } | null;
        };
        regionVisibility: {
            editable: boolean;
            regions: Array<{ mode: Types.EntityRegionModeOutput; regionId: RegionId }>;
        } | null;
    };
};

export type DeleteWorkflowForTenantMutationVariables = Exact<{
    tenantId: TenantId;
    workflowId: WorkflowId;
}>;

export type DeleteWorkflowForTenantMutation = { deleteWorkflowForTenant: boolean };

export type DeleteWorkflowMutationVariables = Exact<{
    workflowId: WorkflowId;
}>;

export type DeleteWorkflowMutation = { deleteWorkflow: boolean };

export type GetWorkflowExecutionsForEntityQueryVariables = Exact<{
    workflowTargetId: WorkflowTargetId;
}>;

export type GetWorkflowExecutionsForEntityQuery = {
    getWorkflowExecutionsForEntity: Array<{
        id: WorkflowExecutionId;
        scheduledAt: any;
        cancelledAt: any;
        isCancelRequested: boolean;
        finishedAt: any;
        result: Types.WorkflowExecutionResult | null;
        workflow: { name: string; id: WorkflowId };
    }>;
};

export type CancelWorkflowExecutionMutationVariables = Exact<{
    workflowExecutionId: WorkflowExecutionId;
}>;

export type CancelWorkflowExecutionMutation = {
    cancelWorkflowExecution: { id: WorkflowExecutionId };
};

export type SetWorkflowSettingMutationVariables = Exact<{
    params: Types.SetWorkflowSettingInput;
}>;

export type SetWorkflowSettingMutation = { setWorkflowSetting: boolean };

export class TypedDocumentString<TResult, TVariables>
    extends String
    implements DocumentTypeDecoration<TResult, TVariables>
{
    __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>["__apiType"]>;
    private value: string;
    public __meta__?: Record<string, any> | undefined;

    constructor(value: string, __meta__?: Record<string, any> | undefined) {
        super(value);
        this.value = value;
        this.__meta__ = __meta__;
    }

    override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
        return this.value;
    }
}
export const AudienceFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment AudienceFragment on UserAudience {
  type
  specific {
    userIds
    groupIds
  }
}
    `,
    { fragmentName: "AudienceFragment" },
);
export const SummaryFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment SummaryFragment on AdvancedSearchRequestSummary {
  aggregates {
    operator
    column
  }
  groupBy
  sort {
    aggregateIndex
    ascending
  }
}
    `,
    { fragmentName: "SummaryFragment" },
);
export const SelectFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment SelectFragment on AdvancedSearchSelect {
  fieldName
  questionId
}
    `,
    { fragmentName: "SelectFragment" },
);
export const ConditionFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}
    `,
    { fragmentName: "ConditionFragment" },
);
export const AudienceBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment AudienceBasics on AudienceResult {
  id
  name
  createdBy
  updatedBy
  dateAdded
  updatedAt
  mailchimp {
    tag
    latestSyncResult {
      historyId
      status
      queuedAt
      startedAt
      completedAt
      errorMessage
      membersAdded
      membersUpdated
      membersRemoved
      totalMembers
      durationMs
    }
  }
  constantContact {
    tag
    latestSyncResult {
      historyId
      status
      queuedAt
      startedAt
      completedAt
      errorMessage
      membersAdded
      membersUpdated
      membersRemoved
      totalMembers
      durationMs
    }
  }
  query {
    queries {
      entityType
      sort {
        ascending
        column
        questionId
      }
      select {
        ...SelectFragment
      }
      condition {
        ...ConditionFragment
        children {
          ...ConditionFragment
          children {
            ...ConditionFragment
            children {
              ...ConditionFragment
              children {
                ...ConditionFragment
                children {
                  ...ConditionFragment
                  children {
                    ...ConditionFragment
                    children {
                      ...ConditionFragment
                      children {
                        ...ConditionFragment
                        children {
                          ...ConditionFragment
                          children {
                            ...ConditionFragment
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}
fragment SelectFragment on AdvancedSearchSelect {
  fieldName
  questionId
}`,
    { fragmentName: "AudienceBasics" },
);
export const BridgeQuestionFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment BridgeQuestionFragment on BridgeQuestion {
  id
  title
  interaction
  annotation
  questionType
  category
  key
  order
  regionMask
  rulesString
  searchBehavior
  source
  editable
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
  prompts {
    id
    title
    key
    order
    annotation
  }
}
    `,
    { fragmentName: "BridgeQuestionFragment" },
);
export const BridgeEntityFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment BridgeEntityFragment on BridgeEntityResult {
  name
  title
  preferredFilterField
  allowAnswers
  layoutParents
  identifierFields
  priorityFields {
    fieldName
    title
  }
  fields {
    name
    title
    questionType
    formatter
    category
    dependsOn
    customListKey
    aliases
    naryValues {
      value
      label
    }
    reportBehavior {
      sortable
      filterable
      selectable
      newReportSelectOrder
      summaryGroupable
      canAggregate
    }
  }
  relations {
    name
    title
    otherEntityName
    kind
    selectable
    aliases
  }
  questions {
    ...BridgeQuestionFragment
  }
  defaultSelect {
    ...SelectFragment
  }
  preferredDisplay {
    fieldName
    relation {
      name
    }
  }
  preferredGroupBy
  supportsTasks
  supportsWorkflows
}
    fragment SelectFragment on AdvancedSearchSelect {
  fieldName
  questionId
}
fragment BridgeQuestionFragment on BridgeQuestion {
  id
  title
  interaction
  annotation
  questionType
  category
  key
  order
  regionMask
  rulesString
  searchBehavior
  source
  editable
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
  prompts {
    id
    title
    key
    order
    annotation
  }
}`,
    { fragmentName: "BridgeEntityFragment" },
);
export const CommunityBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment CommunityBasics on Community {
  address
  caregiverRatio
  city
  contractStatus
  contractExpiresDate
  contractIssuedDate
  contractNotes
  dateAdded
  gender
  feeType
  paymentFee
  paymentPercentage
  id
  latitude
  licenseStatus
  longitude
  mainPhone
  monthlyPrice
  moveInDeposit
  name
  petDeposit
  priceDetails
  priceHighEnd
  rating
  secondPersonFee
  state
  summary
  thumbnailUrl
  unitsOrCapacity
  updatedAt
  virtualTourURL
  website
  zip
  regionId
  email
  county
  contacts {
    cellPhone
    name
    phone1
    role
    email1
  }
  primaryContacts {
    cellPhone
    name
    phone1
    role
    email1
  }
}
    `,
    { fragmentName: "CommunityBasics" },
);
export const FileFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment FileFragment on File {
  id
  entityId
  originalFilename
  mimeType
  description
  byteLength
  shareWithClient
  shareWithCommunity
  shareWithReferral
  shareWithHomeCareLead
  createdAt
  createdBy
  updatedAt
  updatedBy
  hasSignatureTemplate
  isGlobal
  managementLevel
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}
    `,
    { fragmentName: "FileFragment" },
);
export const PhotoBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment PhotoBasics on Photo {
  id
  entityId
  order
  dateAdded
  createdBy
  originalFile {
    ...FileFragment
  }
}
    fragment FileFragment on File {
  id
  entityId
  originalFilename
  mimeType
  description
  byteLength
  shareWithClient
  shareWithCommunity
  shareWithReferral
  shareWithHomeCareLead
  createdAt
  createdBy
  updatedAt
  updatedBy
  hasSignatureTemplate
  isGlobal
  managementLevel
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}`,
    { fragmentName: "PhotoBasics" },
);
export const CommunityCardBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment CommunityCardBasics on Community {
  ...CommunityBasics
  availability {
    status
    notes
    freshness
    lastUpdatedAt
  }
  isConnectedToEvergreen
  pricing {
    minPrice
    maxPrice
    notes
    files {
      id
      name
      mimeType
      size
      downloadUrl
    }
  }
  answers {
    questionId
    booleanAnswer
    numberAnswer
    numberAnswerHigh
    textAnswer
    dateAnswer
    calendarDateAnswer
    selections
    idReferenceAnswer
    selectionAnnotations {
      text
      selectionId
    }
  }
  photoEntities {
    ...PhotoBasics
  }
  licenses {
    id
    licenseNumber
    href
    licensee
    status
    licenseStartDate
    licenseEndDate
  }
  googleInfo {
    rating
    ratingCount
  }
}
    fragment CommunityBasics on Community {
  address
  caregiverRatio
  city
  contractStatus
  contractExpiresDate
  contractIssuedDate
  contractNotes
  dateAdded
  gender
  feeType
  paymentFee
  paymentPercentage
  id
  latitude
  licenseStatus
  longitude
  mainPhone
  monthlyPrice
  moveInDeposit
  name
  petDeposit
  priceDetails
  priceHighEnd
  rating
  secondPersonFee
  state
  summary
  thumbnailUrl
  unitsOrCapacity
  updatedAt
  virtualTourURL
  website
  zip
  regionId
  email
  county
  contacts {
    cellPhone
    name
    phone1
    role
    email1
  }
  primaryContacts {
    cellPhone
    name
    phone1
    role
    email1
  }
}
fragment FileFragment on File {
  id
  entityId
  originalFilename
  mimeType
  description
  byteLength
  shareWithClient
  shareWithCommunity
  shareWithReferral
  shareWithHomeCareLead
  createdAt
  createdBy
  updatedAt
  updatedBy
  hasSignatureTemplate
  isGlobal
  managementLevel
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}
fragment PhotoBasics on Photo {
  id
  entityId
  order
  dateAdded
  createdBy
  originalFile {
    ...FileFragment
  }
}`,
    { fragmentName: "CommunityCardBasics" },
);
export const ClientCommunitiesFragmentDoc = new TypedDocumentString(
    `
    fragment ClientCommunities on Client {
  id
  baseLocation {
    lat
    lng
  }
  clientCommunities {
    order
    relationship
    publicNotes
    privateNotes
    community {
      ...CommunityCardBasics
      evergreenCommunityId
    }
    referred
    tours {
      id
      communityId
      clientId
      userId
      tourDate
      tourTime
      tourDuration
      privateNotes
      familyNotes
      showOnFamilyView
      dateAdded
      updatedAt
    }
  }
}
    fragment CommunityBasics on Community {
  address
  caregiverRatio
  city
  contractStatus
  contractExpiresDate
  contractIssuedDate
  contractNotes
  dateAdded
  gender
  feeType
  paymentFee
  paymentPercentage
  id
  latitude
  licenseStatus
  longitude
  mainPhone
  monthlyPrice
  moveInDeposit
  name
  petDeposit
  priceDetails
  priceHighEnd
  rating
  secondPersonFee
  state
  summary
  thumbnailUrl
  unitsOrCapacity
  updatedAt
  virtualTourURL
  website
  zip
  regionId
  email
  county
  contacts {
    cellPhone
    name
    phone1
    role
    email1
  }
  primaryContacts {
    cellPhone
    name
    phone1
    role
    email1
  }
}
fragment CommunityCardBasics on Community {
  ...CommunityBasics
  availability {
    status
    notes
    freshness
    lastUpdatedAt
  }
  isConnectedToEvergreen
  pricing {
    minPrice
    maxPrice
    notes
    files {
      id
      name
      mimeType
      size
      downloadUrl
    }
  }
  answers {
    questionId
    booleanAnswer
    numberAnswer
    numberAnswerHigh
    textAnswer
    dateAnswer
    calendarDateAnswer
    selections
    idReferenceAnswer
    selectionAnnotations {
      text
      selectionId
    }
  }
  photoEntities {
    ...PhotoBasics
  }
  licenses {
    id
    licenseNumber
    href
    licensee
    status
    licenseStartDate
    licenseEndDate
  }
  googleInfo {
    rating
    ratingCount
  }
}
fragment FileFragment on File {
  id
  entityId
  originalFilename
  mimeType
  description
  byteLength
  shareWithClient
  shareWithCommunity
  shareWithReferral
  shareWithHomeCareLead
  createdAt
  createdBy
  updatedAt
  updatedBy
  hasSignatureTemplate
  isGlobal
  managementLevel
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}
fragment PhotoBasics on Photo {
  id
  entityId
  order
  dateAdded
  createdBy
  originalFile {
    ...FileFragment
  }
}`,
    { fragmentName: "ClientCommunities" },
);
export const DashboardDefaultsFragmentDoc = new TypedDocumentString(
    `
    fragment DashboardDefaults on ClientsSearchResult {
  total
  clients {
    id
    serial
    name
    statusListItemId
    bestContactName
    bestContactPhone
    dateAdded
    invoiceSentDate
    moveTimeline
    moveTimelineActual
    paymentReceivedDate
    statusCheckLaterDate
    statusClosedDate
    updatedAt
    assignedUserId
    phone
    cellPhone
    email
    additionalClientName
    chosenCommunity {
      id
      name
    }
    nextTask {
      id
      text
      status
      dueDate
      dueDateTime
    }
    allContacts {
      name
      email1
      phone1
      cellPhone
      relationship
    }
  }
}
    `,
    { fragmentName: "DashboardDefaults" },
);
export const ReferralResponseFragmentDoc = new TypedDocumentString(
    `
    fragment ReferralResponse on Referral {
  id
  placementId
  referenceBusinessId
  referenceContactId
  communityId
  referralType
  notes
  when
  dateAdded
  createdBy
  updatedAt
  updatedBy
}
    `,
    { fragmentName: "ReferralResponse" },
);
export const CommunityInsightsFragmentLiteFragmentDoc = new TypedDocumentString(
    `
    fragment CommunityInsightsFragmentLite on CommunityInsightsResult {
  id
  evergreenCommunityId
  name
  address
  city
  state
  zip
  googleMapsUrl
  phoneNumbers {
    number
    label
  }
  website
  averageRating
  ratingCount
  lastGooglePlaceSynchronizedAt
  availability {
    status
    lastUpdatedAt
    notes
    lastRequestedAt
    freshness
  }
  pricing {
    minPrice
    maxPrice
    notes
    lastUpdatedAt
    freshness
    files {
      id
      name
      size
      mimeType
      downloadUrl
    }
  }
}
    `,
    { fragmentName: "CommunityInsightsFragmentLite" },
);
export const CommunityInsightsFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment CommunityInsightsFragment on CommunityInsightsResult {
  ...CommunityInsightsFragmentLite
  reviews {
    id
    text
    rating
    authorDisplayName
    authorPhotoUri
    publishedAt
    source
    webUri
  }
}
    fragment CommunityInsightsFragmentLite on CommunityInsightsResult {
  id
  evergreenCommunityId
  name
  address
  city
  state
  zip
  googleMapsUrl
  phoneNumbers {
    number
    label
  }
  website
  averageRating
  ratingCount
  lastGooglePlaceSynchronizedAt
  availability {
    status
    lastUpdatedAt
    notes
    lastRequestedAt
    freshness
  }
  pricing {
    minPrice
    maxPrice
    notes
    lastUpdatedAt
    freshness
    files {
      id
      name
      size
      mimeType
      downloadUrl
    }
  }
}`,
    { fragmentName: "CommunityInsightsFragment" },
);
export const IdentityDefaultsFragmentDoc = new TypedDocumentString(
    `
    fragment IdentityDefaults on Identity {
  id
  name
  successorId
  propagateableLicenseTypes
  thumbnailUrl
}
    `,
    { fragmentName: "IdentityDefaults" },
);
export const LicenseDefaultsFragmentDoc = new TypedDocumentString(
    `
    fragment LicenseDefaults on License {
  status
  jurisdiction
  licenseNumber
  id
  licenseType
}
    `,
    { fragmentName: "LicenseDefaults" },
);
export const PublicRecordDefaultsFragmentDoc = new TypedDocumentString(
    `
    fragment PublicRecordDefaults on PublicRecord {
  id
  name
  address
  city
  state
  zip
  capacity
  licenseType
  licenseNumber
  licensee
  closedDate
  licenseDate
  licenseStatus
  county
  country
  phone
  administrator
  email
  lat
  lng
}
    `,
    { fragmentName: "PublicRecordDefaults" },
);
export const ContactFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment ContactFragment on ContactEntity {
  id
  name
  preferredName
  address1
  address2
  city
  state
  zip
  relationship
  role
  cellPhone
  phone1
  email1
  email1OptOut
  website
  contactType
  type
  fax
  contactNotes
  dateAdded
}
    `,
    { fragmentName: "ContactFragment" },
);
export const ContactWithLinksFragmentDoc = new TypedDocumentString(
    `
    fragment ContactWithLinks on ContactEntity {
  ...ContactFragment
  communityContacts {
    communityId
    contactId
    primary
    community {
      id
      name
      appLink
    }
  }
  clientContacts {
    placementId
    contactId
    relationship
    powerOfAttorney
    placement {
      id
      name
      appLink
    }
  }
  homeCareLeadContacts {
    homeCareLeadId
    contactId
    relationship
    powerOfAttorney
    homeCareLead {
      id
      appLink
    }
  }
  referenceContacts {
    id
    summary
    regionId
    ownerId
    dateAdded
    updatedAt
    updatedBy
    createdBy
    appLink
  }
}
    fragment ContactFragment on ContactEntity {
  id
  name
  preferredName
  address1
  address2
  city
  state
  zip
  relationship
  role
  cellPhone
  phone1
  email1
  email1OptOut
  website
  contactType
  type
  fax
  contactNotes
  dateAdded
}`,
    { fragmentName: "ContactWithLinks" },
);
export const SmsMessageBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment SmsMessageBasics on SmsMessage {
  id
  from
  to
  body
  direction
  dateAdded
  status
  createdBy
  createdByUser {
    id
    displayName
  }
}
    `,
    { fragmentName: "SmsMessageBasics" },
);
export const SentMessageFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment SentMessageFragment on SentMessage {
  id
  messageId
  userId
  from
  subject
  bodyPreview
  status
  latestEventStatus
  createdAt
  to
  cc
  messageType
  attachments
  clientIdUuid
  communityIdUuid
  clickedLinkEvents {
    link
    events
    redacted
    eventsV2 {
      timestamp
      recipient
    }
  }
  intakeCommunity {
    id
    name
    appLink
  }
  intakeClient {
    id
    name
    bestContactName
    appLink
  }
  events {
    eventType
    eventTimestamp
    recipient
  }
}
    `,
    { fragmentName: "SentMessageFragment" },
);
export const ContactSearchHitFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment ContactSearchHitFragment on ContactSearchHit {
  id
  type
  appLink
  fieldMatches {
    type
    phoneNumber
    email
  }
  client {
    id
    name
    phone
    cellPhone
  }
  community {
    id
    name
    mainPhone
  }
  contact {
    id
    name
    phone1
    cellPhone
  }
  clientContact {
    clientId
    contactId
    relationship
  }
  communityContact {
    communityId
    contactId
    primary
  }
  referenceContact {
    id
    contactEntity {
      id
      name
      phone1
      cellPhone
    }
  }
  referenceBusiness {
    id
    businessContactEntity {
      id
      name
      phone1
      cellPhone
    }
  }
}
    `,
    { fragmentName: "ContactSearchHitFragment" },
);
export const ConversationFeedBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment ConversationFeedBasics on ConversationFeed {
  threads {
    id
    type
    latestTimestamp
    ownerUserId
    smsThread {
      contact {
        id
        phoneNumber
      }
      phoneLine {
        id
        userId
        phoneNumber
      }
      messages {
        ...SmsMessageBasics
      }
    }
    voiceCall {
      id
      direction
      from
      to
      dateAdded
      recordingSid
      recordingStatus
      recordingDurationSec
      transcriptStatus
      summary
      createdByUser {
        id
        displayName
      }
    }
    emailThread {
      threadId
      sentMessages {
        ...SentMessageFragment
      }
      mailboxMessages {
        id
        externalId
        subject
        bodyPreview
        receivedTimestamp
        attachmentCount
        from {
          id
          name
          email
          resolvedName
        }
        sender {
          id
          name
          email
          resolvedName
        }
        to {
          id
          contact {
            id
            name
            email
            resolvedName
          }
        }
        cc {
          id
          contact {
            id
            name
            email
            resolvedName
          }
        }
        bcc {
          id
          contact {
            id
            name
            email
            resolvedName
          }
        }
        replyTo {
          id
          contact {
            id
            name
            email
            resolvedName
          }
        }
      }
    }
    contactSearchHits {
      ...ContactSearchHitFragment
    }
  }
}
    fragment ContactSearchHitFragment on ContactSearchHit {
  id
  type
  appLink
  fieldMatches {
    type
    phoneNumber
    email
  }
  client {
    id
    name
    phone
    cellPhone
  }
  community {
    id
    name
    mainPhone
  }
  contact {
    id
    name
    phone1
    cellPhone
  }
  clientContact {
    clientId
    contactId
    relationship
  }
  communityContact {
    communityId
    contactId
    primary
  }
  referenceContact {
    id
    contactEntity {
      id
      name
      phone1
      cellPhone
    }
  }
  referenceBusiness {
    id
    businessContactEntity {
      id
      name
      phone1
      cellPhone
    }
  }
}
fragment SentMessageFragment on SentMessage {
  id
  messageId
  userId
  from
  subject
  bodyPreview
  status
  latestEventStatus
  createdAt
  to
  cc
  messageType
  attachments
  clientIdUuid
  communityIdUuid
  clickedLinkEvents {
    link
    events
    redacted
    eventsV2 {
      timestamp
      recipient
    }
  }
  intakeCommunity {
    id
    name
    appLink
  }
  intakeClient {
    id
    name
    bestContactName
    appLink
  }
  events {
    eventType
    eventTimestamp
    recipient
  }
}
fragment SmsMessageBasics on SmsMessage {
  id
  from
  to
  body
  direction
  dateAdded
  status
  createdBy
  createdByUser {
    id
    displayName
  }
}`,
    { fragmentName: "ConversationFeedBasics" },
);
export const CustomListItemResponseFragmentDoc = new TypedDocumentString(
    `
    fragment CustomListItemResponse on CustomListItem {
  id
  key
  name
  order
  customListCategoryId
  icon
  pipelineVisibility
  pipelineDateLabel
  pipelineDateField
  isArchived
  updateBehavior
}
    `,
    { fragmentName: "CustomListItemResponse" },
);
export const CustomListResponseFragmentDoc = new TypedDocumentString(
    `
    fragment CustomListResponse on CustomList {
  id
  key
  items {
    ...CustomListItemResponse
  }
  categories {
    id
    key
    name
    order
  }
}
    fragment CustomListItemResponse on CustomListItem {
  id
  key
  name
  order
  customListCategoryId
  icon
  pipelineVisibility
  pipelineDateLabel
  pipelineDateField
  isArchived
  updateBehavior
}`,
    { fragmentName: "CustomListResponse" },
);
export const DisclosureAcknowledgementFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment DisclosureAcknowledgementFragment on DisclosureAcknowledgement {
  id
  name
  email
  phone
  agreed
  response
  dateAdded
  createdBy
  ipAddress
}
    `,
    { fragmentName: "DisclosureAcknowledgementFragment" },
);
export const DisclosureFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment DisclosureFragment on Disclosure {
  id
  dateAdded
  publicUrl
  acknowledgements {
    ...DisclosureAcknowledgementFragment
  }
}
    fragment DisclosureAcknowledgementFragment on DisclosureAcknowledgement {
  id
  name
  email
  phone
  agreed
  response
  dateAdded
  createdBy
  ipAddress
}`,
    { fragmentName: "DisclosureFragment" },
);
export const ImportFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment ImportFragment on EvergreenImport {
  created
  dateAdded
  duplicates
  endedAt
  errors
  id
  message
  skips
  delisted
  sourceName
  startedAt
  updatedAt
}
    `,
    { fragmentName: "ImportFragment" },
);
export const EvergreenCommunitySearchHitFragmentFragmentDoc = new TypedDocumentString(
    `
    fragment EvergreenCommunitySearchHitFragment on EvergreenCommunitySearchHit {
  id
  name
  address
  city
  state
  zip
  county
  status
  licenseTypes
  thumbnailUrl
  aliases {
    id
    name
    status
    licenses {
      id
      jurisdiction
      licenseNumber
      status
      licenseTypes
    }
  }
  listings {
    id
    visibility
    preferredName
    website
    summary
    startingPrice
    phoneNumbers {
      number
    }
    amenities
    careTypes
    careServices
    evergreenCommunityId
  }
}
    `,
    { fragmentName: "EvergreenCommunitySearchHitFragment" },
);
export const LayoutItemBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment LayoutItemBasics on LayoutItem {
  id
  order
  questionId
  entityFieldName
  showIfBlank
  naryFormat
  customTitle
  isRequired
}
    `,
    { fragmentName: "LayoutItemBasics" },
);
export const LayoutSectionBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment LayoutSectionBasics on LayoutSection {
  id
  order
  sectionKey
  visible
  entityType
  title
  icon
  renderBehavior
  questionCategory
  legacyPerspective
  includeRelations
  singlePropertyName
  defaultStandardFields
  settings {
    key
    label
    type
    order
  }
  layoutItems {
    ...LayoutItemBasics
  }
}
    fragment LayoutItemBasics on LayoutItem {
  id
  order
  questionId
  entityFieldName
  showIfBlank
  naryFormat
  customTitle
  isRequired
}`,
    { fragmentName: "LayoutSectionBasics" },
);
export const ReferenceBusinessSearchHitDefaultsFragmentDoc = new TypedDocumentString(
    `
    fragment ReferenceBusinessSearchHitDefaults on ReferenceBusinessSearchHit {
  id
  name
  updatedAt
  summary
  organizationType
  city
  state
  zip
  address
  phone
  fax
  email
  numberOfReferrals
  mostRecentReferral
  assignedTo
  latitude
  longitude
}
    `,
    { fragmentName: "ReferenceBusinessSearchHitDefaults" },
);
export const ReferenceCommunitySearchHitDefaultsFragmentDoc = new TypedDocumentString(
    `
    fragment ReferenceCommunitySearchHitDefaults on ReferenceCommunitySearchHit {
  id
  name
  updatedAt
  city
  state
  zip
  address
  phone
  fax
  summary
  numberOfReferrals
  mostRecentReferral
  latitude
  longitude
}
    `,
    { fragmentName: "ReferenceCommunitySearchHitDefaults" },
);
export const ReferralBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment ReferralBasics on Referral {
  id
  placementId
  placement {
    id
    name
    bestContactName
    statusListItemId
    dateAdded
    nextTask {
      id
      text
      dueDate
      dueDateTime
      compositeDueDateTime
      status
    }
    serial
    assignedUserId
    updatedAt
    moveTimeline
    moveTimelineActual
  }
}
    `,
    { fragmentName: "ReferralBasics" },
);
export const NoteResponseFragmentDoc = new TypedDocumentString(
    `
    fragment NoteResponse on NoteDto {
  _id
  text
  dateAdded
  wasEdited
  authorId
  editorId
  updatedAt
  isDraft
  isPinned
}
    `,
    { fragmentName: "NoteResponse" },
);
export const ReferenceContactBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment ReferenceContactBasics on ReferenceContact {
  id
  ownerId
  deleted
  summary
  businessId
  communityId
  regionId
  dateAdded
  updatedAt
  createdBy
  updatedBy
  draftNote
  bestDaysToVisit
  bestTimeToContact
  contactEntity {
    ...ContactFragment
  }
  business {
    id
    ownerId
    deleted
    regionId
    latitude
    longitude
    businessContactEntity {
      ...ContactFragment
    }
  }
  community {
    id
    name
    address
    city
    state
    zip
    mainPhone
    website
    email
    latitude
    longitude
    contractStatus
  }
  answers {
    questionId
    booleanAnswer
    numberAnswer
    numberAnswerHigh
    textAnswer
    dateAnswer
    calendarDateAnswer
    selections
    idReferenceAnswer
    selectionAnnotations {
      text
      selectionId
    }
  }
  notes {
    ...NoteResponse
  }
}
    fragment ContactFragment on ContactEntity {
  id
  name
  preferredName
  address1
  address2
  city
  state
  zip
  relationship
  role
  cellPhone
  phone1
  email1
  email1OptOut
  website
  contactType
  type
  fax
  contactNotes
  dateAdded
}
fragment NoteResponse on NoteDto {
  _id
  text
  dateAdded
  wasEdited
  authorId
  editorId
  updatedAt
  isDraft
  isPinned
}`,
    { fragmentName: "ReferenceContactBasics" },
);
export const ReferenceBusinessBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment ReferenceBusinessBasics on ReferenceBusiness {
  id
  ownerId
  deleted
  summary
  email
  dateAdded
  updatedAt
  createdBy
  updatedBy
  draftNote
  bestDaysToVisit
  bestTimeToContact
  regionId
  latitude
  longitude
  businessContactEntity {
    ...ContactFragment
  }
  answers {
    questionId
    booleanAnswer
    numberAnswer
    numberAnswerHigh
    textAnswer
    dateAnswer
    calendarDateAnswer
    selections
    idReferenceAnswer
    selectionAnnotations {
      text
      selectionId
    }
  }
  notes {
    ...NoteResponse
  }
}
    fragment ContactFragment on ContactEntity {
  id
  name
  preferredName
  address1
  address2
  city
  state
  zip
  relationship
  role
  cellPhone
  phone1
  email1
  email1OptOut
  website
  contactType
  type
  fax
  contactNotes
  dateAdded
}
fragment NoteResponse on NoteDto {
  _id
  text
  dateAdded
  wasEdited
  authorId
  editorId
  updatedAt
  isDraft
  isPinned
}`,
    { fragmentName: "ReferenceBusinessBasics" },
);
export const SmtpCredentialBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment SmtpCredentialBasics on SmtpCredential {
  id
  host
  port
  username
  status
  authenticationType
  provider
}
    `,
    { fragmentName: "SmtpCredentialBasics" },
);
export const TaskBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment TaskBasics on Task {
  id
  completedAt
  dueDate
  dueDateTime
  notes
  ownerId
  parentId
  status
  text
  done
  taskTypeId
  taskType {
    id
    name
  }
  recurrencePattern
  recurrenceInterval
  recurrenceDaysOfWeek
  recurrenceDayOfMonth
}
    `,
    { fragmentName: "TaskBasics" },
);
export const TaskDetailsFragmentDoc = new TypedDocumentString(
    `
    fragment TaskDetails on Task {
  ...TaskBasics
  notes
}
    fragment TaskBasics on Task {
  id
  completedAt
  dueDate
  dueDateTime
  notes
  ownerId
  parentId
  status
  text
  done
  taskTypeId
  taskType {
    id
    name
  }
  recurrencePattern
  recurrenceInterval
  recurrenceDaysOfWeek
  recurrenceDayOfMonth
}`,
    { fragmentName: "TaskDetails" },
);
export const TelecomPhoneLineBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment TelecomPhoneLineBasics on TelecomPhoneLine {
  id
  userId
  phoneNumber
  createdAt
  updatedAt
  status
  credentialType
}
    `,
    { fragmentName: "TelecomPhoneLineBasics" },
);
export const UserManagementUserFragmentDoc = new TypedDocumentString(
    `
    fragment UserManagementUser on User {
  id
  email
  name
  type
  hidden
  disabled
  twoFactorEmailEnabled
  twoFactorSmsEnabled
  permissions {
    role
    region {
      id
      name
      key
      membershipType
    }
  }
}
    `,
    { fragmentName: "UserManagementUser" },
);
export const UserTerritoryBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment UserTerritoryBasics on UserTerritory {
  id
  type
  distance
  address
  city
  county
  state
  zip
  latitude
  longitude
}
    `,
    { fragmentName: "UserTerritoryBasics" },
);
export const UserProfileBasicsFragmentDoc = new TypedDocumentString(
    `
    fragment UserProfileBasics on UserProfile {
  id
  name
  businessName
  website
  email
  phone
  summary
  approach
  certifications
  pictureUrl
  status
  latitude
  longitude
  isComplete
  territories {
    ...UserTerritoryBasics
  }
}
    fragment UserTerritoryBasics on UserTerritory {
  id
  type
  distance
  address
  city
  county
  state
  zip
  latitude
  longitude
}`,
    { fragmentName: "UserProfileBasics" },
);
export const WorkflowDefaultsFragmentDoc = new TypedDocumentString(
    `
    fragment WorkflowDefaults on Workflow {
  actions {
    ... on WorkflowActionEmailEntity {
      __typename
      body
      subject
      to
      discreteTo
      attachments
      bccMyself
    }
    ... on WorkflowActionTaskEntity {
      __typename
      dueDate
      text
      assignedTo
      taskTypeId
      notifyOwner
      notification {
        subject
      }
      recurrencePattern
      recurrenceInterval
      recurrenceDaysOfWeek
      recurrenceDayOfMonth
    }
  }
  attachTo
  createdBy
  dateAdded
  delay
  delayRecipe
  description
  id
  name
  status
  defaultActorId
  actorMode
  settings {
    defaultValue
    description
    key
    label
    type
    scope
  }
  trigger {
    eventType
    entityUpdated {
      fieldMatchers {
        fieldName
        previous {
          values
          specialValue
        }
        current {
          values
          specialValue
        }
      }
    }
    noteAdded {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
    }
    emailOpened {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
      activityThresholdDays
    }
    pipelineStageChanged {
      pipelineId
      toStageIds
    }
    schedule {
      cron
      entityCondition {
        ...ConditionFragment
        children {
          ...ConditionFragment
          children {
            ...ConditionFragment
            children {
              ...ConditionFragment
              children {
                ...ConditionFragment
                children {
                  ...ConditionFragment
                  children {
                    ...ConditionFragment
                    children {
                      ...ConditionFragment
                      children {
                        ...ConditionFragment
                        children {
                          ...ConditionFragment
                          children {
                            ...ConditionFragment
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  updatedAt
  updatedBy
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}`,
    { fragmentName: "WorkflowDefaults" },
);
export const GetActivityFeedDocument = new TypedDocumentString(`
    query getActivityFeed($entityId: ActivityParentEntityId!) {
  getActivityFeed(entityId: $entityId) {
    activities {
      activity {
        id
        activityType
        sourceId
        voiceCallId
        createdAt
        parentEntityId
        createdByEntity {
          preferredName
        }
        parentReferenceContact {
          id
          contactEntity {
            name
          }
          appLink
        }
        parentReferenceBusiness {
          id
          businessContactEntity {
            name
          }
          appLink
        }
        parentCommunity {
          id
          name
          appLink
        }
        metadata {
          events {
            type
            entityFieldChange {
              field
              oldStringValue
              newStringValue
            }
            entityReassign {
              from
              to
            }
            entityCollaboratorAdded {
              collaboratorId
            }
            entityCollaboratorRemoved {
              collaboratorId
            }
            outgoingCall {
              phoneNumber
            }
            smsSent {
              body
            }
            touchpointLogged {
              channel
              note
            }
            touchpointSnoozed {
              scope
              snoozeUntil
              reason
            }
            visitScheduled {
              eventDate
              title
            }
            pipelineStageChange {
              pipelineName
              fromStageName
              toStageName
            }
            sentToPartner {
              integration
              url
            }
          }
        }
      }
      note {
        text
        effectiveCreatedAt
        publishedAt
      }
      task {
        id
        status
        dueDate
        dueDateTime
        completedAt
        type
        taskType {
          id
          name
          category
        }
        text
      }
      file {
        originalFilename
      }
      message {
        subject
        intakeCommunity {
          name
        }
      }
      sourceType
    }
  }
}
    `);

export const useGetActivityFeedQuery = <TData = GetActivityFeedQuery, TError = unknown>(
    variables: GetActivityFeedQueryVariables,
    options?: UseQueryOptions<GetActivityFeedQuery, TError, TData>,
) => {
    return useQuery<GetActivityFeedQuery, TError, TData>(
        ["getActivityFeed", variables],
        graphqlFetcher<GetActivityFeedQuery, GetActivityFeedQueryVariables>(
            GetActivityFeedDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetActivityFeedQuery = <
    TData = GetActivityFeedQuery,
    TError = unknown,
>(
    variables: GetActivityFeedQueryVariables,
    options?: UseInfiniteQueryOptions<GetActivityFeedQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetActivityFeedQuery, TError, TData>(
        ["getActivityFeed.infinite", variables],
        metaData =>
            graphqlFetcher<GetActivityFeedQuery, GetActivityFeedQueryVariables>(
                GetActivityFeedDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const CreateSavedQueryDocument = new TypedDocumentString(`
    mutation createSavedQuery($savedQuery: SavedQueryInput!) {
  createSavedQuery(savedQuery: $savedQuery) {
    id
  }
}
    `);

export const useCreateSavedQueryMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateSavedQueryMutation,
        TError,
        CreateSavedQueryMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateSavedQueryMutation,
        TError,
        CreateSavedQueryMutationVariables,
        TContext
    >(
        ["createSavedQuery"],
        (variables?: CreateSavedQueryMutationVariables) =>
            graphqlFetcher<CreateSavedQueryMutation, CreateSavedQueryMutationVariables>(
                CreateSavedQueryDocument,
                variables,
            )(),
        options,
    );
};

export const UpdateSavedQueryDocument = new TypedDocumentString(`
    mutation updateSavedQuery($savedQuery: SavedQueryInput!) {
  updateSavedQuery(savedQuery: $savedQuery) {
    id
  }
}
    `);

export const useUpdateSavedQueryMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateSavedQueryMutation,
        TError,
        UpdateSavedQueryMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateSavedQueryMutation,
        TError,
        UpdateSavedQueryMutationVariables,
        TContext
    >(
        ["updateSavedQuery"],
        (variables?: UpdateSavedQueryMutationVariables) =>
            graphqlFetcher<UpdateSavedQueryMutation, UpdateSavedQueryMutationVariables>(
                UpdateSavedQueryDocument,
                variables,
            )(),
        options,
    );
};

export const DeleteSavedQueryDocument = new TypedDocumentString(`
    mutation deleteSavedQuery($savedQueryId: SavedQueryId!) {
  deleteSavedQuery(savedQueryId: $savedQueryId)
}
    `);

export const useDeleteSavedQueryMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteSavedQueryMutation,
        TError,
        DeleteSavedQueryMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteSavedQueryMutation,
        TError,
        DeleteSavedQueryMutationVariables,
        TContext
    >(
        ["deleteSavedQuery"],
        (variables?: DeleteSavedQueryMutationVariables) =>
            graphqlFetcher<DeleteSavedQueryMutation, DeleteSavedQueryMutationVariables>(
                DeleteSavedQueryDocument,
                variables,
            )(),
        options,
    );
};

export const UpdateQueryFavoriteStatusDocument = new TypedDocumentString(`
    mutation updateQueryFavoriteStatus($savedQueryId: SavedQueryId!, $isFavorite: Boolean!) {
  updateQueryFavoriteStatus(savedQueryId: $savedQueryId, isFavorite: $isFavorite)
}
    `);

export const useUpdateQueryFavoriteStatusMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        UpdateQueryFavoriteStatusMutation,
        TError,
        UpdateQueryFavoriteStatusMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateQueryFavoriteStatusMutation,
        TError,
        UpdateQueryFavoriteStatusMutationVariables,
        TContext
    >(
        ["updateQueryFavoriteStatus"],
        (variables?: UpdateQueryFavoriteStatusMutationVariables) =>
            graphqlFetcher<
                UpdateQueryFavoriteStatusMutation,
                UpdateQueryFavoriteStatusMutationVariables
            >(UpdateQueryFavoriteStatusDocument, variables)(),
        options,
    );
};

export const UpsertSavedQueryScheduleDocument = new TypedDocumentString(`
    mutation upsertSavedQuerySchedule($schedule: SavedQueryScheduleInput!) {
  upsertSavedQuerySchedule(schedule: $schedule) {
    schedule
    audience {
      ...AudienceFragment
    }
  }
}
    fragment AudienceFragment on UserAudience {
  type
  specific {
    userIds
    groupIds
  }
}`);

export const useUpsertSavedQueryScheduleMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpsertSavedQueryScheduleMutation,
        TError,
        UpsertSavedQueryScheduleMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpsertSavedQueryScheduleMutation,
        TError,
        UpsertSavedQueryScheduleMutationVariables,
        TContext
    >(
        ["upsertSavedQuerySchedule"],
        (variables?: UpsertSavedQueryScheduleMutationVariables) =>
            graphqlFetcher<
                UpsertSavedQueryScheduleMutation,
                UpsertSavedQueryScheduleMutationVariables
            >(UpsertSavedQueryScheduleDocument, variables)(),
        options,
    );
};

export const DeleteSavedQueryScheduleDocument = new TypedDocumentString(`
    mutation deleteSavedQuerySchedule($savedQueryId: SavedQueryId!) {
  deleteSavedQuerySchedule(savedQueryId: $savedQueryId)
}
    `);

export const useDeleteSavedQueryScheduleMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteSavedQueryScheduleMutation,
        TError,
        DeleteSavedQueryScheduleMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteSavedQueryScheduleMutation,
        TError,
        DeleteSavedQueryScheduleMutationVariables,
        TContext
    >(
        ["deleteSavedQuerySchedule"],
        (variables?: DeleteSavedQueryScheduleMutationVariables) =>
            graphqlFetcher<
                DeleteSavedQueryScheduleMutation,
                DeleteSavedQueryScheduleMutationVariables
            >(DeleteSavedQueryScheduleDocument, variables)(),
        options,
    );
};

export const SendSavedQueryEmailDocument = new TypedDocumentString(`
    mutation sendSavedQueryEmail($payload: SavedQuerySendEmailInput!) {
  sendSavedQueryEmail(payload: $payload)
}
    `);

export const useSendSavedQueryEmailMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        SendSavedQueryEmailMutation,
        TError,
        SendSavedQueryEmailMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SendSavedQueryEmailMutation,
        TError,
        SendSavedQueryEmailMutationVariables,
        TContext
    >(
        ["sendSavedQueryEmail"],
        (variables?: SendSavedQueryEmailMutationVariables) =>
            graphqlFetcher<
                SendSavedQueryEmailMutation,
                SendSavedQueryEmailMutationVariables
            >(SendSavedQueryEmailDocument, variables)(),
        options,
    );
};

export const GetSavedQueryFoldersDocument = new TypedDocumentString(`
    query getSavedQueryFolders {
  getSavedQueries {
    savedQueries {
      folderName
      isMyQuery
      isShared
    }
  }
}
    `);

export const useGetSavedQueryFoldersQuery = <
    TData = GetSavedQueryFoldersQuery,
    TError = unknown,
>(
    variables?: GetSavedQueryFoldersQueryVariables,
    options?: UseQueryOptions<GetSavedQueryFoldersQuery, TError, TData>,
) => {
    return useQuery<GetSavedQueryFoldersQuery, TError, TData>(
        variables === undefined
            ? ["getSavedQueryFolders"]
            : ["getSavedQueryFolders", variables],
        graphqlFetcher<GetSavedQueryFoldersQuery, GetSavedQueryFoldersQueryVariables>(
            GetSavedQueryFoldersDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetSavedQueryFoldersQuery = <
    TData = GetSavedQueryFoldersQuery,
    TError = unknown,
>(
    variables?: GetSavedQueryFoldersQueryVariables,
    options?: UseInfiniteQueryOptions<GetSavedQueryFoldersQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetSavedQueryFoldersQuery, TError, TData>(
        variables === undefined
            ? ["getSavedQueryFolders.infinite"]
            : ["getSavedQueryFolders.infinite", variables],
        metaData =>
            graphqlFetcher<GetSavedQueryFoldersQuery, GetSavedQueryFoldersQueryVariables>(
                GetSavedQueryFoldersDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetSavedQueriesDocument = new TypedDocumentString(`
    query getSavedQueries {
  getSavedQueries {
    canSaveAdditional
    savedQueries {
      id
      folderName
      name
      isMyQuery
      isShared
      regionVisibility {
        editable
        regions {
          mode
          regionId
        }
      }
      isFavorite
      editable
      entityType
      createdBy
      warnings {
        location
        message
      }
      schedule {
        schedule
        audience {
          ...AudienceFragment
        }
        emailDescription
      }
      sort {
        ascending
        column
        questionId
      }
      select {
        ...SelectFragment
      }
      summaries {
        ...SummaryFragment
      }
      condition {
        ...ConditionFragment
        children {
          ...ConditionFragment
          children {
            ...ConditionFragment
            children {
              ...ConditionFragment
              children {
                ...ConditionFragment
                children {
                  ...ConditionFragment
                  children {
                    ...ConditionFragment
                    children {
                      ...ConditionFragment
                      children {
                        ...ConditionFragment
                        children {
                          ...ConditionFragment
                          children {
                            ...ConditionFragment
                            children {
                              ...ConditionFragment
                              children {
                                ...ConditionFragment
                                children {
                                  ...ConditionFragment
                                  children {
                                    ...ConditionFragment
                                    children {
                                      ...ConditionFragment
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}
fragment SelectFragment on AdvancedSearchSelect {
  fieldName
  questionId
}
fragment AudienceFragment on UserAudience {
  type
  specific {
    userIds
    groupIds
  }
}
fragment SummaryFragment on AdvancedSearchRequestSummary {
  aggregates {
    operator
    column
  }
  groupBy
  sort {
    aggregateIndex
    ascending
  }
}`);

export const useGetSavedQueriesQuery = <TData = GetSavedQueriesQuery, TError = unknown>(
    variables?: GetSavedQueriesQueryVariables,
    options?: UseQueryOptions<GetSavedQueriesQuery, TError, TData>,
) => {
    return useQuery<GetSavedQueriesQuery, TError, TData>(
        variables === undefined ? ["getSavedQueries"] : ["getSavedQueries", variables],
        graphqlFetcher<GetSavedQueriesQuery, GetSavedQueriesQueryVariables>(
            GetSavedQueriesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetSavedQueriesQuery = <
    TData = GetSavedQueriesQuery,
    TError = unknown,
>(
    variables?: GetSavedQueriesQueryVariables,
    options?: UseInfiniteQueryOptions<GetSavedQueriesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetSavedQueriesQuery, TError, TData>(
        variables === undefined
            ? ["getSavedQueries.infinite"]
            : ["getSavedQueries.infinite", variables],
        metaData =>
            graphqlFetcher<GetSavedQueriesQuery, GetSavedQueriesQueryVariables>(
                GetSavedQueriesDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetEmailProviderListsDocument = new TypedDocumentString(`
    query GetEmailProviderLists($input: GetEmailProviderListsInput!) {
  getEmailProviderLists(input: $input) {
    id
    name
    memberCount
  }
}
    `);

export const useGetEmailProviderListsQuery = <
    TData = GetEmailProviderListsQuery,
    TError = unknown,
>(
    variables: GetEmailProviderListsQueryVariables,
    options?: UseQueryOptions<GetEmailProviderListsQuery, TError, TData>,
) => {
    return useQuery<GetEmailProviderListsQuery, TError, TData>(
        ["GetEmailProviderLists", variables],
        graphqlFetcher<GetEmailProviderListsQuery, GetEmailProviderListsQueryVariables>(
            GetEmailProviderListsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetEmailProviderListsQuery = <
    TData = GetEmailProviderListsQuery,
    TError = unknown,
>(
    variables: GetEmailProviderListsQueryVariables,
    options?: UseInfiniteQueryOptions<GetEmailProviderListsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetEmailProviderListsQuery, TError, TData>(
        ["GetEmailProviderLists.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetEmailProviderListsQuery,
                GetEmailProviderListsQueryVariables
            >(GetEmailProviderListsDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const TriggerAudienceSyncDocument = new TypedDocumentString(`
    mutation TriggerAudienceSync($input: TriggerAudienceSyncGraphQLInput!) {
  triggerAudienceSync(input: $input) {
    historyId
    status
    queuedAt
    startedAt
    completedAt
    errorMessage
    membersAdded
    membersUpdated
    membersRemoved
    totalMembers
    durationMs
  }
}
    `);

export const useTriggerAudienceSyncMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        TriggerAudienceSyncMutation,
        TError,
        TriggerAudienceSyncMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        TriggerAudienceSyncMutation,
        TError,
        TriggerAudienceSyncMutationVariables,
        TContext
    >(
        ["TriggerAudienceSync"],
        (variables?: TriggerAudienceSyncMutationVariables) =>
            graphqlFetcher<
                TriggerAudienceSyncMutation,
                TriggerAudienceSyncMutationVariables
            >(TriggerAudienceSyncDocument, variables)(),
        options,
    );
};

export const RecentAudienceSyncsDocument = new TypedDocumentString(`
    query RecentAudienceSyncs($input: RecentAudienceSyncsInput!) {
  recentAudienceSyncs(input: $input) {
    historyId
    status
    queuedAt
    startedAt
    completedAt
    errorMessage
    membersAdded
    membersUpdated
    membersRemoved
    totalMembers
    durationMs
  }
}
    `);

export const useRecentAudienceSyncsQuery = <
    TData = RecentAudienceSyncsQuery,
    TError = unknown,
>(
    variables: RecentAudienceSyncsQueryVariables,
    options?: UseQueryOptions<RecentAudienceSyncsQuery, TError, TData>,
) => {
    return useQuery<RecentAudienceSyncsQuery, TError, TData>(
        ["RecentAudienceSyncs", variables],
        graphqlFetcher<RecentAudienceSyncsQuery, RecentAudienceSyncsQueryVariables>(
            RecentAudienceSyncsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteRecentAudienceSyncsQuery = <
    TData = RecentAudienceSyncsQuery,
    TError = unknown,
>(
    variables: RecentAudienceSyncsQueryVariables,
    options?: UseInfiniteQueryOptions<RecentAudienceSyncsQuery, TError, TData>,
) => {
    return useInfiniteQuery<RecentAudienceSyncsQuery, TError, TData>(
        ["RecentAudienceSyncs.infinite", variables],
        metaData =>
            graphqlFetcher<RecentAudienceSyncsQuery, RecentAudienceSyncsQueryVariables>(
                RecentAudienceSyncsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const CreateAudienceDocument = new TypedDocumentString(`
    mutation createAudience($input: CreateAudienceGraphQLInput!) {
  createAudience(input: $input) {
    ...AudienceBasics
  }
}
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}
fragment SelectFragment on AdvancedSearchSelect {
  fieldName
  questionId
}
fragment AudienceBasics on AudienceResult {
  id
  name
  createdBy
  updatedBy
  dateAdded
  updatedAt
  mailchimp {
    tag
    latestSyncResult {
      historyId
      status
      queuedAt
      startedAt
      completedAt
      errorMessage
      membersAdded
      membersUpdated
      membersRemoved
      totalMembers
      durationMs
    }
  }
  constantContact {
    tag
    latestSyncResult {
      historyId
      status
      queuedAt
      startedAt
      completedAt
      errorMessage
      membersAdded
      membersUpdated
      membersRemoved
      totalMembers
      durationMs
    }
  }
  query {
    queries {
      entityType
      sort {
        ascending
        column
        questionId
      }
      select {
        ...SelectFragment
      }
      condition {
        ...ConditionFragment
        children {
          ...ConditionFragment
          children {
            ...ConditionFragment
            children {
              ...ConditionFragment
              children {
                ...ConditionFragment
                children {
                  ...ConditionFragment
                  children {
                    ...ConditionFragment
                    children {
                      ...ConditionFragment
                      children {
                        ...ConditionFragment
                        children {
                          ...ConditionFragment
                          children {
                            ...ConditionFragment
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}`);

export const useCreateAudienceMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateAudienceMutation,
        TError,
        CreateAudienceMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateAudienceMutation,
        TError,
        CreateAudienceMutationVariables,
        TContext
    >(
        ["createAudience"],
        (variables?: CreateAudienceMutationVariables) =>
            graphqlFetcher<CreateAudienceMutation, CreateAudienceMutationVariables>(
                CreateAudienceDocument,
                variables,
            )(),
        options,
    );
};

export const UpdateAudienceDocument = new TypedDocumentString(`
    mutation updateAudience($input: UpdateAudienceGraphQLInput!) {
  updateAudience(input: $input) {
    ...AudienceBasics
  }
}
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}
fragment SelectFragment on AdvancedSearchSelect {
  fieldName
  questionId
}
fragment AudienceBasics on AudienceResult {
  id
  name
  createdBy
  updatedBy
  dateAdded
  updatedAt
  mailchimp {
    tag
    latestSyncResult {
      historyId
      status
      queuedAt
      startedAt
      completedAt
      errorMessage
      membersAdded
      membersUpdated
      membersRemoved
      totalMembers
      durationMs
    }
  }
  constantContact {
    tag
    latestSyncResult {
      historyId
      status
      queuedAt
      startedAt
      completedAt
      errorMessage
      membersAdded
      membersUpdated
      membersRemoved
      totalMembers
      durationMs
    }
  }
  query {
    queries {
      entityType
      sort {
        ascending
        column
        questionId
      }
      select {
        ...SelectFragment
      }
      condition {
        ...ConditionFragment
        children {
          ...ConditionFragment
          children {
            ...ConditionFragment
            children {
              ...ConditionFragment
              children {
                ...ConditionFragment
                children {
                  ...ConditionFragment
                  children {
                    ...ConditionFragment
                    children {
                      ...ConditionFragment
                      children {
                        ...ConditionFragment
                        children {
                          ...ConditionFragment
                          children {
                            ...ConditionFragment
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}`);

export const useUpdateAudienceMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateAudienceMutation,
        TError,
        UpdateAudienceMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateAudienceMutation,
        TError,
        UpdateAudienceMutationVariables,
        TContext
    >(
        ["updateAudience"],
        (variables?: UpdateAudienceMutationVariables) =>
            graphqlFetcher<UpdateAudienceMutation, UpdateAudienceMutationVariables>(
                UpdateAudienceDocument,
                variables,
            )(),
        options,
    );
};

export const DeleteAudienceDocument = new TypedDocumentString(`
    mutation deleteAudience($input: DeleteAudienceGraphQLInput!) {
  deleteAudience(input: $input)
}
    `);

export const useDeleteAudienceMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteAudienceMutation,
        TError,
        DeleteAudienceMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteAudienceMutation,
        TError,
        DeleteAudienceMutationVariables,
        TContext
    >(
        ["deleteAudience"],
        (variables?: DeleteAudienceMutationVariables) =>
            graphqlFetcher<DeleteAudienceMutation, DeleteAudienceMutationVariables>(
                DeleteAudienceDocument,
                variables,
            )(),
        options,
    );
};

export const GetAudienceDocument = new TypedDocumentString(`
    query getAudience($input: GetAudienceGraphQLInput!) {
  getAudience(input: $input) {
    ...AudienceBasics
  }
}
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}
fragment SelectFragment on AdvancedSearchSelect {
  fieldName
  questionId
}
fragment AudienceBasics on AudienceResult {
  id
  name
  createdBy
  updatedBy
  dateAdded
  updatedAt
  mailchimp {
    tag
    latestSyncResult {
      historyId
      status
      queuedAt
      startedAt
      completedAt
      errorMessage
      membersAdded
      membersUpdated
      membersRemoved
      totalMembers
      durationMs
    }
  }
  constantContact {
    tag
    latestSyncResult {
      historyId
      status
      queuedAt
      startedAt
      completedAt
      errorMessage
      membersAdded
      membersUpdated
      membersRemoved
      totalMembers
      durationMs
    }
  }
  query {
    queries {
      entityType
      sort {
        ascending
        column
        questionId
      }
      select {
        ...SelectFragment
      }
      condition {
        ...ConditionFragment
        children {
          ...ConditionFragment
          children {
            ...ConditionFragment
            children {
              ...ConditionFragment
              children {
                ...ConditionFragment
                children {
                  ...ConditionFragment
                  children {
                    ...ConditionFragment
                    children {
                      ...ConditionFragment
                      children {
                        ...ConditionFragment
                        children {
                          ...ConditionFragment
                          children {
                            ...ConditionFragment
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}`);

export const useGetAudienceQuery = <TData = GetAudienceQuery, TError = unknown>(
    variables: GetAudienceQueryVariables,
    options?: UseQueryOptions<GetAudienceQuery, TError, TData>,
) => {
    return useQuery<GetAudienceQuery, TError, TData>(
        ["getAudience", variables],
        graphqlFetcher<GetAudienceQuery, GetAudienceQueryVariables>(
            GetAudienceDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetAudienceQuery = <TData = GetAudienceQuery, TError = unknown>(
    variables: GetAudienceQueryVariables,
    options?: UseInfiniteQueryOptions<GetAudienceQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetAudienceQuery, TError, TData>(
        ["getAudience.infinite", variables],
        metaData =>
            graphqlFetcher<GetAudienceQuery, GetAudienceQueryVariables>(
                GetAudienceDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetAudiencesDocument = new TypedDocumentString(`
    query getAudiences($input: GetAudiencesGraphQLInput!) {
  getAudiences(input: $input) {
    ...AudienceBasics
  }
}
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}
fragment SelectFragment on AdvancedSearchSelect {
  fieldName
  questionId
}
fragment AudienceBasics on AudienceResult {
  id
  name
  createdBy
  updatedBy
  dateAdded
  updatedAt
  mailchimp {
    tag
    latestSyncResult {
      historyId
      status
      queuedAt
      startedAt
      completedAt
      errorMessage
      membersAdded
      membersUpdated
      membersRemoved
      totalMembers
      durationMs
    }
  }
  constantContact {
    tag
    latestSyncResult {
      historyId
      status
      queuedAt
      startedAt
      completedAt
      errorMessage
      membersAdded
      membersUpdated
      membersRemoved
      totalMembers
      durationMs
    }
  }
  query {
    queries {
      entityType
      sort {
        ascending
        column
        questionId
      }
      select {
        ...SelectFragment
      }
      condition {
        ...ConditionFragment
        children {
          ...ConditionFragment
          children {
            ...ConditionFragment
            children {
              ...ConditionFragment
              children {
                ...ConditionFragment
                children {
                  ...ConditionFragment
                  children {
                    ...ConditionFragment
                    children {
                      ...ConditionFragment
                      children {
                        ...ConditionFragment
                        children {
                          ...ConditionFragment
                          children {
                            ...ConditionFragment
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}`);

export const useGetAudiencesQuery = <TData = GetAudiencesQuery, TError = unknown>(
    variables: GetAudiencesQueryVariables,
    options?: UseQueryOptions<GetAudiencesQuery, TError, TData>,
) => {
    return useQuery<GetAudiencesQuery, TError, TData>(
        ["getAudiences", variables],
        graphqlFetcher<GetAudiencesQuery, GetAudiencesQueryVariables>(
            GetAudiencesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetAudiencesQuery = <TData = GetAudiencesQuery, TError = unknown>(
    variables: GetAudiencesQueryVariables,
    options?: UseInfiniteQueryOptions<GetAudiencesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetAudiencesQuery, TError, TData>(
        ["getAudiences.infinite", variables],
        metaData =>
            graphqlFetcher<GetAudiencesQuery, GetAudiencesQueryVariables>(
                GetAudiencesDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetAudienceRecipientsDocument = new TypedDocumentString(`
    query getAudienceRecipients($input: GetAudienceRecipientsGraphQLInput!) {
  getAudienceRecipients(input: $input) {
    recipients {
      entityId
      entityType
      name
      firstName
      email
    }
    total
  }
}
    `);

export const useGetAudienceRecipientsQuery = <
    TData = GetAudienceRecipientsQuery,
    TError = unknown,
>(
    variables: GetAudienceRecipientsQueryVariables,
    options?: UseQueryOptions<GetAudienceRecipientsQuery, TError, TData>,
) => {
    return useQuery<GetAudienceRecipientsQuery, TError, TData>(
        ["getAudienceRecipients", variables],
        graphqlFetcher<GetAudienceRecipientsQuery, GetAudienceRecipientsQueryVariables>(
            GetAudienceRecipientsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetAudienceRecipientsQuery = <
    TData = GetAudienceRecipientsQuery,
    TError = unknown,
>(
    variables: GetAudienceRecipientsQueryVariables,
    options?: UseInfiniteQueryOptions<GetAudienceRecipientsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetAudienceRecipientsQuery, TError, TData>(
        ["getAudienceRecipients.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetAudienceRecipientsQuery,
                GetAudienceRecipientsQueryVariables
            >(GetAudienceRecipientsDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const UpdateBillableEntityInternalAttributesDocument = new TypedDocumentString(`
    mutation updateBillableEntityInternalAttributes($billableEntityId: BillableEntityId!, $internalAttributes: DictionaryInput, $dates: BillableEntityDatePayload, $sku: String, $featureTrack: FeatureTrack, $features: [String!]) {
  billableEntityInternalUpdate(
    billableEntityId: $billableEntityId
    internalAttributes: $internalAttributes
    dates: $dates
    sku: $sku
    featureTrack: $featureTrack
    features: $features
  )
}
    `);

export const useUpdateBillableEntityInternalAttributesMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        UpdateBillableEntityInternalAttributesMutation,
        TError,
        UpdateBillableEntityInternalAttributesMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateBillableEntityInternalAttributesMutation,
        TError,
        UpdateBillableEntityInternalAttributesMutationVariables,
        TContext
    >(
        ["updateBillableEntityInternalAttributes"],
        (variables?: UpdateBillableEntityInternalAttributesMutationVariables) =>
            graphqlFetcher<
                UpdateBillableEntityInternalAttributesMutation,
                UpdateBillableEntityInternalAttributesMutationVariables
            >(UpdateBillableEntityInternalAttributesDocument, variables)(),
        options,
    );
};

export const BillableEntityCreateDocument = new TypedDocumentString(`
    mutation billableEntityCreate($billableEntity: BillableEntityCreatePayload!) {
  billableEntityCreate(params: $billableEntity) {
    id
  }
}
    `);

export const useBillableEntityCreateMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        BillableEntityCreateMutation,
        TError,
        BillableEntityCreateMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        BillableEntityCreateMutation,
        TError,
        BillableEntityCreateMutationVariables,
        TContext
    >(
        ["billableEntityCreate"],
        (variables?: BillableEntityCreateMutationVariables) =>
            graphqlFetcher<
                BillableEntityCreateMutation,
                BillableEntityCreateMutationVariables
            >(BillableEntityCreateDocument, variables)(),
        options,
    );
};

export const GetBillableEntityDocument = new TypedDocumentString(`
    query getBillableEntity($billableEntityId: BillableEntityId!) {
  getBillableEntity(billableEntityId: $billableEntityId) {
    id
    name
    sku
    featureTrack
    internalAttributes {
      entries {
        key
        value
      }
    }
    features {
      name
      enabled
      enabledUpstream
    }
    tenantId
    regionId
    activatedAt
    deactivatedAt
    onboardingStartedAt
    demoStartedAt
    demoEndedAt
    premiumActivatedAt
    premiumDeactivatedAt
    clientStats {
      clientsLast30Days
      clientsLast60Days
      clientsLast90Days
    }
  }
}
    `);

export const useGetBillableEntityQuery = <
    TData = GetBillableEntityQuery,
    TError = unknown,
>(
    variables: GetBillableEntityQueryVariables,
    options?: UseQueryOptions<GetBillableEntityQuery, TError, TData>,
) => {
    return useQuery<GetBillableEntityQuery, TError, TData>(
        ["getBillableEntity", variables],
        graphqlFetcher<GetBillableEntityQuery, GetBillableEntityQueryVariables>(
            GetBillableEntityDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetBillableEntityQuery = <
    TData = GetBillableEntityQuery,
    TError = unknown,
>(
    variables: GetBillableEntityQueryVariables,
    options?: UseInfiniteQueryOptions<GetBillableEntityQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetBillableEntityQuery, TError, TData>(
        ["getBillableEntity.infinite", variables],
        metaData =>
            graphqlFetcher<GetBillableEntityQuery, GetBillableEntityQueryVariables>(
                GetBillableEntityDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetBillableEntitiesDocument = new TypedDocumentString(`
    query getBillableEntities {
  getBillableEntities {
    id
    name
    sku
    featureTrack
    internalAttributes {
      entries {
        key
        value
      }
    }
  }
}
    `);

export const useGetBillableEntitiesQuery = <
    TData = GetBillableEntitiesQuery,
    TError = unknown,
>(
    variables?: GetBillableEntitiesQueryVariables,
    options?: UseQueryOptions<GetBillableEntitiesQuery, TError, TData>,
) => {
    return useQuery<GetBillableEntitiesQuery, TError, TData>(
        variables === undefined
            ? ["getBillableEntities"]
            : ["getBillableEntities", variables],
        graphqlFetcher<GetBillableEntitiesQuery, GetBillableEntitiesQueryVariables>(
            GetBillableEntitiesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetBillableEntitiesQuery = <
    TData = GetBillableEntitiesQuery,
    TError = unknown,
>(
    variables?: GetBillableEntitiesQueryVariables,
    options?: UseInfiniteQueryOptions<GetBillableEntitiesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetBillableEntitiesQuery, TError, TData>(
        variables === undefined
            ? ["getBillableEntities.infinite"]
            : ["getBillableEntities.infinite", variables],
        metaData =>
            graphqlFetcher<GetBillableEntitiesQuery, GetBillableEntitiesQueryVariables>(
                GetBillableEntitiesDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetBillableEntitiesWithoutWaveCustomerDocument = new TypedDocumentString(`
    query getBillableEntitiesWithoutWaveCustomer {
  getBillableEntitiesWithoutWaveCustomer {
    id
    name
    sku
    featureTrack
    internalAttributes {
      entries {
        key
        value
      }
    }
    tenantId
    regionId
    activatedAt
    deactivatedAt
  }
}
    `);

export const useGetBillableEntitiesWithoutWaveCustomerQuery = <
    TData = GetBillableEntitiesWithoutWaveCustomerQuery,
    TError = unknown,
>(
    variables?: GetBillableEntitiesWithoutWaveCustomerQueryVariables,
    options?: UseQueryOptions<GetBillableEntitiesWithoutWaveCustomerQuery, TError, TData>,
) => {
    return useQuery<GetBillableEntitiesWithoutWaveCustomerQuery, TError, TData>(
        variables === undefined
            ? ["getBillableEntitiesWithoutWaveCustomer"]
            : ["getBillableEntitiesWithoutWaveCustomer", variables],
        graphqlFetcher<
            GetBillableEntitiesWithoutWaveCustomerQuery,
            GetBillableEntitiesWithoutWaveCustomerQueryVariables
        >(GetBillableEntitiesWithoutWaveCustomerDocument, variables),
        options,
    );
};

export const useInfiniteGetBillableEntitiesWithoutWaveCustomerQuery = <
    TData = GetBillableEntitiesWithoutWaveCustomerQuery,
    TError = unknown,
>(
    variables?: GetBillableEntitiesWithoutWaveCustomerQueryVariables,
    options?: UseInfiniteQueryOptions<
        GetBillableEntitiesWithoutWaveCustomerQuery,
        TError,
        TData
    >,
) => {
    return useInfiniteQuery<GetBillableEntitiesWithoutWaveCustomerQuery, TError, TData>(
        variables === undefined
            ? ["getBillableEntitiesWithoutWaveCustomer.infinite"]
            : ["getBillableEntitiesWithoutWaveCustomer.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetBillableEntitiesWithoutWaveCustomerQuery,
                GetBillableEntitiesWithoutWaveCustomerQueryVariables
            >(GetBillableEntitiesWithoutWaveCustomerDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetEntitiesDocument = new TypedDocumentString(`
    query getEntities {
  getEntities {
    ...BridgeEntityFragment
  }
}
    fragment SelectFragment on AdvancedSearchSelect {
  fieldName
  questionId
}
fragment BridgeEntityFragment on BridgeEntityResult {
  name
  title
  preferredFilterField
  allowAnswers
  layoutParents
  identifierFields
  priorityFields {
    fieldName
    title
  }
  fields {
    name
    title
    questionType
    formatter
    category
    dependsOn
    customListKey
    aliases
    naryValues {
      value
      label
    }
    reportBehavior {
      sortable
      filterable
      selectable
      newReportSelectOrder
      summaryGroupable
      canAggregate
    }
  }
  relations {
    name
    title
    otherEntityName
    kind
    selectable
    aliases
  }
  questions {
    ...BridgeQuestionFragment
  }
  defaultSelect {
    ...SelectFragment
  }
  preferredDisplay {
    fieldName
    relation {
      name
    }
  }
  preferredGroupBy
  supportsTasks
  supportsWorkflows
}
fragment BridgeQuestionFragment on BridgeQuestion {
  id
  title
  interaction
  annotation
  questionType
  category
  key
  order
  regionMask
  rulesString
  searchBehavior
  source
  editable
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
  prompts {
    id
    title
    key
    order
    annotation
  }
}`);

export const useGetEntitiesQuery = <TData = GetEntitiesQuery, TError = unknown>(
    variables?: GetEntitiesQueryVariables,
    options?: UseQueryOptions<GetEntitiesQuery, TError, TData>,
) => {
    return useQuery<GetEntitiesQuery, TError, TData>(
        variables === undefined ? ["getEntities"] : ["getEntities", variables],
        graphqlFetcher<GetEntitiesQuery, GetEntitiesQueryVariables>(
            GetEntitiesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetEntitiesQuery = <TData = GetEntitiesQuery, TError = unknown>(
    variables?: GetEntitiesQueryVariables,
    options?: UseInfiniteQueryOptions<GetEntitiesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetEntitiesQuery, TError, TData>(
        variables === undefined
            ? ["getEntities.infinite"]
            : ["getEntities.infinite", variables],
        metaData =>
            graphqlFetcher<GetEntitiesQuery, GetEntitiesQueryVariables>(
                GetEntitiesDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetEntitiesForTenantDocument = new TypedDocumentString(`
    query getEntitiesForTenant($tenantId: TenantId!) {
  getEntitiesForTenant(tenantId: $tenantId) {
    ...BridgeEntityFragment
  }
}
    fragment SelectFragment on AdvancedSearchSelect {
  fieldName
  questionId
}
fragment BridgeEntityFragment on BridgeEntityResult {
  name
  title
  preferredFilterField
  allowAnswers
  layoutParents
  identifierFields
  priorityFields {
    fieldName
    title
  }
  fields {
    name
    title
    questionType
    formatter
    category
    dependsOn
    customListKey
    aliases
    naryValues {
      value
      label
    }
    reportBehavior {
      sortable
      filterable
      selectable
      newReportSelectOrder
      summaryGroupable
      canAggregate
    }
  }
  relations {
    name
    title
    otherEntityName
    kind
    selectable
    aliases
  }
  questions {
    ...BridgeQuestionFragment
  }
  defaultSelect {
    ...SelectFragment
  }
  preferredDisplay {
    fieldName
    relation {
      name
    }
  }
  preferredGroupBy
  supportsTasks
  supportsWorkflows
}
fragment BridgeQuestionFragment on BridgeQuestion {
  id
  title
  interaction
  annotation
  questionType
  category
  key
  order
  regionMask
  rulesString
  searchBehavior
  source
  editable
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
  prompts {
    id
    title
    key
    order
    annotation
  }
}`);

export const useGetEntitiesForTenantQuery = <
    TData = GetEntitiesForTenantQuery,
    TError = unknown,
>(
    variables: GetEntitiesForTenantQueryVariables,
    options?: UseQueryOptions<GetEntitiesForTenantQuery, TError, TData>,
) => {
    return useQuery<GetEntitiesForTenantQuery, TError, TData>(
        ["getEntitiesForTenant", variables],
        graphqlFetcher<GetEntitiesForTenantQuery, GetEntitiesForTenantQueryVariables>(
            GetEntitiesForTenantDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetEntitiesForTenantQuery = <
    TData = GetEntitiesForTenantQuery,
    TError = unknown,
>(
    variables: GetEntitiesForTenantQueryVariables,
    options?: UseInfiniteQueryOptions<GetEntitiesForTenantQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetEntitiesForTenantQuery, TError, TData>(
        ["getEntitiesForTenant.infinite", variables],
        metaData =>
            graphqlFetcher<GetEntitiesForTenantQuery, GetEntitiesForTenantQueryVariables>(
                GetEntitiesForTenantDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const UpsertEntityQuestionDocument = new TypedDocumentString(`
    mutation upsertEntityQuestion($entityType: String!, $question: BridgeQuestionInput!) {
  upsertEntityQuestion(entityType: $entityType, question: $question) {
    ...BridgeQuestionFragment
  }
}
    fragment BridgeQuestionFragment on BridgeQuestion {
  id
  title
  interaction
  annotation
  questionType
  category
  key
  order
  regionMask
  rulesString
  searchBehavior
  source
  editable
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
  prompts {
    id
    title
    key
    order
    annotation
  }
}`);

export const useUpsertEntityQuestionMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpsertEntityQuestionMutation,
        TError,
        UpsertEntityQuestionMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpsertEntityQuestionMutation,
        TError,
        UpsertEntityQuestionMutationVariables,
        TContext
    >(
        ["upsertEntityQuestion"],
        (variables?: UpsertEntityQuestionMutationVariables) =>
            graphqlFetcher<
                UpsertEntityQuestionMutation,
                UpsertEntityQuestionMutationVariables
            >(UpsertEntityQuestionDocument, variables)(),
        options,
    );
};

export const SetEntityAnswerDocument = new TypedDocumentString(`
    mutation setEntityAnswer($entityId: AnswerEntityId!, $answer: AnswerInput!) {
  setEntityAnswer(entityId: $entityId, answer: $answer) {
    questionId
    booleanAnswer
    numberAnswer
    numberAnswerHigh
    textAnswer
    dateAnswer
    calendarDateAnswer
    selections
    selectionAnnotations {
      selectionId
      text
    }
  }
}
    `);

export const useSetEntityAnswerMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        SetEntityAnswerMutation,
        TError,
        SetEntityAnswerMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SetEntityAnswerMutation,
        TError,
        SetEntityAnswerMutationVariables,
        TContext
    >(
        ["setEntityAnswer"],
        (variables?: SetEntityAnswerMutationVariables) =>
            graphqlFetcher<SetEntityAnswerMutation, SetEntityAnswerMutationVariables>(
                SetEntityAnswerDocument,
                variables,
            )(),
        options,
    );
};

export const DeleteQuestionDocument = new TypedDocumentString(`
    mutation deleteQuestion($questionId: QuestionId!) {
  deleteQuestion(questionId: $questionId) {
    questionId
  }
}
    `);

export const useDeleteQuestionMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteQuestionMutation,
        TError,
        DeleteQuestionMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteQuestionMutation,
        TError,
        DeleteQuestionMutationVariables,
        TContext
    >(
        ["deleteQuestion"],
        (variables?: DeleteQuestionMutationVariables) =>
            graphqlFetcher<DeleteQuestionMutation, DeleteQuestionMutationVariables>(
                DeleteQuestionDocument,
                variables,
            )(),
        options,
    );
};

export const FindClientTransferRequestByClientIdDocument = new TypedDocumentString(`
    query findClientTransferRequestByClientId($clientId: PlacementId!) {
  findClientTransferRequestByClientId(clientId: $clientId) {
    id
    status
    client {
      id
      name
      bestContactName
    }
    toUserProfile {
      name
      email
    }
    createdByEntity {
      name
      email
    }
    dateAdded
    lastEmailSentAt
  }
}
    `);

export const useFindClientTransferRequestByClientIdQuery = <
    TData = FindClientTransferRequestByClientIdQuery,
    TError = unknown,
>(
    variables: FindClientTransferRequestByClientIdQueryVariables,
    options?: UseQueryOptions<FindClientTransferRequestByClientIdQuery, TError, TData>,
) => {
    return useQuery<FindClientTransferRequestByClientIdQuery, TError, TData>(
        ["findClientTransferRequestByClientId", variables],
        graphqlFetcher<
            FindClientTransferRequestByClientIdQuery,
            FindClientTransferRequestByClientIdQueryVariables
        >(FindClientTransferRequestByClientIdDocument, variables),
        options,
    );
};

export const useInfiniteFindClientTransferRequestByClientIdQuery = <
    TData = FindClientTransferRequestByClientIdQuery,
    TError = unknown,
>(
    variables: FindClientTransferRequestByClientIdQueryVariables,
    options?: UseInfiniteQueryOptions<
        FindClientTransferRequestByClientIdQuery,
        TError,
        TData
    >,
) => {
    return useInfiniteQuery<FindClientTransferRequestByClientIdQuery, TError, TData>(
        ["findClientTransferRequestByClientId.infinite", variables],
        metaData =>
            graphqlFetcher<
                FindClientTransferRequestByClientIdQuery,
                FindClientTransferRequestByClientIdQueryVariables
            >(FindClientTransferRequestByClientIdDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const RequestClientTransferDocument = new TypedDocumentString(`
    mutation requestClientTransfer($params: TransferClientRequest!) {
  requestClientTransfer(params: $params)
}
    `);

export const useRequestClientTransferMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        RequestClientTransferMutation,
        TError,
        RequestClientTransferMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        RequestClientTransferMutation,
        TError,
        RequestClientTransferMutationVariables,
        TContext
    >(
        ["requestClientTransfer"],
        (variables?: RequestClientTransferMutationVariables) =>
            graphqlFetcher<
                RequestClientTransferMutation,
                RequestClientTransferMutationVariables
            >(RequestClientTransferDocument, variables)(),
        options,
    );
};

export const GetPendingIncomingClientTransfersDocument = new TypedDocumentString(`
    query getPendingIncomingClientTransfers($regionId: RegionId!) {
  getPendingIncomingClientTransfers(regionId: $regionId) {
    id
    status
    client {
      name
      bestContactName
    }
    createdByEntity {
      name
      email
    }
    toUserProfile {
      name
      email
    }
    dateAdded
  }
}
    `);

export const useGetPendingIncomingClientTransfersQuery = <
    TData = GetPendingIncomingClientTransfersQuery,
    TError = unknown,
>(
    variables: GetPendingIncomingClientTransfersQueryVariables,
    options?: UseQueryOptions<GetPendingIncomingClientTransfersQuery, TError, TData>,
) => {
    return useQuery<GetPendingIncomingClientTransfersQuery, TError, TData>(
        ["getPendingIncomingClientTransfers", variables],
        graphqlFetcher<
            GetPendingIncomingClientTransfersQuery,
            GetPendingIncomingClientTransfersQueryVariables
        >(GetPendingIncomingClientTransfersDocument, variables),
        options,
    );
};

export const useInfiniteGetPendingIncomingClientTransfersQuery = <
    TData = GetPendingIncomingClientTransfersQuery,
    TError = unknown,
>(
    variables: GetPendingIncomingClientTransfersQueryVariables,
    options?: UseInfiniteQueryOptions<
        GetPendingIncomingClientTransfersQuery,
        TError,
        TData
    >,
) => {
    return useInfiniteQuery<GetPendingIncomingClientTransfersQuery, TError, TData>(
        ["getPendingIncomingClientTransfers.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetPendingIncomingClientTransfersQuery,
                GetPendingIncomingClientTransfersQueryVariables
            >(GetPendingIncomingClientTransfersDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetPendingOutgoingClientTransfersDocument = new TypedDocumentString(`
    query getPendingOutgoingClientTransfers($regionId: RegionId!) {
  getPendingOutgoingClientTransfers(regionId: $regionId) {
    id
    status
    client {
      id
      name
      bestContactName
    }
    toUserProfile {
      name
      email
    }
    createdByEntity {
      name
      email
    }
    dateAdded
    lastEmailSentAt
  }
}
    `);

export const useGetPendingOutgoingClientTransfersQuery = <
    TData = GetPendingOutgoingClientTransfersQuery,
    TError = unknown,
>(
    variables: GetPendingOutgoingClientTransfersQueryVariables,
    options?: UseQueryOptions<GetPendingOutgoingClientTransfersQuery, TError, TData>,
) => {
    return useQuery<GetPendingOutgoingClientTransfersQuery, TError, TData>(
        ["getPendingOutgoingClientTransfers", variables],
        graphqlFetcher<
            GetPendingOutgoingClientTransfersQuery,
            GetPendingOutgoingClientTransfersQueryVariables
        >(GetPendingOutgoingClientTransfersDocument, variables),
        options,
    );
};

export const useInfiniteGetPendingOutgoingClientTransfersQuery = <
    TData = GetPendingOutgoingClientTransfersQuery,
    TError = unknown,
>(
    variables: GetPendingOutgoingClientTransfersQueryVariables,
    options?: UseInfiniteQueryOptions<
        GetPendingOutgoingClientTransfersQuery,
        TError,
        TData
    >,
) => {
    return useInfiniteQuery<GetPendingOutgoingClientTransfersQuery, TError, TData>(
        ["getPendingOutgoingClientTransfers.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetPendingOutgoingClientTransfersQuery,
                GetPendingOutgoingClientTransfersQueryVariables
            >(GetPendingOutgoingClientTransfersDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const RejectClientTransferDocument = new TypedDocumentString(`
    mutation rejectClientTransfer($clientTransferRequestId: ClientTransferRequestId!) {
  rejectClientTransfer(clientTransferRequestId: $clientTransferRequestId)
}
    `);

export const useRejectClientTransferMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        RejectClientTransferMutation,
        TError,
        RejectClientTransferMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        RejectClientTransferMutation,
        TError,
        RejectClientTransferMutationVariables,
        TContext
    >(
        ["rejectClientTransfer"],
        (variables?: RejectClientTransferMutationVariables) =>
            graphqlFetcher<
                RejectClientTransferMutation,
                RejectClientTransferMutationVariables
            >(RejectClientTransferDocument, variables)(),
        options,
    );
};

export const CancelClientTransferDocument = new TypedDocumentString(`
    mutation cancelClientTransfer($clientTransferRequestId: ClientTransferRequestId!) {
  cancelClientTransfer(clientTransferRequestId: $clientTransferRequestId)
}
    `);

export const useCancelClientTransferMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CancelClientTransferMutation,
        TError,
        CancelClientTransferMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CancelClientTransferMutation,
        TError,
        CancelClientTransferMutationVariables,
        TContext
    >(
        ["cancelClientTransfer"],
        (variables?: CancelClientTransferMutationVariables) =>
            graphqlFetcher<
                CancelClientTransferMutation,
                CancelClientTransferMutationVariables
            >(CancelClientTransferDocument, variables)(),
        options,
    );
};

export const ResendClientTransferDocument = new TypedDocumentString(`
    mutation resendClientTransfer($clientTransferRequestId: ClientTransferRequestId!) {
  resendClientTransfer(clientTransferRequestId: $clientTransferRequestId)
}
    `);

export const useResendClientTransferMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        ResendClientTransferMutation,
        TError,
        ResendClientTransferMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        ResendClientTransferMutation,
        TError,
        ResendClientTransferMutationVariables,
        TContext
    >(
        ["resendClientTransfer"],
        (variables?: ResendClientTransferMutationVariables) =>
            graphqlFetcher<
                ResendClientTransferMutation,
                ResendClientTransferMutationVariables
            >(ResendClientTransferDocument, variables)(),
        options,
    );
};

export const AcceptClientTransferDocument = new TypedDocumentString(`
    mutation acceptClientTransfer($clientTransferRequestId: ClientTransferRequestId!, $regionId: RegionId!) {
  acceptClientTransfer(
    clientTransferRequestId: $clientTransferRequestId
    regionId: $regionId
  ) {
    id
    name
    statusListItemId
    status
  }
}
    `);

export const useAcceptClientTransferMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        AcceptClientTransferMutation,
        TError,
        AcceptClientTransferMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        AcceptClientTransferMutation,
        TError,
        AcceptClientTransferMutationVariables,
        TContext
    >(
        ["acceptClientTransfer"],
        (variables?: AcceptClientTransferMutationVariables) =>
            graphqlFetcher<
                AcceptClientTransferMutation,
                AcceptClientTransferMutationVariables
            >(AcceptClientTransferDocument, variables)(),
        options,
    );
};

export const GetClientDocument = new TypedDocumentString(`
    query getClient($id: PlacementId!) {
  getClient(clientId: $id) {
    id
    totalInvoiceAmountCents
    invoices {
      id
      amountCents
      paidDate
      sentDate
      dueDate
      serial
      status
      placementId
      clientName
      communityId
      fileId
      notes
      externalReference
      remainingBalanceCents
      totalPaidCents
      hasOutstandingBalance
      lineItems {
        id
        description
        quantity
        unitPriceInCents
      }
      paymentSchedule {
        items {
          id
          amountInCents
          dueDate
          paidDate
          status
        }
      }
      isExternallyManaged
      community {
        name
        id
      }
    }
    bestContactName
    bestContactPhone
    bestContactEmail
    disclosures {
      ...DisclosureFragment
    }
  }
}
    fragment DisclosureFragment on Disclosure {
  id
  dateAdded
  publicUrl
  acknowledgements {
    ...DisclosureAcknowledgementFragment
  }
}
fragment DisclosureAcknowledgementFragment on DisclosureAcknowledgement {
  id
  name
  email
  phone
  agreed
  response
  dateAdded
  createdBy
  ipAddress
}`);

export const useGetClientQuery = <TData = GetClientQuery, TError = unknown>(
    variables: GetClientQueryVariables,
    options?: UseQueryOptions<GetClientQuery, TError, TData>,
) => {
    return useQuery<GetClientQuery, TError, TData>(
        ["getClient", variables],
        graphqlFetcher<GetClientQuery, GetClientQueryVariables>(
            GetClientDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetClientQuery = <TData = GetClientQuery, TError = unknown>(
    variables: GetClientQueryVariables,
    options?: UseInfiniteQueryOptions<GetClientQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetClientQuery, TError, TData>(
        ["getClient.infinite", variables],
        metaData =>
            graphqlFetcher<GetClientQuery, GetClientQueryVariables>(GetClientDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetClientCommunitiesDocument = new TypedDocumentString(`
    query getClientCommunities($id: PlacementId!, $questionIds: [QuestionId!]!) {
  getClient(clientId: $id, options: {includeCommunityQuestionIds: $questionIds}) {
    ...ClientCommunities
  }
}
    fragment ClientCommunities on Client {
  id
  baseLocation {
    lat
    lng
  }
  clientCommunities {
    order
    relationship
    publicNotes
    privateNotes
    community {
      ...CommunityCardBasics
      evergreenCommunityId
    }
    referred
    tours {
      id
      communityId
      clientId
      userId
      tourDate
      tourTime
      tourDuration
      privateNotes
      familyNotes
      showOnFamilyView
      dateAdded
      updatedAt
    }
  }
}
fragment CommunityBasics on Community {
  address
  caregiverRatio
  city
  contractStatus
  contractExpiresDate
  contractIssuedDate
  contractNotes
  dateAdded
  gender
  feeType
  paymentFee
  paymentPercentage
  id
  latitude
  licenseStatus
  longitude
  mainPhone
  monthlyPrice
  moveInDeposit
  name
  petDeposit
  priceDetails
  priceHighEnd
  rating
  secondPersonFee
  state
  summary
  thumbnailUrl
  unitsOrCapacity
  updatedAt
  virtualTourURL
  website
  zip
  regionId
  email
  county
  contacts {
    cellPhone
    name
    phone1
    role
    email1
  }
  primaryContacts {
    cellPhone
    name
    phone1
    role
    email1
  }
}
fragment CommunityCardBasics on Community {
  ...CommunityBasics
  availability {
    status
    notes
    freshness
    lastUpdatedAt
  }
  isConnectedToEvergreen
  pricing {
    minPrice
    maxPrice
    notes
    files {
      id
      name
      mimeType
      size
      downloadUrl
    }
  }
  answers {
    questionId
    booleanAnswer
    numberAnswer
    numberAnswerHigh
    textAnswer
    dateAnswer
    calendarDateAnswer
    selections
    idReferenceAnswer
    selectionAnnotations {
      text
      selectionId
    }
  }
  photoEntities {
    ...PhotoBasics
  }
  licenses {
    id
    licenseNumber
    href
    licensee
    status
    licenseStartDate
    licenseEndDate
  }
  googleInfo {
    rating
    ratingCount
  }
}
fragment FileFragment on File {
  id
  entityId
  originalFilename
  mimeType
  description
  byteLength
  shareWithClient
  shareWithCommunity
  shareWithReferral
  shareWithHomeCareLead
  createdAt
  createdBy
  updatedAt
  updatedBy
  hasSignatureTemplate
  isGlobal
  managementLevel
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}
fragment PhotoBasics on Photo {
  id
  entityId
  order
  dateAdded
  createdBy
  originalFile {
    ...FileFragment
  }
}`);

export const useGetClientCommunitiesQuery = <
    TData = GetClientCommunitiesQuery,
    TError = unknown,
>(
    variables: GetClientCommunitiesQueryVariables,
    options?: UseQueryOptions<GetClientCommunitiesQuery, TError, TData>,
) => {
    return useQuery<GetClientCommunitiesQuery, TError, TData>(
        ["getClientCommunities", variables],
        graphqlFetcher<GetClientCommunitiesQuery, GetClientCommunitiesQueryVariables>(
            GetClientCommunitiesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetClientCommunitiesQuery = <
    TData = GetClientCommunitiesQuery,
    TError = unknown,
>(
    variables: GetClientCommunitiesQueryVariables,
    options?: UseInfiniteQueryOptions<GetClientCommunitiesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetClientCommunitiesQuery, TError, TData>(
        ["getClientCommunities.infinite", variables],
        metaData =>
            graphqlFetcher<GetClientCommunitiesQuery, GetClientCommunitiesQueryVariables>(
                GetClientCommunitiesDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetClientMatchesDocument = new TypedDocumentString(`
    query getClientMatches($id: PlacementId!) {
  getClient(clientId: $id) {
    id
    potentialDuplicates {
      id
      name
      bestContactName
      dateAdded
      viewable
      assignedUser {
        id
        preferredName
      }
    }
  }
}
    `);

export const useGetClientMatchesQuery = <TData = GetClientMatchesQuery, TError = unknown>(
    variables: GetClientMatchesQueryVariables,
    options?: UseQueryOptions<GetClientMatchesQuery, TError, TData>,
) => {
    return useQuery<GetClientMatchesQuery, TError, TData>(
        ["getClientMatches", variables],
        graphqlFetcher<GetClientMatchesQuery, GetClientMatchesQueryVariables>(
            GetClientMatchesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetClientMatchesQuery = <
    TData = GetClientMatchesQuery,
    TError = unknown,
>(
    variables: GetClientMatchesQueryVariables,
    options?: UseInfiniteQueryOptions<GetClientMatchesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetClientMatchesQuery, TError, TData>(
        ["getClientMatches.infinite", variables],
        metaData =>
            graphqlFetcher<GetClientMatchesQuery, GetClientMatchesQueryVariables>(
                GetClientMatchesDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const CreateClientDocument = new TypedDocumentString(`
    mutation createClient($regionId: RegionId!) {
  createClient(regionId: $regionId) {
    id
  }
}
    `);

export const useCreateClientMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateClientMutation,
        TError,
        CreateClientMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateClientMutation,
        TError,
        CreateClientMutationVariables,
        TContext
    >(
        ["createClient"],
        (variables?: CreateClientMutationVariables) =>
            graphqlFetcher<CreateClientMutation, CreateClientMutationVariables>(
                CreateClientDocument,
                variables,
            )(),
        options,
    );
};

export const DeleteClientMatchesDocument = new TypedDocumentString(`
    mutation deleteClientMatches($id: PlacementId!) {
  deleteClientMatches(clientId: $id) {
    id
  }
}
    `);

export const useDeleteClientMatchesMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteClientMatchesMutation,
        TError,
        DeleteClientMatchesMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteClientMatchesMutation,
        TError,
        DeleteClientMatchesMutationVariables,
        TContext
    >(
        ["deleteClientMatches"],
        (variables?: DeleteClientMatchesMutationVariables) =>
            graphqlFetcher<
                DeleteClientMatchesMutation,
                DeleteClientMatchesMutationVariables
            >(DeleteClientMatchesDocument, variables)(),
        options,
    );
};

export const SetCommunityOrderDocument = new TypedDocumentString(`
    mutation setCommunityOrder($clientId: PlacementId!, $questionIds: [QuestionId!]!, $ordering: [CommunityOrdering!]!) {
  setCommunityOrder(
    clientId: $clientId
    ordering: $ordering
    includeCommunityQuestionIds: $questionIds
  ) {
    ...ClientCommunities
  }
}
    fragment ClientCommunities on Client {
  id
  baseLocation {
    lat
    lng
  }
  clientCommunities {
    order
    relationship
    publicNotes
    privateNotes
    community {
      ...CommunityCardBasics
      evergreenCommunityId
    }
    referred
    tours {
      id
      communityId
      clientId
      userId
      tourDate
      tourTime
      tourDuration
      privateNotes
      familyNotes
      showOnFamilyView
      dateAdded
      updatedAt
    }
  }
}
fragment CommunityBasics on Community {
  address
  caregiverRatio
  city
  contractStatus
  contractExpiresDate
  contractIssuedDate
  contractNotes
  dateAdded
  gender
  feeType
  paymentFee
  paymentPercentage
  id
  latitude
  licenseStatus
  longitude
  mainPhone
  monthlyPrice
  moveInDeposit
  name
  petDeposit
  priceDetails
  priceHighEnd
  rating
  secondPersonFee
  state
  summary
  thumbnailUrl
  unitsOrCapacity
  updatedAt
  virtualTourURL
  website
  zip
  regionId
  email
  county
  contacts {
    cellPhone
    name
    phone1
    role
    email1
  }
  primaryContacts {
    cellPhone
    name
    phone1
    role
    email1
  }
}
fragment CommunityCardBasics on Community {
  ...CommunityBasics
  availability {
    status
    notes
    freshness
    lastUpdatedAt
  }
  isConnectedToEvergreen
  pricing {
    minPrice
    maxPrice
    notes
    files {
      id
      name
      mimeType
      size
      downloadUrl
    }
  }
  answers {
    questionId
    booleanAnswer
    numberAnswer
    numberAnswerHigh
    textAnswer
    dateAnswer
    calendarDateAnswer
    selections
    idReferenceAnswer
    selectionAnnotations {
      text
      selectionId
    }
  }
  photoEntities {
    ...PhotoBasics
  }
  licenses {
    id
    licenseNumber
    href
    licensee
    status
    licenseStartDate
    licenseEndDate
  }
  googleInfo {
    rating
    ratingCount
  }
}
fragment FileFragment on File {
  id
  entityId
  originalFilename
  mimeType
  description
  byteLength
  shareWithClient
  shareWithCommunity
  shareWithReferral
  shareWithHomeCareLead
  createdAt
  createdBy
  updatedAt
  updatedBy
  hasSignatureTemplate
  isGlobal
  managementLevel
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}
fragment PhotoBasics on Photo {
  id
  entityId
  order
  dateAdded
  createdBy
  originalFile {
    ...FileFragment
  }
}`);

export const useSetCommunityOrderMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        SetCommunityOrderMutation,
        TError,
        SetCommunityOrderMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SetCommunityOrderMutation,
        TError,
        SetCommunityOrderMutationVariables,
        TContext
    >(
        ["setCommunityOrder"],
        (variables?: SetCommunityOrderMutationVariables) =>
            graphqlFetcher<SetCommunityOrderMutation, SetCommunityOrderMutationVariables>(
                SetCommunityOrderDocument,
                variables,
            )(),
        options,
    );
};

export const AddClientCommunitiesDocument = new TypedDocumentString(`
    mutation addClientCommunities($clientId: PlacementId!, $questionIds: [QuestionId!]!, $params: [AddClientCommunityRequest!]!) {
  addClientCommunities(
    clientId: $clientId
    params: $params
    includeCommunityQuestionIds: $questionIds
  ) {
    ...ClientCommunities
  }
}
    fragment ClientCommunities on Client {
  id
  baseLocation {
    lat
    lng
  }
  clientCommunities {
    order
    relationship
    publicNotes
    privateNotes
    community {
      ...CommunityCardBasics
      evergreenCommunityId
    }
    referred
    tours {
      id
      communityId
      clientId
      userId
      tourDate
      tourTime
      tourDuration
      privateNotes
      familyNotes
      showOnFamilyView
      dateAdded
      updatedAt
    }
  }
}
fragment CommunityBasics on Community {
  address
  caregiverRatio
  city
  contractStatus
  contractExpiresDate
  contractIssuedDate
  contractNotes
  dateAdded
  gender
  feeType
  paymentFee
  paymentPercentage
  id
  latitude
  licenseStatus
  longitude
  mainPhone
  monthlyPrice
  moveInDeposit
  name
  petDeposit
  priceDetails
  priceHighEnd
  rating
  secondPersonFee
  state
  summary
  thumbnailUrl
  unitsOrCapacity
  updatedAt
  virtualTourURL
  website
  zip
  regionId
  email
  county
  contacts {
    cellPhone
    name
    phone1
    role
    email1
  }
  primaryContacts {
    cellPhone
    name
    phone1
    role
    email1
  }
}
fragment CommunityCardBasics on Community {
  ...CommunityBasics
  availability {
    status
    notes
    freshness
    lastUpdatedAt
  }
  isConnectedToEvergreen
  pricing {
    minPrice
    maxPrice
    notes
    files {
      id
      name
      mimeType
      size
      downloadUrl
    }
  }
  answers {
    questionId
    booleanAnswer
    numberAnswer
    numberAnswerHigh
    textAnswer
    dateAnswer
    calendarDateAnswer
    selections
    idReferenceAnswer
    selectionAnnotations {
      text
      selectionId
    }
  }
  photoEntities {
    ...PhotoBasics
  }
  licenses {
    id
    licenseNumber
    href
    licensee
    status
    licenseStartDate
    licenseEndDate
  }
  googleInfo {
    rating
    ratingCount
  }
}
fragment FileFragment on File {
  id
  entityId
  originalFilename
  mimeType
  description
  byteLength
  shareWithClient
  shareWithCommunity
  shareWithReferral
  shareWithHomeCareLead
  createdAt
  createdBy
  updatedAt
  updatedBy
  hasSignatureTemplate
  isGlobal
  managementLevel
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}
fragment PhotoBasics on Photo {
  id
  entityId
  order
  dateAdded
  createdBy
  originalFile {
    ...FileFragment
  }
}`);

export const useAddClientCommunitiesMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        AddClientCommunitiesMutation,
        TError,
        AddClientCommunitiesMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        AddClientCommunitiesMutation,
        TError,
        AddClientCommunitiesMutationVariables,
        TContext
    >(
        ["addClientCommunities"],
        (variables?: AddClientCommunitiesMutationVariables) =>
            graphqlFetcher<
                AddClientCommunitiesMutation,
                AddClientCommunitiesMutationVariables
            >(AddClientCommunitiesDocument, variables)(),
        options,
    );
};

export const RemoveClientCommunitiesDocument = new TypedDocumentString(`
    mutation removeClientCommunities($clientId: PlacementId!, $params: [RemoveClientCommunityRequest!]!) {
  removeClientCommunities(clientId: $clientId, params: $params) {
    id
  }
}
    `);

export const useRemoveClientCommunitiesMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        RemoveClientCommunitiesMutation,
        TError,
        RemoveClientCommunitiesMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        RemoveClientCommunitiesMutation,
        TError,
        RemoveClientCommunitiesMutationVariables,
        TContext
    >(
        ["removeClientCommunities"],
        (variables?: RemoveClientCommunitiesMutationVariables) =>
            graphqlFetcher<
                RemoveClientCommunitiesMutation,
                RemoveClientCommunitiesMutationVariables
            >(RemoveClientCommunitiesDocument, variables)(),
        options,
    );
};

export const GetClientsForTimelineDocument = new TypedDocumentString(`
    query getClientsForTimeline($regionId: RegionId!, $searchString: String, $assignedUserId: UserId, $assignedReferralUserId: UserId, $collaboratorUserId: UserId, $searchType: String, $openClosed: String, $page: Int!, $perPage: Int!, $includeStatuses: [String!]) {
  pastDue: getClients(
    params: {regionId: $regionId, searchString: $searchString, assignedUserId: $assignedUserId, assignedReferralUserId: $assignedReferralUserId, collaboratorUserId: $collaboratorUserId, searchType: $searchType, moveTimelineWindow: "pastDue", archived: false, openClosed: $openClosed, includeStatuses: $includeStatuses, page: $page, perPage: $perPage, sort: UpdatedAtDesc}
  ) {
    ...DashboardDefaults
  }
  oneWeek: getClients(
    params: {regionId: $regionId, searchString: $searchString, assignedUserId: $assignedUserId, assignedReferralUserId: $assignedReferralUserId, collaboratorUserId: $collaboratorUserId, searchType: $searchType, moveTimelineWindow: "oneWeek", archived: false, openClosed: $openClosed, includeStatuses: $includeStatuses, page: $page, perPage: $perPage, sort: UpdatedAtDesc}
  ) {
    ...DashboardDefaults
  }
  oneToTwoWeeks: getClients(
    params: {regionId: $regionId, searchString: $searchString, assignedUserId: $assignedUserId, assignedReferralUserId: $assignedReferralUserId, collaboratorUserId: $collaboratorUserId, searchType: $searchType, moveTimelineWindow: "oneToTwoWeeks", archived: false, openClosed: $openClosed, includeStatuses: $includeStatuses, page: $page, perPage: $perPage, sort: UpdatedAtDesc}
  ) {
    ...DashboardDefaults
  }
  twoToFourWeeks: getClients(
    params: {regionId: $regionId, searchString: $searchString, assignedUserId: $assignedUserId, assignedReferralUserId: $assignedReferralUserId, collaboratorUserId: $collaboratorUserId, searchType: $searchType, moveTimelineWindow: "twoToFourWeeks", archived: false, openClosed: $openClosed, includeStatuses: $includeStatuses, page: $page, perPage: $perPage, sort: UpdatedAtDesc}
  ) {
    ...DashboardDefaults
  }
  fourOrMoreWeeks: getClients(
    params: {regionId: $regionId, searchString: $searchString, assignedUserId: $assignedUserId, assignedReferralUserId: $assignedReferralUserId, collaboratorUserId: $collaboratorUserId, searchType: $searchType, moveTimelineWindow: "fourOrMoreWeeks", archived: false, openClosed: $openClosed, includeStatuses: $includeStatuses, page: $page, perPage: $perPage, sort: UpdatedAtDesc}
  ) {
    ...DashboardDefaults
  }
  unscheduled: getClients(
    params: {regionId: $regionId, searchString: $searchString, assignedUserId: $assignedUserId, assignedReferralUserId: $assignedReferralUserId, searchType: $searchType, moveTimelineWindow: "unscheduled", archived: false, openClosed: $openClosed, includeStatuses: $includeStatuses, page: $page, perPage: $perPage, sort: UpdatedAtDesc}
  ) {
    ...DashboardDefaults
  }
}
    fragment DashboardDefaults on ClientsSearchResult {
  total
  clients {
    id
    serial
    name
    statusListItemId
    bestContactName
    bestContactPhone
    dateAdded
    invoiceSentDate
    moveTimeline
    moveTimelineActual
    paymentReceivedDate
    statusCheckLaterDate
    statusClosedDate
    updatedAt
    assignedUserId
    phone
    cellPhone
    email
    additionalClientName
    chosenCommunity {
      id
      name
    }
    nextTask {
      id
      text
      status
      dueDate
      dueDateTime
    }
    allContacts {
      name
      email1
      phone1
      cellPhone
      relationship
    }
  }
}`);

export const useGetClientsForTimelineQuery = <
    TData = GetClientsForTimelineQuery,
    TError = unknown,
>(
    variables: GetClientsForTimelineQueryVariables,
    options?: UseQueryOptions<GetClientsForTimelineQuery, TError, TData>,
) => {
    return useQuery<GetClientsForTimelineQuery, TError, TData>(
        ["getClientsForTimeline", variables],
        graphqlFetcher<GetClientsForTimelineQuery, GetClientsForTimelineQueryVariables>(
            GetClientsForTimelineDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetClientsForTimelineQuery = <
    TData = GetClientsForTimelineQuery,
    TError = unknown,
>(
    variables: GetClientsForTimelineQueryVariables,
    options?: UseInfiniteQueryOptions<GetClientsForTimelineQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetClientsForTimelineQuery, TError, TData>(
        ["getClientsForTimeline.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetClientsForTimelineQuery,
                GetClientsForTimelineQueryVariables
            >(GetClientsForTimelineDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetClientsForDetailsDocument = new TypedDocumentString(`
    query getClientsForDetails($regionId: RegionId!, $searchString: String, $assignedUserId: UserId, $assignedReferralUserId: UserId, $collaboratorUserId: UserId, $searchType: String, $openClosed: String, $page: Int!, $perPage: Int!, $includeStatuses: [String!], $excludeStatuses: [String!], $statusAtOrLater: String, $status: String, $sort: ClientSearchSort!) {
  getClients(
    params: {regionId: $regionId, searchString: $searchString, assignedUserId: $assignedUserId, assignedReferralUserId: $assignedReferralUserId, collaboratorUserId: $collaboratorUserId, sort: $sort, searchType: $searchType, includeStatuses: $includeStatuses, excludeStatuses: $excludeStatuses, statusAtOrLater: $statusAtOrLater, status: $status, openClosed: $openClosed, page: $page, perPage: $perPage}
  ) {
    ...DashboardDefaults
  }
}
    fragment DashboardDefaults on ClientsSearchResult {
  total
  clients {
    id
    serial
    name
    statusListItemId
    bestContactName
    bestContactPhone
    dateAdded
    invoiceSentDate
    moveTimeline
    moveTimelineActual
    paymentReceivedDate
    statusCheckLaterDate
    statusClosedDate
    updatedAt
    assignedUserId
    phone
    cellPhone
    email
    additionalClientName
    chosenCommunity {
      id
      name
    }
    nextTask {
      id
      text
      status
      dueDate
      dueDateTime
    }
    allContacts {
      name
      email1
      phone1
      cellPhone
      relationship
    }
  }
}`);

export const useGetClientsForDetailsQuery = <
    TData = GetClientsForDetailsQuery,
    TError = unknown,
>(
    variables: GetClientsForDetailsQueryVariables,
    options?: UseQueryOptions<GetClientsForDetailsQuery, TError, TData>,
) => {
    return useQuery<GetClientsForDetailsQuery, TError, TData>(
        ["getClientsForDetails", variables],
        graphqlFetcher<GetClientsForDetailsQuery, GetClientsForDetailsQueryVariables>(
            GetClientsForDetailsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetClientsForDetailsQuery = <
    TData = GetClientsForDetailsQuery,
    TError = unknown,
>(
    variables: GetClientsForDetailsQueryVariables,
    options?: UseInfiniteQueryOptions<GetClientsForDetailsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetClientsForDetailsQuery, TError, TData>(
        ["getClientsForDetails.infinite", variables],
        metaData =>
            graphqlFetcher<GetClientsForDetailsQuery, GetClientsForDetailsQueryVariables>(
                GetClientsForDetailsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const SearchClientsDocument = new TypedDocumentString(`
    query searchClients($regionId: RegionId!, $searchString: String, $assignedUserId: UserId, $assignedReferralUserId: UserId, $searchType: String, $openClosed: String, $referralCommunityId: CommunityId, $referralContactId: ReferenceContactId, $referralBusinessId: ReferenceBusinessId, $relatedTo: [PlacementId!], $page: Int!, $perPage: Int!) {
  getClients(
    params: {regionId: $regionId, searchString: $searchString, assignedUserId: $assignedUserId, assignedReferralUserId: $assignedReferralUserId, searchType: $searchType, openClosed: $openClosed, referralCommunityId: $referralCommunityId, referralContactId: $referralContactId, referralBusinessId: $referralBusinessId, relatedTo: $relatedTo, page: $page, perPage: $perPage, sort: NameAsc}
  ) {
    ...DashboardDefaults
  }
}
    fragment DashboardDefaults on ClientsSearchResult {
  total
  clients {
    id
    serial
    name
    statusListItemId
    bestContactName
    bestContactPhone
    dateAdded
    invoiceSentDate
    moveTimeline
    moveTimelineActual
    paymentReceivedDate
    statusCheckLaterDate
    statusClosedDate
    updatedAt
    assignedUserId
    phone
    cellPhone
    email
    additionalClientName
    chosenCommunity {
      id
      name
    }
    nextTask {
      id
      text
      status
      dueDate
      dueDateTime
    }
    allContacts {
      name
      email1
      phone1
      cellPhone
      relationship
    }
  }
}`);

export const useSearchClientsQuery = <TData = SearchClientsQuery, TError = unknown>(
    variables: SearchClientsQueryVariables,
    options?: UseQueryOptions<SearchClientsQuery, TError, TData>,
) => {
    return useQuery<SearchClientsQuery, TError, TData>(
        ["searchClients", variables],
        graphqlFetcher<SearchClientsQuery, SearchClientsQueryVariables>(
            SearchClientsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteSearchClientsQuery = <
    TData = SearchClientsQuery,
    TError = unknown,
>(
    variables: SearchClientsQueryVariables,
    options?: UseInfiniteQueryOptions<SearchClientsQuery, TError, TData>,
) => {
    return useInfiniteQuery<SearchClientsQuery, TError, TData>(
        ["searchClients.infinite", variables],
        metaData =>
            graphqlFetcher<SearchClientsQuery, SearchClientsQueryVariables>(
                SearchClientsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetClientReferralDetailsDocument = new TypedDocumentString(`
    query getClientReferralDetails($id: PlacementId!) {
  getClient(clientId: $id) {
    bestContactName
    bestContactPhone
    address
    city
    state
    zip
  }
}
    `);

export const useGetClientReferralDetailsQuery = <
    TData = GetClientReferralDetailsQuery,
    TError = unknown,
>(
    variables: GetClientReferralDetailsQueryVariables,
    options?: UseQueryOptions<GetClientReferralDetailsQuery, TError, TData>,
) => {
    return useQuery<GetClientReferralDetailsQuery, TError, TData>(
        ["getClientReferralDetails", variables],
        graphqlFetcher<
            GetClientReferralDetailsQuery,
            GetClientReferralDetailsQueryVariables
        >(GetClientReferralDetailsDocument, variables),
        options,
    );
};

export const useInfiniteGetClientReferralDetailsQuery = <
    TData = GetClientReferralDetailsQuery,
    TError = unknown,
>(
    variables: GetClientReferralDetailsQueryVariables,
    options?: UseInfiniteQueryOptions<GetClientReferralDetailsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetClientReferralDetailsQuery, TError, TData>(
        ["getClientReferralDetails.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetClientReferralDetailsQuery,
                GetClientReferralDetailsQueryVariables
            >(GetClientReferralDetailsDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetOutboundClientReferralsDocument = new TypedDocumentString(`
    query getOutboundClientReferrals($id: PlacementId!) {
  getClientReferrals(clientId: $id) {
    ...ReferralResponse
    referenceBusiness {
      id
      businessContactEntity {
        name
      }
    }
    referenceContact {
      id
      contactEntity {
        name
      }
    }
    community {
      id
      name
    }
  }
}
    fragment ReferralResponse on Referral {
  id
  placementId
  referenceBusinessId
  referenceContactId
  communityId
  referralType
  notes
  when
  dateAdded
  createdBy
  updatedAt
  updatedBy
}`);

export const useGetOutboundClientReferralsQuery = <
    TData = GetOutboundClientReferralsQuery,
    TError = unknown,
>(
    variables: GetOutboundClientReferralsQueryVariables,
    options?: UseQueryOptions<GetOutboundClientReferralsQuery, TError, TData>,
) => {
    return useQuery<GetOutboundClientReferralsQuery, TError, TData>(
        ["getOutboundClientReferrals", variables],
        graphqlFetcher<
            GetOutboundClientReferralsQuery,
            GetOutboundClientReferralsQueryVariables
        >(GetOutboundClientReferralsDocument, variables),
        options,
    );
};

export const useInfiniteGetOutboundClientReferralsQuery = <
    TData = GetOutboundClientReferralsQuery,
    TError = unknown,
>(
    variables: GetOutboundClientReferralsQueryVariables,
    options?: UseInfiniteQueryOptions<GetOutboundClientReferralsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetOutboundClientReferralsQuery, TError, TData>(
        ["getOutboundClientReferrals.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetOutboundClientReferralsQuery,
                GetOutboundClientReferralsQueryVariables
            >(GetOutboundClientReferralsDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const CreateClientReferralDocument = new TypedDocumentString(`
    mutation createClientReferral($clientId: PlacementId!, $referralType: String!) {
  createClientReferral(clientId: $clientId, referralType: $referralType) {
    ...ReferralResponse
  }
}
    fragment ReferralResponse on Referral {
  id
  placementId
  referenceBusinessId
  referenceContactId
  communityId
  referralType
  notes
  when
  dateAdded
  createdBy
  updatedAt
  updatedBy
}`);

export const useCreateClientReferralMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateClientReferralMutation,
        TError,
        CreateClientReferralMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateClientReferralMutation,
        TError,
        CreateClientReferralMutationVariables,
        TContext
    >(
        ["createClientReferral"],
        (variables?: CreateClientReferralMutationVariables) =>
            graphqlFetcher<
                CreateClientReferralMutation,
                CreateClientReferralMutationVariables
            >(CreateClientReferralDocument, variables)(),
        options,
    );
};

export const PatchClientReferralDocument = new TypedDocumentString(`
    mutation patchClientReferral($id: ReferralId!, $clientId: PlacementId!, $notes: String, $when: DateTime, $deleted: Boolean, $referenceBusinessId: ReferenceBusinessId, $referenceContactId: ReferenceContactId, $communityId: CommunityId) {
  patchClientReferral(
    id: $id
    clientId: $clientId
    notes: $notes
    when: $when
    deleted: $deleted
    referenceBusinessId: $referenceBusinessId
    referenceContactId: $referenceContactId
    communityId: $communityId
  ) {
    ...ReferralResponse
  }
}
    fragment ReferralResponse on Referral {
  id
  placementId
  referenceBusinessId
  referenceContactId
  communityId
  referralType
  notes
  when
  dateAdded
  createdBy
  updatedAt
  updatedBy
}`);

export const usePatchClientReferralMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PatchClientReferralMutation,
        TError,
        PatchClientReferralMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PatchClientReferralMutation,
        TError,
        PatchClientReferralMutationVariables,
        TContext
    >(
        ["patchClientReferral"],
        (variables?: PatchClientReferralMutationVariables) =>
            graphqlFetcher<
                PatchClientReferralMutation,
                PatchClientReferralMutationVariables
            >(PatchClientReferralDocument, variables)(),
        options,
    );
};

export const GetClientForIntakeEmailDocument = new TypedDocumentString(`
    query getClientForIntakeEmail($clientId: PlacementId!) {
  getClient(clientId: $clientId) {
    name
    clientCommunities {
      relationship
      referred
      community {
        id
        name
        email
        emailOptOut
        availability {
          status
          notes
          freshness
          lastUpdatedAt
        }
        isConnectedToEvergreen
        communityContacts {
          order
          primary
          contact {
            name
            email1
            email1OptOut
          }
        }
      }
    }
  }
}
    `);

export const useGetClientForIntakeEmailQuery = <
    TData = GetClientForIntakeEmailQuery,
    TError = unknown,
>(
    variables: GetClientForIntakeEmailQueryVariables,
    options?: UseQueryOptions<GetClientForIntakeEmailQuery, TError, TData>,
) => {
    return useQuery<GetClientForIntakeEmailQuery, TError, TData>(
        ["getClientForIntakeEmail", variables],
        graphqlFetcher<
            GetClientForIntakeEmailQuery,
            GetClientForIntakeEmailQueryVariables
        >(GetClientForIntakeEmailDocument, variables),
        options,
    );
};

export const useInfiniteGetClientForIntakeEmailQuery = <
    TData = GetClientForIntakeEmailQuery,
    TError = unknown,
>(
    variables: GetClientForIntakeEmailQueryVariables,
    options?: UseInfiniteQueryOptions<GetClientForIntakeEmailQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetClientForIntakeEmailQuery, TError, TData>(
        ["getClientForIntakeEmail.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetClientForIntakeEmailQuery,
                GetClientForIntakeEmailQueryVariables
            >(GetClientForIntakeEmailDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const DetachAdditionalClientDocument = new TypedDocumentString(`
    mutation detachAdditionalClient($clientId: PlacementId!) {
  detachAdditionalClient(clientId: $clientId)
}
    `);

export const useDetachAdditionalClientMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DetachAdditionalClientMutation,
        TError,
        DetachAdditionalClientMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DetachAdditionalClientMutation,
        TError,
        DetachAdditionalClientMutationVariables,
        TContext
    >(
        ["detachAdditionalClient"],
        (variables?: DetachAdditionalClientMutationVariables) =>
            graphqlFetcher<
                DetachAdditionalClientMutation,
                DetachAdditionalClientMutationVariables
            >(DetachAdditionalClientDocument, variables)(),
        options,
    );
};

export const GetCommunityDocument = new TypedDocumentString(`
    query getCommunity($id: CommunityId!) {
  getCommunity(communityId: $id) {
    ...CommunityBasics
    invoices {
      id
      amountCents
      paidDate
      sentDate
      dueDate
      serial
      status
      placementId
      communityId
      fileId
      clientName
      notes
      externalReference
      remainingBalanceCents
      totalPaidCents
      hasOutstandingBalance
      lineItems {
        id
        description
        quantity
        unitPriceInCents
      }
      paymentSchedule {
        items {
          id
          amountInCents
          dueDate
          paidDate
          status
        }
      }
      isExternallyManaged
    }
    licenses {
      href
      id
      licenseEndDate
      licenseNumber
      licenseStartDate
      licensee
      status
      communityId
      tenantId
    }
  }
}
    fragment CommunityBasics on Community {
  address
  caregiverRatio
  city
  contractStatus
  contractExpiresDate
  contractIssuedDate
  contractNotes
  dateAdded
  gender
  feeType
  paymentFee
  paymentPercentage
  id
  latitude
  licenseStatus
  longitude
  mainPhone
  monthlyPrice
  moveInDeposit
  name
  petDeposit
  priceDetails
  priceHighEnd
  rating
  secondPersonFee
  state
  summary
  thumbnailUrl
  unitsOrCapacity
  updatedAt
  virtualTourURL
  website
  zip
  regionId
  email
  county
  contacts {
    cellPhone
    name
    phone1
    role
    email1
  }
  primaryContacts {
    cellPhone
    name
    phone1
    role
    email1
  }
}`);

export const useGetCommunityQuery = <TData = GetCommunityQuery, TError = unknown>(
    variables: GetCommunityQueryVariables,
    options?: UseQueryOptions<GetCommunityQuery, TError, TData>,
) => {
    return useQuery<GetCommunityQuery, TError, TData>(
        ["getCommunity", variables],
        graphqlFetcher<GetCommunityQuery, GetCommunityQueryVariables>(
            GetCommunityDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetCommunityQuery = <TData = GetCommunityQuery, TError = unknown>(
    variables: GetCommunityQueryVariables,
    options?: UseInfiniteQueryOptions<GetCommunityQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetCommunityQuery, TError, TData>(
        ["getCommunity.infinite", variables],
        metaData =>
            graphqlFetcher<GetCommunityQuery, GetCommunityQueryVariables>(
                GetCommunityDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetCommunityAdvancedDocument = new TypedDocumentString(`
    query getCommunityAdvanced($id: CommunityId!) {
  getCommunity(communityId: $id) {
    id
    communityMasterId
    communityMasterVersion
    evergreenCommunityId
    matchInfo {
      matchRule
      matchScore
      originalMatchDate
      evergreenLicenseId
      evergreenCommunityAliasId
    }
  }
}
    `);

export const useGetCommunityAdvancedQuery = <
    TData = GetCommunityAdvancedQuery,
    TError = unknown,
>(
    variables: GetCommunityAdvancedQueryVariables,
    options?: UseQueryOptions<GetCommunityAdvancedQuery, TError, TData>,
) => {
    return useQuery<GetCommunityAdvancedQuery, TError, TData>(
        ["getCommunityAdvanced", variables],
        graphqlFetcher<GetCommunityAdvancedQuery, GetCommunityAdvancedQueryVariables>(
            GetCommunityAdvancedDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetCommunityAdvancedQuery = <
    TData = GetCommunityAdvancedQuery,
    TError = unknown,
>(
    variables: GetCommunityAdvancedQueryVariables,
    options?: UseInfiniteQueryOptions<GetCommunityAdvancedQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetCommunityAdvancedQuery, TError, TData>(
        ["getCommunityAdvanced.infinite", variables],
        metaData =>
            graphqlFetcher<GetCommunityAdvancedQuery, GetCommunityAdvancedQueryVariables>(
                GetCommunityAdvancedDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const DeleteCommunityLicenseDocument = new TypedDocumentString(`
    mutation deleteCommunityLicense($communityId: CommunityId!, $licenseId: CommunityLicenseId!) {
  deleteCommunityLicense(communityId: $communityId, licenseId: $licenseId)
}
    `);

export const useDeleteCommunityLicenseMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteCommunityLicenseMutation,
        TError,
        DeleteCommunityLicenseMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteCommunityLicenseMutation,
        TError,
        DeleteCommunityLicenseMutationVariables,
        TContext
    >(
        ["deleteCommunityLicense"],
        (variables?: DeleteCommunityLicenseMutationVariables) =>
            graphqlFetcher<
                DeleteCommunityLicenseMutation,
                DeleteCommunityLicenseMutationVariables
            >(DeleteCommunityLicenseDocument, variables)(),
        options,
    );
};

export const UpdateCommunityLicenseDocument = new TypedDocumentString(`
    mutation updateCommunityLicense($licenseId: CommunityLicenseId!, $license: CommunityLicenseInput!) {
  updateCommunityLicense(licenseId: $licenseId, license: $license)
}
    `);

export const useUpdateCommunityLicenseMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateCommunityLicenseMutation,
        TError,
        UpdateCommunityLicenseMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateCommunityLicenseMutation,
        TError,
        UpdateCommunityLicenseMutationVariables,
        TContext
    >(
        ["updateCommunityLicense"],
        (variables?: UpdateCommunityLicenseMutationVariables) =>
            graphqlFetcher<
                UpdateCommunityLicenseMutation,
                UpdateCommunityLicenseMutationVariables
            >(UpdateCommunityLicenseDocument, variables)(),
        options,
    );
};

export const CreateCommunityLicenseDocument = new TypedDocumentString(`
    mutation createCommunityLicense($communityId: CommunityId!) {
  createCommunityLicense(communityId: $communityId) {
    id
  }
}
    `);

export const useCreateCommunityLicenseMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateCommunityLicenseMutation,
        TError,
        CreateCommunityLicenseMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateCommunityLicenseMutation,
        TError,
        CreateCommunityLicenseMutationVariables,
        TContext
    >(
        ["createCommunityLicense"],
        (variables?: CreateCommunityLicenseMutationVariables) =>
            graphqlFetcher<
                CreateCommunityLicenseMutation,
                CreateCommunityLicenseMutationVariables
            >(CreateCommunityLicenseDocument, variables)(),
        options,
    );
};

export const GetReferralsToCommunityDocument = new TypedDocumentString(`
    query getReferralsToCommunity($communityId: CommunityId!) {
  getReferralsToCommunity(communityId: $communityId) {
    ...ReferralBasics
  }
}
    fragment ReferralBasics on Referral {
  id
  placementId
  placement {
    id
    name
    bestContactName
    statusListItemId
    dateAdded
    nextTask {
      id
      text
      dueDate
      dueDateTime
      compositeDueDateTime
      status
    }
    serial
    assignedUserId
    updatedAt
    moveTimeline
    moveTimelineActual
  }
}`);

export const useGetReferralsToCommunityQuery = <
    TData = GetReferralsToCommunityQuery,
    TError = unknown,
>(
    variables: GetReferralsToCommunityQueryVariables,
    options?: UseQueryOptions<GetReferralsToCommunityQuery, TError, TData>,
) => {
    return useQuery<GetReferralsToCommunityQuery, TError, TData>(
        ["getReferralsToCommunity", variables],
        graphqlFetcher<
            GetReferralsToCommunityQuery,
            GetReferralsToCommunityQueryVariables
        >(GetReferralsToCommunityDocument, variables),
        options,
    );
};

export const useInfiniteGetReferralsToCommunityQuery = <
    TData = GetReferralsToCommunityQuery,
    TError = unknown,
>(
    variables: GetReferralsToCommunityQueryVariables,
    options?: UseInfiniteQueryOptions<GetReferralsToCommunityQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetReferralsToCommunityQuery, TError, TData>(
        ["getReferralsToCommunity.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetReferralsToCommunityQuery,
                GetReferralsToCommunityQueryVariables
            >(GetReferralsToCommunityDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const FindOrCreateCommunityInsightsDocument = new TypedDocumentString(`
    mutation findOrCreateCommunityInsights($communityId: CommunityId!) {
  findOrCreateCommunityInsights(communityId: $communityId) {
    ...CommunityInsightsFragment
  }
}
    fragment CommunityInsightsFragment on CommunityInsightsResult {
  ...CommunityInsightsFragmentLite
  reviews {
    id
    text
    rating
    authorDisplayName
    authorPhotoUri
    publishedAt
    source
    webUri
  }
}
fragment CommunityInsightsFragmentLite on CommunityInsightsResult {
  id
  evergreenCommunityId
  name
  address
  city
  state
  zip
  googleMapsUrl
  phoneNumbers {
    number
    label
  }
  website
  averageRating
  ratingCount
  lastGooglePlaceSynchronizedAt
  availability {
    status
    lastUpdatedAt
    notes
    lastRequestedAt
    freshness
  }
  pricing {
    minPrice
    maxPrice
    notes
    lastUpdatedAt
    freshness
    files {
      id
      name
      size
      mimeType
      downloadUrl
    }
  }
}`);

export const useFindOrCreateCommunityInsightsMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        FindOrCreateCommunityInsightsMutation,
        TError,
        FindOrCreateCommunityInsightsMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        FindOrCreateCommunityInsightsMutation,
        TError,
        FindOrCreateCommunityInsightsMutationVariables,
        TContext
    >(
        ["findOrCreateCommunityInsights"],
        (variables?: FindOrCreateCommunityInsightsMutationVariables) =>
            graphqlFetcher<
                FindOrCreateCommunityInsightsMutation,
                FindOrCreateCommunityInsightsMutationVariables
            >(FindOrCreateCommunityInsightsDocument, variables)(),
        options,
    );
};

export const FindOrCreateCommunityInsightsManyDocument = new TypedDocumentString(`
    mutation findOrCreateCommunityInsightsMany($communityIds: [CommunityId!]!) {
  findOrCreateCommunityInsightsMany(communityIds: $communityIds) {
    ...CommunityInsightsFragmentLite
  }
}
    fragment CommunityInsightsFragmentLite on CommunityInsightsResult {
  id
  evergreenCommunityId
  name
  address
  city
  state
  zip
  googleMapsUrl
  phoneNumbers {
    number
    label
  }
  website
  averageRating
  ratingCount
  lastGooglePlaceSynchronizedAt
  availability {
    status
    lastUpdatedAt
    notes
    lastRequestedAt
    freshness
  }
  pricing {
    minPrice
    maxPrice
    notes
    lastUpdatedAt
    freshness
    files {
      id
      name
      size
      mimeType
      downloadUrl
    }
  }
}`);

export const useFindOrCreateCommunityInsightsManyMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        FindOrCreateCommunityInsightsManyMutation,
        TError,
        FindOrCreateCommunityInsightsManyMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        FindOrCreateCommunityInsightsManyMutation,
        TError,
        FindOrCreateCommunityInsightsManyMutationVariables,
        TContext
    >(
        ["findOrCreateCommunityInsightsMany"],
        (variables?: FindOrCreateCommunityInsightsManyMutationVariables) =>
            graphqlFetcher<
                FindOrCreateCommunityInsightsManyMutation,
                FindOrCreateCommunityInsightsManyMutationVariables
            >(FindOrCreateCommunityInsightsManyDocument, variables)(),
        options,
    );
};

export const RestoreCommunityDocument = new TypedDocumentString(`
    mutation restoreCommunity($communityId: CommunityId!) {
  restoreCommunity(communityId: $communityId)
}
    `);

export const useRestoreCommunityMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        RestoreCommunityMutation,
        TError,
        RestoreCommunityMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        RestoreCommunityMutation,
        TError,
        RestoreCommunityMutationVariables,
        TContext
    >(
        ["restoreCommunity"],
        (variables?: RestoreCommunityMutationVariables) =>
            graphqlFetcher<RestoreCommunityMutation, RestoreCommunityMutationVariables>(
                RestoreCommunityDocument,
                variables,
            )(),
        options,
    );
};

export const RequestCommunityListingParticipationDocument = new TypedDocumentString(`
    mutation requestCommunityListingParticipation($communityId: CommunityId!, $outboundMessage: OutboundMessage!, $observationFocus: ObservationKind) {
  requestCommunityListingParticipation(
    communityId: $communityId
    outboundMessage: $outboundMessage
    observationFocus: $observationFocus
  ) {
    status
    sentMessage {
      id
    }
    accepted
    rejected
    code
  }
}
    `);

export const useRequestCommunityListingParticipationMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        RequestCommunityListingParticipationMutation,
        TError,
        RequestCommunityListingParticipationMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        RequestCommunityListingParticipationMutation,
        TError,
        RequestCommunityListingParticipationMutationVariables,
        TContext
    >(
        ["requestCommunityListingParticipation"],
        (variables?: RequestCommunityListingParticipationMutationVariables) =>
            graphqlFetcher<
                RequestCommunityListingParticipationMutation,
                RequestCommunityListingParticipationMutationVariables
            >(RequestCommunityListingParticipationDocument, variables)(),
        options,
    );
};

export const RequestCommunityListingUpdateDocument = new TypedDocumentString(`
    mutation requestCommunityListingUpdate($communityId: CommunityId!, $message: PredeterminedRecipientMessage!, $observationFocus: ObservationKind!) {
  requestCommunityListingUpdate(
    communityId: $communityId
    message: $message
    observationFocus: $observationFocus
  ) {
    status
    sentMessage {
      id
    }
    accepted
    rejected
    code
  }
}
    `);

export const useRequestCommunityListingUpdateMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        RequestCommunityListingUpdateMutation,
        TError,
        RequestCommunityListingUpdateMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        RequestCommunityListingUpdateMutation,
        TError,
        RequestCommunityListingUpdateMutationVariables,
        TContext
    >(
        ["requestCommunityListingUpdate"],
        (variables?: RequestCommunityListingUpdateMutationVariables) =>
            graphqlFetcher<
                RequestCommunityListingUpdateMutation,
                RequestCommunityListingUpdateMutationVariables
            >(RequestCommunityListingUpdateDocument, variables)(),
        options,
    );
};

export const GetLicenseDocument = new TypedDocumentString(`
    query getLicense($id: LicenseId!) {
  getLicense(licenseId: $id) {
    ...LicenseDefaults
    publicRecords {
      ...PublicRecordDefaults
    }
    identity {
      ...IdentityDefaults
    }
  }
}
    fragment IdentityDefaults on Identity {
  id
  name
  successorId
  propagateableLicenseTypes
  thumbnailUrl
}
fragment LicenseDefaults on License {
  status
  jurisdiction
  licenseNumber
  id
  licenseType
}
fragment PublicRecordDefaults on PublicRecord {
  id
  name
  address
  city
  state
  zip
  capacity
  licenseType
  licenseNumber
  licensee
  closedDate
  licenseDate
  licenseStatus
  county
  country
  phone
  administrator
  email
  lat
  lng
}`);

export const useGetLicenseQuery = <TData = GetLicenseQuery, TError = unknown>(
    variables: GetLicenseQueryVariables,
    options?: UseQueryOptions<GetLicenseQuery, TError, TData>,
) => {
    return useQuery<GetLicenseQuery, TError, TData>(
        ["getLicense", variables],
        graphqlFetcher<GetLicenseQuery, GetLicenseQueryVariables>(
            GetLicenseDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetLicenseQuery = <TData = GetLicenseQuery, TError = unknown>(
    variables: GetLicenseQueryVariables,
    options?: UseInfiniteQueryOptions<GetLicenseQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetLicenseQuery, TError, TData>(
        ["getLicense.infinite", variables],
        metaData =>
            graphqlFetcher<GetLicenseQuery, GetLicenseQueryVariables>(
                GetLicenseDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetIdentityDocument = new TypedDocumentString(`
    query getIdentity($id: IdentityId!) {
  getIdentity(identityId: $id) {
    ...IdentityDefaults
    licenses {
      ...LicenseDefaults
      publicRecords {
        ...PublicRecordDefaults
        publicRecordContacts {
          address1
          address2
          cellPhone
          city
          contactNotes
          email1
          fax
          id
          name
          phone1
          relationship
          role
          state
          website
          zip
        }
      }
    }
  }
}
    fragment IdentityDefaults on Identity {
  id
  name
  successorId
  propagateableLicenseTypes
  thumbnailUrl
}
fragment LicenseDefaults on License {
  status
  jurisdiction
  licenseNumber
  id
  licenseType
}
fragment PublicRecordDefaults on PublicRecord {
  id
  name
  address
  city
  state
  zip
  capacity
  licenseType
  licenseNumber
  licensee
  closedDate
  licenseDate
  licenseStatus
  county
  country
  phone
  administrator
  email
  lat
  lng
}`);

export const useGetIdentityQuery = <TData = GetIdentityQuery, TError = unknown>(
    variables: GetIdentityQueryVariables,
    options?: UseQueryOptions<GetIdentityQuery, TError, TData>,
) => {
    return useQuery<GetIdentityQuery, TError, TData>(
        ["getIdentity", variables],
        graphqlFetcher<GetIdentityQuery, GetIdentityQueryVariables>(
            GetIdentityDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetIdentityQuery = <TData = GetIdentityQuery, TError = unknown>(
    variables: GetIdentityQueryVariables,
    options?: UseInfiniteQueryOptions<GetIdentityQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetIdentityQuery, TError, TData>(
        ["getIdentity.infinite", variables],
        metaData =>
            graphqlFetcher<GetIdentityQuery, GetIdentityQueryVariables>(
                GetIdentityDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const PublicRecordSearchDocument = new TypedDocumentString(`
    query publicRecordSearch($params: PublicRecordSearchRequest!) {
  publicRecordSearch(params: $params) {
    count
    records {
      ...PublicRecordDefaults
      license {
        ...LicenseDefaults
        identity {
          ...IdentityDefaults
        }
      }
    }
  }
}
    fragment IdentityDefaults on Identity {
  id
  name
  successorId
  propagateableLicenseTypes
  thumbnailUrl
}
fragment LicenseDefaults on License {
  status
  jurisdiction
  licenseNumber
  id
  licenseType
}
fragment PublicRecordDefaults on PublicRecord {
  id
  name
  address
  city
  state
  zip
  capacity
  licenseType
  licenseNumber
  licensee
  closedDate
  licenseDate
  licenseStatus
  county
  country
  phone
  administrator
  email
  lat
  lng
}`);

export const usePublicRecordSearchQuery = <
    TData = PublicRecordSearchQuery,
    TError = unknown,
>(
    variables: PublicRecordSearchQueryVariables,
    options?: UseQueryOptions<PublicRecordSearchQuery, TError, TData>,
) => {
    return useQuery<PublicRecordSearchQuery, TError, TData>(
        ["publicRecordSearch", variables],
        graphqlFetcher<PublicRecordSearchQuery, PublicRecordSearchQueryVariables>(
            PublicRecordSearchDocument,
            variables,
        ),
        options,
    );
};

export const useInfinitePublicRecordSearchQuery = <
    TData = PublicRecordSearchQuery,
    TError = unknown,
>(
    variables: PublicRecordSearchQueryVariables,
    options?: UseInfiniteQueryOptions<PublicRecordSearchQuery, TError, TData>,
) => {
    return useInfiniteQuery<PublicRecordSearchQuery, TError, TData>(
        ["publicRecordSearch.infinite", variables],
        metaData =>
            graphqlFetcher<PublicRecordSearchQuery, PublicRecordSearchQueryVariables>(
                PublicRecordSearchDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetEvergreenLicenseTypesDocument = new TypedDocumentString(`
    query getEvergreenLicenseTypes {
  getEvergreenLicenseTypes {
    id
    state
    licenseType
  }
}
    `);

export const useGetEvergreenLicenseTypesQuery = <
    TData = GetEvergreenLicenseTypesQuery,
    TError = unknown,
>(
    variables?: GetEvergreenLicenseTypesQueryVariables,
    options?: UseQueryOptions<GetEvergreenLicenseTypesQuery, TError, TData>,
) => {
    return useQuery<GetEvergreenLicenseTypesQuery, TError, TData>(
        variables === undefined
            ? ["getEvergreenLicenseTypes"]
            : ["getEvergreenLicenseTypes", variables],
        graphqlFetcher<
            GetEvergreenLicenseTypesQuery,
            GetEvergreenLicenseTypesQueryVariables
        >(GetEvergreenLicenseTypesDocument, variables),
        options,
    );
};

export const useInfiniteGetEvergreenLicenseTypesQuery = <
    TData = GetEvergreenLicenseTypesQuery,
    TError = unknown,
>(
    variables?: GetEvergreenLicenseTypesQueryVariables,
    options?: UseInfiniteQueryOptions<GetEvergreenLicenseTypesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetEvergreenLicenseTypesQuery, TError, TData>(
        variables === undefined
            ? ["getEvergreenLicenseTypes.infinite"]
            : ["getEvergreenLicenseTypes.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetEvergreenLicenseTypesQuery,
                GetEvergreenLicenseTypesQueryVariables
            >(GetEvergreenLicenseTypesDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const UpdateEvergreenLicenseTypeDocument = new TypedDocumentString(`
    mutation updateEvergreenLicenseType($input: EvergreenLicenseTypeInput!) {
  updateEvergreenLicenseType(input: $input) {
    id
  }
}
    `);

export const useUpdateEvergreenLicenseTypeMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        UpdateEvergreenLicenseTypeMutation,
        TError,
        UpdateEvergreenLicenseTypeMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateEvergreenLicenseTypeMutation,
        TError,
        UpdateEvergreenLicenseTypeMutationVariables,
        TContext
    >(
        ["updateEvergreenLicenseType"],
        (variables?: UpdateEvergreenLicenseTypeMutationVariables) =>
            graphqlFetcher<
                UpdateEvergreenLicenseTypeMutation,
                UpdateEvergreenLicenseTypeMutationVariables
            >(UpdateEvergreenLicenseTypeDocument, variables)(),
        options,
    );
};

export const DeleteEvergreenLicenseTypeDocument = new TypedDocumentString(`
    mutation deleteEvergreenLicenseType($id: EvergreenLicenseTypeId!) {
  deleteEvergreenLicenseType(licenseTypeId: $id)
}
    `);

export const useDeleteEvergreenLicenseTypeMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        DeleteEvergreenLicenseTypeMutation,
        TError,
        DeleteEvergreenLicenseTypeMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteEvergreenLicenseTypeMutation,
        TError,
        DeleteEvergreenLicenseTypeMutationVariables,
        TContext
    >(
        ["deleteEvergreenLicenseType"],
        (variables?: DeleteEvergreenLicenseTypeMutationVariables) =>
            graphqlFetcher<
                DeleteEvergreenLicenseTypeMutation,
                DeleteEvergreenLicenseTypeMutationVariables
            >(DeleteEvergreenLicenseTypeDocument, variables)(),
        options,
    );
};

export const CommunityNamesDocument = new TypedDocumentString(`
    query CommunityNames($regionKey: String!, $ids: [CommunityId!]) {
  communityNames(regionKey: $regionKey, ids: $ids) {
    communities {
      id
      name
    }
  }
}
    `);

export const useCommunityNamesQuery = <TData = CommunityNamesQuery, TError = unknown>(
    variables: CommunityNamesQueryVariables,
    options?: UseQueryOptions<CommunityNamesQuery, TError, TData>,
) => {
    return useQuery<CommunityNamesQuery, TError, TData>(
        ["CommunityNames", variables],
        graphqlFetcher<CommunityNamesQuery, CommunityNamesQueryVariables>(
            CommunityNamesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteCommunityNamesQuery = <
    TData = CommunityNamesQuery,
    TError = unknown,
>(
    variables: CommunityNamesQueryVariables,
    options?: UseInfiniteQueryOptions<CommunityNamesQuery, TError, TData>,
) => {
    return useInfiniteQuery<CommunityNamesQuery, TError, TData>(
        ["CommunityNames.infinite", variables],
        metaData =>
            graphqlFetcher<CommunityNamesQuery, CommunityNamesQueryVariables>(
                CommunityNamesDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const CommunitySearchDocument = new TypedDocumentString(`
    query CommunitySearch($params: CommunitySearchRequest!) {
  communitySearch(params: $params) {
    total
    coordinates {
      lat
      lng
    }
    hits {
      address
      contractStatus
      contractExpiresDate
      contractIssuedDate
      contractNotes
      paymentFee
      paymentPercentage
      gender
      id
      lat
      licenseStatus
      lng
      mostRecentPlacement
      name
      phone
      placements
      rating
      summary
      thumbnail
      url
      unitsOrCapacity
      showInSearchResults
      monthlyPrice
      priceHighEnd
      zip
      state
      city
      mainPhone
      feeType
      secondPersonFee
      petDeposit
      moveInDeposit
      priceDetails
      caregiverRatio
      updatedAt
      dateAdded
      regionId
      referralCount
      email
      county
      isConnectedToEvergreen
      matchedAliases
      evergreenAliases
      deleted
      pricing {
        minPrice
        maxPrice
        notes
        files {
          id
          name
          mimeType
          size
          downloadUrl
        }
      }
      availability {
        status
        notes
        freshness
        lastUpdatedAt
      }
      contacts {
        cellPhone
        name
        phone1
        email1
        role
      }
      primaryContacts {
        cellPhone
        name
        phone1
        email1
        role
      }
      answers {
        questionId
        booleanAnswer
        numberAnswer
        numberAnswerHigh
        textAnswer
        dateAnswer
        calendarDateAnswer
        selections
        idReferenceAnswer
        selectionAnnotations {
          text
          selectionId
        }
      }
      licenses {
        id
        licenseNumber
        href
        licensee
        status
        licenseStartDate
        licenseEndDate
      }
      googleInfo {
        rating
        ratingCount
      }
    }
    deletedHits {
      address
      id
      lat
      lng
      name
      phone
      rating
      summary
      thumbnail
      url
      zip
      state
      city
      mainPhone
      updatedAt
      dateAdded
      regionId
      email
      county
      deleted
    }
  }
}
    `);

export const useCommunitySearchQuery = <TData = CommunitySearchQuery, TError = unknown>(
    variables: CommunitySearchQueryVariables,
    options?: UseQueryOptions<CommunitySearchQuery, TError, TData>,
) => {
    return useQuery<CommunitySearchQuery, TError, TData>(
        ["CommunitySearch", variables],
        graphqlFetcher<CommunitySearchQuery, CommunitySearchQueryVariables>(
            CommunitySearchDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteCommunitySearchQuery = <
    TData = CommunitySearchQuery,
    TError = unknown,
>(
    variables: CommunitySearchQueryVariables,
    options?: UseInfiniteQueryOptions<CommunitySearchQuery, TError, TData>,
) => {
    return useInfiniteQuery<CommunitySearchQuery, TError, TData>(
        ["CommunitySearch.infinite", variables],
        metaData =>
            graphqlFetcher<CommunitySearchQuery, CommunitySearchQueryVariables>(
                CommunitySearchDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const CommunitySearchMapViewDocument = new TypedDocumentString(`
    query CommunitySearchMapView($searchParams: CommunitySearchRequest!, $mapView: CommunitySearchRequestMapComponent!) {
  communitySearchMapView(searchParams: $searchParams, mapView: $mapView) {
    recommendedZoom
    coordinates {
      lat
      lng
    }
    communityCoordinates {
      lat
      lng
      name
      contractStatus
      id
      appLink
    }
    communityClusters {
      lat
      lng
      count
    }
  }
}
    `);

export const useCommunitySearchMapViewQuery = <
    TData = CommunitySearchMapViewQuery,
    TError = unknown,
>(
    variables: CommunitySearchMapViewQueryVariables,
    options?: UseQueryOptions<CommunitySearchMapViewQuery, TError, TData>,
) => {
    return useQuery<CommunitySearchMapViewQuery, TError, TData>(
        ["CommunitySearchMapView", variables],
        graphqlFetcher<CommunitySearchMapViewQuery, CommunitySearchMapViewQueryVariables>(
            CommunitySearchMapViewDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteCommunitySearchMapViewQuery = <
    TData = CommunitySearchMapViewQuery,
    TError = unknown,
>(
    variables: CommunitySearchMapViewQueryVariables,
    options?: UseInfiniteQueryOptions<CommunitySearchMapViewQuery, TError, TData>,
) => {
    return useInfiniteQuery<CommunitySearchMapViewQuery, TError, TData>(
        ["CommunitySearchMapView.infinite", variables],
        metaData =>
            graphqlFetcher<
                CommunitySearchMapViewQuery,
                CommunitySearchMapViewQueryVariables
            >(CommunitySearchMapViewDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetCommunityContactsDocument = new TypedDocumentString(`
    query getCommunityContacts($communityId: CommunityId!, $regionId: RegionId!) {
  getCommunityContacts(communityId: $communityId, regionId: $regionId) {
    contactId
    communityId
    primary
    contact {
      ...ContactFragment
    }
    referenceContact {
      id
      summary
      regionId
    }
  }
}
    fragment ContactFragment on ContactEntity {
  id
  name
  preferredName
  address1
  address2
  city
  state
  zip
  relationship
  role
  cellPhone
  phone1
  email1
  email1OptOut
  website
  contactType
  type
  fax
  contactNotes
  dateAdded
}`);

export const useGetCommunityContactsQuery = <
    TData = GetCommunityContactsQuery,
    TError = unknown,
>(
    variables: GetCommunityContactsQueryVariables,
    options?: UseQueryOptions<GetCommunityContactsQuery, TError, TData>,
) => {
    return useQuery<GetCommunityContactsQuery, TError, TData>(
        ["getCommunityContacts", variables],
        graphqlFetcher<GetCommunityContactsQuery, GetCommunityContactsQueryVariables>(
            GetCommunityContactsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetCommunityContactsQuery = <
    TData = GetCommunityContactsQuery,
    TError = unknown,
>(
    variables: GetCommunityContactsQueryVariables,
    options?: UseInfiniteQueryOptions<GetCommunityContactsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetCommunityContactsQuery, TError, TData>(
        ["getCommunityContacts.infinite", variables],
        metaData =>
            graphqlFetcher<GetCommunityContactsQuery, GetCommunityContactsQueryVariables>(
                GetCommunityContactsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const PromoteCommunityContactDocument = new TypedDocumentString(`
    mutation promoteCommunityContact($communityId: CommunityId!, $contactId: ContactId!, $regionId: RegionId!) {
  promoteCommunityContact(
    communityId: $communityId
    contactId: $contactId
    regionId: $regionId
  ) {
    contactId
    communityId
    primary
    contact {
      ...ContactFragment
    }
    referenceContact {
      id
      summary
      regionId
      ownerId
      dateAdded
      updatedAt
      updatedBy
      createdBy
    }
  }
}
    fragment ContactFragment on ContactEntity {
  id
  name
  preferredName
  address1
  address2
  city
  state
  zip
  relationship
  role
  cellPhone
  phone1
  email1
  email1OptOut
  website
  contactType
  type
  fax
  contactNotes
  dateAdded
}`);

export const usePromoteCommunityContactMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PromoteCommunityContactMutation,
        TError,
        PromoteCommunityContactMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PromoteCommunityContactMutation,
        TError,
        PromoteCommunityContactMutationVariables,
        TContext
    >(
        ["promoteCommunityContact"],
        (variables?: PromoteCommunityContactMutationVariables) =>
            graphqlFetcher<
                PromoteCommunityContactMutation,
                PromoteCommunityContactMutationVariables
            >(PromoteCommunityContactDocument, variables)(),
        options,
    );
};

export const DeleteCommunityContactDocument = new TypedDocumentString(`
    mutation deleteCommunityContact($communityId: CommunityId!, $contactId: ContactId!) {
  deleteCommunityContact(communityId: $communityId, contactId: $contactId) {
    contactId
    communityId
  }
}
    `);

export const useDeleteCommunityContactMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteCommunityContactMutation,
        TError,
        DeleteCommunityContactMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteCommunityContactMutation,
        TError,
        DeleteCommunityContactMutationVariables,
        TContext
    >(
        ["deleteCommunityContact"],
        (variables?: DeleteCommunityContactMutationVariables) =>
            graphqlFetcher<
                DeleteCommunityContactMutation,
                DeleteCommunityContactMutationVariables
            >(DeleteCommunityContactDocument, variables)(),
        options,
    );
};

export const DeleteClientContactDocument = new TypedDocumentString(`
    mutation deleteClientContact($clientId: PlacementId!, $contactId: ContactId!, $relationship: ClientContactRelationship!) {
  deleteClientContact(
    clientId: $clientId
    contactId: $contactId
    relationship: $relationship
  ) {
    contactId
    clientId
  }
}
    `);

export const useDeleteClientContactMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteClientContactMutation,
        TError,
        DeleteClientContactMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteClientContactMutation,
        TError,
        DeleteClientContactMutationVariables,
        TContext
    >(
        ["deleteClientContact"],
        (variables?: DeleteClientContactMutationVariables) =>
            graphqlFetcher<
                DeleteClientContactMutation,
                DeleteClientContactMutationVariables
            >(DeleteClientContactDocument, variables)(),
        options,
    );
};

export const GetClientContactsDocument = new TypedDocumentString(`
    query getClientContacts($params: GetClientContactsRequest!) {
  getClientContacts(params: $params) {
    contactId
    placementId
    relationship
    powerOfAttorney
    contact {
      ...ContactFragment
      referenceContacts {
        id
        appLink
      }
    }
  }
}
    fragment ContactFragment on ContactEntity {
  id
  name
  preferredName
  address1
  address2
  city
  state
  zip
  relationship
  role
  cellPhone
  phone1
  email1
  email1OptOut
  website
  contactType
  type
  fax
  contactNotes
  dateAdded
}`);

export const useGetClientContactsQuery = <
    TData = GetClientContactsQuery,
    TError = unknown,
>(
    variables: GetClientContactsQueryVariables,
    options?: UseQueryOptions<GetClientContactsQuery, TError, TData>,
) => {
    return useQuery<GetClientContactsQuery, TError, TData>(
        ["getClientContacts", variables],
        graphqlFetcher<GetClientContactsQuery, GetClientContactsQueryVariables>(
            GetClientContactsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetClientContactsQuery = <
    TData = GetClientContactsQuery,
    TError = unknown,
>(
    variables: GetClientContactsQueryVariables,
    options?: UseInfiniteQueryOptions<GetClientContactsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetClientContactsQuery, TError, TData>(
        ["getClientContacts.infinite", variables],
        metaData =>
            graphqlFetcher<GetClientContactsQuery, GetClientContactsQueryVariables>(
                GetClientContactsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetContactDocument = new TypedDocumentString(`
    query getContact($contactId: ContactId!) {
  getContact(contactId: $contactId) {
    ...ContactWithLinks
  }
}
    fragment ContactFragment on ContactEntity {
  id
  name
  preferredName
  address1
  address2
  city
  state
  zip
  relationship
  role
  cellPhone
  phone1
  email1
  email1OptOut
  website
  contactType
  type
  fax
  contactNotes
  dateAdded
}
fragment ContactWithLinks on ContactEntity {
  ...ContactFragment
  communityContacts {
    communityId
    contactId
    primary
    community {
      id
      name
      appLink
    }
  }
  clientContacts {
    placementId
    contactId
    relationship
    powerOfAttorney
    placement {
      id
      name
      appLink
    }
  }
  homeCareLeadContacts {
    homeCareLeadId
    contactId
    relationship
    powerOfAttorney
    homeCareLead {
      id
      appLink
    }
  }
  referenceContacts {
    id
    summary
    regionId
    ownerId
    dateAdded
    updatedAt
    updatedBy
    createdBy
    appLink
  }
}`);

export const useGetContactQuery = <TData = GetContactQuery, TError = unknown>(
    variables: GetContactQueryVariables,
    options?: UseQueryOptions<GetContactQuery, TError, TData>,
) => {
    return useQuery<GetContactQuery, TError, TData>(
        ["getContact", variables],
        graphqlFetcher<GetContactQuery, GetContactQueryVariables>(
            GetContactDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetContactQuery = <TData = GetContactQuery, TError = unknown>(
    variables: GetContactQueryVariables,
    options?: UseInfiniteQueryOptions<GetContactQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetContactQuery, TError, TData>(
        ["getContact.infinite", variables],
        metaData =>
            graphqlFetcher<GetContactQuery, GetContactQueryVariables>(
                GetContactDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const CreateContactDocument = new TypedDocumentString(`
    mutation createContact($params: CreateContactInput!) {
  createContact(params: $params) {
    ...ContactWithLinks
  }
}
    fragment ContactFragment on ContactEntity {
  id
  name
  preferredName
  address1
  address2
  city
  state
  zip
  relationship
  role
  cellPhone
  phone1
  email1
  email1OptOut
  website
  contactType
  type
  fax
  contactNotes
  dateAdded
}
fragment ContactWithLinks on ContactEntity {
  ...ContactFragment
  communityContacts {
    communityId
    contactId
    primary
    community {
      id
      name
      appLink
    }
  }
  clientContacts {
    placementId
    contactId
    relationship
    powerOfAttorney
    placement {
      id
      name
      appLink
    }
  }
  homeCareLeadContacts {
    homeCareLeadId
    contactId
    relationship
    powerOfAttorney
    homeCareLead {
      id
      appLink
    }
  }
  referenceContacts {
    id
    summary
    regionId
    ownerId
    dateAdded
    updatedAt
    updatedBy
    createdBy
    appLink
  }
}`);

export const useCreateContactMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateContactMutation,
        TError,
        CreateContactMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateContactMutation,
        TError,
        CreateContactMutationVariables,
        TContext
    >(
        ["createContact"],
        (variables?: CreateContactMutationVariables) =>
            graphqlFetcher<CreateContactMutation, CreateContactMutationVariables>(
                CreateContactDocument,
                variables,
            )(),
        options,
    );
};

export const PatchContactDocument = new TypedDocumentString(`
    mutation patchContact($params: PatchContactInput!) {
  patchContact(params: $params) {
    ...ContactWithLinks
  }
}
    fragment ContactFragment on ContactEntity {
  id
  name
  preferredName
  address1
  address2
  city
  state
  zip
  relationship
  role
  cellPhone
  phone1
  email1
  email1OptOut
  website
  contactType
  type
  fax
  contactNotes
  dateAdded
}
fragment ContactWithLinks on ContactEntity {
  ...ContactFragment
  communityContacts {
    communityId
    contactId
    primary
    community {
      id
      name
      appLink
    }
  }
  clientContacts {
    placementId
    contactId
    relationship
    powerOfAttorney
    placement {
      id
      name
      appLink
    }
  }
  homeCareLeadContacts {
    homeCareLeadId
    contactId
    relationship
    powerOfAttorney
    homeCareLead {
      id
      appLink
    }
  }
  referenceContacts {
    id
    summary
    regionId
    ownerId
    dateAdded
    updatedAt
    updatedBy
    createdBy
    appLink
  }
}`);

export const usePatchContactMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PatchContactMutation,
        TError,
        PatchContactMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PatchContactMutation,
        TError,
        PatchContactMutationVariables,
        TContext
    >(
        ["patchContact"],
        (variables?: PatchContactMutationVariables) =>
            graphqlFetcher<PatchContactMutation, PatchContactMutationVariables>(
                PatchContactDocument,
                variables,
            )(),
        options,
    );
};

export const GetConversationFeedForEntityDocument = new TypedDocumentString(`
    query getConversationFeedForEntity($entityId: EmailEntityId!, $regionId: RegionId!) {
  getConversationFeedForEntity(entityId: $entityId, regionId: $regionId) {
    ...ConversationFeedBasics
  }
}
    fragment ConversationFeedBasics on ConversationFeed {
  threads {
    id
    type
    latestTimestamp
    ownerUserId
    smsThread {
      contact {
        id
        phoneNumber
      }
      phoneLine {
        id
        userId
        phoneNumber
      }
      messages {
        ...SmsMessageBasics
      }
    }
    voiceCall {
      id
      direction
      from
      to
      dateAdded
      recordingSid
      recordingStatus
      recordingDurationSec
      transcriptStatus
      summary
      createdByUser {
        id
        displayName
      }
    }
    emailThread {
      threadId
      sentMessages {
        ...SentMessageFragment
      }
      mailboxMessages {
        id
        externalId
        subject
        bodyPreview
        receivedTimestamp
        attachmentCount
        from {
          id
          name
          email
          resolvedName
        }
        sender {
          id
          name
          email
          resolvedName
        }
        to {
          id
          contact {
            id
            name
            email
            resolvedName
          }
        }
        cc {
          id
          contact {
            id
            name
            email
            resolvedName
          }
        }
        bcc {
          id
          contact {
            id
            name
            email
            resolvedName
          }
        }
        replyTo {
          id
          contact {
            id
            name
            email
            resolvedName
          }
        }
      }
    }
    contactSearchHits {
      ...ContactSearchHitFragment
    }
  }
}
fragment ContactSearchHitFragment on ContactSearchHit {
  id
  type
  appLink
  fieldMatches {
    type
    phoneNumber
    email
  }
  client {
    id
    name
    phone
    cellPhone
  }
  community {
    id
    name
    mainPhone
  }
  contact {
    id
    name
    phone1
    cellPhone
  }
  clientContact {
    clientId
    contactId
    relationship
  }
  communityContact {
    communityId
    contactId
    primary
  }
  referenceContact {
    id
    contactEntity {
      id
      name
      phone1
      cellPhone
    }
  }
  referenceBusiness {
    id
    businessContactEntity {
      id
      name
      phone1
      cellPhone
    }
  }
}
fragment SentMessageFragment on SentMessage {
  id
  messageId
  userId
  from
  subject
  bodyPreview
  status
  latestEventStatus
  createdAt
  to
  cc
  messageType
  attachments
  clientIdUuid
  communityIdUuid
  clickedLinkEvents {
    link
    events
    redacted
    eventsV2 {
      timestamp
      recipient
    }
  }
  intakeCommunity {
    id
    name
    appLink
  }
  intakeClient {
    id
    name
    bestContactName
    appLink
  }
  events {
    eventType
    eventTimestamp
    recipient
  }
}
fragment SmsMessageBasics on SmsMessage {
  id
  from
  to
  body
  direction
  dateAdded
  status
  createdBy
  createdByUser {
    id
    displayName
  }
}`);

export const useGetConversationFeedForEntityQuery = <
    TData = GetConversationFeedForEntityQuery,
    TError = unknown,
>(
    variables: GetConversationFeedForEntityQueryVariables,
    options?: UseQueryOptions<GetConversationFeedForEntityQuery, TError, TData>,
) => {
    return useQuery<GetConversationFeedForEntityQuery, TError, TData>(
        ["getConversationFeedForEntity", variables],
        graphqlFetcher<
            GetConversationFeedForEntityQuery,
            GetConversationFeedForEntityQueryVariables
        >(GetConversationFeedForEntityDocument, variables),
        options,
    );
};

export const useInfiniteGetConversationFeedForEntityQuery = <
    TData = GetConversationFeedForEntityQuery,
    TError = unknown,
>(
    variables: GetConversationFeedForEntityQueryVariables,
    options?: UseInfiniteQueryOptions<GetConversationFeedForEntityQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetConversationFeedForEntityQuery, TError, TData>(
        ["getConversationFeedForEntity.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetConversationFeedForEntityQuery,
                GetConversationFeedForEntityQueryVariables
            >(GetConversationFeedForEntityDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetRecentConversationFeedForUserDocument = new TypedDocumentString(`
    query getRecentConversationFeedForUser($regionId: RegionId!) {
  getRecentConversationFeedForUser(regionId: $regionId) {
    ...ConversationFeedBasics
  }
}
    fragment ConversationFeedBasics on ConversationFeed {
  threads {
    id
    type
    latestTimestamp
    ownerUserId
    smsThread {
      contact {
        id
        phoneNumber
      }
      phoneLine {
        id
        userId
        phoneNumber
      }
      messages {
        ...SmsMessageBasics
      }
    }
    voiceCall {
      id
      direction
      from
      to
      dateAdded
      recordingSid
      recordingStatus
      recordingDurationSec
      transcriptStatus
      summary
      createdByUser {
        id
        displayName
      }
    }
    emailThread {
      threadId
      sentMessages {
        ...SentMessageFragment
      }
      mailboxMessages {
        id
        externalId
        subject
        bodyPreview
        receivedTimestamp
        attachmentCount
        from {
          id
          name
          email
          resolvedName
        }
        sender {
          id
          name
          email
          resolvedName
        }
        to {
          id
          contact {
            id
            name
            email
            resolvedName
          }
        }
        cc {
          id
          contact {
            id
            name
            email
            resolvedName
          }
        }
        bcc {
          id
          contact {
            id
            name
            email
            resolvedName
          }
        }
        replyTo {
          id
          contact {
            id
            name
            email
            resolvedName
          }
        }
      }
    }
    contactSearchHits {
      ...ContactSearchHitFragment
    }
  }
}
fragment ContactSearchHitFragment on ContactSearchHit {
  id
  type
  appLink
  fieldMatches {
    type
    phoneNumber
    email
  }
  client {
    id
    name
    phone
    cellPhone
  }
  community {
    id
    name
    mainPhone
  }
  contact {
    id
    name
    phone1
    cellPhone
  }
  clientContact {
    clientId
    contactId
    relationship
  }
  communityContact {
    communityId
    contactId
    primary
  }
  referenceContact {
    id
    contactEntity {
      id
      name
      phone1
      cellPhone
    }
  }
  referenceBusiness {
    id
    businessContactEntity {
      id
      name
      phone1
      cellPhone
    }
  }
}
fragment SentMessageFragment on SentMessage {
  id
  messageId
  userId
  from
  subject
  bodyPreview
  status
  latestEventStatus
  createdAt
  to
  cc
  messageType
  attachments
  clientIdUuid
  communityIdUuid
  clickedLinkEvents {
    link
    events
    redacted
    eventsV2 {
      timestamp
      recipient
    }
  }
  intakeCommunity {
    id
    name
    appLink
  }
  intakeClient {
    id
    name
    bestContactName
    appLink
  }
  events {
    eventType
    eventTimestamp
    recipient
  }
}
fragment SmsMessageBasics on SmsMessage {
  id
  from
  to
  body
  direction
  dateAdded
  status
  createdBy
  createdByUser {
    id
    displayName
  }
}`);

export const useGetRecentConversationFeedForUserQuery = <
    TData = GetRecentConversationFeedForUserQuery,
    TError = unknown,
>(
    variables: GetRecentConversationFeedForUserQueryVariables,
    options?: UseQueryOptions<GetRecentConversationFeedForUserQuery, TError, TData>,
) => {
    return useQuery<GetRecentConversationFeedForUserQuery, TError, TData>(
        ["getRecentConversationFeedForUser", variables],
        graphqlFetcher<
            GetRecentConversationFeedForUserQuery,
            GetRecentConversationFeedForUserQueryVariables
        >(GetRecentConversationFeedForUserDocument, variables),
        options,
    );
};

export const useInfiniteGetRecentConversationFeedForUserQuery = <
    TData = GetRecentConversationFeedForUserQuery,
    TError = unknown,
>(
    variables: GetRecentConversationFeedForUserQueryVariables,
    options?: UseInfiniteQueryOptions<
        GetRecentConversationFeedForUserQuery,
        TError,
        TData
    >,
) => {
    return useInfiniteQuery<GetRecentConversationFeedForUserQuery, TError, TData>(
        ["getRecentConversationFeedForUser.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetRecentConversationFeedForUserQuery,
                GetRecentConversationFeedForUserQueryVariables
            >(GetRecentConversationFeedForUserDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetCustomListDocument = new TypedDocumentString(`
    query getCustomList($key: CustomListKey!) {
  getCustomList(key: $key) {
    ...CustomListResponse
  }
}
    fragment CustomListResponse on CustomList {
  id
  key
  items {
    ...CustomListItemResponse
  }
  categories {
    id
    key
    name
    order
  }
}
fragment CustomListItemResponse on CustomListItem {
  id
  key
  name
  order
  customListCategoryId
  icon
  pipelineVisibility
  pipelineDateLabel
  pipelineDateField
  isArchived
  updateBehavior
}`);

export const useGetCustomListQuery = <TData = GetCustomListQuery, TError = unknown>(
    variables: GetCustomListQueryVariables,
    options?: UseQueryOptions<GetCustomListQuery, TError, TData>,
) => {
    return useQuery<GetCustomListQuery, TError, TData>(
        ["getCustomList", variables],
        graphqlFetcher<GetCustomListQuery, GetCustomListQueryVariables>(
            GetCustomListDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetCustomListQuery = <
    TData = GetCustomListQuery,
    TError = unknown,
>(
    variables: GetCustomListQueryVariables,
    options?: UseInfiniteQueryOptions<GetCustomListQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetCustomListQuery, TError, TData>(
        ["getCustomList.infinite", variables],
        metaData =>
            graphqlFetcher<GetCustomListQuery, GetCustomListQueryVariables>(
                GetCustomListDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetCustomListsDocument = new TypedDocumentString(`
    query getCustomLists {
  getCustomLists {
    ...CustomListResponse
  }
}
    fragment CustomListResponse on CustomList {
  id
  key
  items {
    ...CustomListItemResponse
  }
  categories {
    id
    key
    name
    order
  }
}
fragment CustomListItemResponse on CustomListItem {
  id
  key
  name
  order
  customListCategoryId
  icon
  pipelineVisibility
  pipelineDateLabel
  pipelineDateField
  isArchived
  updateBehavior
}`);

export const useGetCustomListsQuery = <TData = GetCustomListsQuery, TError = unknown>(
    variables?: GetCustomListsQueryVariables,
    options?: UseQueryOptions<GetCustomListsQuery, TError, TData>,
) => {
    return useQuery<GetCustomListsQuery, TError, TData>(
        variables === undefined ? ["getCustomLists"] : ["getCustomLists", variables],
        graphqlFetcher<GetCustomListsQuery, GetCustomListsQueryVariables>(
            GetCustomListsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetCustomListsQuery = <
    TData = GetCustomListsQuery,
    TError = unknown,
>(
    variables?: GetCustomListsQueryVariables,
    options?: UseInfiniteQueryOptions<GetCustomListsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetCustomListsQuery, TError, TData>(
        variables === undefined
            ? ["getCustomLists.infinite"]
            : ["getCustomLists.infinite", variables],
        metaData =>
            graphqlFetcher<GetCustomListsQuery, GetCustomListsQueryVariables>(
                GetCustomListsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const CreateCustomListItemDocument = new TypedDocumentString(`
    mutation createCustomListItem($params: CreateCustomListItemRequest!) {
  createCustomListItem(params: $params) {
    ...CustomListItemResponse
  }
}
    fragment CustomListItemResponse on CustomListItem {
  id
  key
  name
  order
  customListCategoryId
  icon
  pipelineVisibility
  pipelineDateLabel
  pipelineDateField
  isArchived
  updateBehavior
}`);

export const useCreateCustomListItemMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateCustomListItemMutation,
        TError,
        CreateCustomListItemMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateCustomListItemMutation,
        TError,
        CreateCustomListItemMutationVariables,
        TContext
    >(
        ["createCustomListItem"],
        (variables?: CreateCustomListItemMutationVariables) =>
            graphqlFetcher<
                CreateCustomListItemMutation,
                CreateCustomListItemMutationVariables
            >(CreateCustomListItemDocument, variables)(),
        options,
    );
};

export const PatchCustomListItemDocument = new TypedDocumentString(`
    mutation patchCustomListItem($params: PatchCustomListItemRequest!) {
  patchCustomListItem(params: $params) {
    ...CustomListItemResponse
  }
}
    fragment CustomListItemResponse on CustomListItem {
  id
  key
  name
  order
  customListCategoryId
  icon
  pipelineVisibility
  pipelineDateLabel
  pipelineDateField
  isArchived
  updateBehavior
}`);

export const usePatchCustomListItemMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PatchCustomListItemMutation,
        TError,
        PatchCustomListItemMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PatchCustomListItemMutation,
        TError,
        PatchCustomListItemMutationVariables,
        TContext
    >(
        ["patchCustomListItem"],
        (variables?: PatchCustomListItemMutationVariables) =>
            graphqlFetcher<
                PatchCustomListItemMutation,
                PatchCustomListItemMutationVariables
            >(PatchCustomListItemDocument, variables)(),
        options,
    );
};

export const PatchCustomListItemsDocument = new TypedDocumentString(`
    mutation patchCustomListItems($params: [PatchCustomListItemRequest!]!) {
  patchCustomListItems(params: $params) {
    ...CustomListItemResponse
  }
}
    fragment CustomListItemResponse on CustomListItem {
  id
  key
  name
  order
  customListCategoryId
  icon
  pipelineVisibility
  pipelineDateLabel
  pipelineDateField
  isArchived
  updateBehavior
}`);

export const usePatchCustomListItemsMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PatchCustomListItemsMutation,
        TError,
        PatchCustomListItemsMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PatchCustomListItemsMutation,
        TError,
        PatchCustomListItemsMutationVariables,
        TContext
    >(
        ["patchCustomListItems"],
        (variables?: PatchCustomListItemsMutationVariables) =>
            graphqlFetcher<
                PatchCustomListItemsMutation,
                PatchCustomListItemsMutationVariables
            >(PatchCustomListItemsDocument, variables)(),
        options,
    );
};

export const GraphqlDebugDocument = new TypedDocumentString(`
    query graphqlDebug($expectedResult: String!) {
  graphqlDebug(expectedResult: $expectedResult) {
    message
  }
}
    `);

export const useGraphqlDebugQuery = <TData = GraphqlDebugQuery, TError = unknown>(
    variables: GraphqlDebugQueryVariables,
    options?: UseQueryOptions<GraphqlDebugQuery, TError, TData>,
) => {
    return useQuery<GraphqlDebugQuery, TError, TData>(
        ["graphqlDebug", variables],
        graphqlFetcher<GraphqlDebugQuery, GraphqlDebugQueryVariables>(
            GraphqlDebugDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGraphqlDebugQuery = <TData = GraphqlDebugQuery, TError = unknown>(
    variables: GraphqlDebugQueryVariables,
    options?: UseInfiniteQueryOptions<GraphqlDebugQuery, TError, TData>,
) => {
    return useInfiniteQuery<GraphqlDebugQuery, TError, TData>(
        ["graphqlDebug.infinite", variables],
        metaData =>
            graphqlFetcher<GraphqlDebugQuery, GraphqlDebugQueryVariables>(
                GraphqlDebugDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const CreateDisclosureDocument = new TypedDocumentString(`
    mutation createDisclosure($clientId: PlacementId!) {
  createDisclosure(clientId: $clientId) {
    ...DisclosureFragment
  }
}
    fragment DisclosureFragment on Disclosure {
  id
  dateAdded
  publicUrl
  acknowledgements {
    ...DisclosureAcknowledgementFragment
  }
}
fragment DisclosureAcknowledgementFragment on DisclosureAcknowledgement {
  id
  name
  email
  phone
  agreed
  response
  dateAdded
  createdBy
  ipAddress
}`);

export const useCreateDisclosureMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateDisclosureMutation,
        TError,
        CreateDisclosureMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateDisclosureMutation,
        TError,
        CreateDisclosureMutationVariables,
        TContext
    >(
        ["createDisclosure"],
        (variables?: CreateDisclosureMutationVariables) =>
            graphqlFetcher<CreateDisclosureMutation, CreateDisclosureMutationVariables>(
                CreateDisclosureDocument,
                variables,
            )(),
        options,
    );
};

export const CreateDisclosureAcknowledgementDocument = new TypedDocumentString(`
    mutation createDisclosureAcknowledgement($disclosureId: DisclosureId!, $acknowledgement: DisclosureAcknowledgementInput!) {
  createDisclosureAcknowledgement(
    disclosureId: $disclosureId
    acknowledgement: $acknowledgement
  ) {
    ...DisclosureAcknowledgementFragment
  }
}
    fragment DisclosureAcknowledgementFragment on DisclosureAcknowledgement {
  id
  name
  email
  phone
  agreed
  response
  dateAdded
  createdBy
  ipAddress
}`);

export const useCreateDisclosureAcknowledgementMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        CreateDisclosureAcknowledgementMutation,
        TError,
        CreateDisclosureAcknowledgementMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateDisclosureAcknowledgementMutation,
        TError,
        CreateDisclosureAcknowledgementMutationVariables,
        TContext
    >(
        ["createDisclosureAcknowledgement"],
        (variables?: CreateDisclosureAcknowledgementMutationVariables) =>
            graphqlFetcher<
                CreateDisclosureAcknowledgementMutation,
                CreateDisclosureAcknowledgementMutationVariables
            >(CreateDisclosureAcknowledgementDocument, variables)(),
        options,
    );
};

export const UpdateEvergreenDataSourceDocument = new TypedDocumentString(`
    mutation updateEvergreenDataSource($input: EvergreenDataSourceInput!) {
  updateEvergreenDataSource(input: $input) {
    id
  }
}
    `);

export const useUpdateEvergreenDataSourceMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        UpdateEvergreenDataSourceMutation,
        TError,
        UpdateEvergreenDataSourceMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateEvergreenDataSourceMutation,
        TError,
        UpdateEvergreenDataSourceMutationVariables,
        TContext
    >(
        ["updateEvergreenDataSource"],
        (variables?: UpdateEvergreenDataSourceMutationVariables) =>
            graphqlFetcher<
                UpdateEvergreenDataSourceMutation,
                UpdateEvergreenDataSourceMutationVariables
            >(UpdateEvergreenDataSourceDocument, variables)(),
        options,
    );
};

export const UpdateEvergreenImportDocument = new TypedDocumentString(`
    mutation updateEvergreenImport($input: EvergreenImportInput!) {
  updateEvergreenImport(input: $input) {
    id
  }
}
    `);

export const useUpdateEvergreenImportMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateEvergreenImportMutation,
        TError,
        UpdateEvergreenImportMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateEvergreenImportMutation,
        TError,
        UpdateEvergreenImportMutationVariables,
        TContext
    >(
        ["updateEvergreenImport"],
        (variables?: UpdateEvergreenImportMutationVariables) =>
            graphqlFetcher<
                UpdateEvergreenImportMutation,
                UpdateEvergreenImportMutationVariables
            >(UpdateEvergreenImportDocument, variables)(),
        options,
    );
};

export const GetEvergreenDataSourcesDocument = new TypedDocumentString(`
    query getEvergreenDataSources {
  getEvergreenDataSources {
    id
    name
    lastSuccessfulRun
    updateFrequencyInDays
    updatedAt
    dateAdded
    updateRequested
    warningsSilenced
    warningsSnoozedUntil
    warningsSnoozeReason
    duplicatesDetectionWindowInDays
  }
}
    `);

export const useGetEvergreenDataSourcesQuery = <
    TData = GetEvergreenDataSourcesQuery,
    TError = unknown,
>(
    variables?: GetEvergreenDataSourcesQueryVariables,
    options?: UseQueryOptions<GetEvergreenDataSourcesQuery, TError, TData>,
) => {
    return useQuery<GetEvergreenDataSourcesQuery, TError, TData>(
        variables === undefined
            ? ["getEvergreenDataSources"]
            : ["getEvergreenDataSources", variables],
        graphqlFetcher<
            GetEvergreenDataSourcesQuery,
            GetEvergreenDataSourcesQueryVariables
        >(GetEvergreenDataSourcesDocument, variables),
        options,
    );
};

export const useInfiniteGetEvergreenDataSourcesQuery = <
    TData = GetEvergreenDataSourcesQuery,
    TError = unknown,
>(
    variables?: GetEvergreenDataSourcesQueryVariables,
    options?: UseInfiniteQueryOptions<GetEvergreenDataSourcesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetEvergreenDataSourcesQuery, TError, TData>(
        variables === undefined
            ? ["getEvergreenDataSources.infinite"]
            : ["getEvergreenDataSources.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetEvergreenDataSourcesQuery,
                GetEvergreenDataSourcesQueryVariables
            >(GetEvergreenDataSourcesDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetEvergreenDataSourceDocument = new TypedDocumentString(`
    query getEvergreenDataSource($name: String!) {
  getEvergreenDataSource(sourceName: $name) {
    id
    name
    lastSuccessfulRun
    updateFrequencyInDays
    warningsSilenced
    warningsSnoozedUntil
    warningsSnoozeReason
    duplicatesDetectionWindowInDays
    updatedAt
    dateAdded
    updateRequested
    imports {
      ...ImportFragment
    }
  }
}
    fragment ImportFragment on EvergreenImport {
  created
  dateAdded
  duplicates
  endedAt
  errors
  id
  message
  skips
  delisted
  sourceName
  startedAt
  updatedAt
}`);

export const useGetEvergreenDataSourceQuery = <
    TData = GetEvergreenDataSourceQuery,
    TError = unknown,
>(
    variables: GetEvergreenDataSourceQueryVariables,
    options?: UseQueryOptions<GetEvergreenDataSourceQuery, TError, TData>,
) => {
    return useQuery<GetEvergreenDataSourceQuery, TError, TData>(
        ["getEvergreenDataSource", variables],
        graphqlFetcher<GetEvergreenDataSourceQuery, GetEvergreenDataSourceQueryVariables>(
            GetEvergreenDataSourceDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetEvergreenDataSourceQuery = <
    TData = GetEvergreenDataSourceQuery,
    TError = unknown,
>(
    variables: GetEvergreenDataSourceQueryVariables,
    options?: UseInfiniteQueryOptions<GetEvergreenDataSourceQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetEvergreenDataSourceQuery, TError, TData>(
        ["getEvergreenDataSource.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetEvergreenDataSourceQuery,
                GetEvergreenDataSourceQueryVariables
            >(GetEvergreenDataSourceDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetEvergreenImportsDocument = new TypedDocumentString(`
    query getEvergreenImports($skip: Int!, $take: Int!) {
  getEvergreenImports(skip: $skip, take: $take) {
    ...ImportFragment
  }
}
    fragment ImportFragment on EvergreenImport {
  created
  dateAdded
  duplicates
  endedAt
  errors
  id
  message
  skips
  delisted
  sourceName
  startedAt
  updatedAt
}`);

export const useGetEvergreenImportsQuery = <
    TData = GetEvergreenImportsQuery,
    TError = unknown,
>(
    variables: GetEvergreenImportsQueryVariables,
    options?: UseQueryOptions<GetEvergreenImportsQuery, TError, TData>,
) => {
    return useQuery<GetEvergreenImportsQuery, TError, TData>(
        ["getEvergreenImports", variables],
        graphqlFetcher<GetEvergreenImportsQuery, GetEvergreenImportsQueryVariables>(
            GetEvergreenImportsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetEvergreenImportsQuery = <
    TData = GetEvergreenImportsQuery,
    TError = unknown,
>(
    variables: GetEvergreenImportsQueryVariables,
    options?: UseInfiniteQueryOptions<GetEvergreenImportsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetEvergreenImportsQuery, TError, TData>(
        ["getEvergreenImports.infinite", variables],
        metaData =>
            graphqlFetcher<GetEvergreenImportsQuery, GetEvergreenImportsQueryVariables>(
                GetEvergreenImportsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const EvergreenCommunitySearchDocument = new TypedDocumentString(`
    query evergreenCommunitySearch($params: EvergreenCommunitySearchRequest!) {
  evergreenCommunitySearch(params: $params) {
    hits {
      ...EvergreenCommunitySearchHitFragment
    }
    count
  }
}
    fragment EvergreenCommunitySearchHitFragment on EvergreenCommunitySearchHit {
  id
  name
  address
  city
  state
  zip
  county
  status
  licenseTypes
  thumbnailUrl
  aliases {
    id
    name
    status
    licenses {
      id
      jurisdiction
      licenseNumber
      status
      licenseTypes
    }
  }
  listings {
    id
    visibility
    preferredName
    website
    summary
    startingPrice
    phoneNumbers {
      number
    }
    amenities
    careTypes
    careServices
    evergreenCommunityId
  }
}`);

export const useEvergreenCommunitySearchQuery = <
    TData = EvergreenCommunitySearchQuery,
    TError = unknown,
>(
    variables: EvergreenCommunitySearchQueryVariables,
    options?: UseQueryOptions<EvergreenCommunitySearchQuery, TError, TData>,
) => {
    return useQuery<EvergreenCommunitySearchQuery, TError, TData>(
        ["evergreenCommunitySearch", variables],
        graphqlFetcher<
            EvergreenCommunitySearchQuery,
            EvergreenCommunitySearchQueryVariables
        >(EvergreenCommunitySearchDocument, variables),
        options,
    );
};

export const useInfiniteEvergreenCommunitySearchQuery = <
    TData = EvergreenCommunitySearchQuery,
    TError = unknown,
>(
    variables: EvergreenCommunitySearchQueryVariables,
    options?: UseInfiniteQueryOptions<EvergreenCommunitySearchQuery, TError, TData>,
) => {
    return useInfiniteQuery<EvergreenCommunitySearchQuery, TError, TData>(
        ["evergreenCommunitySearch.infinite", variables],
        metaData =>
            graphqlFetcher<
                EvergreenCommunitySearchQuery,
                EvergreenCommunitySearchQueryVariables
            >(EvergreenCommunitySearchDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetPopularEvergreenCommunitiesDocument = new TypedDocumentString(`
    query getPopularEvergreenCommunities($params: GetPopularEvergreenCommunitiesRequest!) {
  getPopularEvergreenCommunities(params: $params) {
    hits {
      community {
        ...EvergreenCommunitySearchHitFragment
      }
      placementCount
    }
    count
  }
}
    fragment EvergreenCommunitySearchHitFragment on EvergreenCommunitySearchHit {
  id
  name
  address
  city
  state
  zip
  county
  status
  licenseTypes
  thumbnailUrl
  aliases {
    id
    name
    status
    licenses {
      id
      jurisdiction
      licenseNumber
      status
      licenseTypes
    }
  }
  listings {
    id
    visibility
    preferredName
    website
    summary
    startingPrice
    phoneNumbers {
      number
    }
    amenities
    careTypes
    careServices
    evergreenCommunityId
  }
}`);

export const useGetPopularEvergreenCommunitiesQuery = <
    TData = GetPopularEvergreenCommunitiesQuery,
    TError = unknown,
>(
    variables: GetPopularEvergreenCommunitiesQueryVariables,
    options?: UseQueryOptions<GetPopularEvergreenCommunitiesQuery, TError, TData>,
) => {
    return useQuery<GetPopularEvergreenCommunitiesQuery, TError, TData>(
        ["getPopularEvergreenCommunities", variables],
        graphqlFetcher<
            GetPopularEvergreenCommunitiesQuery,
            GetPopularEvergreenCommunitiesQueryVariables
        >(GetPopularEvergreenCommunitiesDocument, variables),
        options,
    );
};

export const useInfiniteGetPopularEvergreenCommunitiesQuery = <
    TData = GetPopularEvergreenCommunitiesQuery,
    TError = unknown,
>(
    variables: GetPopularEvergreenCommunitiesQueryVariables,
    options?: UseInfiniteQueryOptions<GetPopularEvergreenCommunitiesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetPopularEvergreenCommunitiesQuery, TError, TData>(
        ["getPopularEvergreenCommunities.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetPopularEvergreenCommunitiesQuery,
                GetPopularEvergreenCommunitiesQueryVariables
            >(GetPopularEvergreenCommunitiesDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const CreateEvergreenCommunityListingDocument = new TypedDocumentString(`
    mutation createEvergreenCommunityListing($params: CreateEvergreenCommunityListingRequest!) {
  createEvergreenCommunityListing(params: $params)
}
    `);

export const useCreateEvergreenCommunityListingMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        CreateEvergreenCommunityListingMutation,
        TError,
        CreateEvergreenCommunityListingMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateEvergreenCommunityListingMutation,
        TError,
        CreateEvergreenCommunityListingMutationVariables,
        TContext
    >(
        ["createEvergreenCommunityListing"],
        (variables?: CreateEvergreenCommunityListingMutationVariables) =>
            graphqlFetcher<
                CreateEvergreenCommunityListingMutation,
                CreateEvergreenCommunityListingMutationVariables
            >(CreateEvergreenCommunityListingDocument, variables)(),
        options,
    );
};

export const RegenerateEvergreenListingSlugsDocument = new TypedDocumentString(`
    mutation regenerateEvergreenListingSlugs {
  regenerateEvergreenListingSlugs
}
    `);

export const useRegenerateEvergreenListingSlugsMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        RegenerateEvergreenListingSlugsMutation,
        TError,
        RegenerateEvergreenListingSlugsMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        RegenerateEvergreenListingSlugsMutation,
        TError,
        RegenerateEvergreenListingSlugsMutationVariables,
        TContext
    >(
        ["regenerateEvergreenListingSlugs"],
        (variables?: RegenerateEvergreenListingSlugsMutationVariables) =>
            graphqlFetcher<
                RegenerateEvergreenListingSlugsMutation,
                RegenerateEvergreenListingSlugsMutationVariables
            >(RegenerateEvergreenListingSlugsDocument, variables)(),
        options,
    );
};

export const GetListingsForEvergreenCommunityDocument = new TypedDocumentString(`
    query getListingsForEvergreenCommunity($evergreenCommunityId: EvergreenCommunityId!) {
  getListingsForEvergreenCommunity(evergreenCommunityId: $evergreenCommunityId) {
    id
    slug
    evergreenCommunityId
    preferredName
    website
    summary
    visibility
    startingPrice
    phoneNumbers {
      number
      label
    }
    amenities
    careTypes
    careServices
    reviews {
      id
      text
      webUri
      rating
      publishedAt
      authorDisplayName
      authorUri
      authorPhotoUri
      source
      externalId
      createdAt
      updatedAt
    }
    googlePlace {
      id
      externalId
      displayName
      googleMapsUrl
    }
    mostRecentDataRequestUrl
  }
}
    `);

export const useGetListingsForEvergreenCommunityQuery = <
    TData = GetListingsForEvergreenCommunityQuery,
    TError = unknown,
>(
    variables: GetListingsForEvergreenCommunityQueryVariables,
    options?: UseQueryOptions<GetListingsForEvergreenCommunityQuery, TError, TData>,
) => {
    return useQuery<GetListingsForEvergreenCommunityQuery, TError, TData>(
        ["getListingsForEvergreenCommunity", variables],
        graphqlFetcher<
            GetListingsForEvergreenCommunityQuery,
            GetListingsForEvergreenCommunityQueryVariables
        >(GetListingsForEvergreenCommunityDocument, variables),
        options,
    );
};

export const useInfiniteGetListingsForEvergreenCommunityQuery = <
    TData = GetListingsForEvergreenCommunityQuery,
    TError = unknown,
>(
    variables: GetListingsForEvergreenCommunityQueryVariables,
    options?: UseInfiniteQueryOptions<
        GetListingsForEvergreenCommunityQuery,
        TError,
        TData
    >,
) => {
    return useInfiniteQuery<GetListingsForEvergreenCommunityQuery, TError, TData>(
        ["getListingsForEvergreenCommunity.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetListingsForEvergreenCommunityQuery,
                GetListingsForEvergreenCommunityQueryVariables
            >(GetListingsForEvergreenCommunityDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetEvergreenCommunityDocument = new TypedDocumentString(`
    query getEvergreenCommunity($id: EvergreenCommunityId!) {
  getEvergreenCommunity(id: $id) {
    id
    name
    address
    city
    state
    zip
    county
    status
    thumbnailUrl
    activeLicenseTypes
    aliases {
      id
      name
      status
      licenses {
        id
        jurisdiction
        licenseNumber
        status
        licenseTypes
      }
    }
  }
}
    `);

export const useGetEvergreenCommunityQuery = <
    TData = GetEvergreenCommunityQuery,
    TError = unknown,
>(
    variables: GetEvergreenCommunityQueryVariables,
    options?: UseQueryOptions<GetEvergreenCommunityQuery, TError, TData>,
) => {
    return useQuery<GetEvergreenCommunityQuery, TError, TData>(
        ["getEvergreenCommunity", variables],
        graphqlFetcher<GetEvergreenCommunityQuery, GetEvergreenCommunityQueryVariables>(
            GetEvergreenCommunityDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetEvergreenCommunityQuery = <
    TData = GetEvergreenCommunityQuery,
    TError = unknown,
>(
    variables: GetEvergreenCommunityQueryVariables,
    options?: UseInfiniteQueryOptions<GetEvergreenCommunityQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetEvergreenCommunityQuery, TError, TData>(
        ["getEvergreenCommunity.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetEvergreenCommunityQuery,
                GetEvergreenCommunityQueryVariables
            >(GetEvergreenCommunityDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const PatchEvergreenCommunityListingDocument = new TypedDocumentString(`
    mutation patchEvergreenCommunityListing($params: PatchEvergreenCommunityListingRequest!) {
  patchEvergreenCommunityListing(params: $params) {
    id
    slug
    evergreenCommunityId
    preferredName
    website
    summary
    visibility
    startingPrice
    phoneNumbers {
      number
      label
    }
    amenities
    careTypes
    careServices
  }
}
    `);

export const usePatchEvergreenCommunityListingMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        PatchEvergreenCommunityListingMutation,
        TError,
        PatchEvergreenCommunityListingMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PatchEvergreenCommunityListingMutation,
        TError,
        PatchEvergreenCommunityListingMutationVariables,
        TContext
    >(
        ["patchEvergreenCommunityListing"],
        (variables?: PatchEvergreenCommunityListingMutationVariables) =>
            graphqlFetcher<
                PatchEvergreenCommunityListingMutation,
                PatchEvergreenCommunityListingMutationVariables
            >(PatchEvergreenCommunityListingDocument, variables)(),
        options,
    );
};

export const DeleteEvergreenCommunityListingDocument = new TypedDocumentString(`
    mutation deleteEvergreenCommunityListing($id: EvergreenCommunityListingId!) {
  deleteEvergreenCommunityListing(id: $id)
}
    `);

export const useDeleteEvergreenCommunityListingMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        DeleteEvergreenCommunityListingMutation,
        TError,
        DeleteEvergreenCommunityListingMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteEvergreenCommunityListingMutation,
        TError,
        DeleteEvergreenCommunityListingMutationVariables,
        TContext
    >(
        ["deleteEvergreenCommunityListing"],
        (variables?: DeleteEvergreenCommunityListingMutationVariables) =>
            graphqlFetcher<
                DeleteEvergreenCommunityListingMutation,
                DeleteEvergreenCommunityListingMutationVariables
            >(DeleteEvergreenCommunityListingDocument, variables)(),
        options,
    );
};

export const SynchronizeEvergreenListingWithGooglePlaceDocument =
    new TypedDocumentString(`
    mutation synchronizeEvergreenListingWithGooglePlace($id: EvergreenCommunityListingId!) {
  synchronizeEvergreenListingWithGooglePlace(id: $id)
}
    `);

export const useSynchronizeEvergreenListingWithGooglePlaceMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        SynchronizeEvergreenListingWithGooglePlaceMutation,
        TError,
        SynchronizeEvergreenListingWithGooglePlaceMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SynchronizeEvergreenListingWithGooglePlaceMutation,
        TError,
        SynchronizeEvergreenListingWithGooglePlaceMutationVariables,
        TContext
    >(
        ["synchronizeEvergreenListingWithGooglePlace"],
        (variables?: SynchronizeEvergreenListingWithGooglePlaceMutationVariables) =>
            graphqlFetcher<
                SynchronizeEvergreenListingWithGooglePlaceMutation,
                SynchronizeEvergreenListingWithGooglePlaceMutationVariables
            >(SynchronizeEvergreenListingWithGooglePlaceDocument, variables)(),
        options,
    );
};

export const UpdateGooglePlaceDocument = new TypedDocumentString(`
    mutation updateGooglePlace($id: EvergreenCommunityListingId!, $externalId: String!) {
  updateGooglePlace(id: $id, externalId: $externalId)
}
    `);

export const useUpdateGooglePlaceMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateGooglePlaceMutation,
        TError,
        UpdateGooglePlaceMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateGooglePlaceMutation,
        TError,
        UpdateGooglePlaceMutationVariables,
        TContext
    >(
        ["updateGooglePlace"],
        (variables?: UpdateGooglePlaceMutationVariables) =>
            graphqlFetcher<UpdateGooglePlaceMutation, UpdateGooglePlaceMutationVariables>(
                UpdateGooglePlaceDocument,
                variables,
            )(),
        options,
    );
};

export const GenerateEvergreenCommunityInsightsDocument = new TypedDocumentString(`
    mutation generateEvergreenCommunityInsights($id: EvergreenCommunityId!) {
  generateEvergreenCommunityInsights(id: $id)
}
    `);

export const useGenerateEvergreenCommunityInsightsMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        GenerateEvergreenCommunityInsightsMutation,
        TError,
        GenerateEvergreenCommunityInsightsMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        GenerateEvergreenCommunityInsightsMutation,
        TError,
        GenerateEvergreenCommunityInsightsMutationVariables,
        TContext
    >(
        ["generateEvergreenCommunityInsights"],
        (variables?: GenerateEvergreenCommunityInsightsMutationVariables) =>
            graphqlFetcher<
                GenerateEvergreenCommunityInsightsMutation,
                GenerateEvergreenCommunityInsightsMutationVariables
            >(GenerateEvergreenCommunityInsightsDocument, variables)(),
        options,
    );
};

export const SearchEvergreenCommunityListingsDocument = new TypedDocumentString(`
    query searchEvergreenCommunityListings($params: SearchEvergreenCommunityListingsRequest!) {
  searchEvergreenCommunityListings(params: $params) {
    id
    slug
    evergreenCommunityId
    preferredName
    website
    summary
    startingPrice
    visibility
    phoneNumbers {
      number
      label
    }
    amenities
    careTypes
    careServices
    evergreenCommunity {
      id
      address
      city
      state
      zip
      county
      lat
      lng
    }
  }
}
    `);

export const useSearchEvergreenCommunityListingsQuery = <
    TData = SearchEvergreenCommunityListingsQuery,
    TError = unknown,
>(
    variables: SearchEvergreenCommunityListingsQueryVariables,
    options?: UseQueryOptions<SearchEvergreenCommunityListingsQuery, TError, TData>,
) => {
    return useQuery<SearchEvergreenCommunityListingsQuery, TError, TData>(
        ["searchEvergreenCommunityListings", variables],
        graphqlFetcher<
            SearchEvergreenCommunityListingsQuery,
            SearchEvergreenCommunityListingsQueryVariables
        >(SearchEvergreenCommunityListingsDocument, variables),
        options,
    );
};

export const useInfiniteSearchEvergreenCommunityListingsQuery = <
    TData = SearchEvergreenCommunityListingsQuery,
    TError = unknown,
>(
    variables: SearchEvergreenCommunityListingsQueryVariables,
    options?: UseInfiniteQueryOptions<
        SearchEvergreenCommunityListingsQuery,
        TError,
        TData
    >,
) => {
    return useInfiniteQuery<SearchEvergreenCommunityListingsQuery, TError, TData>(
        ["searchEvergreenCommunityListings.infinite", variables],
        metaData =>
            graphqlFetcher<
                SearchEvergreenCommunityListingsQuery,
                SearchEvergreenCommunityListingsQueryVariables
            >(SearchEvergreenCommunityListingsDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const UpdateFeatureFlagDocument = new TypedDocumentString(`
    mutation updateFeatureFlag($name: ID!, $status: FeatureStatus!) {
  updateFeatureFlag(name: $name, status: $status) {
    name
    status
  }
}
    `);

export const useUpdateFeatureFlagMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateFeatureFlagMutation,
        TError,
        UpdateFeatureFlagMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateFeatureFlagMutation,
        TError,
        UpdateFeatureFlagMutationVariables,
        TContext
    >(
        ["updateFeatureFlag"],
        (variables?: UpdateFeatureFlagMutationVariables) =>
            graphqlFetcher<UpdateFeatureFlagMutation, UpdateFeatureFlagMutationVariables>(
                UpdateFeatureFlagDocument,
                variables,
            )(),
        options,
    );
};

export const GetGlobalFeaturesDocument = new TypedDocumentString(`
    query getGlobalFeatures {
  getGlobalFeatures {
    skus
    features {
      actual {
        name
        status
      }
      skus
      key
      friendlyTitle
      description
      createdOn
      intent
      packageEnabled
    }
  }
}
    `);

export const useGetGlobalFeaturesQuery = <
    TData = GetGlobalFeaturesQuery,
    TError = unknown,
>(
    variables?: GetGlobalFeaturesQueryVariables,
    options?: UseQueryOptions<GetGlobalFeaturesQuery, TError, TData>,
) => {
    return useQuery<GetGlobalFeaturesQuery, TError, TData>(
        variables === undefined
            ? ["getGlobalFeatures"]
            : ["getGlobalFeatures", variables],
        graphqlFetcher<GetGlobalFeaturesQuery, GetGlobalFeaturesQueryVariables>(
            GetGlobalFeaturesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetGlobalFeaturesQuery = <
    TData = GetGlobalFeaturesQuery,
    TError = unknown,
>(
    variables?: GetGlobalFeaturesQueryVariables,
    options?: UseInfiniteQueryOptions<GetGlobalFeaturesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetGlobalFeaturesQuery, TError, TData>(
        variables === undefined
            ? ["getGlobalFeatures.infinite"]
            : ["getGlobalFeatures.infinite", variables],
        metaData =>
            graphqlFetcher<GetGlobalFeaturesQuery, GetGlobalFeaturesQueryVariables>(
                GetGlobalFeaturesDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetFeaturesForRegionDocument = new TypedDocumentString(`
    query getFeaturesForRegion($params: GetRegionFeaturesRequest!) {
  getFeaturesForRegion(params: $params) {
    features {
      name
      enabled
    }
  }
}
    `);

export const useGetFeaturesForRegionQuery = <
    TData = GetFeaturesForRegionQuery,
    TError = unknown,
>(
    variables: GetFeaturesForRegionQueryVariables,
    options?: UseQueryOptions<GetFeaturesForRegionQuery, TError, TData>,
) => {
    return useQuery<GetFeaturesForRegionQuery, TError, TData>(
        ["getFeaturesForRegion", variables],
        graphqlFetcher<GetFeaturesForRegionQuery, GetFeaturesForRegionQueryVariables>(
            GetFeaturesForRegionDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetFeaturesForRegionQuery = <
    TData = GetFeaturesForRegionQuery,
    TError = unknown,
>(
    variables: GetFeaturesForRegionQueryVariables,
    options?: UseInfiniteQueryOptions<GetFeaturesForRegionQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetFeaturesForRegionQuery, TError, TData>(
        ["getFeaturesForRegion.infinite", variables],
        metaData =>
            graphqlFetcher<GetFeaturesForRegionQuery, GetFeaturesForRegionQueryVariables>(
                GetFeaturesForRegionDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetFilesDocument = new TypedDocumentString(`
    query getFiles($entityId: FileEntityId!) {
  getFiles(entityId: $entityId) {
    files {
      ...FileFragment
    }
  }
}
    fragment FileFragment on File {
  id
  entityId
  originalFilename
  mimeType
  description
  byteLength
  shareWithClient
  shareWithCommunity
  shareWithReferral
  shareWithHomeCareLead
  createdAt
  createdBy
  updatedAt
  updatedBy
  hasSignatureTemplate
  isGlobal
  managementLevel
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}`);

export const useGetFilesQuery = <TData = GetFilesQuery, TError = unknown>(
    variables: GetFilesQueryVariables,
    options?: UseQueryOptions<GetFilesQuery, TError, TData>,
) => {
    return useQuery<GetFilesQuery, TError, TData>(
        ["getFiles", variables],
        graphqlFetcher<GetFilesQuery, GetFilesQueryVariables>(
            GetFilesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetFilesQuery = <TData = GetFilesQuery, TError = unknown>(
    variables: GetFilesQueryVariables,
    options?: UseInfiniteQueryOptions<GetFilesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetFilesQuery, TError, TData>(
        ["getFiles.infinite", variables],
        metaData =>
            graphqlFetcher<GetFilesQuery, GetFilesQueryVariables>(GetFilesDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetFileDocument = new TypedDocumentString(`
    query getFile($fileId: FileId!) {
  getFile(fileId: $fileId) {
    ...FileFragment
    outstandingSignatureRequestsCount
    accessControls {
      id
      groupId
    }
  }
}
    fragment FileFragment on File {
  id
  entityId
  originalFilename
  mimeType
  description
  byteLength
  shareWithClient
  shareWithCommunity
  shareWithReferral
  shareWithHomeCareLead
  createdAt
  createdBy
  updatedAt
  updatedBy
  hasSignatureTemplate
  isGlobal
  managementLevel
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}`);

export const useGetFileQuery = <TData = GetFileQuery, TError = unknown>(
    variables: GetFileQueryVariables,
    options?: UseQueryOptions<GetFileQuery, TError, TData>,
) => {
    return useQuery<GetFileQuery, TError, TData>(
        ["getFile", variables],
        graphqlFetcher<GetFileQuery, GetFileQueryVariables>(GetFileDocument, variables),
        options,
    );
};

export const useInfiniteGetFileQuery = <TData = GetFileQuery, TError = unknown>(
    variables: GetFileQueryVariables,
    options?: UseInfiniteQueryOptions<GetFileQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetFileQuery, TError, TData>(
        ["getFile.infinite", variables],
        metaData =>
            graphqlFetcher<GetFileQuery, GetFileQueryVariables>(GetFileDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const UpdateFileDocument = new TypedDocumentString(`
    mutation updateFile($fileId: FileId!, $details: FileUpdateInput!, $accessControls: [AccessControlInput!], $regionVisibility: EntityRegionVisibilityInput) {
  updateFile(
    fileId: $fileId
    details: $details
    accessControls: $accessControls
    regionVisibility: $regionVisibility
  ) {
    ...FileFragment
    accessControls {
      id
      groupId
    }
    outstandingSignatureRequestsCount
  }
}
    fragment FileFragment on File {
  id
  entityId
  originalFilename
  mimeType
  description
  byteLength
  shareWithClient
  shareWithCommunity
  shareWithReferral
  shareWithHomeCareLead
  createdAt
  createdBy
  updatedAt
  updatedBy
  hasSignatureTemplate
  isGlobal
  managementLevel
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}`);

export const useUpdateFileMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateFileMutation,
        TError,
        UpdateFileMutationVariables,
        TContext
    >,
) => {
    return useMutation<UpdateFileMutation, TError, UpdateFileMutationVariables, TContext>(
        ["updateFile"],
        (variables?: UpdateFileMutationVariables) =>
            graphqlFetcher<UpdateFileMutation, UpdateFileMutationVariables>(
                UpdateFileDocument,
                variables,
            )(),
        options,
    );
};

export const DeleteFileDocument = new TypedDocumentString(`
    mutation deleteFile($fileId: FileId!) {
  deleteFile(fileId: $fileId)
}
    `);

export const useDeleteFileMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteFileMutation,
        TError,
        DeleteFileMutationVariables,
        TContext
    >,
) => {
    return useMutation<DeleteFileMutation, TError, DeleteFileMutationVariables, TContext>(
        ["deleteFile"],
        (variables?: DeleteFileMutationVariables) =>
            graphqlFetcher<DeleteFileMutation, DeleteFileMutationVariables>(
                DeleteFileDocument,
                variables,
            )(),
        options,
    );
};

export const DeleteHostedFormDocument = new TypedDocumentString(`
    mutation deleteHostedForm($hostedFormId: HostedFormId!) {
  deleteHostedForm(hostedFormId: $hostedFormId)
}
    `);

export const useDeleteHostedFormMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteHostedFormMutation,
        TError,
        DeleteHostedFormMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteHostedFormMutation,
        TError,
        DeleteHostedFormMutationVariables,
        TContext
    >(
        ["deleteHostedForm"],
        (variables?: DeleteHostedFormMutationVariables) =>
            graphqlFetcher<DeleteHostedFormMutation, DeleteHostedFormMutationVariables>(
                DeleteHostedFormDocument,
                variables,
            )(),
        options,
    );
};

export const CreateHostedFormDocument = new TypedDocumentString(`
    mutation createHostedForm {
  createHostedForm {
    id
  }
}
    `);

export const useCreateHostedFormMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateHostedFormMutation,
        TError,
        CreateHostedFormMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateHostedFormMutation,
        TError,
        CreateHostedFormMutationVariables,
        TContext
    >(
        ["createHostedForm"],
        (variables?: CreateHostedFormMutationVariables) =>
            graphqlFetcher<CreateHostedFormMutation, CreateHostedFormMutationVariables>(
                CreateHostedFormDocument,
                variables,
            )(),
        options,
    );
};

export const PatchHostedFormDocument = new TypedDocumentString(`
    mutation patchHostedForm($params: PatchHostedFormRequest!) {
  patchHostedForm(params: $params) {
    id
  }
}
    `);

export const usePatchHostedFormMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PatchHostedFormMutation,
        TError,
        PatchHostedFormMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PatchHostedFormMutation,
        TError,
        PatchHostedFormMutationVariables,
        TContext
    >(
        ["patchHostedForm"],
        (variables?: PatchHostedFormMutationVariables) =>
            graphqlFetcher<PatchHostedFormMutation, PatchHostedFormMutationVariables>(
                PatchHostedFormDocument,
                variables,
            )(),
        options,
    );
};

export const GetHostedFormsDocument = new TypedDocumentString(`
    query getHostedForms($regionId: RegionId!) {
  getHostedForms(regionId: $regionId) {
    id
    name
    displayName
    url
  }
}
    `);

export const useGetHostedFormsQuery = <TData = GetHostedFormsQuery, TError = unknown>(
    variables: GetHostedFormsQueryVariables,
    options?: UseQueryOptions<GetHostedFormsQuery, TError, TData>,
) => {
    return useQuery<GetHostedFormsQuery, TError, TData>(
        ["getHostedForms", variables],
        graphqlFetcher<GetHostedFormsQuery, GetHostedFormsQueryVariables>(
            GetHostedFormsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetHostedFormsQuery = <
    TData = GetHostedFormsQuery,
    TError = unknown,
>(
    variables: GetHostedFormsQueryVariables,
    options?: UseInfiniteQueryOptions<GetHostedFormsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetHostedFormsQuery, TError, TData>(
        ["getHostedForms.infinite", variables],
        metaData =>
            graphqlFetcher<GetHostedFormsQuery, GetHostedFormsQueryVariables>(
                GetHostedFormsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetHostedFormByIdDocument = new TypedDocumentString(`
    query getHostedFormById($regionId: RegionId!, $id: HostedFormId!) {
  getHostedFormById(regionId: $regionId, id: $id) {
    id
    name
    displayName
    dateAdded
    configuration {
      style
      fileUploadEnabled
      regions {
        regionId
        userId
        recipientUserIds
      }
    }
    layoutSections {
      id
      order
      sectionKey
      visible
      layoutItems {
        id
        order
        questionId
        entityFieldName
        showIfBlank
      }
    }
    customizable
    url
    slugs {
      slug
      id
      regionId
      referenceContactId
      referenceBusinessId
      hostedFormId
    }
  }
}
    `);

export const useGetHostedFormByIdQuery = <
    TData = GetHostedFormByIdQuery,
    TError = unknown,
>(
    variables: GetHostedFormByIdQueryVariables,
    options?: UseQueryOptions<GetHostedFormByIdQuery, TError, TData>,
) => {
    return useQuery<GetHostedFormByIdQuery, TError, TData>(
        ["getHostedFormById", variables],
        graphqlFetcher<GetHostedFormByIdQuery, GetHostedFormByIdQueryVariables>(
            GetHostedFormByIdDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetHostedFormByIdQuery = <
    TData = GetHostedFormByIdQuery,
    TError = unknown,
>(
    variables: GetHostedFormByIdQueryVariables,
    options?: UseInfiniteQueryOptions<GetHostedFormByIdQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetHostedFormByIdQuery, TError, TData>(
        ["getHostedFormById.infinite", variables],
        metaData =>
            graphqlFetcher<GetHostedFormByIdQuery, GetHostedFormByIdQueryVariables>(
                GetHostedFormByIdDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const SendHostedFormWithTokenDocument = new TypedDocumentString(`
    mutation sendHostedFormWithToken($params: SendHostedFormWithTokenRequest!) {
  sendHostedFormWithToken(params: $params) {
    token {
      id
      expiresAt
      updatedAt
      dateAdded
      createdBy
      updatedBy
    }
  }
}
    `);

export const useSendHostedFormWithTokenMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        SendHostedFormWithTokenMutation,
        TError,
        SendHostedFormWithTokenMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SendHostedFormWithTokenMutation,
        TError,
        SendHostedFormWithTokenMutationVariables,
        TContext
    >(
        ["sendHostedFormWithToken"],
        (variables?: SendHostedFormWithTokenMutationVariables) =>
            graphqlFetcher<
                SendHostedFormWithTokenMutation,
                SendHostedFormWithTokenMutationVariables
            >(SendHostedFormWithTokenDocument, variables)(),
        options,
    );
};

export const SendHostedFormWithPermalinkDocument = new TypedDocumentString(`
    mutation sendHostedFormWithPermalink($params: SendHostedFormWithPermalinkRequest!) {
  sendHostedFormWithPermalink(params: $params)
}
    `);

export const useSendHostedFormWithPermalinkMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        SendHostedFormWithPermalinkMutation,
        TError,
        SendHostedFormWithPermalinkMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SendHostedFormWithPermalinkMutation,
        TError,
        SendHostedFormWithPermalinkMutationVariables,
        TContext
    >(
        ["sendHostedFormWithPermalink"],
        (variables?: SendHostedFormWithPermalinkMutationVariables) =>
            graphqlFetcher<
                SendHostedFormWithPermalinkMutation,
                SendHostedFormWithPermalinkMutationVariables
            >(SendHostedFormWithPermalinkDocument, variables)(),
        options,
    );
};

export const GetHostedFormTokensForEntityDocument = new TypedDocumentString(`
    query getHostedFormTokensForEntity($entityId: HostedFormEntityId!) {
  getHostedFormTokensForEntity(entityId: $entityId) {
    id
    expiresAt
    completedAt
    hostedFormId
    updatedAt
    dateAdded
    createdBy
    updatedBy
    status
    sentMessages {
      to
    }
  }
}
    `);

export const useGetHostedFormTokensForEntityQuery = <
    TData = GetHostedFormTokensForEntityQuery,
    TError = unknown,
>(
    variables: GetHostedFormTokensForEntityQueryVariables,
    options?: UseQueryOptions<GetHostedFormTokensForEntityQuery, TError, TData>,
) => {
    return useQuery<GetHostedFormTokensForEntityQuery, TError, TData>(
        ["getHostedFormTokensForEntity", variables],
        graphqlFetcher<
            GetHostedFormTokensForEntityQuery,
            GetHostedFormTokensForEntityQueryVariables
        >(GetHostedFormTokensForEntityDocument, variables),
        options,
    );
};

export const useInfiniteGetHostedFormTokensForEntityQuery = <
    TData = GetHostedFormTokensForEntityQuery,
    TError = unknown,
>(
    variables: GetHostedFormTokensForEntityQueryVariables,
    options?: UseInfiniteQueryOptions<GetHostedFormTokensForEntityQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetHostedFormTokensForEntityQuery, TError, TData>(
        ["getHostedFormTokensForEntity.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetHostedFormTokensForEntityQuery,
                GetHostedFormTokensForEntityQueryVariables
            >(GetHostedFormTokensForEntityDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const DeleteHostedFormTokenDocument = new TypedDocumentString(`
    mutation deleteHostedFormToken($tokenId: HostedFormTokenId!) {
  deleteHostedFormToken(tokenId: $tokenId)
}
    `);

export const useDeleteHostedFormTokenMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteHostedFormTokenMutation,
        TError,
        DeleteHostedFormTokenMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteHostedFormTokenMutation,
        TError,
        DeleteHostedFormTokenMutationVariables,
        TContext
    >(
        ["deleteHostedFormToken"],
        (variables?: DeleteHostedFormTokenMutationVariables) =>
            graphqlFetcher<
                DeleteHostedFormTokenMutation,
                DeleteHostedFormTokenMutationVariables
            >(DeleteHostedFormTokenDocument, variables)(),
        options,
    );
};

export const CheckHostedFormSlugDocument = new TypedDocumentString(`
    query checkHostedFormSlug($slug: String!) {
  checkHostedFormSlug(slug: $slug) {
    available
    errorMessage
  }
}
    `);

export const useCheckHostedFormSlugQuery = <
    TData = CheckHostedFormSlugQuery,
    TError = unknown,
>(
    variables: CheckHostedFormSlugQueryVariables,
    options?: UseQueryOptions<CheckHostedFormSlugQuery, TError, TData>,
) => {
    return useQuery<CheckHostedFormSlugQuery, TError, TData>(
        ["checkHostedFormSlug", variables],
        graphqlFetcher<CheckHostedFormSlugQuery, CheckHostedFormSlugQueryVariables>(
            CheckHostedFormSlugDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteCheckHostedFormSlugQuery = <
    TData = CheckHostedFormSlugQuery,
    TError = unknown,
>(
    variables: CheckHostedFormSlugQueryVariables,
    options?: UseInfiniteQueryOptions<CheckHostedFormSlugQuery, TError, TData>,
) => {
    return useInfiniteQuery<CheckHostedFormSlugQuery, TError, TData>(
        ["checkHostedFormSlug.infinite", variables],
        metaData =>
            graphqlFetcher<CheckHostedFormSlugQuery, CheckHostedFormSlugQueryVariables>(
                CheckHostedFormSlugDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const ClaimHostedFormSlugDocument = new TypedDocumentString(`
    mutation claimHostedFormSlug($claim: HostedFormSlugClaim!) {
  claimHostedFormSlug(claim: $claim)
}
    `);

export const useClaimHostedFormSlugMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        ClaimHostedFormSlugMutation,
        TError,
        ClaimHostedFormSlugMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        ClaimHostedFormSlugMutation,
        TError,
        ClaimHostedFormSlugMutationVariables,
        TContext
    >(
        ["claimHostedFormSlug"],
        (variables?: ClaimHostedFormSlugMutationVariables) =>
            graphqlFetcher<
                ClaimHostedFormSlugMutation,
                ClaimHostedFormSlugMutationVariables
            >(ClaimHostedFormSlugDocument, variables)(),
        options,
    );
};

export const DeleteHostedFormSlugDocument = new TypedDocumentString(`
    mutation deleteHostedFormSlug($hostedFormSlugId: HostedFormSlugId!) {
  deleteHostedFormSlug(hostedFormSlugId: $hostedFormSlugId)
}
    `);

export const useDeleteHostedFormSlugMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteHostedFormSlugMutation,
        TError,
        DeleteHostedFormSlugMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteHostedFormSlugMutation,
        TError,
        DeleteHostedFormSlugMutationVariables,
        TContext
    >(
        ["deleteHostedFormSlug"],
        (variables?: DeleteHostedFormSlugMutationVariables) =>
            graphqlFetcher<
                DeleteHostedFormSlugMutation,
                DeleteHostedFormSlugMutationVariables
            >(DeleteHostedFormSlugDocument, variables)(),
        options,
    );
};

export const RelocateImageDocument = new TypedDocumentString(`
    mutation relocateImage($payload: RelocateImageInput!) {
  relocateImage(payload: $payload) {
    id
    src
  }
}
    `);

export const useRelocateImageMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        RelocateImageMutation,
        TError,
        RelocateImageMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        RelocateImageMutation,
        TError,
        RelocateImageMutationVariables,
        TContext
    >(
        ["relocateImage"],
        (variables?: RelocateImageMutationVariables) =>
            graphqlFetcher<RelocateImageMutation, RelocateImageMutationVariables>(
                RelocateImageDocument,
                variables,
            )(),
        options,
    );
};

export const CreateInvoiceDocument = new TypedDocumentString(`
    mutation createInvoice($clientId: PlacementId!, $serial: String, $amountCents: CurrencyInCents!, $status: String!, $sentDate: String, $dueDate: String, $generatePdf: Boolean!, $notes: String, $message: OutboundMessage, $lineItems: [InvoiceLineItemInput!], $paymentSchedule: InvoicePaymentScheduleInput, $externalReference: String, $commissions: [InvoiceCommissionCreateInput!]) {
  createInvoice(
    clientId: $clientId
    serial: $serial
    amountCents: $amountCents
    status: $status
    sentDate: $sentDate
    dueDate: $dueDate
    generatePdf: $generatePdf
    notes: $notes
    message: $message
    lineItems: $lineItems
    paymentSchedule: $paymentSchedule
    externalReference: $externalReference
    commissions: $commissions
  ) {
    invoices {
      id
      serial
    }
    errors {
      message
      fields {
        name
        message
      }
    }
  }
}
    `);

export const useCreateInvoiceMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateInvoiceMutation,
        TError,
        CreateInvoiceMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateInvoiceMutation,
        TError,
        CreateInvoiceMutationVariables,
        TContext
    >(
        ["createInvoice"],
        (variables?: CreateInvoiceMutationVariables) =>
            graphqlFetcher<CreateInvoiceMutation, CreateInvoiceMutationVariables>(
                CreateInvoiceDocument,
                variables,
            )(),
        options,
    );
};

export const ResendInvoiceDocument = new TypedDocumentString(`
    mutation resendInvoice($invoiceId: InvoiceId!, $message: OutboundMessage!) {
  resendInvoice(invoiceId: $invoiceId, message: $message) {
    invoices {
      id
      serial
    }
    errors {
      message
      fields {
        name
        message
      }
    }
  }
}
    `);

export const useResendInvoiceMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        ResendInvoiceMutation,
        TError,
        ResendInvoiceMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        ResendInvoiceMutation,
        TError,
        ResendInvoiceMutationVariables,
        TContext
    >(
        ["resendInvoice"],
        (variables?: ResendInvoiceMutationVariables) =>
            graphqlFetcher<ResendInvoiceMutation, ResendInvoiceMutationVariables>(
                ResendInvoiceDocument,
                variables,
            )(),
        options,
    );
};

export const GenerateInvoiceDocument = new TypedDocumentString(`
    mutation generateInvoice($id: InvoiceId!) {
  generateInvoice(invoiceId: $id) {
    invoices {
      id
      serial
    }
    errors {
      message
      fields {
        name
        message
      }
    }
  }
}
    `);

export const useGenerateInvoiceMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        GenerateInvoiceMutation,
        TError,
        GenerateInvoiceMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        GenerateInvoiceMutation,
        TError,
        GenerateInvoiceMutationVariables,
        TContext
    >(
        ["generateInvoice"],
        (variables?: GenerateInvoiceMutationVariables) =>
            graphqlFetcher<GenerateInvoiceMutation, GenerateInvoiceMutationVariables>(
                GenerateInvoiceDocument,
                variables,
            )(),
        options,
    );
};

export const UpdateInvoiceDocument = new TypedDocumentString(`
    mutation updateInvoice($invoiceId: InvoiceId!, $status: String!, $sentDate: String, $paidDate: String, $serial: String!, $amountCents: CurrencyInCents!, $notes: String, $dueDate: String, $lineItems: [InvoiceLineItemInput!], $paymentSchedule: InvoicePaymentScheduleInput, $externalReference: String) {
  updateInvoice(
    invoiceId: $invoiceId
    status: $status
    sentDate: $sentDate
    paidDate: $paidDate
    serial: $serial
    amountCents: $amountCents
    notes: $notes
    dueDate: $dueDate
    lineItems: $lineItems
    paymentSchedule: $paymentSchedule
    externalReference: $externalReference
  ) {
    invoices {
      id
      placementId
      communityId
    }
    errors {
      message
      fields {
        name
        message
      }
    }
  }
}
    `);

export const useUpdateInvoiceMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateInvoiceMutation,
        TError,
        UpdateInvoiceMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateInvoiceMutation,
        TError,
        UpdateInvoiceMutationVariables,
        TContext
    >(
        ["updateInvoice"],
        (variables?: UpdateInvoiceMutationVariables) =>
            graphqlFetcher<UpdateInvoiceMutation, UpdateInvoiceMutationVariables>(
                UpdateInvoiceDocument,
                variables,
            )(),
        options,
    );
};

export const CreateExternalInvoicesDocument = new TypedDocumentString(`
    mutation createExternalInvoices($params: ExternalInvoicesCreateRequest!) {
  createExternalInvoices(params: $params) {
    invoices {
      id
      serial
    }
    errors {
      message
      fields {
        name
        message
      }
    }
  }
}
    `);

export const useCreateExternalInvoicesMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateExternalInvoicesMutation,
        TError,
        CreateExternalInvoicesMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateExternalInvoicesMutation,
        TError,
        CreateExternalInvoicesMutationVariables,
        TContext
    >(
        ["createExternalInvoices"],
        (variables?: CreateExternalInvoicesMutationVariables) =>
            graphqlFetcher<
                CreateExternalInvoicesMutation,
                CreateExternalInvoicesMutationVariables
            >(CreateExternalInvoicesDocument, variables)(),
        options,
    );
};

export const GetInvoiceCreateExperienceDocument = new TypedDocumentString(`
    query getInvoiceCreateExperience($regionId: RegionId!) {
  getInvoiceCreateExperience(regionId: $regionId) {
    experience
  }
}
    `);

export const useGetInvoiceCreateExperienceQuery = <
    TData = GetInvoiceCreateExperienceQuery,
    TError = unknown,
>(
    variables: GetInvoiceCreateExperienceQueryVariables,
    options?: UseQueryOptions<GetInvoiceCreateExperienceQuery, TError, TData>,
) => {
    return useQuery<GetInvoiceCreateExperienceQuery, TError, TData>(
        ["getInvoiceCreateExperience", variables],
        graphqlFetcher<
            GetInvoiceCreateExperienceQuery,
            GetInvoiceCreateExperienceQueryVariables
        >(GetInvoiceCreateExperienceDocument, variables),
        options,
    );
};

export const useInfiniteGetInvoiceCreateExperienceQuery = <
    TData = GetInvoiceCreateExperienceQuery,
    TError = unknown,
>(
    variables: GetInvoiceCreateExperienceQueryVariables,
    options?: UseInfiniteQueryOptions<GetInvoiceCreateExperienceQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetInvoiceCreateExperienceQuery, TError, TData>(
        ["getInvoiceCreateExperience.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetInvoiceCreateExperienceQuery,
                GetInvoiceCreateExperienceQueryVariables
            >(GetInvoiceCreateExperienceDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetExternalInvoiceAccountSettingsDocument = new TypedDocumentString(`
    query getExternalInvoiceAccountSettings($params: GetExternalInvoiceAccountSettingsRequest!) {
  getExternalInvoiceAccountSettings(params: $params) {
    paymentTerms {
      id
      name
      dueDays
      default
    }
  }
}
    `);

export const useGetExternalInvoiceAccountSettingsQuery = <
    TData = GetExternalInvoiceAccountSettingsQuery,
    TError = unknown,
>(
    variables: GetExternalInvoiceAccountSettingsQueryVariables,
    options?: UseQueryOptions<GetExternalInvoiceAccountSettingsQuery, TError, TData>,
) => {
    return useQuery<GetExternalInvoiceAccountSettingsQuery, TError, TData>(
        ["getExternalInvoiceAccountSettings", variables],
        graphqlFetcher<
            GetExternalInvoiceAccountSettingsQuery,
            GetExternalInvoiceAccountSettingsQueryVariables
        >(GetExternalInvoiceAccountSettingsDocument, variables),
        options,
    );
};

export const useInfiniteGetExternalInvoiceAccountSettingsQuery = <
    TData = GetExternalInvoiceAccountSettingsQuery,
    TError = unknown,
>(
    variables: GetExternalInvoiceAccountSettingsQueryVariables,
    options?: UseInfiniteQueryOptions<
        GetExternalInvoiceAccountSettingsQuery,
        TError,
        TData
    >,
) => {
    return useInfiniteQuery<GetExternalInvoiceAccountSettingsQuery, TError, TData>(
        ["getExternalInvoiceAccountSettings.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetExternalInvoiceAccountSettingsQuery,
                GetExternalInvoiceAccountSettingsQueryVariables
            >(GetExternalInvoiceAccountSettingsDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const SearchExternalInvoicesDocument = new TypedDocumentString(`
    query searchExternalInvoices($params: ExternalInvoiceSearchRequest!) {
  searchExternalInvoices(params: $params) {
    count
    hits {
      externalId
      serial
      customer
      invoiceDate
      dueDate
      amountInCents
    }
  }
}
    `);

export const useSearchExternalInvoicesQuery = <
    TData = SearchExternalInvoicesQuery,
    TError = unknown,
>(
    variables: SearchExternalInvoicesQueryVariables,
    options?: UseQueryOptions<SearchExternalInvoicesQuery, TError, TData>,
) => {
    return useQuery<SearchExternalInvoicesQuery, TError, TData>(
        ["searchExternalInvoices", variables],
        graphqlFetcher<SearchExternalInvoicesQuery, SearchExternalInvoicesQueryVariables>(
            SearchExternalInvoicesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteSearchExternalInvoicesQuery = <
    TData = SearchExternalInvoicesQuery,
    TError = unknown,
>(
    variables: SearchExternalInvoicesQueryVariables,
    options?: UseInfiniteQueryOptions<SearchExternalInvoicesQuery, TError, TData>,
) => {
    return useInfiniteQuery<SearchExternalInvoicesQuery, TError, TData>(
        ["searchExternalInvoices.infinite", variables],
        metaData =>
            graphqlFetcher<
                SearchExternalInvoicesQuery,
                SearchExternalInvoicesQueryVariables
            >(SearchExternalInvoicesDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const AddExistingExternalInvoiceDocument = new TypedDocumentString(`
    mutation addExistingExternalInvoice($params: AddExternalInvoiceRequest!) {
  addExistingExternalInvoice(params: $params) {
    id
  }
}
    `);

export const useAddExistingExternalInvoiceMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        AddExistingExternalInvoiceMutation,
        TError,
        AddExistingExternalInvoiceMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        AddExistingExternalInvoiceMutation,
        TError,
        AddExistingExternalInvoiceMutationVariables,
        TContext
    >(
        ["addExistingExternalInvoice"],
        (variables?: AddExistingExternalInvoiceMutationVariables) =>
            graphqlFetcher<
                AddExistingExternalInvoiceMutation,
                AddExistingExternalInvoiceMutationVariables
            >(AddExistingExternalInvoiceDocument, variables)(),
        options,
    );
};

export const SearchExternalInvoiceCustomersDocument = new TypedDocumentString(`
    query searchExternalInvoiceCustomers($params: ExternalInvoiceCustomerSearchRequest!) {
  searchExternalInvoiceCustomers(params: $params) {
    count
    hits {
      id
      name
    }
  }
}
    `);

export const useSearchExternalInvoiceCustomersQuery = <
    TData = SearchExternalInvoiceCustomersQuery,
    TError = unknown,
>(
    variables: SearchExternalInvoiceCustomersQueryVariables,
    options?: UseQueryOptions<SearchExternalInvoiceCustomersQuery, TError, TData>,
) => {
    return useQuery<SearchExternalInvoiceCustomersQuery, TError, TData>(
        ["searchExternalInvoiceCustomers", variables],
        graphqlFetcher<
            SearchExternalInvoiceCustomersQuery,
            SearchExternalInvoiceCustomersQueryVariables
        >(SearchExternalInvoiceCustomersDocument, variables),
        options,
    );
};

export const useInfiniteSearchExternalInvoiceCustomersQuery = <
    TData = SearchExternalInvoiceCustomersQuery,
    TError = unknown,
>(
    variables: SearchExternalInvoiceCustomersQueryVariables,
    options?: UseInfiniteQueryOptions<SearchExternalInvoiceCustomersQuery, TError, TData>,
) => {
    return useInfiniteQuery<SearchExternalInvoiceCustomersQuery, TError, TData>(
        ["searchExternalInvoiceCustomers.infinite", variables],
        metaData =>
            graphqlFetcher<
                SearchExternalInvoiceCustomersQuery,
                SearchExternalInvoiceCustomersQueryVariables
            >(SearchExternalInvoiceCustomersDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetExternalInvoiceInitialParametersDocument = new TypedDocumentString(`
    query getExternalInvoiceInitialParameters($params: GetExternalInvoiceInitialParametersRequest!) {
  getExternalInvoiceInitialParameters(params: $params) {
    customer {
      id
      name
    }
    createCustomerName
  }
}
    `);

export const useGetExternalInvoiceInitialParametersQuery = <
    TData = GetExternalInvoiceInitialParametersQuery,
    TError = unknown,
>(
    variables: GetExternalInvoiceInitialParametersQueryVariables,
    options?: UseQueryOptions<GetExternalInvoiceInitialParametersQuery, TError, TData>,
) => {
    return useQuery<GetExternalInvoiceInitialParametersQuery, TError, TData>(
        ["getExternalInvoiceInitialParameters", variables],
        graphqlFetcher<
            GetExternalInvoiceInitialParametersQuery,
            GetExternalInvoiceInitialParametersQueryVariables
        >(GetExternalInvoiceInitialParametersDocument, variables),
        options,
    );
};

export const useInfiniteGetExternalInvoiceInitialParametersQuery = <
    TData = GetExternalInvoiceInitialParametersQuery,
    TError = unknown,
>(
    variables: GetExternalInvoiceInitialParametersQueryVariables,
    options?: UseInfiniteQueryOptions<
        GetExternalInvoiceInitialParametersQuery,
        TError,
        TData
    >,
) => {
    return useInfiniteQuery<GetExternalInvoiceInitialParametersQuery, TError, TData>(
        ["getExternalInvoiceInitialParameters.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetExternalInvoiceInitialParametersQuery,
                GetExternalInvoiceInitialParametersQueryVariables
            >(GetExternalInvoiceInitialParametersDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const CreateExternalInvoiceCustomerDocument = new TypedDocumentString(`
    mutation createExternalInvoiceCustomer($params: CreateExternalInvoiceCustomerRequest!) {
  createExternalInvoiceCustomer(params: $params) {
    id
    name
  }
}
    `);

export const useCreateExternalInvoiceCustomerMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        CreateExternalInvoiceCustomerMutation,
        TError,
        CreateExternalInvoiceCustomerMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateExternalInvoiceCustomerMutation,
        TError,
        CreateExternalInvoiceCustomerMutationVariables,
        TContext
    >(
        ["createExternalInvoiceCustomer"],
        (variables?: CreateExternalInvoiceCustomerMutationVariables) =>
            graphqlFetcher<
                CreateExternalInvoiceCustomerMutation,
                CreateExternalInvoiceCustomerMutationVariables
            >(CreateExternalInvoiceCustomerDocument, variables)(),
        options,
    );
};

export const DeleteInvoiceDocument = new TypedDocumentString(`
    mutation deleteInvoice($invoiceId: InvoiceId!) {
  deleteInvoice(invoiceId: $invoiceId)
}
    `);

export const useDeleteInvoiceMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteInvoiceMutation,
        TError,
        DeleteInvoiceMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteInvoiceMutation,
        TError,
        DeleteInvoiceMutationVariables,
        TContext
    >(
        ["deleteInvoice"],
        (variables?: DeleteInvoiceMutationVariables) =>
            graphqlFetcher<DeleteInvoiceMutation, DeleteInvoiceMutationVariables>(
                DeleteInvoiceDocument,
                variables,
            )(),
        options,
    );
};

export const GetLayoutSectionDocument = new TypedDocumentString(`
    query getLayoutSection($id: LayoutSectionId!) {
  getLayoutSection(id: $id) {
    ...LayoutSectionBasics
  }
}
    fragment LayoutSectionBasics on LayoutSection {
  id
  order
  sectionKey
  visible
  entityType
  title
  icon
  renderBehavior
  questionCategory
  legacyPerspective
  includeRelations
  singlePropertyName
  defaultStandardFields
  settings {
    key
    label
    type
    order
  }
  layoutItems {
    ...LayoutItemBasics
  }
}
fragment LayoutItemBasics on LayoutItem {
  id
  order
  questionId
  entityFieldName
  showIfBlank
  naryFormat
  customTitle
  isRequired
}`);

export const useGetLayoutSectionQuery = <TData = GetLayoutSectionQuery, TError = unknown>(
    variables: GetLayoutSectionQueryVariables,
    options?: UseQueryOptions<GetLayoutSectionQuery, TError, TData>,
) => {
    return useQuery<GetLayoutSectionQuery, TError, TData>(
        ["getLayoutSection", variables],
        graphqlFetcher<GetLayoutSectionQuery, GetLayoutSectionQueryVariables>(
            GetLayoutSectionDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetLayoutSectionQuery = <
    TData = GetLayoutSectionQuery,
    TError = unknown,
>(
    variables: GetLayoutSectionQueryVariables,
    options?: UseInfiniteQueryOptions<GetLayoutSectionQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetLayoutSectionQuery, TError, TData>(
        ["getLayoutSection.infinite", variables],
        metaData =>
            graphqlFetcher<GetLayoutSectionQuery, GetLayoutSectionQueryVariables>(
                GetLayoutSectionDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const CreateLayoutSectionDocument = new TypedDocumentString(`
    mutation createLayoutSection($params: CreateLayoutSectionInput!) {
  createLayoutSection(params: $params) {
    ...LayoutSectionBasics
  }
}
    fragment LayoutSectionBasics on LayoutSection {
  id
  order
  sectionKey
  visible
  entityType
  title
  icon
  renderBehavior
  questionCategory
  legacyPerspective
  includeRelations
  singlePropertyName
  defaultStandardFields
  settings {
    key
    label
    type
    order
  }
  layoutItems {
    ...LayoutItemBasics
  }
}
fragment LayoutItemBasics on LayoutItem {
  id
  order
  questionId
  entityFieldName
  showIfBlank
  naryFormat
  customTitle
  isRequired
}`);

export const useCreateLayoutSectionMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateLayoutSectionMutation,
        TError,
        CreateLayoutSectionMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateLayoutSectionMutation,
        TError,
        CreateLayoutSectionMutationVariables,
        TContext
    >(
        ["createLayoutSection"],
        (variables?: CreateLayoutSectionMutationVariables) =>
            graphqlFetcher<
                CreateLayoutSectionMutation,
                CreateLayoutSectionMutationVariables
            >(CreateLayoutSectionDocument, variables)(),
        options,
    );
};

export const PatchLayoutSectionDocument = new TypedDocumentString(`
    mutation patchLayoutSection($params: PatchLayoutSectionInput!) {
  patchLayoutSection(params: $params) {
    ...LayoutSectionBasics
  }
}
    fragment LayoutSectionBasics on LayoutSection {
  id
  order
  sectionKey
  visible
  entityType
  title
  icon
  renderBehavior
  questionCategory
  legacyPerspective
  includeRelations
  singlePropertyName
  defaultStandardFields
  settings {
    key
    label
    type
    order
  }
  layoutItems {
    ...LayoutItemBasics
  }
}
fragment LayoutItemBasics on LayoutItem {
  id
  order
  questionId
  entityFieldName
  showIfBlank
  naryFormat
  customTitle
  isRequired
}`);

export const usePatchLayoutSectionMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PatchLayoutSectionMutation,
        TError,
        PatchLayoutSectionMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PatchLayoutSectionMutation,
        TError,
        PatchLayoutSectionMutationVariables,
        TContext
    >(
        ["patchLayoutSection"],
        (variables?: PatchLayoutSectionMutationVariables) =>
            graphqlFetcher<
                PatchLayoutSectionMutation,
                PatchLayoutSectionMutationVariables
            >(PatchLayoutSectionDocument, variables)(),
        options,
    );
};

export const PatchLayoutSectionsDocument = new TypedDocumentString(`
    mutation patchLayoutSections($params: PatchLayoutSectionsInput!) {
  patchLayoutSections(params: $params) {
    ...LayoutSectionBasics
  }
}
    fragment LayoutSectionBasics on LayoutSection {
  id
  order
  sectionKey
  visible
  entityType
  title
  icon
  renderBehavior
  questionCategory
  legacyPerspective
  includeRelations
  singlePropertyName
  defaultStandardFields
  settings {
    key
    label
    type
    order
  }
  layoutItems {
    ...LayoutItemBasics
  }
}
fragment LayoutItemBasics on LayoutItem {
  id
  order
  questionId
  entityFieldName
  showIfBlank
  naryFormat
  customTitle
  isRequired
}`);

export const usePatchLayoutSectionsMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PatchLayoutSectionsMutation,
        TError,
        PatchLayoutSectionsMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PatchLayoutSectionsMutation,
        TError,
        PatchLayoutSectionsMutationVariables,
        TContext
    >(
        ["patchLayoutSections"],
        (variables?: PatchLayoutSectionsMutationVariables) =>
            graphqlFetcher<
                PatchLayoutSectionsMutation,
                PatchLayoutSectionsMutationVariables
            >(PatchLayoutSectionsDocument, variables)(),
        options,
    );
};

export const PatchLayoutItemDocument = new TypedDocumentString(`
    mutation patchLayoutItem($params: PatchLayoutItemInput!) {
  patchLayoutItem(params: $params) {
    ...LayoutItemBasics
  }
}
    fragment LayoutItemBasics on LayoutItem {
  id
  order
  questionId
  entityFieldName
  showIfBlank
  naryFormat
  customTitle
  isRequired
}`);

export const usePatchLayoutItemMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PatchLayoutItemMutation,
        TError,
        PatchLayoutItemMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PatchLayoutItemMutation,
        TError,
        PatchLayoutItemMutationVariables,
        TContext
    >(
        ["patchLayoutItem"],
        (variables?: PatchLayoutItemMutationVariables) =>
            graphqlFetcher<PatchLayoutItemMutation, PatchLayoutItemMutationVariables>(
                PatchLayoutItemDocument,
                variables,
            )(),
        options,
    );
};

export const DeleteLayoutSectionDocument = new TypedDocumentString(`
    mutation deleteLayoutSection($id: LayoutSectionId!) {
  deleteLayoutSection(id: $id)
}
    `);

export const useDeleteLayoutSectionMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteLayoutSectionMutation,
        TError,
        DeleteLayoutSectionMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteLayoutSectionMutation,
        TError,
        DeleteLayoutSectionMutationVariables,
        TContext
    >(
        ["deleteLayoutSection"],
        (variables?: DeleteLayoutSectionMutationVariables) =>
            graphqlFetcher<
                DeleteLayoutSectionMutation,
                DeleteLayoutSectionMutationVariables
            >(DeleteLayoutSectionDocument, variables)(),
        options,
    );
};

export const GetLayoutSectionsDocument = new TypedDocumentString(`
    query getLayoutSections($parentKey: LayoutSectionParentKey!) {
  getLayoutSections(parentKey: $parentKey) {
    layoutSections {
      ...LayoutSectionBasics
    }
  }
}
    fragment LayoutSectionBasics on LayoutSection {
  id
  order
  sectionKey
  visible
  entityType
  title
  icon
  renderBehavior
  questionCategory
  legacyPerspective
  includeRelations
  singlePropertyName
  defaultStandardFields
  settings {
    key
    label
    type
    order
  }
  layoutItems {
    ...LayoutItemBasics
  }
}
fragment LayoutItemBasics on LayoutItem {
  id
  order
  questionId
  entityFieldName
  showIfBlank
  naryFormat
  customTitle
  isRequired
}`);

export const useGetLayoutSectionsQuery = <
    TData = GetLayoutSectionsQuery,
    TError = unknown,
>(
    variables: GetLayoutSectionsQueryVariables,
    options?: UseQueryOptions<GetLayoutSectionsQuery, TError, TData>,
) => {
    return useQuery<GetLayoutSectionsQuery, TError, TData>(
        ["getLayoutSections", variables],
        graphqlFetcher<GetLayoutSectionsQuery, GetLayoutSectionsQueryVariables>(
            GetLayoutSectionsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetLayoutSectionsQuery = <
    TData = GetLayoutSectionsQuery,
    TError = unknown,
>(
    variables: GetLayoutSectionsQueryVariables,
    options?: UseInfiniteQueryOptions<GetLayoutSectionsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetLayoutSectionsQuery, TError, TData>(
        ["getLayoutSections.infinite", variables],
        metaData =>
            graphqlFetcher<GetLayoutSectionsQuery, GetLayoutSectionsQueryVariables>(
                GetLayoutSectionsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetLayoutContainerByKeyDocument = new TypedDocumentString(`
    query getLayoutContainerByKey($key: String!) {
  getLayoutContainerByKey(key: $key) {
    id
    title
    key
    layoutSections {
      ...LayoutSectionBasics
    }
  }
}
    fragment LayoutSectionBasics on LayoutSection {
  id
  order
  sectionKey
  visible
  entityType
  title
  icon
  renderBehavior
  questionCategory
  legacyPerspective
  includeRelations
  singlePropertyName
  defaultStandardFields
  settings {
    key
    label
    type
    order
  }
  layoutItems {
    ...LayoutItemBasics
  }
}
fragment LayoutItemBasics on LayoutItem {
  id
  order
  questionId
  entityFieldName
  showIfBlank
  naryFormat
  customTitle
  isRequired
}`);

export const useGetLayoutContainerByKeyQuery = <
    TData = GetLayoutContainerByKeyQuery,
    TError = unknown,
>(
    variables: GetLayoutContainerByKeyQueryVariables,
    options?: UseQueryOptions<GetLayoutContainerByKeyQuery, TError, TData>,
) => {
    return useQuery<GetLayoutContainerByKeyQuery, TError, TData>(
        ["getLayoutContainerByKey", variables],
        graphqlFetcher<
            GetLayoutContainerByKeyQuery,
            GetLayoutContainerByKeyQueryVariables
        >(GetLayoutContainerByKeyDocument, variables),
        options,
    );
};

export const useInfiniteGetLayoutContainerByKeyQuery = <
    TData = GetLayoutContainerByKeyQuery,
    TError = unknown,
>(
    variables: GetLayoutContainerByKeyQueryVariables,
    options?: UseInfiniteQueryOptions<GetLayoutContainerByKeyQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetLayoutContainerByKeyQuery, TError, TData>(
        ["getLayoutContainerByKey.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetLayoutContainerByKeyQuery,
                GetLayoutContainerByKeyQueryVariables
            >(GetLayoutContainerByKeyDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetLayoutContainersByKeyDocument = new TypedDocumentString(`
    query getLayoutContainersByKey($keys: [LayoutSectionParentKey!]!) {
  getLayoutContainersByKey(keys: $keys) {
    id
    title
    key
    layoutSections {
      ...LayoutSectionBasics
    }
  }
}
    fragment LayoutSectionBasics on LayoutSection {
  id
  order
  sectionKey
  visible
  entityType
  title
  icon
  renderBehavior
  questionCategory
  legacyPerspective
  includeRelations
  singlePropertyName
  defaultStandardFields
  settings {
    key
    label
    type
    order
  }
  layoutItems {
    ...LayoutItemBasics
  }
}
fragment LayoutItemBasics on LayoutItem {
  id
  order
  questionId
  entityFieldName
  showIfBlank
  naryFormat
  customTitle
  isRequired
}`);

export const useGetLayoutContainersByKeyQuery = <
    TData = GetLayoutContainersByKeyQuery,
    TError = unknown,
>(
    variables: GetLayoutContainersByKeyQueryVariables,
    options?: UseQueryOptions<GetLayoutContainersByKeyQuery, TError, TData>,
) => {
    return useQuery<GetLayoutContainersByKeyQuery, TError, TData>(
        ["getLayoutContainersByKey", variables],
        graphqlFetcher<
            GetLayoutContainersByKeyQuery,
            GetLayoutContainersByKeyQueryVariables
        >(GetLayoutContainersByKeyDocument, variables),
        options,
    );
};

export const useInfiniteGetLayoutContainersByKeyQuery = <
    TData = GetLayoutContainersByKeyQuery,
    TError = unknown,
>(
    variables: GetLayoutContainersByKeyQueryVariables,
    options?: UseInfiniteQueryOptions<GetLayoutContainersByKeyQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetLayoutContainersByKeyQuery, TError, TData>(
        ["getLayoutContainersByKey.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetLayoutContainersByKeyQuery,
                GetLayoutContainersByKeyQueryVariables
            >(GetLayoutContainersByKeyDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetClientIntakeFaxRecipientsDocument = new TypedDocumentString(`
    query getClientIntakeFaxRecipients($clientId: PlacementId!) {
  getClientIntakeFaxRecipients(clientId: $clientId) {
    faxNumber
    communityName
    communityId
  }
}
    `);

export const useGetClientIntakeFaxRecipientsQuery = <
    TData = GetClientIntakeFaxRecipientsQuery,
    TError = unknown,
>(
    variables: GetClientIntakeFaxRecipientsQueryVariables,
    options?: UseQueryOptions<GetClientIntakeFaxRecipientsQuery, TError, TData>,
) => {
    return useQuery<GetClientIntakeFaxRecipientsQuery, TError, TData>(
        ["getClientIntakeFaxRecipients", variables],
        graphqlFetcher<
            GetClientIntakeFaxRecipientsQuery,
            GetClientIntakeFaxRecipientsQueryVariables
        >(GetClientIntakeFaxRecipientsDocument, variables),
        options,
    );
};

export const useInfiniteGetClientIntakeFaxRecipientsQuery = <
    TData = GetClientIntakeFaxRecipientsQuery,
    TError = unknown,
>(
    variables: GetClientIntakeFaxRecipientsQueryVariables,
    options?: UseInfiniteQueryOptions<GetClientIntakeFaxRecipientsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetClientIntakeFaxRecipientsQuery, TError, TData>(
        ["getClientIntakeFaxRecipients.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetClientIntakeFaxRecipientsQuery,
                GetClientIntakeFaxRecipientsQueryVariables
            >(GetClientIntakeFaxRecipientsDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const SendClientIntakeFaxDocument = new TypedDocumentString(`
    mutation sendClientIntakeFax($message: OutboundClientIntakeFax!) {
  sendClientIntakeFax(message: $message)
}
    `);

export const useSendClientIntakeFaxMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        SendClientIntakeFaxMutation,
        TError,
        SendClientIntakeFaxMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SendClientIntakeFaxMutation,
        TError,
        SendClientIntakeFaxMutationVariables,
        TContext
    >(
        ["sendClientIntakeFax"],
        (variables?: SendClientIntakeFaxMutationVariables) =>
            graphqlFetcher<
                SendClientIntakeFaxMutation,
                SendClientIntakeFaxMutationVariables
            >(SendClientIntakeFaxDocument, variables)(),
        options,
    );
};

export const ComposeMessageDocument = new TypedDocumentString(`
    query composeMessage($entityId: EmailEntityId!, $specialMessageExperience: String) {
  getTemplates {
    templates {
      isDefaultSignature
      template {
        id
        body
        subject
        description
        type
        migrationVersion
        attachments
      }
    }
  }
  composeMessage(
    id: $entityId
    specialMessageExperience: $specialMessageExperience
  ) {
    recipients
    recipientSuggestions
    specialCommunityComparisonDefaults {
      showSummary
      showRecords
      showCoverPage
      coverPageTemplateId
      liveLinkText
      attachCommunityPDFByDefault
      useLiveLinkByDefault
    }
    files {
      entityName
      files {
        id
        originalFilename
        mimeType
        byteLength
      }
    }
  }
}
    `);

export const useComposeMessageQuery = <TData = ComposeMessageQuery, TError = unknown>(
    variables: ComposeMessageQueryVariables,
    options?: UseQueryOptions<ComposeMessageQuery, TError, TData>,
) => {
    return useQuery<ComposeMessageQuery, TError, TData>(
        ["composeMessage", variables],
        graphqlFetcher<ComposeMessageQuery, ComposeMessageQueryVariables>(
            ComposeMessageDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteComposeMessageQuery = <
    TData = ComposeMessageQuery,
    TError = unknown,
>(
    variables: ComposeMessageQueryVariables,
    options?: UseInfiniteQueryOptions<ComposeMessageQuery, TError, TData>,
) => {
    return useInfiniteQuery<ComposeMessageQuery, TError, TData>(
        ["composeMessage.infinite", variables],
        metaData =>
            graphqlFetcher<ComposeMessageQuery, ComposeMessageQueryVariables>(
                ComposeMessageDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const ComposeMessageBasicDocument = new TypedDocumentString(`
    query composeMessageBasic {
  getTemplates {
    templates {
      isDefaultSignature
      template {
        id
        body
        subject
        description
        type
        migrationVersion
        attachments
      }
    }
  }
}
    `);

export const useComposeMessageBasicQuery = <
    TData = ComposeMessageBasicQuery,
    TError = unknown,
>(
    variables?: ComposeMessageBasicQueryVariables,
    options?: UseQueryOptions<ComposeMessageBasicQuery, TError, TData>,
) => {
    return useQuery<ComposeMessageBasicQuery, TError, TData>(
        variables === undefined
            ? ["composeMessageBasic"]
            : ["composeMessageBasic", variables],
        graphqlFetcher<ComposeMessageBasicQuery, ComposeMessageBasicQueryVariables>(
            ComposeMessageBasicDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteComposeMessageBasicQuery = <
    TData = ComposeMessageBasicQuery,
    TError = unknown,
>(
    variables?: ComposeMessageBasicQueryVariables,
    options?: UseInfiniteQueryOptions<ComposeMessageBasicQuery, TError, TData>,
) => {
    return useInfiniteQuery<ComposeMessageBasicQuery, TError, TData>(
        variables === undefined
            ? ["composeMessageBasic.infinite"]
            : ["composeMessageBasic.infinite", variables],
        metaData =>
            graphqlFetcher<ComposeMessageBasicQuery, ComposeMessageBasicQueryVariables>(
                ComposeMessageBasicDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetMessagesForEntityDocument = new TypedDocumentString(`
    query getMessagesForEntity($id: EmailEntityId!) {
  getMessagesForEntity(entityId: $id) {
    ...SentMessageFragment
  }
}
    fragment SentMessageFragment on SentMessage {
  id
  messageId
  userId
  from
  subject
  bodyPreview
  status
  latestEventStatus
  createdAt
  to
  cc
  messageType
  attachments
  clientIdUuid
  communityIdUuid
  clickedLinkEvents {
    link
    events
    redacted
    eventsV2 {
      timestamp
      recipient
    }
  }
  intakeCommunity {
    id
    name
    appLink
  }
  intakeClient {
    id
    name
    bestContactName
    appLink
  }
  events {
    eventType
    eventTimestamp
    recipient
  }
}`);

export const useGetMessagesForEntityQuery = <
    TData = GetMessagesForEntityQuery,
    TError = unknown,
>(
    variables: GetMessagesForEntityQueryVariables,
    options?: UseQueryOptions<GetMessagesForEntityQuery, TError, TData>,
) => {
    return useQuery<GetMessagesForEntityQuery, TError, TData>(
        ["getMessagesForEntity", variables],
        graphqlFetcher<GetMessagesForEntityQuery, GetMessagesForEntityQueryVariables>(
            GetMessagesForEntityDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetMessagesForEntityQuery = <
    TData = GetMessagesForEntityQuery,
    TError = unknown,
>(
    variables: GetMessagesForEntityQueryVariables,
    options?: UseInfiniteQueryOptions<GetMessagesForEntityQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetMessagesForEntityQuery, TError, TData>(
        ["getMessagesForEntity.infinite", variables],
        metaData =>
            graphqlFetcher<GetMessagesForEntityQuery, GetMessagesForEntityQueryVariables>(
                GetMessagesForEntityDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetMessageDocument = new TypedDocumentString(`
    query getMessage($id: SentMessageId!) {
  getMessage(sentMessageId: $id) {
    id
    subject
    bouncedRecipients
    status
    createdAt
    body
    to
    cc
    messageType
    attachments
    intakeCommunity {
      name
    }
    invoice {
      id
      serial
    }
    clickedLinkEvents {
      link
      events
      redacted
      eventsV2 {
        timestamp
        recipient
      }
    }
    events {
      eventType
      eventTimestamp
      recipient
    }
  }
}
    `);

export const useGetMessageQuery = <TData = GetMessageQuery, TError = unknown>(
    variables: GetMessageQueryVariables,
    options?: UseQueryOptions<GetMessageQuery, TError, TData>,
) => {
    return useQuery<GetMessageQuery, TError, TData>(
        ["getMessage", variables],
        graphqlFetcher<GetMessageQuery, GetMessageQueryVariables>(
            GetMessageDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetMessageQuery = <TData = GetMessageQuery, TError = unknown>(
    variables: GetMessageQueryVariables,
    options?: UseInfiniteQueryOptions<GetMessageQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetMessageQuery, TError, TData>(
        ["getMessage.infinite", variables],
        metaData =>
            graphqlFetcher<GetMessageQuery, GetMessageQueryVariables>(
                GetMessageDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const SendMessageDocument = new TypedDocumentString(`
    mutation sendMessage($message: OutboundMessage!) {
  sendMessage(message: $message) {
    status
    sentMessage {
      id
    }
    accepted
    rejected
    code
  }
}
    `);

export const useSendMessageMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        SendMessageMutation,
        TError,
        SendMessageMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SendMessageMutation,
        TError,
        SendMessageMutationVariables,
        TContext
    >(
        ["sendMessage"],
        (variables?: SendMessageMutationVariables) =>
            graphqlFetcher<SendMessageMutation, SendMessageMutationVariables>(
                SendMessageDocument,
                variables,
            )(),
        options,
    );
};

export const ValidateTemplateDocument = new TypedDocumentString(`
    query validateTemplate($emailTemplateId: EmailTemplateId!, $messageType: String!) {
  validateTemplate(emailTemplateId: $emailTemplateId, messageType: $messageType) {
    isValid
    message
    missingTokens {
      tokenKey
      description
      humanFriendlyName
    }
  }
}
    `);

export const useValidateTemplateQuery = <TData = ValidateTemplateQuery, TError = unknown>(
    variables: ValidateTemplateQueryVariables,
    options?: UseQueryOptions<ValidateTemplateQuery, TError, TData>,
) => {
    return useQuery<ValidateTemplateQuery, TError, TData>(
        ["validateTemplate", variables],
        graphqlFetcher<ValidateTemplateQuery, ValidateTemplateQueryVariables>(
            ValidateTemplateDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteValidateTemplateQuery = <
    TData = ValidateTemplateQuery,
    TError = unknown,
>(
    variables: ValidateTemplateQueryVariables,
    options?: UseInfiniteQueryOptions<ValidateTemplateQuery, TError, TData>,
) => {
    return useInfiniteQuery<ValidateTemplateQuery, TError, TData>(
        ["validateTemplate.infinite", variables],
        metaData =>
            graphqlFetcher<ValidateTemplateQuery, ValidateTemplateQueryVariables>(
                ValidateTemplateDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetMessagingConfigurationDocument = new TypedDocumentString(`
    query getMessagingConfiguration {
  getMessagingConfiguration {
    user {
      directSmtp
    }
    tenant {
      customDomains
      trackingLevel
    }
  }
}
    `);

export const useGetMessagingConfigurationQuery = <
    TData = GetMessagingConfigurationQuery,
    TError = unknown,
>(
    variables?: GetMessagingConfigurationQueryVariables,
    options?: UseQueryOptions<GetMessagingConfigurationQuery, TError, TData>,
) => {
    return useQuery<GetMessagingConfigurationQuery, TError, TData>(
        variables === undefined
            ? ["getMessagingConfiguration"]
            : ["getMessagingConfiguration", variables],
        graphqlFetcher<
            GetMessagingConfigurationQuery,
            GetMessagingConfigurationQueryVariables
        >(GetMessagingConfigurationDocument, variables),
        options,
    );
};

export const useInfiniteGetMessagingConfigurationQuery = <
    TData = GetMessagingConfigurationQuery,
    TError = unknown,
>(
    variables?: GetMessagingConfigurationQueryVariables,
    options?: UseInfiniteQueryOptions<GetMessagingConfigurationQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetMessagingConfigurationQuery, TError, TData>(
        variables === undefined
            ? ["getMessagingConfiguration.infinite"]
            : ["getMessagingConfiguration.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetMessagingConfigurationQuery,
                GetMessagingConfigurationQueryVariables
            >(GetMessagingConfigurationDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const CreateNoteDocument = new TypedDocumentString(`
    mutation createNote($entityType: String!, $entityId: NoteEntityId!, $params: CreateNoteInput!) {
  createNote(entityType: $entityType, entityId: $entityId, params: $params) {
    ...NoteResponse
  }
}
    fragment NoteResponse on NoteDto {
  _id
  text
  dateAdded
  wasEdited
  authorId
  editorId
  updatedAt
  isDraft
  isPinned
}`);

export const useCreateNoteMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateNoteMutation,
        TError,
        CreateNoteMutationVariables,
        TContext
    >,
) => {
    return useMutation<CreateNoteMutation, TError, CreateNoteMutationVariables, TContext>(
        ["createNote"],
        (variables?: CreateNoteMutationVariables) =>
            graphqlFetcher<CreateNoteMutation, CreateNoteMutationVariables>(
                CreateNoteDocument,
                variables,
            )(),
        options,
    );
};

export const UpdateNoteDocument = new TypedDocumentString(`
    mutation updateNote($entityId: NoteEntityId!, $noteId: NoteId!, $params: UpdateNoteInput!) {
  updateNote(entityId: $entityId, noteId: $noteId, params: $params) {
    ...NoteResponse
  }
}
    fragment NoteResponse on NoteDto {
  _id
  text
  dateAdded
  wasEdited
  authorId
  editorId
  updatedAt
  isDraft
  isPinned
}`);

export const useUpdateNoteMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateNoteMutation,
        TError,
        UpdateNoteMutationVariables,
        TContext
    >,
) => {
    return useMutation<UpdateNoteMutation, TError, UpdateNoteMutationVariables, TContext>(
        ["updateNote"],
        (variables?: UpdateNoteMutationVariables) =>
            graphqlFetcher<UpdateNoteMutation, UpdateNoteMutationVariables>(
                UpdateNoteDocument,
                variables,
            )(),
        options,
    );
};

export const DeleteNoteDocument = new TypedDocumentString(`
    mutation deleteNote($entityType: String!, $entityId: NoteEntityId!, $noteId: NoteId!) {
  deleteNote(entityType: $entityType, entityId: $entityId, noteId: $noteId) {
    _id
  }
}
    `);

export const useDeleteNoteMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteNoteMutation,
        TError,
        DeleteNoteMutationVariables,
        TContext
    >,
) => {
    return useMutation<DeleteNoteMutation, TError, DeleteNoteMutationVariables, TContext>(
        ["deleteNote"],
        (variables?: DeleteNoteMutationVariables) =>
            graphqlFetcher<DeleteNoteMutation, DeleteNoteMutationVariables>(
                DeleteNoteDocument,
                variables,
            )(),
        options,
    );
};

export const GetNotesForEntityDocument = new TypedDocumentString(`
    query getNotesForEntity($entityId: NoteEntityId!, $filter: GetNotesForEntityFilter) {
  getNotesForEntity(entityId: $entityId, filter: $filter) {
    ...NoteResponse
  }
}
    fragment NoteResponse on NoteDto {
  _id
  text
  dateAdded
  wasEdited
  authorId
  editorId
  updatedAt
  isDraft
  isPinned
}`);

export const useGetNotesForEntityQuery = <
    TData = GetNotesForEntityQuery,
    TError = unknown,
>(
    variables: GetNotesForEntityQueryVariables,
    options?: UseQueryOptions<GetNotesForEntityQuery, TError, TData>,
) => {
    return useQuery<GetNotesForEntityQuery, TError, TData>(
        ["getNotesForEntity", variables],
        graphqlFetcher<GetNotesForEntityQuery, GetNotesForEntityQueryVariables>(
            GetNotesForEntityDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetNotesForEntityQuery = <
    TData = GetNotesForEntityQuery,
    TError = unknown,
>(
    variables: GetNotesForEntityQueryVariables,
    options?: UseInfiniteQueryOptions<GetNotesForEntityQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetNotesForEntityQuery, TError, TData>(
        ["getNotesForEntity.infinite", variables],
        metaData =>
            graphqlFetcher<GetNotesForEntityQuery, GetNotesForEntityQueryVariables>(
                GetNotesForEntityDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetPhotosForEntityDocument = new TypedDocumentString(`
    query getPhotosForEntity($entityId: FileEntityId!) {
  getPhotosForEntity(entityId: $entityId) {
    ...PhotoBasics
  }
}
    fragment FileFragment on File {
  id
  entityId
  originalFilename
  mimeType
  description
  byteLength
  shareWithClient
  shareWithCommunity
  shareWithReferral
  shareWithHomeCareLead
  createdAt
  createdBy
  updatedAt
  updatedBy
  hasSignatureTemplate
  isGlobal
  managementLevel
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}
fragment PhotoBasics on Photo {
  id
  entityId
  order
  dateAdded
  createdBy
  originalFile {
    ...FileFragment
  }
}`);

export const useGetPhotosForEntityQuery = <
    TData = GetPhotosForEntityQuery,
    TError = unknown,
>(
    variables: GetPhotosForEntityQueryVariables,
    options?: UseQueryOptions<GetPhotosForEntityQuery, TError, TData>,
) => {
    return useQuery<GetPhotosForEntityQuery, TError, TData>(
        ["getPhotosForEntity", variables],
        graphqlFetcher<GetPhotosForEntityQuery, GetPhotosForEntityQueryVariables>(
            GetPhotosForEntityDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetPhotosForEntityQuery = <
    TData = GetPhotosForEntityQuery,
    TError = unknown,
>(
    variables: GetPhotosForEntityQueryVariables,
    options?: UseInfiniteQueryOptions<GetPhotosForEntityQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetPhotosForEntityQuery, TError, TData>(
        ["getPhotosForEntity.infinite", variables],
        metaData =>
            graphqlFetcher<GetPhotosForEntityQuery, GetPhotosForEntityQueryVariables>(
                GetPhotosForEntityDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const PatchPhotoDocument = new TypedDocumentString(`
    mutation patchPhoto($params: PatchPhotoInput!) {
  patchPhoto(params: $params) {
    ...PhotoBasics
  }
}
    fragment FileFragment on File {
  id
  entityId
  originalFilename
  mimeType
  description
  byteLength
  shareWithClient
  shareWithCommunity
  shareWithReferral
  shareWithHomeCareLead
  createdAt
  createdBy
  updatedAt
  updatedBy
  hasSignatureTemplate
  isGlobal
  managementLevel
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}
fragment PhotoBasics on Photo {
  id
  entityId
  order
  dateAdded
  createdBy
  originalFile {
    ...FileFragment
  }
}`);

export const usePatchPhotoMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PatchPhotoMutation,
        TError,
        PatchPhotoMutationVariables,
        TContext
    >,
) => {
    return useMutation<PatchPhotoMutation, TError, PatchPhotoMutationVariables, TContext>(
        ["patchPhoto"],
        (variables?: PatchPhotoMutationVariables) =>
            graphqlFetcher<PatchPhotoMutation, PatchPhotoMutationVariables>(
                PatchPhotoDocument,
                variables,
            )(),
        options,
    );
};

export const DeletePhotoDocument = new TypedDocumentString(`
    mutation deletePhoto($params: DeletePhotoInput!) {
  deletePhoto(params: $params)
}
    `);

export const useDeletePhotoMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeletePhotoMutation,
        TError,
        DeletePhotoMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeletePhotoMutation,
        TError,
        DeletePhotoMutationVariables,
        TContext
    >(
        ["deletePhoto"],
        (variables?: DeletePhotoMutationVariables) =>
            graphqlFetcher<DeletePhotoMutation, DeletePhotoMutationVariables>(
                DeletePhotoDocument,
                variables,
            )(),
        options,
    );
};

export const PatchPhotosDocument = new TypedDocumentString(`
    mutation patchPhotos($params: [PatchPhotoInput!]!) {
  patchPhotos(params: $params) {
    ...PhotoBasics
  }
}
    fragment FileFragment on File {
  id
  entityId
  originalFilename
  mimeType
  description
  byteLength
  shareWithClient
  shareWithCommunity
  shareWithReferral
  shareWithHomeCareLead
  createdAt
  createdBy
  updatedAt
  updatedBy
  hasSignatureTemplate
  isGlobal
  managementLevel
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}
fragment PhotoBasics on Photo {
  id
  entityId
  order
  dateAdded
  createdBy
  originalFile {
    ...FileFragment
  }
}`);

export const usePatchPhotosMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PatchPhotosMutation,
        TError,
        PatchPhotosMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PatchPhotosMutation,
        TError,
        PatchPhotosMutationVariables,
        TContext
    >(
        ["patchPhotos"],
        (variables?: PatchPhotosMutationVariables) =>
            graphqlFetcher<PatchPhotosMutation, PatchPhotosMutationVariables>(
                PatchPhotosDocument,
                variables,
            )(),
        options,
    );
};

export const BulkCreateLegacyQuestionsDocument = new TypedDocumentString(`
    mutation bulkCreateLegacyQuestions($payload: QuestionBulkCreatePayload!) {
  bulkCreateLegacyQuestions(bulkPayload: $payload) {
    id
  }
}
    `);

export const useBulkCreateLegacyQuestionsMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        BulkCreateLegacyQuestionsMutation,
        TError,
        BulkCreateLegacyQuestionsMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        BulkCreateLegacyQuestionsMutation,
        TError,
        BulkCreateLegacyQuestionsMutationVariables,
        TContext
    >(
        ["bulkCreateLegacyQuestions"],
        (variables?: BulkCreateLegacyQuestionsMutationVariables) =>
            graphqlFetcher<
                BulkCreateLegacyQuestionsMutation,
                BulkCreateLegacyQuestionsMutationVariables
            >(BulkCreateLegacyQuestionsDocument, variables)(),
        options,
    );
};

export const GetLegacyQuestionsForTenantDocument = new TypedDocumentString(`
    query getLegacyQuestionsForTenant($tenantId: TenantId!) {
  getLegacyQuestionsForTenant(tenantId: $tenantId) {
    id
    key
    order
    category
    clientTitle
    communityTitle
    questionType
    clientInteraction
    clientPdfBehavior
    clientSearchBehavior
    clientAnnotation
    communityInteraction
    communityPdfBehavior
    communitySearchBehavior
    communityAnnotation
    clientRulesString
    communityRulesString
    prompts {
      id
      client
      community
      key
      order
    }
  }
}
    `);

export const useGetLegacyQuestionsForTenantQuery = <
    TData = GetLegacyQuestionsForTenantQuery,
    TError = unknown,
>(
    variables: GetLegacyQuestionsForTenantQueryVariables,
    options?: UseQueryOptions<GetLegacyQuestionsForTenantQuery, TError, TData>,
) => {
    return useQuery<GetLegacyQuestionsForTenantQuery, TError, TData>(
        ["getLegacyQuestionsForTenant", variables],
        graphqlFetcher<
            GetLegacyQuestionsForTenantQuery,
            GetLegacyQuestionsForTenantQueryVariables
        >(GetLegacyQuestionsForTenantDocument, variables),
        options,
    );
};

export const useInfiniteGetLegacyQuestionsForTenantQuery = <
    TData = GetLegacyQuestionsForTenantQuery,
    TError = unknown,
>(
    variables: GetLegacyQuestionsForTenantQueryVariables,
    options?: UseInfiniteQueryOptions<GetLegacyQuestionsForTenantQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetLegacyQuestionsForTenantQuery, TError, TData>(
        ["getLegacyQuestionsForTenant.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetLegacyQuestionsForTenantQuery,
                GetLegacyQuestionsForTenantQueryVariables
            >(GetLegacyQuestionsForTenantDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const ReorderLegacyQuestionsDocument = new TypedDocumentString(`
    mutation reorderLegacyQuestions($questionReorderPayload: QuestionReorderPayload!) {
  reorderLegacyQuestions(questionReorderPayload: $questionReorderPayload)
}
    `);

export const useReorderLegacyQuestionsMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        ReorderLegacyQuestionsMutation,
        TError,
        ReorderLegacyQuestionsMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        ReorderLegacyQuestionsMutation,
        TError,
        ReorderLegacyQuestionsMutationVariables,
        TContext
    >(
        ["reorderLegacyQuestions"],
        (variables?: ReorderLegacyQuestionsMutationVariables) =>
            graphqlFetcher<
                ReorderLegacyQuestionsMutation,
                ReorderLegacyQuestionsMutationVariables
            >(ReorderLegacyQuestionsDocument, variables)(),
        options,
    );
};

export const GetReferenceContactDocument = new TypedDocumentString(`
    query getReferenceContact($id: ReferenceContactId!) {
  getReferenceContact(referenceContactId: $id) {
    ...ReferenceContactBasics
  }
}
    fragment ContactFragment on ContactEntity {
  id
  name
  preferredName
  address1
  address2
  city
  state
  zip
  relationship
  role
  cellPhone
  phone1
  email1
  email1OptOut
  website
  contactType
  type
  fax
  contactNotes
  dateAdded
}
fragment NoteResponse on NoteDto {
  _id
  text
  dateAdded
  wasEdited
  authorId
  editorId
  updatedAt
  isDraft
  isPinned
}
fragment ReferenceContactBasics on ReferenceContact {
  id
  ownerId
  deleted
  summary
  businessId
  communityId
  regionId
  dateAdded
  updatedAt
  createdBy
  updatedBy
  draftNote
  bestDaysToVisit
  bestTimeToContact
  contactEntity {
    ...ContactFragment
  }
  business {
    id
    ownerId
    deleted
    regionId
    latitude
    longitude
    businessContactEntity {
      ...ContactFragment
    }
  }
  community {
    id
    name
    address
    city
    state
    zip
    mainPhone
    website
    email
    latitude
    longitude
    contractStatus
  }
  answers {
    questionId
    booleanAnswer
    numberAnswer
    numberAnswerHigh
    textAnswer
    dateAnswer
    calendarDateAnswer
    selections
    idReferenceAnswer
    selectionAnnotations {
      text
      selectionId
    }
  }
  notes {
    ...NoteResponse
  }
}`);

export const useGetReferenceContactQuery = <
    TData = GetReferenceContactQuery,
    TError = unknown,
>(
    variables: GetReferenceContactQueryVariables,
    options?: UseQueryOptions<GetReferenceContactQuery, TError, TData>,
) => {
    return useQuery<GetReferenceContactQuery, TError, TData>(
        ["getReferenceContact", variables],
        graphqlFetcher<GetReferenceContactQuery, GetReferenceContactQueryVariables>(
            GetReferenceContactDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetReferenceContactQuery = <
    TData = GetReferenceContactQuery,
    TError = unknown,
>(
    variables: GetReferenceContactQueryVariables,
    options?: UseInfiniteQueryOptions<GetReferenceContactQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetReferenceContactQuery, TError, TData>(
        ["getReferenceContact.infinite", variables],
        metaData =>
            graphqlFetcher<GetReferenceContactQuery, GetReferenceContactQueryVariables>(
                GetReferenceContactDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetReferenceBusinessDocument = new TypedDocumentString(`
    query getReferenceBusiness($id: ReferenceBusinessId!) {
  getReferenceBusiness(referenceBusinessId: $id) {
    ...ReferenceBusinessBasics
  }
}
    fragment ContactFragment on ContactEntity {
  id
  name
  preferredName
  address1
  address2
  city
  state
  zip
  relationship
  role
  cellPhone
  phone1
  email1
  email1OptOut
  website
  contactType
  type
  fax
  contactNotes
  dateAdded
}
fragment NoteResponse on NoteDto {
  _id
  text
  dateAdded
  wasEdited
  authorId
  editorId
  updatedAt
  isDraft
  isPinned
}
fragment ReferenceBusinessBasics on ReferenceBusiness {
  id
  ownerId
  deleted
  summary
  email
  dateAdded
  updatedAt
  createdBy
  updatedBy
  draftNote
  bestDaysToVisit
  bestTimeToContact
  regionId
  latitude
  longitude
  businessContactEntity {
    ...ContactFragment
  }
  answers {
    questionId
    booleanAnswer
    numberAnswer
    numberAnswerHigh
    textAnswer
    dateAnswer
    calendarDateAnswer
    selections
    idReferenceAnswer
    selectionAnnotations {
      text
      selectionId
    }
  }
  notes {
    ...NoteResponse
  }
}`);

export const useGetReferenceBusinessQuery = <
    TData = GetReferenceBusinessQuery,
    TError = unknown,
>(
    variables: GetReferenceBusinessQueryVariables,
    options?: UseQueryOptions<GetReferenceBusinessQuery, TError, TData>,
) => {
    return useQuery<GetReferenceBusinessQuery, TError, TData>(
        ["getReferenceBusiness", variables],
        graphqlFetcher<GetReferenceBusinessQuery, GetReferenceBusinessQueryVariables>(
            GetReferenceBusinessDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetReferenceBusinessQuery = <
    TData = GetReferenceBusinessQuery,
    TError = unknown,
>(
    variables: GetReferenceBusinessQueryVariables,
    options?: UseInfiniteQueryOptions<GetReferenceBusinessQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetReferenceBusinessQuery, TError, TData>(
        ["getReferenceBusiness.infinite", variables],
        metaData =>
            graphqlFetcher<GetReferenceBusinessQuery, GetReferenceBusinessQueryVariables>(
                GetReferenceBusinessDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const ReferenceContactSearchDocument = new TypedDocumentString(`
    query referenceContactSearch($search: ReferenceContactSearchRequest!) {
  referenceContactSearch(search: $search) {
    total
    coordinates {
      lat
      lng
    }
    hits {
      id
      name
      role
      updatedAt
      organizationName
      organizationType
      organizationPhone
      organizationFax
      address
      city
      state
      zip
      email
      cellPhone
      phone
      numberOfReferrals
      mostRecentReferral
      assignedTo
      businessId
      communityId
      summary
      latitude
      longitude
      appLink
      answers {
        questionId
        booleanAnswer
        numberAnswer
        numberAnswerHigh
        textAnswer
        dateAnswer
        calendarDateAnswer
        selections
        idReferenceAnswer
        selectionAnnotations {
          text
          selectionId
        }
      }
      organizationAnswers {
        questionId
        booleanAnswer
        numberAnswer
        numberAnswerHigh
        textAnswer
        dateAnswer
        calendarDateAnswer
        selections
        idReferenceAnswer
        selectionAnnotations {
          text
          selectionId
        }
      }
    }
  }
}
    `);

export const useReferenceContactSearchQuery = <
    TData = ReferenceContactSearchQuery,
    TError = unknown,
>(
    variables: ReferenceContactSearchQueryVariables,
    options?: UseQueryOptions<ReferenceContactSearchQuery, TError, TData>,
) => {
    return useQuery<ReferenceContactSearchQuery, TError, TData>(
        ["referenceContactSearch", variables],
        graphqlFetcher<ReferenceContactSearchQuery, ReferenceContactSearchQueryVariables>(
            ReferenceContactSearchDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteReferenceContactSearchQuery = <
    TData = ReferenceContactSearchQuery,
    TError = unknown,
>(
    variables: ReferenceContactSearchQueryVariables,
    options?: UseInfiniteQueryOptions<ReferenceContactSearchQuery, TError, TData>,
) => {
    return useInfiniteQuery<ReferenceContactSearchQuery, TError, TData>(
        ["referenceContactSearch.infinite", variables],
        metaData =>
            graphqlFetcher<
                ReferenceContactSearchQuery,
                ReferenceContactSearchQueryVariables
            >(ReferenceContactSearchDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const ReferenceContactMapSearchDocument = new TypedDocumentString(`
    query referenceContactMapSearch($searchParams: ReferenceContactSearchRequest!, $mapView: CommunitySearchRequestMapComponent!) {
  referenceContactMapSearch(searchParams: $searchParams, mapView: $mapView) {
    referenceContactCoordinates {
      businessId
      communityId
      name
      lat
      lng
      appLink
      contacts {
        id
        name
        appLink
      }
    }
    referenceContactClusters {
      lat
      lng
      count
    }
    recommendedZoom
    coordinates {
      lat
      lng
    }
  }
}
    `);

export const useReferenceContactMapSearchQuery = <
    TData = ReferenceContactMapSearchQuery,
    TError = unknown,
>(
    variables: ReferenceContactMapSearchQueryVariables,
    options?: UseQueryOptions<ReferenceContactMapSearchQuery, TError, TData>,
) => {
    return useQuery<ReferenceContactMapSearchQuery, TError, TData>(
        ["referenceContactMapSearch", variables],
        graphqlFetcher<
            ReferenceContactMapSearchQuery,
            ReferenceContactMapSearchQueryVariables
        >(ReferenceContactMapSearchDocument, variables),
        options,
    );
};

export const useInfiniteReferenceContactMapSearchQuery = <
    TData = ReferenceContactMapSearchQuery,
    TError = unknown,
>(
    variables: ReferenceContactMapSearchQueryVariables,
    options?: UseInfiniteQueryOptions<ReferenceContactMapSearchQuery, TError, TData>,
) => {
    return useInfiniteQuery<ReferenceContactMapSearchQuery, TError, TData>(
        ["referenceContactMapSearch.infinite", variables],
        metaData =>
            graphqlFetcher<
                ReferenceContactMapSearchQuery,
                ReferenceContactMapSearchQueryVariables
            >(ReferenceContactMapSearchDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const ReferenceBusinessSearchDocument = new TypedDocumentString(`
    query referenceBusinessSearch($search: ReferenceBusinessSearchRequest!) {
  referenceBusinessSearch(search: $search) {
    total
    coordinates {
      lat
      lng
    }
    hits {
      ...ReferenceBusinessSearchHitDefaults
    }
  }
}
    fragment ReferenceBusinessSearchHitDefaults on ReferenceBusinessSearchHit {
  id
  name
  updatedAt
  summary
  organizationType
  city
  state
  zip
  address
  phone
  fax
  email
  numberOfReferrals
  mostRecentReferral
  assignedTo
  latitude
  longitude
}`);

export const useReferenceBusinessSearchQuery = <
    TData = ReferenceBusinessSearchQuery,
    TError = unknown,
>(
    variables: ReferenceBusinessSearchQueryVariables,
    options?: UseQueryOptions<ReferenceBusinessSearchQuery, TError, TData>,
) => {
    return useQuery<ReferenceBusinessSearchQuery, TError, TData>(
        ["referenceBusinessSearch", variables],
        graphqlFetcher<
            ReferenceBusinessSearchQuery,
            ReferenceBusinessSearchQueryVariables
        >(ReferenceBusinessSearchDocument, variables),
        options,
    );
};

export const useInfiniteReferenceBusinessSearchQuery = <
    TData = ReferenceBusinessSearchQuery,
    TError = unknown,
>(
    variables: ReferenceBusinessSearchQueryVariables,
    options?: UseInfiniteQueryOptions<ReferenceBusinessSearchQuery, TError, TData>,
) => {
    return useInfiniteQuery<ReferenceBusinessSearchQuery, TError, TData>(
        ["referenceBusinessSearch.infinite", variables],
        metaData =>
            graphqlFetcher<
                ReferenceBusinessSearchQuery,
                ReferenceBusinessSearchQueryVariables
            >(ReferenceBusinessSearchDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const ReferenceBusinessMapSearchDocument = new TypedDocumentString(`
    query referenceBusinessMapSearch($searchParams: ReferenceBusinessSearchRequest!, $mapView: CommunitySearchRequestMapComponent!) {
  referenceBusinessMapSearch(searchParams: $searchParams, mapView: $mapView) {
    referenceBusinessCoordinates {
      id
      name
      lat
      lng
      appLink
    }
    referenceBusinessClusters {
      lat
      lng
      count
    }
    recommendedZoom
    coordinates {
      lat
      lng
    }
  }
}
    `);

export const useReferenceBusinessMapSearchQuery = <
    TData = ReferenceBusinessMapSearchQuery,
    TError = unknown,
>(
    variables: ReferenceBusinessMapSearchQueryVariables,
    options?: UseQueryOptions<ReferenceBusinessMapSearchQuery, TError, TData>,
) => {
    return useQuery<ReferenceBusinessMapSearchQuery, TError, TData>(
        ["referenceBusinessMapSearch", variables],
        graphqlFetcher<
            ReferenceBusinessMapSearchQuery,
            ReferenceBusinessMapSearchQueryVariables
        >(ReferenceBusinessMapSearchDocument, variables),
        options,
    );
};

export const useInfiniteReferenceBusinessMapSearchQuery = <
    TData = ReferenceBusinessMapSearchQuery,
    TError = unknown,
>(
    variables: ReferenceBusinessMapSearchQueryVariables,
    options?: UseInfiniteQueryOptions<ReferenceBusinessMapSearchQuery, TError, TData>,
) => {
    return useInfiniteQuery<ReferenceBusinessMapSearchQuery, TError, TData>(
        ["referenceBusinessMapSearch.infinite", variables],
        metaData =>
            graphqlFetcher<
                ReferenceBusinessMapSearchQuery,
                ReferenceBusinessMapSearchQueryVariables
            >(ReferenceBusinessMapSearchDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const ReferenceCommunitySearchDocument = new TypedDocumentString(`
    query referenceCommunitySearch($search: ReferenceCommunitySearchRequest!) {
  referenceCommunitySearch(search: $search) {
    total
    hits {
      ...ReferenceCommunitySearchHitDefaults
    }
  }
}
    fragment ReferenceCommunitySearchHitDefaults on ReferenceCommunitySearchHit {
  id
  name
  updatedAt
  city
  state
  zip
  address
  phone
  fax
  summary
  numberOfReferrals
  mostRecentReferral
  latitude
  longitude
}`);

export const useReferenceCommunitySearchQuery = <
    TData = ReferenceCommunitySearchQuery,
    TError = unknown,
>(
    variables: ReferenceCommunitySearchQueryVariables,
    options?: UseQueryOptions<ReferenceCommunitySearchQuery, TError, TData>,
) => {
    return useQuery<ReferenceCommunitySearchQuery, TError, TData>(
        ["referenceCommunitySearch", variables],
        graphqlFetcher<
            ReferenceCommunitySearchQuery,
            ReferenceCommunitySearchQueryVariables
        >(ReferenceCommunitySearchDocument, variables),
        options,
    );
};

export const useInfiniteReferenceCommunitySearchQuery = <
    TData = ReferenceCommunitySearchQuery,
    TError = unknown,
>(
    variables: ReferenceCommunitySearchQueryVariables,
    options?: UseInfiniteQueryOptions<ReferenceCommunitySearchQuery, TError, TData>,
) => {
    return useInfiniteQuery<ReferenceCommunitySearchQuery, TError, TData>(
        ["referenceCommunitySearch.infinite", variables],
        metaData =>
            graphqlFetcher<
                ReferenceCommunitySearchQuery,
                ReferenceCommunitySearchQueryVariables
            >(ReferenceCommunitySearchDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const ReferenceCommunityMapSearchDocument = new TypedDocumentString(`
    query referenceCommunityMapSearch($searchParams: ReferenceCommunitySearchRequest!, $mapView: CommunitySearchRequestMapComponent!) {
  referenceCommunityMapSearch(searchParams: $searchParams, mapView: $mapView) {
    communityCoordinates {
      id
      name
      lat
      lng
      appLink
    }
    communityClusters {
      lat
      lng
      count
    }
    recommendedZoom
    coordinates {
      lat
      lng
    }
  }
}
    `);

export const useReferenceCommunityMapSearchQuery = <
    TData = ReferenceCommunityMapSearchQuery,
    TError = unknown,
>(
    variables: ReferenceCommunityMapSearchQueryVariables,
    options?: UseQueryOptions<ReferenceCommunityMapSearchQuery, TError, TData>,
) => {
    return useQuery<ReferenceCommunityMapSearchQuery, TError, TData>(
        ["referenceCommunityMapSearch", variables],
        graphqlFetcher<
            ReferenceCommunityMapSearchQuery,
            ReferenceCommunityMapSearchQueryVariables
        >(ReferenceCommunityMapSearchDocument, variables),
        options,
    );
};

export const useInfiniteReferenceCommunityMapSearchQuery = <
    TData = ReferenceCommunityMapSearchQuery,
    TError = unknown,
>(
    variables: ReferenceCommunityMapSearchQueryVariables,
    options?: UseInfiniteQueryOptions<ReferenceCommunityMapSearchQuery, TError, TData>,
) => {
    return useInfiniteQuery<ReferenceCommunityMapSearchQuery, TError, TData>(
        ["referenceCommunityMapSearch.infinite", variables],
        metaData =>
            graphqlFetcher<
                ReferenceCommunityMapSearchQuery,
                ReferenceCommunityMapSearchQueryVariables
            >(ReferenceCommunityMapSearchDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const ReferenceOrganizationSearchDocument = new TypedDocumentString(`
    query referenceOrganizationSearch($search: ReferenceOrganizationSearchRequest!) {
  referenceOrganizationSearch(search: $search) {
    total
    hits {
      id
      name
      updatedAt
      summary
      organizationType
      city
      state
      zip
      address
      phone
      fax
      email
      numberOfReferrals
      mostRecentReferral
      assignedTo
      latitude
      longitude
      appLink
      entityType
      contacts {
        cellPhone
        name
        phone
        email
        role
        appLink
      }
      answers {
        questionId
        booleanAnswer
        numberAnswer
        numberAnswerHigh
        textAnswer
        dateAnswer
        calendarDateAnswer
        selections
        idReferenceAnswer
        selectionAnnotations {
          text
          selectionId
        }
      }
    }
  }
}
    `);

export const useReferenceOrganizationSearchQuery = <
    TData = ReferenceOrganizationSearchQuery,
    TError = unknown,
>(
    variables: ReferenceOrganizationSearchQueryVariables,
    options?: UseQueryOptions<ReferenceOrganizationSearchQuery, TError, TData>,
) => {
    return useQuery<ReferenceOrganizationSearchQuery, TError, TData>(
        ["referenceOrganizationSearch", variables],
        graphqlFetcher<
            ReferenceOrganizationSearchQuery,
            ReferenceOrganizationSearchQueryVariables
        >(ReferenceOrganizationSearchDocument, variables),
        options,
    );
};

export const useInfiniteReferenceOrganizationSearchQuery = <
    TData = ReferenceOrganizationSearchQuery,
    TError = unknown,
>(
    variables: ReferenceOrganizationSearchQueryVariables,
    options?: UseInfiniteQueryOptions<ReferenceOrganizationSearchQuery, TError, TData>,
) => {
    return useInfiniteQuery<ReferenceOrganizationSearchQuery, TError, TData>(
        ["referenceOrganizationSearch.infinite", variables],
        metaData =>
            graphqlFetcher<
                ReferenceOrganizationSearchQuery,
                ReferenceOrganizationSearchQueryVariables
            >(ReferenceOrganizationSearchDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const ReferenceOrganizationMapSearchDocument = new TypedDocumentString(`
    query referenceOrganizationMapSearch($searchParams: ReferenceOrganizationSearchRequest!, $mapView: CommunitySearchRequestMapComponent!) {
  referenceOrganizationMapSearch(searchParams: $searchParams, mapView: $mapView) {
    organizationCoordinates {
      id
      name
      lat
      lng
      appLink
    }
    organizationClusters {
      lat
      lng
      count
    }
    recommendedZoom
    coordinates {
      lat
      lng
    }
  }
}
    `);

export const useReferenceOrganizationMapSearchQuery = <
    TData = ReferenceOrganizationMapSearchQuery,
    TError = unknown,
>(
    variables: ReferenceOrganizationMapSearchQueryVariables,
    options?: UseQueryOptions<ReferenceOrganizationMapSearchQuery, TError, TData>,
) => {
    return useQuery<ReferenceOrganizationMapSearchQuery, TError, TData>(
        ["referenceOrganizationMapSearch", variables],
        graphqlFetcher<
            ReferenceOrganizationMapSearchQuery,
            ReferenceOrganizationMapSearchQueryVariables
        >(ReferenceOrganizationMapSearchDocument, variables),
        options,
    );
};

export const useInfiniteReferenceOrganizationMapSearchQuery = <
    TData = ReferenceOrganizationMapSearchQuery,
    TError = unknown,
>(
    variables: ReferenceOrganizationMapSearchQueryVariables,
    options?: UseInfiniteQueryOptions<ReferenceOrganizationMapSearchQuery, TError, TData>,
) => {
    return useInfiniteQuery<ReferenceOrganizationMapSearchQuery, TError, TData>(
        ["referenceOrganizationMapSearch.infinite", variables],
        metaData =>
            graphqlFetcher<
                ReferenceOrganizationMapSearchQuery,
                ReferenceOrganizationMapSearchQueryVariables
            >(ReferenceOrganizationMapSearchDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetReferralsToContactDocument = new TypedDocumentString(`
    query getReferralsToContact($referenceContactId: ReferenceContactId!) {
  getReferralsToContact(referenceContactId: $referenceContactId) {
    ...ReferralBasics
  }
}
    fragment ReferralBasics on Referral {
  id
  placementId
  placement {
    id
    name
    bestContactName
    statusListItemId
    dateAdded
    nextTask {
      id
      text
      dueDate
      dueDateTime
      compositeDueDateTime
      status
    }
    serial
    assignedUserId
    updatedAt
    moveTimeline
    moveTimelineActual
  }
}`);

export const useGetReferralsToContactQuery = <
    TData = GetReferralsToContactQuery,
    TError = unknown,
>(
    variables: GetReferralsToContactQueryVariables,
    options?: UseQueryOptions<GetReferralsToContactQuery, TError, TData>,
) => {
    return useQuery<GetReferralsToContactQuery, TError, TData>(
        ["getReferralsToContact", variables],
        graphqlFetcher<GetReferralsToContactQuery, GetReferralsToContactQueryVariables>(
            GetReferralsToContactDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetReferralsToContactQuery = <
    TData = GetReferralsToContactQuery,
    TError = unknown,
>(
    variables: GetReferralsToContactQueryVariables,
    options?: UseInfiniteQueryOptions<GetReferralsToContactQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetReferralsToContactQuery, TError, TData>(
        ["getReferralsToContact.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetReferralsToContactQuery,
                GetReferralsToContactQueryVariables
            >(GetReferralsToContactDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const CreateReferenceContactDocument = new TypedDocumentString(`
    mutation createReferenceContact($regionId: RegionId!) {
  createReferenceContact(regionId: $regionId) {
    id
  }
}
    `);

export const useCreateReferenceContactMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateReferenceContactMutation,
        TError,
        CreateReferenceContactMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateReferenceContactMutation,
        TError,
        CreateReferenceContactMutationVariables,
        TContext
    >(
        ["createReferenceContact"],
        (variables?: CreateReferenceContactMutationVariables) =>
            graphqlFetcher<
                CreateReferenceContactMutation,
                CreateReferenceContactMutationVariables
            >(CreateReferenceContactDocument, variables)(),
        options,
    );
};

export const PatchReferenceContactDocument = new TypedDocumentString(`
    mutation patchReferenceContact($id: ReferenceContactId!, $summary: String, $ownerId: UserId, $businessId: ReferenceBusinessId, $communityId: CommunityId, $contactEntity: ContactInput, $deleted: Boolean, $draftNote: String, $bestDaysToVisit: [String!], $bestTimeToContact: [String!]) {
  patchReferenceContact(
    id: $id
    summary: $summary
    ownerId: $ownerId
    businessId: $businessId
    communityId: $communityId
    contactEntity: $contactEntity
    deleted: $deleted
    draftNote: $draftNote
    bestDaysToVisit: $bestDaysToVisit
    bestTimeToContact: $bestTimeToContact
  ) {
    ...ReferenceContactBasics
  }
}
    fragment ContactFragment on ContactEntity {
  id
  name
  preferredName
  address1
  address2
  city
  state
  zip
  relationship
  role
  cellPhone
  phone1
  email1
  email1OptOut
  website
  contactType
  type
  fax
  contactNotes
  dateAdded
}
fragment NoteResponse on NoteDto {
  _id
  text
  dateAdded
  wasEdited
  authorId
  editorId
  updatedAt
  isDraft
  isPinned
}
fragment ReferenceContactBasics on ReferenceContact {
  id
  ownerId
  deleted
  summary
  businessId
  communityId
  regionId
  dateAdded
  updatedAt
  createdBy
  updatedBy
  draftNote
  bestDaysToVisit
  bestTimeToContact
  contactEntity {
    ...ContactFragment
  }
  business {
    id
    ownerId
    deleted
    regionId
    latitude
    longitude
    businessContactEntity {
      ...ContactFragment
    }
  }
  community {
    id
    name
    address
    city
    state
    zip
    mainPhone
    website
    email
    latitude
    longitude
    contractStatus
  }
  answers {
    questionId
    booleanAnswer
    numberAnswer
    numberAnswerHigh
    textAnswer
    dateAnswer
    calendarDateAnswer
    selections
    idReferenceAnswer
    selectionAnnotations {
      text
      selectionId
    }
  }
  notes {
    ...NoteResponse
  }
}`);

export const usePatchReferenceContactMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PatchReferenceContactMutation,
        TError,
        PatchReferenceContactMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PatchReferenceContactMutation,
        TError,
        PatchReferenceContactMutationVariables,
        TContext
    >(
        ["patchReferenceContact"],
        (variables?: PatchReferenceContactMutationVariables) =>
            graphqlFetcher<
                PatchReferenceContactMutation,
                PatchReferenceContactMutationVariables
            >(PatchReferenceContactDocument, variables)(),
        options,
    );
};

export const CreateReferenceBusinessDocument = new TypedDocumentString(`
    mutation createReferenceBusiness($regionId: RegionId!) {
  createReferenceBusiness(regionId: $regionId) {
    id
  }
}
    `);

export const useCreateReferenceBusinessMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateReferenceBusinessMutation,
        TError,
        CreateReferenceBusinessMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateReferenceBusinessMutation,
        TError,
        CreateReferenceBusinessMutationVariables,
        TContext
    >(
        ["createReferenceBusiness"],
        (variables?: CreateReferenceBusinessMutationVariables) =>
            graphqlFetcher<
                CreateReferenceBusinessMutation,
                CreateReferenceBusinessMutationVariables
            >(CreateReferenceBusinessDocument, variables)(),
        options,
    );
};

export const PatchReferenceBusinessDocument = new TypedDocumentString(`
    mutation patchReferenceBusiness($id: ReferenceBusinessId!, $summary: String, $email: String, $ownerId: UserId, $businessContactEntity: ContactInput, $deleted: Boolean, $draftNote: String, $bestDaysToVisit: [String!], $bestTimeToContact: [String!]) {
  patchReferenceBusiness(
    id: $id
    summary: $summary
    email: $email
    ownerId: $ownerId
    businessContactEntity: $businessContactEntity
    deleted: $deleted
    draftNote: $draftNote
    bestDaysToVisit: $bestDaysToVisit
    bestTimeToContact: $bestTimeToContact
  ) {
    ...ReferenceBusinessBasics
  }
}
    fragment ContactFragment on ContactEntity {
  id
  name
  preferredName
  address1
  address2
  city
  state
  zip
  relationship
  role
  cellPhone
  phone1
  email1
  email1OptOut
  website
  contactType
  type
  fax
  contactNotes
  dateAdded
}
fragment NoteResponse on NoteDto {
  _id
  text
  dateAdded
  wasEdited
  authorId
  editorId
  updatedAt
  isDraft
  isPinned
}
fragment ReferenceBusinessBasics on ReferenceBusiness {
  id
  ownerId
  deleted
  summary
  email
  dateAdded
  updatedAt
  createdBy
  updatedBy
  draftNote
  bestDaysToVisit
  bestTimeToContact
  regionId
  latitude
  longitude
  businessContactEntity {
    ...ContactFragment
  }
  answers {
    questionId
    booleanAnswer
    numberAnswer
    numberAnswerHigh
    textAnswer
    dateAnswer
    calendarDateAnswer
    selections
    idReferenceAnswer
    selectionAnnotations {
      text
      selectionId
    }
  }
  notes {
    ...NoteResponse
  }
}`);

export const usePatchReferenceBusinessMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PatchReferenceBusinessMutation,
        TError,
        PatchReferenceBusinessMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PatchReferenceBusinessMutation,
        TError,
        PatchReferenceBusinessMutationVariables,
        TContext
    >(
        ["patchReferenceBusiness"],
        (variables?: PatchReferenceBusinessMutationVariables) =>
            graphqlFetcher<
                PatchReferenceBusinessMutation,
                PatchReferenceBusinessMutationVariables
            >(PatchReferenceBusinessDocument, variables)(),
        options,
    );
};

export const GetReferralsToBusinessDocument = new TypedDocumentString(`
    query getReferralsToBusiness($referenceBusinessId: ReferenceBusinessId!) {
  getReferralsToBusiness(referenceBusinessId: $referenceBusinessId) {
    ...ReferralBasics
  }
}
    fragment ReferralBasics on Referral {
  id
  placementId
  placement {
    id
    name
    bestContactName
    statusListItemId
    dateAdded
    nextTask {
      id
      text
      dueDate
      dueDateTime
      compositeDueDateTime
      status
    }
    serial
    assignedUserId
    updatedAt
    moveTimeline
    moveTimelineActual
  }
}`);

export const useGetReferralsToBusinessQuery = <
    TData = GetReferralsToBusinessQuery,
    TError = unknown,
>(
    variables: GetReferralsToBusinessQueryVariables,
    options?: UseQueryOptions<GetReferralsToBusinessQuery, TError, TData>,
) => {
    return useQuery<GetReferralsToBusinessQuery, TError, TData>(
        ["getReferralsToBusiness", variables],
        graphqlFetcher<GetReferralsToBusinessQuery, GetReferralsToBusinessQueryVariables>(
            GetReferralsToBusinessDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetReferralsToBusinessQuery = <
    TData = GetReferralsToBusinessQuery,
    TError = unknown,
>(
    variables: GetReferralsToBusinessQueryVariables,
    options?: UseInfiniteQueryOptions<GetReferralsToBusinessQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetReferralsToBusinessQuery, TError, TData>(
        ["getReferralsToBusiness.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetReferralsToBusinessQuery,
                GetReferralsToBusinessQueryVariables
            >(GetReferralsToBusinessDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GeocodeReferenceBusinessesDocument = new TypedDocumentString(`
    mutation geocodeReferenceBusinesses {
  geocodeReferenceBusinesses
}
    `);

export const useGeocodeReferenceBusinessesMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        GeocodeReferenceBusinessesMutation,
        TError,
        GeocodeReferenceBusinessesMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        GeocodeReferenceBusinessesMutation,
        TError,
        GeocodeReferenceBusinessesMutationVariables,
        TContext
    >(
        ["geocodeReferenceBusinesses"],
        (variables?: GeocodeReferenceBusinessesMutationVariables) =>
            graphqlFetcher<
                GeocodeReferenceBusinessesMutation,
                GeocodeReferenceBusinessesMutationVariables
            >(GeocodeReferenceBusinessesDocument, variables)(),
        options,
    );
};

export const AddReferenceContactFromAgentDirectoryDocument = new TypedDocumentString(`
    mutation addReferenceContactFromAgentDirectory($userProfileId: UserProfileId!, $regionId: RegionId!) {
  addReferenceContactFromAgentDirectory(
    userProfileId: $userProfileId
    regionId: $regionId
  ) {
    id
    userProfileId
  }
}
    `);

export const useAddReferenceContactFromAgentDirectoryMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        AddReferenceContactFromAgentDirectoryMutation,
        TError,
        AddReferenceContactFromAgentDirectoryMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        AddReferenceContactFromAgentDirectoryMutation,
        TError,
        AddReferenceContactFromAgentDirectoryMutationVariables,
        TContext
    >(
        ["addReferenceContactFromAgentDirectory"],
        (variables?: AddReferenceContactFromAgentDirectoryMutationVariables) =>
            graphqlFetcher<
                AddReferenceContactFromAgentDirectoryMutation,
                AddReferenceContactFromAgentDirectoryMutationVariables
            >(AddReferenceContactFromAgentDirectoryDocument, variables)(),
        options,
    );
};

export const GetRegionDocument = new TypedDocumentString(`
    query getRegion($id: RegionId!, $tenantId: TenantId!) {
  getRegion(regionId: $id, tenantId: $tenantId) {
    id
    key
    name
    parentRegionId
    billableEntityId
    regionLicenseMaps {
      id
      autoPropagateEnabled
      identityFilterObjects {
        state
        zip
        county
      }
      licenseMappingObjects {
        licenseIdentifier
        promptId
        questionId
        annotationOnly
      }
      settings {
        mode
        updateAttributes
        closedHandling
      }
    }
  }
}
    `);

export const useGetRegionQuery = <TData = GetRegionQuery, TError = unknown>(
    variables: GetRegionQueryVariables,
    options?: UseQueryOptions<GetRegionQuery, TError, TData>,
) => {
    return useQuery<GetRegionQuery, TError, TData>(
        ["getRegion", variables],
        graphqlFetcher<GetRegionQuery, GetRegionQueryVariables>(
            GetRegionDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetRegionQuery = <TData = GetRegionQuery, TError = unknown>(
    variables: GetRegionQueryVariables,
    options?: UseInfiniteQueryOptions<GetRegionQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetRegionQuery, TError, TData>(
        ["getRegion.infinite", variables],
        metaData =>
            graphqlFetcher<GetRegionQuery, GetRegionQueryVariables>(GetRegionDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetStateReportDocument = new TypedDocumentString(`
    query getStateReport {
  getStateReport {
    thecount
    totalrlmcount
    state
    regionid
    regionkey
    regionname
    tenantname
    tenantid
  }
}
    `);

export const useGetStateReportQuery = <TData = GetStateReportQuery, TError = unknown>(
    variables?: GetStateReportQueryVariables,
    options?: UseQueryOptions<GetStateReportQuery, TError, TData>,
) => {
    return useQuery<GetStateReportQuery, TError, TData>(
        variables === undefined ? ["getStateReport"] : ["getStateReport", variables],
        graphqlFetcher<GetStateReportQuery, GetStateReportQueryVariables>(
            GetStateReportDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetStateReportQuery = <
    TData = GetStateReportQuery,
    TError = unknown,
>(
    variables?: GetStateReportQueryVariables,
    options?: UseInfiniteQueryOptions<GetStateReportQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetStateReportQuery, TError, TData>(
        variables === undefined
            ? ["getStateReport.infinite"]
            : ["getStateReport.infinite", variables],
        metaData =>
            graphqlFetcher<GetStateReportQuery, GetStateReportQueryVariables>(
                GetStateReportDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const PropagateChangesDocument = new TypedDocumentString(`
    mutation propagateChanges($tenantId: TenantId!, $regionId: RegionId!) {
  createCommunitiesInRegion(tenantId: $tenantId, regionId: $regionId) {
    propagations {
      regionLicenseMapId
      communitiesCreated
      communitiesMatched
      communitiesWithExistingLinks
    }
  }
}
    `);

export const usePropagateChangesMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PropagateChangesMutation,
        TError,
        PropagateChangesMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PropagateChangesMutation,
        TError,
        PropagateChangesMutationVariables,
        TContext
    >(
        ["propagateChanges"],
        (variables?: PropagateChangesMutationVariables) =>
            graphqlFetcher<PropagateChangesMutation, PropagateChangesMutationVariables>(
                PropagateChangesDocument,
                variables,
            )(),
        options,
    );
};

export const CreateOrUpdateRegionLicenseMapDocument = new TypedDocumentString(`
    mutation createOrUpdateRegionLicenseMap($regionLicenseMap: RegionLicenseMapUpdateRequest!) {
  createOrUpdateRegionLicenseMap(params: $regionLicenseMap) {
    id
  }
}
    `);

export const useCreateOrUpdateRegionLicenseMapMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        CreateOrUpdateRegionLicenseMapMutation,
        TError,
        CreateOrUpdateRegionLicenseMapMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateOrUpdateRegionLicenseMapMutation,
        TError,
        CreateOrUpdateRegionLicenseMapMutationVariables,
        TContext
    >(
        ["createOrUpdateRegionLicenseMap"],
        (variables?: CreateOrUpdateRegionLicenseMapMutationVariables) =>
            graphqlFetcher<
                CreateOrUpdateRegionLicenseMapMutation,
                CreateOrUpdateRegionLicenseMapMutationVariables
            >(CreateOrUpdateRegionLicenseMapDocument, variables)(),
        options,
    );
};

export const DeleteRegionLicenseMapDocument = new TypedDocumentString(`
    mutation deleteRegionLicenseMap($id: RegionLicenseMapId!) {
  deleteRegionLicenseMap(regionLicenseMapId: $id)
}
    `);

export const useDeleteRegionLicenseMapMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteRegionLicenseMapMutation,
        TError,
        DeleteRegionLicenseMapMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteRegionLicenseMapMutation,
        TError,
        DeleteRegionLicenseMapMutationVariables,
        TContext
    >(
        ["deleteRegionLicenseMap"],
        (variables?: DeleteRegionLicenseMapMutationVariables) =>
            graphqlFetcher<
                DeleteRegionLicenseMapMutation,
                DeleteRegionLicenseMapMutationVariables
            >(DeleteRegionLicenseMapDocument, variables)(),
        options,
    );
};

export const GetEntityRegionPermissionsDocument = new TypedDocumentString(`
    query getEntityRegionPermissions($params: GetEntityRegionPermissionsParams!) {
  getEntityRegionPermissions(params: $params) {
    editableRegions {
      id
      key
      name
      type
    }
  }
}
    `);

export const useGetEntityRegionPermissionsQuery = <
    TData = GetEntityRegionPermissionsQuery,
    TError = unknown,
>(
    variables: GetEntityRegionPermissionsQueryVariables,
    options?: UseQueryOptions<GetEntityRegionPermissionsQuery, TError, TData>,
) => {
    return useQuery<GetEntityRegionPermissionsQuery, TError, TData>(
        ["getEntityRegionPermissions", variables],
        graphqlFetcher<
            GetEntityRegionPermissionsQuery,
            GetEntityRegionPermissionsQueryVariables
        >(GetEntityRegionPermissionsDocument, variables),
        options,
    );
};

export const useInfiniteGetEntityRegionPermissionsQuery = <
    TData = GetEntityRegionPermissionsQuery,
    TError = unknown,
>(
    variables: GetEntityRegionPermissionsQueryVariables,
    options?: UseInfiniteQueryOptions<GetEntityRegionPermissionsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetEntityRegionPermissionsQuery, TError, TData>(
        ["getEntityRegionPermissions.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetEntityRegionPermissionsQuery,
                GetEntityRegionPermissionsQueryVariables
            >(GetEntityRegionPermissionsDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const ClientPlacementRevenueDocument = new TypedDocumentString(`
    query ClientPlacementRevenue($startDate: String!, $endDate: String!, $region: String!, $revenueBasis: RevenueBasis!, $amountBasis: RevenueAmountBasis!) {
  reportClientPlacementRevenue(
    startDate: $startDate
    endDate: $endDate
    region: $region
    revenueBasis: $revenueBasis
    amountBasis: $amountBasis
  ) {
    segments {
      startDate
      revenue
      moves
      users {
        user {
          id
          displayName
        }
        revenue
        moves
      }
    }
  }
}
    `);

export const useClientPlacementRevenueQuery = <
    TData = ClientPlacementRevenueQuery,
    TError = unknown,
>(
    variables: ClientPlacementRevenueQueryVariables,
    options?: UseQueryOptions<ClientPlacementRevenueQuery, TError, TData>,
) => {
    return useQuery<ClientPlacementRevenueQuery, TError, TData>(
        ["ClientPlacementRevenue", variables],
        graphqlFetcher<ClientPlacementRevenueQuery, ClientPlacementRevenueQueryVariables>(
            ClientPlacementRevenueDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteClientPlacementRevenueQuery = <
    TData = ClientPlacementRevenueQuery,
    TError = unknown,
>(
    variables: ClientPlacementRevenueQueryVariables,
    options?: UseInfiniteQueryOptions<ClientPlacementRevenueQuery, TError, TData>,
) => {
    return useInfiniteQuery<ClientPlacementRevenueQuery, TError, TData>(
        ["ClientPlacementRevenue.infinite", variables],
        metaData =>
            graphqlFetcher<
                ClientPlacementRevenueQuery,
                ClientPlacementRevenueQueryVariables
            >(ClientPlacementRevenueDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetRegionSettingsDocument = new TypedDocumentString(`
    query getRegionSettings($params: GetRegionSettingsRequest!) {
  getRegionSettings(params: $params) {
    regionId
    settings {
      key
      source
      value {
        textValue
        intValue
        booleanValue
        jsonValue
      }
    }
  }
}
    `);

export const useGetRegionSettingsQuery = <
    TData = GetRegionSettingsQuery,
    TError = unknown,
>(
    variables: GetRegionSettingsQueryVariables,
    options?: UseQueryOptions<GetRegionSettingsQuery, TError, TData>,
) => {
    return useQuery<GetRegionSettingsQuery, TError, TData>(
        ["getRegionSettings", variables],
        graphqlFetcher<GetRegionSettingsQuery, GetRegionSettingsQueryVariables>(
            GetRegionSettingsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetRegionSettingsQuery = <
    TData = GetRegionSettingsQuery,
    TError = unknown,
>(
    variables: GetRegionSettingsQueryVariables,
    options?: UseInfiniteQueryOptions<GetRegionSettingsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetRegionSettingsQuery, TError, TData>(
        ["getRegionSettings.infinite", variables],
        metaData =>
            graphqlFetcher<GetRegionSettingsQuery, GetRegionSettingsQueryVariables>(
                GetRegionSettingsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const PutRegionSettingDocument = new TypedDocumentString(`
    mutation putRegionSetting($params: PutRegionSettingRequest!) {
  putRegionSetting(params: $params)
}
    `);

export const usePutRegionSettingMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PutRegionSettingMutation,
        TError,
        PutRegionSettingMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PutRegionSettingMutation,
        TError,
        PutRegionSettingMutationVariables,
        TContext
    >(
        ["putRegionSetting"],
        (variables?: PutRegionSettingMutationVariables) =>
            graphqlFetcher<PutRegionSettingMutation, PutRegionSettingMutationVariables>(
                PutRegionSettingDocument,
                variables,
            )(),
        options,
    );
};

export const PutRegionSettingsDocument = new TypedDocumentString(`
    mutation putRegionSettings($params: PutRegionSettingsRequest!) {
  putRegionSettings(params: $params)
}
    `);

export const usePutRegionSettingsMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PutRegionSettingsMutation,
        TError,
        PutRegionSettingsMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PutRegionSettingsMutation,
        TError,
        PutRegionSettingsMutationVariables,
        TContext
    >(
        ["putRegionSettings"],
        (variables?: PutRegionSettingsMutationVariables) =>
            graphqlFetcher<PutRegionSettingsMutation, PutRegionSettingsMutationVariables>(
                PutRegionSettingsDocument,
                variables,
            )(),
        options,
    );
};

export const DeleteRegionSettingDocument = new TypedDocumentString(`
    mutation deleteRegionSetting($params: DeleteRegionSettingRequest!) {
  deleteRegionSetting(params: $params)
}
    `);

export const useDeleteRegionSettingMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteRegionSettingMutation,
        TError,
        DeleteRegionSettingMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteRegionSettingMutation,
        TError,
        DeleteRegionSettingMutationVariables,
        TContext
    >(
        ["deleteRegionSetting"],
        (variables?: DeleteRegionSettingMutationVariables) =>
            graphqlFetcher<
                DeleteRegionSettingMutation,
                DeleteRegionSettingMutationVariables
            >(DeleteRegionSettingDocument, variables)(),
        options,
    );
};

export const DeleteRegionSettingsDocument = new TypedDocumentString(`
    mutation deleteRegionSettings($params: DeleteRegionSettingsRequest!) {
  deleteRegionSettings(params: $params)
}
    `);

export const useDeleteRegionSettingsMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteRegionSettingsMutation,
        TError,
        DeleteRegionSettingsMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteRegionSettingsMutation,
        TError,
        DeleteRegionSettingsMutationVariables,
        TContext
    >(
        ["deleteRegionSettings"],
        (variables?: DeleteRegionSettingsMutationVariables) =>
            graphqlFetcher<
                DeleteRegionSettingsMutation,
                DeleteRegionSettingsMutationVariables
            >(DeleteRegionSettingsDocument, variables)(),
        options,
    );
};

export const PutTenantSettingDocument = new TypedDocumentString(`
    mutation putTenantSetting($params: PutTenantSettingRequest!) {
  putTenantSetting(params: $params)
}
    `);

export const usePutTenantSettingMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PutTenantSettingMutation,
        TError,
        PutTenantSettingMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PutTenantSettingMutation,
        TError,
        PutTenantSettingMutationVariables,
        TContext
    >(
        ["putTenantSetting"],
        (variables?: PutTenantSettingMutationVariables) =>
            graphqlFetcher<PutTenantSettingMutation, PutTenantSettingMutationVariables>(
                PutTenantSettingDocument,
                variables,
            )(),
        options,
    );
};

export const GetTenantSettingsDocument = new TypedDocumentString(`
    query getTenantSettings {
  getTenantSettings {
    tenantId
    settings {
      key
      value {
        textValue
        intValue
        booleanValue
        jsonValue
      }
    }
  }
}
    `);

export const useGetTenantSettingsQuery = <
    TData = GetTenantSettingsQuery,
    TError = unknown,
>(
    variables?: GetTenantSettingsQueryVariables,
    options?: UseQueryOptions<GetTenantSettingsQuery, TError, TData>,
) => {
    return useQuery<GetTenantSettingsQuery, TError, TData>(
        variables === undefined
            ? ["getTenantSettings"]
            : ["getTenantSettings", variables],
        graphqlFetcher<GetTenantSettingsQuery, GetTenantSettingsQueryVariables>(
            GetTenantSettingsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetTenantSettingsQuery = <
    TData = GetTenantSettingsQuery,
    TError = unknown,
>(
    variables?: GetTenantSettingsQueryVariables,
    options?: UseInfiniteQueryOptions<GetTenantSettingsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetTenantSettingsQuery, TError, TData>(
        variables === undefined
            ? ["getTenantSettings.infinite"]
            : ["getTenantSettings.infinite", variables],
        metaData =>
            graphqlFetcher<GetTenantSettingsQuery, GetTenantSettingsQueryVariables>(
                GetTenantSettingsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetSignatureTemplateDocument = new TypedDocumentString(`
    query getSignatureTemplate($fileId: FileId!) {
  getFile(fileId: $fileId) {
    originalFilename
  }
  getSignatureTemplate(fileId: $fileId) {
    id
    recipientCount
    emailTemplate {
      subject
      body
      attachments
    }
    completedEmailTemplate {
      subject
      body
      attachments
    }
    signatureTemplateFields {
      defaultTextValue
      defaultBooleanValue
      isRequired
      placeholder
      fieldType
      font
      fontSize
      height
      id
      order
      page
      recipientIndex
      width
      x
      y
    }
  }
}
    `);

export const useGetSignatureTemplateQuery = <
    TData = GetSignatureTemplateQuery,
    TError = unknown,
>(
    variables: GetSignatureTemplateQueryVariables,
    options?: UseQueryOptions<GetSignatureTemplateQuery, TError, TData>,
) => {
    return useQuery<GetSignatureTemplateQuery, TError, TData>(
        ["getSignatureTemplate", variables],
        graphqlFetcher<GetSignatureTemplateQuery, GetSignatureTemplateQueryVariables>(
            GetSignatureTemplateDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetSignatureTemplateQuery = <
    TData = GetSignatureTemplateQuery,
    TError = unknown,
>(
    variables: GetSignatureTemplateQueryVariables,
    options?: UseInfiniteQueryOptions<GetSignatureTemplateQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetSignatureTemplateQuery, TError, TData>(
        ["getSignatureTemplate.infinite", variables],
        metaData =>
            graphqlFetcher<GetSignatureTemplateQuery, GetSignatureTemplateQueryVariables>(
                GetSignatureTemplateDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const SetSignatureTemplateDocument = new TypedDocumentString(`
    mutation setSignatureTemplate($fileId: FileId!, $signatureTemplate: SignatureTemplateInput!) {
  setSignatureTemplate(fileId: $fileId, signatureTemplate: $signatureTemplate) {
    id
  }
}
    `);

export const useSetSignatureTemplateMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        SetSignatureTemplateMutation,
        TError,
        SetSignatureTemplateMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SetSignatureTemplateMutation,
        TError,
        SetSignatureTemplateMutationVariables,
        TContext
    >(
        ["setSignatureTemplate"],
        (variables?: SetSignatureTemplateMutationVariables) =>
            graphqlFetcher<
                SetSignatureTemplateMutation,
                SetSignatureTemplateMutationVariables
            >(SetSignatureTemplateDocument, variables)(),
        options,
    );
};

export const GetAllSignatureRequestsDocument = new TypedDocumentString(`
    query getAllSignatureRequests($signatureRequestFilter: SignatureRequestFilter!) {
  getAllSignatureRequests(signatureRequestFilter: $signatureRequestFilter) {
    total
    page
    signatureRequests {
      entity {
        displayName
        kind
        kindTitle
        path
      }
      signatureRequest {
        id
        entityId
        createdAt
        updatedAt
        filename
        sourceFileId
        signatureTemplateFileId
        state
        signatureRequestRecipients {
          email
          isComplete
          id
          order
          remindAfter
        }
      }
    }
  }
}
    `);

export const useGetAllSignatureRequestsQuery = <
    TData = GetAllSignatureRequestsQuery,
    TError = unknown,
>(
    variables: GetAllSignatureRequestsQueryVariables,
    options?: UseQueryOptions<GetAllSignatureRequestsQuery, TError, TData>,
) => {
    return useQuery<GetAllSignatureRequestsQuery, TError, TData>(
        ["getAllSignatureRequests", variables],
        graphqlFetcher<
            GetAllSignatureRequestsQuery,
            GetAllSignatureRequestsQueryVariables
        >(GetAllSignatureRequestsDocument, variables),
        options,
    );
};

export const useInfiniteGetAllSignatureRequestsQuery = <
    TData = GetAllSignatureRequestsQuery,
    TError = unknown,
>(
    variables: GetAllSignatureRequestsQueryVariables,
    options?: UseInfiniteQueryOptions<GetAllSignatureRequestsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetAllSignatureRequestsQuery, TError, TData>(
        ["getAllSignatureRequests.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetAllSignatureRequestsQuery,
                GetAllSignatureRequestsQueryVariables
            >(GetAllSignatureRequestsDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetSignableTemplatesDocument = new TypedDocumentString(`
    query getSignableTemplates($entityType: FileEntityType!) {
  getSignableTemplates(entityType: $entityType) {
    originalFilename
    id
  }
}
    `);

export const useGetSignableTemplatesQuery = <
    TData = GetSignableTemplatesQuery,
    TError = unknown,
>(
    variables: GetSignableTemplatesQueryVariables,
    options?: UseQueryOptions<GetSignableTemplatesQuery, TError, TData>,
) => {
    return useQuery<GetSignableTemplatesQuery, TError, TData>(
        ["getSignableTemplates", variables],
        graphqlFetcher<GetSignableTemplatesQuery, GetSignableTemplatesQueryVariables>(
            GetSignableTemplatesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetSignableTemplatesQuery = <
    TData = GetSignableTemplatesQuery,
    TError = unknown,
>(
    variables: GetSignableTemplatesQueryVariables,
    options?: UseInfiniteQueryOptions<GetSignableTemplatesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetSignableTemplatesQuery, TError, TData>(
        ["getSignableTemplates.infinite", variables],
        metaData =>
            graphqlFetcher<GetSignableTemplatesQuery, GetSignableTemplatesQueryVariables>(
                GetSignableTemplatesDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetSignatureRequestsForEntityDocument = new TypedDocumentString(`
    query getSignatureRequestsForEntity($entityId: FileEntityId!) {
  getSignatureRequestsForEntity(entityId: $entityId) {
    id
    entityId
    createdAt
    updatedAt
    filename
    sourceFileId
    signatureTemplateFileId
    state
    signatureRequestRecipients {
      email
      isComplete
      id
      order
      remindAfter
    }
  }
}
    `);

export const useGetSignatureRequestsForEntityQuery = <
    TData = GetSignatureRequestsForEntityQuery,
    TError = unknown,
>(
    variables: GetSignatureRequestsForEntityQueryVariables,
    options?: UseQueryOptions<GetSignatureRequestsForEntityQuery, TError, TData>,
) => {
    return useQuery<GetSignatureRequestsForEntityQuery, TError, TData>(
        ["getSignatureRequestsForEntity", variables],
        graphqlFetcher<
            GetSignatureRequestsForEntityQuery,
            GetSignatureRequestsForEntityQueryVariables
        >(GetSignatureRequestsForEntityDocument, variables),
        options,
    );
};

export const useInfiniteGetSignatureRequestsForEntityQuery = <
    TData = GetSignatureRequestsForEntityQuery,
    TError = unknown,
>(
    variables: GetSignatureRequestsForEntityQueryVariables,
    options?: UseInfiniteQueryOptions<GetSignatureRequestsForEntityQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetSignatureRequestsForEntityQuery, TError, TData>(
        ["getSignatureRequestsForEntity.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetSignatureRequestsForEntityQuery,
                GetSignatureRequestsForEntityQueryVariables
            >(GetSignatureRequestsForEntityDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetSignatureRequestDocument = new TypedDocumentString(`
    query getSignatureRequest($signatureRequestId: SignatureRequestId!) {
  getSignatureRequest(signatureRequestId: $signatureRequestId) {
    id
    createdAt
    updatedAt
    entityId
    filename
    sourceFileId
    signatureTemplateFileId
    state
    signatureRequestFields {
      id
      page
      recipientIndex
      order
      x
      y
      width
      height
      font
      fontSize
      fieldType
      textValue
      booleanValue
      isRequired
      placeholder
      filledBy
    }
    signatureRequestRecipients {
      id
      order
      remindAfter
      remindersSent
      createdAt
      updatedAt
      completedAt
      email
      name
      isComplete
    }
    ccRecipients {
      email
      name
    }
  }
}
    `);

export const useGetSignatureRequestQuery = <
    TData = GetSignatureRequestQuery,
    TError = unknown,
>(
    variables: GetSignatureRequestQueryVariables,
    options?: UseQueryOptions<GetSignatureRequestQuery, TError, TData>,
) => {
    return useQuery<GetSignatureRequestQuery, TError, TData>(
        ["getSignatureRequest", variables],
        graphqlFetcher<GetSignatureRequestQuery, GetSignatureRequestQueryVariables>(
            GetSignatureRequestDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetSignatureRequestQuery = <
    TData = GetSignatureRequestQuery,
    TError = unknown,
>(
    variables: GetSignatureRequestQueryVariables,
    options?: UseInfiniteQueryOptions<GetSignatureRequestQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetSignatureRequestQuery, TError, TData>(
        ["getSignatureRequest.infinite", variables],
        metaData =>
            graphqlFetcher<GetSignatureRequestQuery, GetSignatureRequestQueryVariables>(
                GetSignatureRequestDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const PlaceholderPerspectiveDocument = new TypedDocumentString(`
    query placeholderPerspective {
  placeholderPerspective {
    recipientIndex
  }
}
    `);

export const usePlaceholderPerspectiveQuery = <
    TData = PlaceholderPerspectiveQuery,
    TError = unknown,
>(
    variables?: PlaceholderPerspectiveQueryVariables,
    options?: UseQueryOptions<PlaceholderPerspectiveQuery, TError, TData>,
) => {
    return useQuery<PlaceholderPerspectiveQuery, TError, TData>(
        variables === undefined
            ? ["placeholderPerspective"]
            : ["placeholderPerspective", variables],
        graphqlFetcher<PlaceholderPerspectiveQuery, PlaceholderPerspectiveQueryVariables>(
            PlaceholderPerspectiveDocument,
            variables,
        ),
        options,
    );
};

export const useInfinitePlaceholderPerspectiveQuery = <
    TData = PlaceholderPerspectiveQuery,
    TError = unknown,
>(
    variables?: PlaceholderPerspectiveQueryVariables,
    options?: UseInfiniteQueryOptions<PlaceholderPerspectiveQuery, TError, TData>,
) => {
    return useInfiniteQuery<PlaceholderPerspectiveQuery, TError, TData>(
        variables === undefined
            ? ["placeholderPerspective.infinite"]
            : ["placeholderPerspective.infinite", variables],
        metaData =>
            graphqlFetcher<
                PlaceholderPerspectiveQuery,
                PlaceholderPerspectiveQueryVariables
            >(PlaceholderPerspectiveDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const CreateSignatureRequestDocument = new TypedDocumentString(`
    mutation createSignatureRequest($signatureRequestPayload: SignatureRequestPayload!) {
  createSignatureRequest(signatureRequestPayload: $signatureRequestPayload) {
    id
    signatureRequestRecipients {
      id
    }
  }
}
    `);

export const useCreateSignatureRequestMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateSignatureRequestMutation,
        TError,
        CreateSignatureRequestMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateSignatureRequestMutation,
        TError,
        CreateSignatureRequestMutationVariables,
        TContext
    >(
        ["createSignatureRequest"],
        (variables?: CreateSignatureRequestMutationVariables) =>
            graphqlFetcher<
                CreateSignatureRequestMutation,
                CreateSignatureRequestMutationVariables
            >(CreateSignatureRequestDocument, variables)(),
        options,
    );
};

export const GetDraftSignatureRequestDocument = new TypedDocumentString(`
    query getDraftSignatureRequest($entityId: FileEntityId!, $fileId: FileId!, $signatureRequestId: SignatureRequestId) {
  getFile(fileId: $fileId) {
    originalFilename
  }
  getDraftSignatureRequest(
    entityId: $entityId
    fileId: $fileId
    signatureRequestId: $signatureRequestId
  ) {
    potentialRecipients {
      name
      email
      source
    }
    signatureTemplate {
      id
      recipientCount
      signatureTemplateFields {
        isRequired
        defaultTextValue
        defaultBooleanValue
        placeholder
        fieldType
        font
        fontSize
        height
        id
        order
        page
        recipientIndex
        width
        x
        y
      }
    }
    actualRecipients {
      name
      email
    }
    draftFields {
      signatureTemplateFieldId
      textValue
      booleanValue
      placeholder
      isRequired
    }
    emailTemplate {
      subject
      body
      attachments
    }
    completedEmailTemplate {
      subject
      body
      attachments
    }
    entityType
    ccRecipients {
      name
      email
    }
  }
}
    `);

export const useGetDraftSignatureRequestQuery = <
    TData = GetDraftSignatureRequestQuery,
    TError = unknown,
>(
    variables: GetDraftSignatureRequestQueryVariables,
    options?: UseQueryOptions<GetDraftSignatureRequestQuery, TError, TData>,
) => {
    return useQuery<GetDraftSignatureRequestQuery, TError, TData>(
        ["getDraftSignatureRequest", variables],
        graphqlFetcher<
            GetDraftSignatureRequestQuery,
            GetDraftSignatureRequestQueryVariables
        >(GetDraftSignatureRequestDocument, variables),
        options,
    );
};

export const useInfiniteGetDraftSignatureRequestQuery = <
    TData = GetDraftSignatureRequestQuery,
    TError = unknown,
>(
    variables: GetDraftSignatureRequestQueryVariables,
    options?: UseInfiniteQueryOptions<GetDraftSignatureRequestQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetDraftSignatureRequestQuery, TError, TData>(
        ["getDraftSignatureRequest.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetDraftSignatureRequestQuery,
                GetDraftSignatureRequestQueryVariables
            >(GetDraftSignatureRequestDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const ResendSignatureRequestDocument = new TypedDocumentString(`
    mutation resendSignatureRequest($signatureRequestId: SignatureRequestId!) {
  resendSignatureRequest(signatureRequestId: $signatureRequestId)
}
    `);

export const useResendSignatureRequestMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        ResendSignatureRequestMutation,
        TError,
        ResendSignatureRequestMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        ResendSignatureRequestMutation,
        TError,
        ResendSignatureRequestMutationVariables,
        TContext
    >(
        ["resendSignatureRequest"],
        (variables?: ResendSignatureRequestMutationVariables) =>
            graphqlFetcher<
                ResendSignatureRequestMutation,
                ResendSignatureRequestMutationVariables
            >(ResendSignatureRequestDocument, variables)(),
        options,
    );
};

export const AbandonSignatureRequestDocument = new TypedDocumentString(`
    mutation abandonSignatureRequest($signatureRequestId: SignatureRequestId!) {
  abandonSignatureRequest(signatureRequestId: $signatureRequestId)
}
    `);

export const useAbandonSignatureRequestMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        AbandonSignatureRequestMutation,
        TError,
        AbandonSignatureRequestMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        AbandonSignatureRequestMutation,
        TError,
        AbandonSignatureRequestMutationVariables,
        TContext
    >(
        ["abandonSignatureRequest"],
        (variables?: AbandonSignatureRequestMutationVariables) =>
            graphqlFetcher<
                AbandonSignatureRequestMutation,
                AbandonSignatureRequestMutationVariables
            >(AbandonSignatureRequestDocument, variables)(),
        options,
    );
};

export const SimpleSearchDocument = new TypedDocumentString(`
    query simpleSearch($query: SimpleSearchRequest!) {
  simpleSearch(query: $query) {
    hits {
      type
      excerpt
      entityId
    }
  }
}
    `);

export const useSimpleSearchQuery = <TData = SimpleSearchQuery, TError = unknown>(
    variables: SimpleSearchQueryVariables,
    options?: UseQueryOptions<SimpleSearchQuery, TError, TData>,
) => {
    return useQuery<SimpleSearchQuery, TError, TData>(
        ["simpleSearch", variables],
        graphqlFetcher<SimpleSearchQuery, SimpleSearchQueryVariables>(
            SimpleSearchDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteSimpleSearchQuery = <TData = SimpleSearchQuery, TError = unknown>(
    variables: SimpleSearchQueryVariables,
    options?: UseInfiniteQueryOptions<SimpleSearchQuery, TError, TData>,
) => {
    return useInfiniteQuery<SimpleSearchQuery, TError, TData>(
        ["simpleSearch.infinite", variables],
        metaData =>
            graphqlFetcher<SimpleSearchQuery, SimpleSearchQueryVariables>(
                SimpleSearchDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const CreateSmtpCredentialDocument = new TypedDocumentString(`
    mutation createSmtpCredential($params: CreateSmtpCredentialRequest!) {
  createSmtpCredential(params: $params) {
    success
    message
  }
}
    `);

export const useCreateSmtpCredentialMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateSmtpCredentialMutation,
        TError,
        CreateSmtpCredentialMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateSmtpCredentialMutation,
        TError,
        CreateSmtpCredentialMutationVariables,
        TContext
    >(
        ["createSmtpCredential"],
        (variables?: CreateSmtpCredentialMutationVariables) =>
            graphqlFetcher<
                CreateSmtpCredentialMutation,
                CreateSmtpCredentialMutationVariables
            >(CreateSmtpCredentialDocument, variables)(),
        options,
    );
};

export const GetSmtpCredentialForUserDocument = new TypedDocumentString(`
    query getSmtpCredentialForUser {
  getSmtpCredentialForUser {
    ...SmtpCredentialBasics
  }
}
    fragment SmtpCredentialBasics on SmtpCredential {
  id
  host
  port
  username
  status
  authenticationType
  provider
}`);

export const useGetSmtpCredentialForUserQuery = <
    TData = GetSmtpCredentialForUserQuery,
    TError = unknown,
>(
    variables?: GetSmtpCredentialForUserQueryVariables,
    options?: UseQueryOptions<GetSmtpCredentialForUserQuery, TError, TData>,
) => {
    return useQuery<GetSmtpCredentialForUserQuery, TError, TData>(
        variables === undefined
            ? ["getSmtpCredentialForUser"]
            : ["getSmtpCredentialForUser", variables],
        graphqlFetcher<
            GetSmtpCredentialForUserQuery,
            GetSmtpCredentialForUserQueryVariables
        >(GetSmtpCredentialForUserDocument, variables),
        options,
    );
};

export const useInfiniteGetSmtpCredentialForUserQuery = <
    TData = GetSmtpCredentialForUserQuery,
    TError = unknown,
>(
    variables?: GetSmtpCredentialForUserQueryVariables,
    options?: UseInfiniteQueryOptions<GetSmtpCredentialForUserQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetSmtpCredentialForUserQuery, TError, TData>(
        variables === undefined
            ? ["getSmtpCredentialForUser.infinite"]
            : ["getSmtpCredentialForUser.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetSmtpCredentialForUserQuery,
                GetSmtpCredentialForUserQueryVariables
            >(GetSmtpCredentialForUserDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const DeleteSmtpCredentialForUserDocument = new TypedDocumentString(`
    mutation deleteSmtpCredentialForUser {
  deleteSmtpCredentialForUser
}
    `);

export const useDeleteSmtpCredentialForUserMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        DeleteSmtpCredentialForUserMutation,
        TError,
        DeleteSmtpCredentialForUserMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteSmtpCredentialForUserMutation,
        TError,
        DeleteSmtpCredentialForUserMutationVariables,
        TContext
    >(
        ["deleteSmtpCredentialForUser"],
        (variables?: DeleteSmtpCredentialForUserMutationVariables) =>
            graphqlFetcher<
                DeleteSmtpCredentialForUserMutation,
                DeleteSmtpCredentialForUserMutationVariables
            >(DeleteSmtpCredentialForUserDocument, variables)(),
        options,
    );
};

export const SystemDocument = new TypedDocumentString(`
    query system {
  jobStats {
    complete
    pending
    available
    started
    failed
  }
  systemConfiguration {
    fileStorage
  }
}
    `);

export const useSystemQuery = <TData = SystemQuery, TError = unknown>(
    variables?: SystemQueryVariables,
    options?: UseQueryOptions<SystemQuery, TError, TData>,
) => {
    return useQuery<SystemQuery, TError, TData>(
        variables === undefined ? ["system"] : ["system", variables],
        graphqlFetcher<SystemQuery, SystemQueryVariables>(SystemDocument, variables),
        options,
    );
};

export const useInfiniteSystemQuery = <TData = SystemQuery, TError = unknown>(
    variables?: SystemQueryVariables,
    options?: UseInfiniteQueryOptions<SystemQuery, TError, TData>,
) => {
    return useInfiniteQuery<SystemQuery, TError, TData>(
        variables === undefined ? ["system.infinite"] : ["system.infinite", variables],
        metaData =>
            graphqlFetcher<SystemQuery, SystemQueryVariables>(SystemDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetTaskDocument = new TypedDocumentString(`
    query getTask($regionId: RegionId!, $taskId: TaskId!) {
  getTask(regionId: $regionId, taskId: $taskId) {
    populatedTask {
      task {
        ...TaskBasics
      }
      entity {
        displayName
        path
        kind
      }
    }
  }
}
    fragment TaskBasics on Task {
  id
  completedAt
  dueDate
  dueDateTime
  notes
  ownerId
  parentId
  status
  text
  done
  taskTypeId
  taskType {
    id
    name
  }
  recurrencePattern
  recurrenceInterval
  recurrenceDaysOfWeek
  recurrenceDayOfMonth
}`);

export const useGetTaskQuery = <TData = GetTaskQuery, TError = unknown>(
    variables: GetTaskQueryVariables,
    options?: UseQueryOptions<GetTaskQuery, TError, TData>,
) => {
    return useQuery<GetTaskQuery, TError, TData>(
        ["getTask", variables],
        graphqlFetcher<GetTaskQuery, GetTaskQueryVariables>(GetTaskDocument, variables),
        options,
    );
};

export const useInfiniteGetTaskQuery = <TData = GetTaskQuery, TError = unknown>(
    variables: GetTaskQueryVariables,
    options?: UseInfiniteQueryOptions<GetTaskQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetTaskQuery, TError, TData>(
        ["getTask.infinite", variables],
        metaData =>
            graphqlFetcher<GetTaskQuery, GetTaskQueryVariables>(GetTaskDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetTasksDocument = new TypedDocumentString(`
    query getTasks($activeOnly: Boolean!, $regionId: ID!, $page: Int!) {
  getTasks(activeOnly: $activeOnly, regionId: $regionId, page: $page) {
    page
    totalPages
    tasks {
      task {
        ...TaskBasics
      }
      entity {
        displayName
        path
        kind
      }
    }
  }
}
    fragment TaskBasics on Task {
  id
  completedAt
  dueDate
  dueDateTime
  notes
  ownerId
  parentId
  status
  text
  done
  taskTypeId
  taskType {
    id
    name
  }
  recurrencePattern
  recurrenceInterval
  recurrenceDaysOfWeek
  recurrenceDayOfMonth
}`);

export const useGetTasksQuery = <TData = GetTasksQuery, TError = unknown>(
    variables: GetTasksQueryVariables,
    options?: UseQueryOptions<GetTasksQuery, TError, TData>,
) => {
    return useQuery<GetTasksQuery, TError, TData>(
        ["getTasks", variables],
        graphqlFetcher<GetTasksQuery, GetTasksQueryVariables>(
            GetTasksDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetTasksQuery = <TData = GetTasksQuery, TError = unknown>(
    variables: GetTasksQueryVariables,
    options?: UseInfiniteQueryOptions<GetTasksQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetTasksQuery, TError, TData>(
        ["getTasks.infinite", variables],
        metaData =>
            graphqlFetcher<GetTasksQuery, GetTasksQueryVariables>(GetTasksDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetTasksv2Document = new TypedDocumentString(`
    query getTasksv2($params: GetTasksRequest!) {
  getTasksv2(params: $params) {
    task {
      ...TaskBasics
    }
    entity {
      displayName
      path
      kind
      contact {
        name
        phoneNumber
        contactType
      }
    }
  }
}
    fragment TaskBasics on Task {
  id
  completedAt
  dueDate
  dueDateTime
  notes
  ownerId
  parentId
  status
  text
  done
  taskTypeId
  taskType {
    id
    name
  }
  recurrencePattern
  recurrenceInterval
  recurrenceDaysOfWeek
  recurrenceDayOfMonth
}`);

export const useGetTasksv2Query = <TData = GetTasksv2Query, TError = unknown>(
    variables: GetTasksv2QueryVariables,
    options?: UseQueryOptions<GetTasksv2Query, TError, TData>,
) => {
    return useQuery<GetTasksv2Query, TError, TData>(
        ["getTasksv2", variables],
        graphqlFetcher<GetTasksv2Query, GetTasksv2QueryVariables>(
            GetTasksv2Document,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetTasksv2Query = <TData = GetTasksv2Query, TError = unknown>(
    variables: GetTasksv2QueryVariables,
    options?: UseInfiniteQueryOptions<GetTasksv2Query, TError, TData>,
) => {
    return useInfiniteQuery<GetTasksv2Query, TError, TData>(
        ["getTasksv2.infinite", variables],
        metaData =>
            graphqlFetcher<GetTasksv2Query, GetTasksv2QueryVariables>(
                GetTasksv2Document,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetPopulatedTasksForParentDocument = new TypedDocumentString(`
    query getPopulatedTasksForParent($regionId: RegionId!, $parentId: TaskParentId!) {
  getPopulatedTasksForParent(regionId: $regionId, parentId: $parentId) {
    task {
      ...TaskBasics
    }
    entity {
      displayName
      path
      kind
    }
  }
}
    fragment TaskBasics on Task {
  id
  completedAt
  dueDate
  dueDateTime
  notes
  ownerId
  parentId
  status
  text
  done
  taskTypeId
  taskType {
    id
    name
  }
  recurrencePattern
  recurrenceInterval
  recurrenceDaysOfWeek
  recurrenceDayOfMonth
}`);

export const useGetPopulatedTasksForParentQuery = <
    TData = GetPopulatedTasksForParentQuery,
    TError = unknown,
>(
    variables: GetPopulatedTasksForParentQueryVariables,
    options?: UseQueryOptions<GetPopulatedTasksForParentQuery, TError, TData>,
) => {
    return useQuery<GetPopulatedTasksForParentQuery, TError, TData>(
        ["getPopulatedTasksForParent", variables],
        graphqlFetcher<
            GetPopulatedTasksForParentQuery,
            GetPopulatedTasksForParentQueryVariables
        >(GetPopulatedTasksForParentDocument, variables),
        options,
    );
};

export const useInfiniteGetPopulatedTasksForParentQuery = <
    TData = GetPopulatedTasksForParentQuery,
    TError = unknown,
>(
    variables: GetPopulatedTasksForParentQueryVariables,
    options?: UseInfiniteQueryOptions<GetPopulatedTasksForParentQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetPopulatedTasksForParentQuery, TError, TData>(
        ["getPopulatedTasksForParent.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetPopulatedTasksForParentQuery,
                GetPopulatedTasksForParentQueryVariables
            >(GetPopulatedTasksForParentDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetNextTasksForParentIdsDocument = new TypedDocumentString(`
    query getNextTasksForParentIds($parentIds: [TaskParentId!]!) {
  getNextTasksForParentIds(parentIds: $parentIds) {
    ...TaskBasics
  }
}
    fragment TaskBasics on Task {
  id
  completedAt
  dueDate
  dueDateTime
  notes
  ownerId
  parentId
  status
  text
  done
  taskTypeId
  taskType {
    id
    name
  }
  recurrencePattern
  recurrenceInterval
  recurrenceDaysOfWeek
  recurrenceDayOfMonth
}`);

export const useGetNextTasksForParentIdsQuery = <
    TData = GetNextTasksForParentIdsQuery,
    TError = unknown,
>(
    variables: GetNextTasksForParentIdsQueryVariables,
    options?: UseQueryOptions<GetNextTasksForParentIdsQuery, TError, TData>,
) => {
    return useQuery<GetNextTasksForParentIdsQuery, TError, TData>(
        ["getNextTasksForParentIds", variables],
        graphqlFetcher<
            GetNextTasksForParentIdsQuery,
            GetNextTasksForParentIdsQueryVariables
        >(GetNextTasksForParentIdsDocument, variables),
        options,
    );
};

export const useInfiniteGetNextTasksForParentIdsQuery = <
    TData = GetNextTasksForParentIdsQuery,
    TError = unknown,
>(
    variables: GetNextTasksForParentIdsQueryVariables,
    options?: UseInfiniteQueryOptions<GetNextTasksForParentIdsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetNextTasksForParentIdsQuery, TError, TData>(
        ["getNextTasksForParentIds.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetNextTasksForParentIdsQuery,
                GetNextTasksForParentIdsQueryVariables
            >(GetNextTasksForParentIdsDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetTasksForEntityDocument = new TypedDocumentString(`
    query getTasksForEntity($parentId: TaskParentId!) {
  getTasksForEntity(parentId: $parentId) {
    ...TaskDetails
  }
}
    fragment TaskBasics on Task {
  id
  completedAt
  dueDate
  dueDateTime
  notes
  ownerId
  parentId
  status
  text
  done
  taskTypeId
  taskType {
    id
    name
  }
  recurrencePattern
  recurrenceInterval
  recurrenceDaysOfWeek
  recurrenceDayOfMonth
}
fragment TaskDetails on Task {
  ...TaskBasics
  notes
}`);

export const useGetTasksForEntityQuery = <
    TData = GetTasksForEntityQuery,
    TError = unknown,
>(
    variables: GetTasksForEntityQueryVariables,
    options?: UseQueryOptions<GetTasksForEntityQuery, TError, TData>,
) => {
    return useQuery<GetTasksForEntityQuery, TError, TData>(
        ["getTasksForEntity", variables],
        graphqlFetcher<GetTasksForEntityQuery, GetTasksForEntityQueryVariables>(
            GetTasksForEntityDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetTasksForEntityQuery = <
    TData = GetTasksForEntityQuery,
    TError = unknown,
>(
    variables: GetTasksForEntityQueryVariables,
    options?: UseInfiniteQueryOptions<GetTasksForEntityQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetTasksForEntityQuery, TError, TData>(
        ["getTasksForEntity.infinite", variables],
        metaData =>
            graphqlFetcher<GetTasksForEntityQuery, GetTasksForEntityQueryVariables>(
                GetTasksForEntityDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const CreateTaskDocument = new TypedDocumentString(`
    mutation createTask($parentId: TaskParentId!, $params: CreateTaskPayload!) {
  createTask(parentId: $parentId, params: $params) {
    ...TaskDetails
  }
}
    fragment TaskBasics on Task {
  id
  completedAt
  dueDate
  dueDateTime
  notes
  ownerId
  parentId
  status
  text
  done
  taskTypeId
  taskType {
    id
    name
  }
  recurrencePattern
  recurrenceInterval
  recurrenceDaysOfWeek
  recurrenceDayOfMonth
}
fragment TaskDetails on Task {
  ...TaskBasics
  notes
}`);

export const useCreateTaskMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateTaskMutation,
        TError,
        CreateTaskMutationVariables,
        TContext
    >,
) => {
    return useMutation<CreateTaskMutation, TError, CreateTaskMutationVariables, TContext>(
        ["createTask"],
        (variables?: CreateTaskMutationVariables) =>
            graphqlFetcher<CreateTaskMutation, CreateTaskMutationVariables>(
                CreateTaskDocument,
                variables,
            )(),
        options,
    );
};

export const UpdateTaskDocument = new TypedDocumentString(`
    mutation updateTask($taskId: TaskId!, $params: UpdateTaskPayload!) {
  updateTask(taskId: $taskId, params: $params) {
    ...TaskDetails
  }
}
    fragment TaskBasics on Task {
  id
  completedAt
  dueDate
  dueDateTime
  notes
  ownerId
  parentId
  status
  text
  done
  taskTypeId
  taskType {
    id
    name
  }
  recurrencePattern
  recurrenceInterval
  recurrenceDaysOfWeek
  recurrenceDayOfMonth
}
fragment TaskDetails on Task {
  ...TaskBasics
  notes
}`);

export const useUpdateTaskMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateTaskMutation,
        TError,
        UpdateTaskMutationVariables,
        TContext
    >,
) => {
    return useMutation<UpdateTaskMutation, TError, UpdateTaskMutationVariables, TContext>(
        ["updateTask"],
        (variables?: UpdateTaskMutationVariables) =>
            graphqlFetcher<UpdateTaskMutation, UpdateTaskMutationVariables>(
                UpdateTaskDocument,
                variables,
            )(),
        options,
    );
};

export const DeleteTaskDocument = new TypedDocumentString(`
    mutation deleteTask($taskId: TaskId!) {
  deleteTask(taskId: $taskId)
}
    `);

export const useDeleteTaskMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteTaskMutation,
        TError,
        DeleteTaskMutationVariables,
        TContext
    >,
) => {
    return useMutation<DeleteTaskMutation, TError, DeleteTaskMutationVariables, TContext>(
        ["deleteTask"],
        (variables?: DeleteTaskMutationVariables) =>
            graphqlFetcher<DeleteTaskMutation, DeleteTaskMutationVariables>(
                DeleteTaskDocument,
                variables,
            )(),
        options,
    );
};

export const GetTaskTypesDocument = new TypedDocumentString(`
    query getTaskTypes {
  getTaskTypes {
    id
    name
    isArchived
    category
    recordTypes
  }
}
    `);

export const useGetTaskTypesQuery = <TData = GetTaskTypesQuery, TError = unknown>(
    variables?: GetTaskTypesQueryVariables,
    options?: UseQueryOptions<GetTaskTypesQuery, TError, TData>,
) => {
    return useQuery<GetTaskTypesQuery, TError, TData>(
        variables === undefined ? ["getTaskTypes"] : ["getTaskTypes", variables],
        graphqlFetcher<GetTaskTypesQuery, GetTaskTypesQueryVariables>(
            GetTaskTypesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetTaskTypesQuery = <TData = GetTaskTypesQuery, TError = unknown>(
    variables?: GetTaskTypesQueryVariables,
    options?: UseInfiniteQueryOptions<GetTaskTypesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetTaskTypesQuery, TError, TData>(
        variables === undefined
            ? ["getTaskTypes.infinite"]
            : ["getTaskTypes.infinite", variables],
        metaData =>
            graphqlFetcher<GetTaskTypesQuery, GetTaskTypesQueryVariables>(
                GetTaskTypesDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const CreateTaskTypeDocument = new TypedDocumentString(`
    mutation createTaskType($payload: CreateTaskTypePayload!) {
  createTaskType(payload: $payload) {
    id
  }
}
    `);

export const useCreateTaskTypeMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateTaskTypeMutation,
        TError,
        CreateTaskTypeMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateTaskTypeMutation,
        TError,
        CreateTaskTypeMutationVariables,
        TContext
    >(
        ["createTaskType"],
        (variables?: CreateTaskTypeMutationVariables) =>
            graphqlFetcher<CreateTaskTypeMutation, CreateTaskTypeMutationVariables>(
                CreateTaskTypeDocument,
                variables,
            )(),
        options,
    );
};

export const UpdateTaskTypeDocument = new TypedDocumentString(`
    mutation updateTaskType($payload: UpdateTaskTypePayload!) {
  updateTaskType(payload: $payload) {
    id
  }
}
    `);

export const useUpdateTaskTypeMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateTaskTypeMutation,
        TError,
        UpdateTaskTypeMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateTaskTypeMutation,
        TError,
        UpdateTaskTypeMutationVariables,
        TContext
    >(
        ["updateTaskType"],
        (variables?: UpdateTaskTypeMutationVariables) =>
            graphqlFetcher<UpdateTaskTypeMutation, UpdateTaskTypeMutationVariables>(
                UpdateTaskTypeDocument,
                variables,
            )(),
        options,
    );
};

export const GetEntitiesWithoutActiveTasksPaginatedDocument = new TypedDocumentString(`
    query getEntitiesWithoutActiveTasksPaginated($regionId: RegionId!, $entityType: AdvancedSearchEntityType!, $page: Int!, $pageSize: Int, $assignedUserId: String) {
  getEntitiesWithoutActiveTasksPaginated(
    regionId: $regionId
    entityType: $entityType
    page: $page
    pageSize: $pageSize
    assignedUserId: $assignedUserId
  ) {
    entities {
      id
      displayName
      path
      kind
      kindTitle
      contact {
        name
        phoneNumber
        contactType
      }
    }
    page
    totalPages
    totalCount
  }
}
    `);

export const useGetEntitiesWithoutActiveTasksPaginatedQuery = <
    TData = GetEntitiesWithoutActiveTasksPaginatedQuery,
    TError = unknown,
>(
    variables: GetEntitiesWithoutActiveTasksPaginatedQueryVariables,
    options?: UseQueryOptions<GetEntitiesWithoutActiveTasksPaginatedQuery, TError, TData>,
) => {
    return useQuery<GetEntitiesWithoutActiveTasksPaginatedQuery, TError, TData>(
        ["getEntitiesWithoutActiveTasksPaginated", variables],
        graphqlFetcher<
            GetEntitiesWithoutActiveTasksPaginatedQuery,
            GetEntitiesWithoutActiveTasksPaginatedQueryVariables
        >(GetEntitiesWithoutActiveTasksPaginatedDocument, variables),
        options,
    );
};

export const useInfiniteGetEntitiesWithoutActiveTasksPaginatedQuery = <
    TData = GetEntitiesWithoutActiveTasksPaginatedQuery,
    TError = unknown,
>(
    variables: GetEntitiesWithoutActiveTasksPaginatedQueryVariables,
    options?: UseInfiniteQueryOptions<
        GetEntitiesWithoutActiveTasksPaginatedQuery,
        TError,
        TData
    >,
) => {
    return useInfiniteQuery<GetEntitiesWithoutActiveTasksPaginatedQuery, TError, TData>(
        ["getEntitiesWithoutActiveTasksPaginated.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetEntitiesWithoutActiveTasksPaginatedQuery,
                GetEntitiesWithoutActiveTasksPaginatedQueryVariables
            >(GetEntitiesWithoutActiveTasksPaginatedDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const SendSmsDocument = new TypedDocumentString(`
    mutation sendSms($request: SendSmsRequest!) {
  sendSms(request: $request)
}
    `);

export const useSendSmsMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        SendSmsMutation,
        TError,
        SendSmsMutationVariables,
        TContext
    >,
) => {
    return useMutation<SendSmsMutation, TError, SendSmsMutationVariables, TContext>(
        ["sendSms"],
        (variables?: SendSmsMutationVariables) =>
            graphqlFetcher<SendSmsMutation, SendSmsMutationVariables>(
                SendSmsDocument,
                variables,
            )(),
        options,
    );
};

export const ListRentablePhoneNumbersDocument = new TypedDocumentString(`
    query listRentablePhoneNumbers($request: ListRentablePhoneNumbersRequest!) {
  listRentablePhoneNumbers(request: $request) {
    phoneNumbers {
      phoneNumber
      friendlyName
      locality
      region
      postalCode
      friendlyLocation
    }
    suggestedAreaCodes {
      areaCode
      metroName
    }
  }
}
    `);

export const useListRentablePhoneNumbersQuery = <
    TData = ListRentablePhoneNumbersQuery,
    TError = unknown,
>(
    variables: ListRentablePhoneNumbersQueryVariables,
    options?: UseQueryOptions<ListRentablePhoneNumbersQuery, TError, TData>,
) => {
    return useQuery<ListRentablePhoneNumbersQuery, TError, TData>(
        ["listRentablePhoneNumbers", variables],
        graphqlFetcher<
            ListRentablePhoneNumbersQuery,
            ListRentablePhoneNumbersQueryVariables
        >(ListRentablePhoneNumbersDocument, variables),
        options,
    );
};

export const useInfiniteListRentablePhoneNumbersQuery = <
    TData = ListRentablePhoneNumbersQuery,
    TError = unknown,
>(
    variables: ListRentablePhoneNumbersQueryVariables,
    options?: UseInfiniteQueryOptions<ListRentablePhoneNumbersQuery, TError, TData>,
) => {
    return useInfiniteQuery<ListRentablePhoneNumbersQuery, TError, TData>(
        ["listRentablePhoneNumbers.infinite", variables],
        metaData =>
            graphqlFetcher<
                ListRentablePhoneNumbersQuery,
                ListRentablePhoneNumbersQueryVariables
            >(ListRentablePhoneNumbersDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetPersonalPhoneNumbersDocument = new TypedDocumentString(`
    query getPersonalPhoneNumbers {
  getPersonalPhoneNumbers {
    ...TelecomPhoneLineBasics
  }
}
    fragment TelecomPhoneLineBasics on TelecomPhoneLine {
  id
  userId
  phoneNumber
  createdAt
  updatedAt
  status
  credentialType
}`);

export const useGetPersonalPhoneNumbersQuery = <
    TData = GetPersonalPhoneNumbersQuery,
    TError = unknown,
>(
    variables?: GetPersonalPhoneNumbersQueryVariables,
    options?: UseQueryOptions<GetPersonalPhoneNumbersQuery, TError, TData>,
) => {
    return useQuery<GetPersonalPhoneNumbersQuery, TError, TData>(
        variables === undefined
            ? ["getPersonalPhoneNumbers"]
            : ["getPersonalPhoneNumbers", variables],
        graphqlFetcher<
            GetPersonalPhoneNumbersQuery,
            GetPersonalPhoneNumbersQueryVariables
        >(GetPersonalPhoneNumbersDocument, variables),
        options,
    );
};

export const useInfiniteGetPersonalPhoneNumbersQuery = <
    TData = GetPersonalPhoneNumbersQuery,
    TError = unknown,
>(
    variables?: GetPersonalPhoneNumbersQueryVariables,
    options?: UseInfiniteQueryOptions<GetPersonalPhoneNumbersQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetPersonalPhoneNumbersQuery, TError, TData>(
        variables === undefined
            ? ["getPersonalPhoneNumbers.infinite"]
            : ["getPersonalPhoneNumbers.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetPersonalPhoneNumbersQuery,
                GetPersonalPhoneNumbersQueryVariables
            >(GetPersonalPhoneNumbersDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const ClaimPhoneNumberDocument = new TypedDocumentString(`
    mutation claimPhoneNumber($request: ClaimPhoneNumberRequest!) {
  claimPhoneNumber(request: $request) {
    ...TelecomPhoneLineBasics
  }
}
    fragment TelecomPhoneLineBasics on TelecomPhoneLine {
  id
  userId
  phoneNumber
  createdAt
  updatedAt
  status
  credentialType
}`);

export const useClaimPhoneNumberMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        ClaimPhoneNumberMutation,
        TError,
        ClaimPhoneNumberMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        ClaimPhoneNumberMutation,
        TError,
        ClaimPhoneNumberMutationVariables,
        TContext
    >(
        ["claimPhoneNumber"],
        (variables?: ClaimPhoneNumberMutationVariables) =>
            graphqlFetcher<ClaimPhoneNumberMutation, ClaimPhoneNumberMutationVariables>(
                ClaimPhoneNumberDocument,
                variables,
            )(),
        options,
    );
};

export const ReleasePhoneNumberDocument = new TypedDocumentString(`
    mutation releasePhoneNumber($id: TelecomPhoneLineId!) {
  releasePhoneNumber(id: $id)
}
    `);

export const useReleasePhoneNumberMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        ReleasePhoneNumberMutation,
        TError,
        ReleasePhoneNumberMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        ReleasePhoneNumberMutation,
        TError,
        ReleasePhoneNumberMutationVariables,
        TContext
    >(
        ["releasePhoneNumber"],
        (variables?: ReleasePhoneNumberMutationVariables) =>
            graphqlFetcher<
                ReleasePhoneNumberMutation,
                ReleasePhoneNumberMutationVariables
            >(ReleasePhoneNumberDocument, variables)(),
        options,
    );
};

export const GetSmsConversationDocument = new TypedDocumentString(`
    query getSmsConversation($request: GetSmsConversationRequest!) {
  getSmsConversation(request: $request) {
    ...SmsMessageBasics
  }
}
    fragment SmsMessageBasics on SmsMessage {
  id
  from
  to
  body
  direction
  dateAdded
  status
  createdBy
  createdByUser {
    id
    displayName
  }
}`);

export const useGetSmsConversationQuery = <
    TData = GetSmsConversationQuery,
    TError = unknown,
>(
    variables: GetSmsConversationQueryVariables,
    options?: UseQueryOptions<GetSmsConversationQuery, TError, TData>,
) => {
    return useQuery<GetSmsConversationQuery, TError, TData>(
        ["getSmsConversation", variables],
        graphqlFetcher<GetSmsConversationQuery, GetSmsConversationQueryVariables>(
            GetSmsConversationDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetSmsConversationQuery = <
    TData = GetSmsConversationQuery,
    TError = unknown,
>(
    variables: GetSmsConversationQueryVariables,
    options?: UseInfiniteQueryOptions<GetSmsConversationQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetSmsConversationQuery, TError, TData>(
        ["getSmsConversation.infinite", variables],
        metaData =>
            graphqlFetcher<GetSmsConversationQuery, GetSmsConversationQueryVariables>(
                GetSmsConversationDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetSmsConversationByPhoneLineDocument = new TypedDocumentString(`
    query getSmsConversationByPhoneLine($request: GetSmsConversationByPhoneLineRequest!) {
  getSmsConversationByPhoneLine(request: $request) {
    ...SmsMessageBasics
  }
}
    fragment SmsMessageBasics on SmsMessage {
  id
  from
  to
  body
  direction
  dateAdded
  status
  createdBy
  createdByUser {
    id
    displayName
  }
}`);

export const useGetSmsConversationByPhoneLineQuery = <
    TData = GetSmsConversationByPhoneLineQuery,
    TError = unknown,
>(
    variables: GetSmsConversationByPhoneLineQueryVariables,
    options?: UseQueryOptions<GetSmsConversationByPhoneLineQuery, TError, TData>,
) => {
    return useQuery<GetSmsConversationByPhoneLineQuery, TError, TData>(
        ["getSmsConversationByPhoneLine", variables],
        graphqlFetcher<
            GetSmsConversationByPhoneLineQuery,
            GetSmsConversationByPhoneLineQueryVariables
        >(GetSmsConversationByPhoneLineDocument, variables),
        options,
    );
};

export const useInfiniteGetSmsConversationByPhoneLineQuery = <
    TData = GetSmsConversationByPhoneLineQuery,
    TError = unknown,
>(
    variables: GetSmsConversationByPhoneLineQueryVariables,
    options?: UseInfiniteQueryOptions<GetSmsConversationByPhoneLineQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetSmsConversationByPhoneLineQuery, TError, TData>(
        ["getSmsConversationByPhoneLine.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetSmsConversationByPhoneLineQuery,
                GetSmsConversationByPhoneLineQueryVariables
            >(GetSmsConversationByPhoneLineDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const MarkPriorSmsConsentDocument = new TypedDocumentString(`
    mutation markPriorSmsConsent($request: MarkPriorSmsConsentRequest!) {
  markPriorSmsConsent(request: $request)
}
    `);

export const useMarkPriorSmsConsentMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        MarkPriorSmsConsentMutation,
        TError,
        MarkPriorSmsConsentMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        MarkPriorSmsConsentMutation,
        TError,
        MarkPriorSmsConsentMutationVariables,
        TContext
    >(
        ["markPriorSmsConsent"],
        (variables?: MarkPriorSmsConsentMutationVariables) =>
            graphqlFetcher<
                MarkPriorSmsConsentMutation,
                MarkPriorSmsConsentMutationVariables
            >(MarkPriorSmsConsentDocument, variables)(),
        options,
    );
};

export const GetTelecomContactStatusDocument = new TypedDocumentString(`
    query getTelecomContactStatus($request: GetTelecomContactStatusRequest!) {
  getTelecomContactStatus(request: $request) {
    phoneNumber
    consentStatus
    canSend
  }
}
    `);

export const useGetTelecomContactStatusQuery = <
    TData = GetTelecomContactStatusQuery,
    TError = unknown,
>(
    variables: GetTelecomContactStatusQueryVariables,
    options?: UseQueryOptions<GetTelecomContactStatusQuery, TError, TData>,
) => {
    return useQuery<GetTelecomContactStatusQuery, TError, TData>(
        ["getTelecomContactStatus", variables],
        graphqlFetcher<
            GetTelecomContactStatusQuery,
            GetTelecomContactStatusQueryVariables
        >(GetTelecomContactStatusDocument, variables),
        options,
    );
};

export const useInfiniteGetTelecomContactStatusQuery = <
    TData = GetTelecomContactStatusQuery,
    TError = unknown,
>(
    variables: GetTelecomContactStatusQueryVariables,
    options?: UseInfiniteQueryOptions<GetTelecomContactStatusQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetTelecomContactStatusQuery, TError, TData>(
        ["getTelecomContactStatus.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetTelecomContactStatusQuery,
                GetTelecomContactStatusQueryVariables
            >(GetTelecomContactStatusDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const RequestVoiceTokenDocument = new TypedDocumentString(`
    mutation requestVoiceToken($phoneLineId: TelecomPhoneLineId!) {
  requestVoiceToken(phoneLineId: $phoneLineId) {
    token
    identity
  }
}
    `);

export const useRequestVoiceTokenMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        RequestVoiceTokenMutation,
        TError,
        RequestVoiceTokenMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        RequestVoiceTokenMutation,
        TError,
        RequestVoiceTokenMutationVariables,
        TContext
    >(
        ["requestVoiceToken"],
        (variables?: RequestVoiceTokenMutationVariables) =>
            graphqlFetcher<RequestVoiceTokenMutation, RequestVoiceTokenMutationVariables>(
                RequestVoiceTokenDocument,
                variables,
            )(),
        options,
    );
};

export const LogVoiceCallDocument = new TypedDocumentString(`
    mutation logVoiceCall($entityId: ActivityParentEntityId!, $phoneNumber: String!, $phoneLineId: TelecomPhoneLineId!, $activityTypeId: TaskTypeId) {
  logVoiceCall(
    entityId: $entityId
    phoneNumber: $phoneNumber
    phoneLineId: $phoneLineId
    activityTypeId: $activityTypeId
  )
}
    `);

export const useLogVoiceCallMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        LogVoiceCallMutation,
        TError,
        LogVoiceCallMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        LogVoiceCallMutation,
        TError,
        LogVoiceCallMutationVariables,
        TContext
    >(
        ["logVoiceCall"],
        (variables?: LogVoiceCallMutationVariables) =>
            graphqlFetcher<LogVoiceCallMutation, LogVoiceCallMutationVariables>(
                LogVoiceCallDocument,
                variables,
            )(),
        options,
    );
};

export const GetTemplateDocument = new TypedDocumentString(`
    query getTemplate($id: EmailTemplateId!) {
  getTemplate(emailTemplateId: $id) {
    isEditable
    isDefaultSignature
    template {
      id
      body
      description
      isShared
      type
      subject
      migrationVersion
      attachments
      regionVisibility {
        editable
        regions {
          mode
          regionId
        }
      }
    }
    author {
      name
      email
    }
  }
}
    `);

export const useGetTemplateQuery = <TData = GetTemplateQuery, TError = unknown>(
    variables: GetTemplateQueryVariables,
    options?: UseQueryOptions<GetTemplateQuery, TError, TData>,
) => {
    return useQuery<GetTemplateQuery, TError, TData>(
        ["getTemplate", variables],
        graphqlFetcher<GetTemplateQuery, GetTemplateQueryVariables>(
            GetTemplateDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetTemplateQuery = <TData = GetTemplateQuery, TError = unknown>(
    variables: GetTemplateQueryVariables,
    options?: UseInfiniteQueryOptions<GetTemplateQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetTemplateQuery, TError, TData>(
        ["getTemplate.infinite", variables],
        metaData =>
            graphqlFetcher<GetTemplateQuery, GetTemplateQueryVariables>(
                GetTemplateDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetTemplatesDocument = new TypedDocumentString(`
    query getTemplates {
  getTemplates {
    templates {
      isEditable
      isDefaultSignature
      template {
        id
        body
        description
        isShared
        type
        subject
        migrationVersion
        attachments
        regionVisibility {
          editable
          regions {
            mode
            regionId
          }
        }
      }
      author {
        name
        email
      }
    }
  }
}
    `);

export const useGetTemplatesQuery = <TData = GetTemplatesQuery, TError = unknown>(
    variables?: GetTemplatesQueryVariables,
    options?: UseQueryOptions<GetTemplatesQuery, TError, TData>,
) => {
    return useQuery<GetTemplatesQuery, TError, TData>(
        variables === undefined ? ["getTemplates"] : ["getTemplates", variables],
        graphqlFetcher<GetTemplatesQuery, GetTemplatesQueryVariables>(
            GetTemplatesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetTemplatesQuery = <TData = GetTemplatesQuery, TError = unknown>(
    variables?: GetTemplatesQueryVariables,
    options?: UseInfiniteQueryOptions<GetTemplatesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetTemplatesQuery, TError, TData>(
        variables === undefined
            ? ["getTemplates.infinite"]
            : ["getTemplates.infinite", variables],
        metaData =>
            graphqlFetcher<GetTemplatesQuery, GetTemplatesQueryVariables>(
                GetTemplatesDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const CanCreateSignatureTemplateDocument = new TypedDocumentString(`
    query canCreateSignatureTemplate($fileId: FileId!) {
  canCreateSignatureTemplate(fileId: $fileId)
}
    `);

export const useCanCreateSignatureTemplateQuery = <
    TData = CanCreateSignatureTemplateQuery,
    TError = unknown,
>(
    variables: CanCreateSignatureTemplateQueryVariables,
    options?: UseQueryOptions<CanCreateSignatureTemplateQuery, TError, TData>,
) => {
    return useQuery<CanCreateSignatureTemplateQuery, TError, TData>(
        ["canCreateSignatureTemplate", variables],
        graphqlFetcher<
            CanCreateSignatureTemplateQuery,
            CanCreateSignatureTemplateQueryVariables
        >(CanCreateSignatureTemplateDocument, variables),
        options,
    );
};

export const useInfiniteCanCreateSignatureTemplateQuery = <
    TData = CanCreateSignatureTemplateQuery,
    TError = unknown,
>(
    variables: CanCreateSignatureTemplateQueryVariables,
    options?: UseInfiniteQueryOptions<CanCreateSignatureTemplateQuery, TError, TData>,
) => {
    return useInfiniteQuery<CanCreateSignatureTemplateQuery, TError, TData>(
        ["canCreateSignatureTemplate.infinite", variables],
        metaData =>
            graphqlFetcher<
                CanCreateSignatureTemplateQuery,
                CanCreateSignatureTemplateQueryVariables
            >(CanCreateSignatureTemplateDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const DeleteSignatureTemplateDocument = new TypedDocumentString(`
    mutation deleteSignatureTemplate($signatureTemplateId: SignatureTemplateId!) {
  deleteSignatureTemplate(signatureTemplateId: $signatureTemplateId) {
    success
    message
  }
}
    `);

export const useDeleteSignatureTemplateMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteSignatureTemplateMutation,
        TError,
        DeleteSignatureTemplateMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteSignatureTemplateMutation,
        TError,
        DeleteSignatureTemplateMutationVariables,
        TContext
    >(
        ["deleteSignatureTemplate"],
        (variables?: DeleteSignatureTemplateMutationVariables) =>
            graphqlFetcher<
                DeleteSignatureTemplateMutation,
                DeleteSignatureTemplateMutationVariables
            >(DeleteSignatureTemplateDocument, variables)(),
        options,
    );
};

export const CreateOrUpdateTemplateDocument = new TypedDocumentString(`
    mutation createOrUpdateTemplate($template: TemplateUpdate!) {
  createOrUpdateTemplate(template: $template) {
    template {
      id
    }
  }
}
    `);

export const useCreateOrUpdateTemplateMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateOrUpdateTemplateMutation,
        TError,
        CreateOrUpdateTemplateMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateOrUpdateTemplateMutation,
        TError,
        CreateOrUpdateTemplateMutationVariables,
        TContext
    >(
        ["createOrUpdateTemplate"],
        (variables?: CreateOrUpdateTemplateMutationVariables) =>
            graphqlFetcher<
                CreateOrUpdateTemplateMutation,
                CreateOrUpdateTemplateMutationVariables
            >(CreateOrUpdateTemplateDocument, variables)(),
        options,
    );
};

export const DeleteTemplateDocument = new TypedDocumentString(`
    mutation deleteTemplate($id: EmailTemplateId!) {
  deleteTemplate(emailTemplateId: $id)
}
    `);

export const useDeleteTemplateMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteTemplateMutation,
        TError,
        DeleteTemplateMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteTemplateMutation,
        TError,
        DeleteTemplateMutationVariables,
        TContext
    >(
        ["deleteTemplate"],
        (variables?: DeleteTemplateMutationVariables) =>
            graphqlFetcher<DeleteTemplateMutation, DeleteTemplateMutationVariables>(
                DeleteTemplateDocument,
                variables,
            )(),
        options,
    );
};

export const GetTenantCredentialsDocument = new TypedDocumentString(`
    query getTenantCredentials {
  getTenantCredentials {
    id
    type
    status
    quickBooksDetails {
      realmId
    }
  }
}
    `);

export const useGetTenantCredentialsQuery = <
    TData = GetTenantCredentialsQuery,
    TError = unknown,
>(
    variables?: GetTenantCredentialsQueryVariables,
    options?: UseQueryOptions<GetTenantCredentialsQuery, TError, TData>,
) => {
    return useQuery<GetTenantCredentialsQuery, TError, TData>(
        variables === undefined
            ? ["getTenantCredentials"]
            : ["getTenantCredentials", variables],
        graphqlFetcher<GetTenantCredentialsQuery, GetTenantCredentialsQueryVariables>(
            GetTenantCredentialsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetTenantCredentialsQuery = <
    TData = GetTenantCredentialsQuery,
    TError = unknown,
>(
    variables?: GetTenantCredentialsQueryVariables,
    options?: UseInfiniteQueryOptions<GetTenantCredentialsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetTenantCredentialsQuery, TError, TData>(
        variables === undefined
            ? ["getTenantCredentials.infinite"]
            : ["getTenantCredentials.infinite", variables],
        metaData =>
            graphqlFetcher<GetTenantCredentialsQuery, GetTenantCredentialsQueryVariables>(
                GetTenantCredentialsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const DeleteTenantCredentialDocument = new TypedDocumentString(`
    mutation deleteTenantCredential($id: TenantCredentialId!) {
  deleteTenantCredential(id: $id)
}
    `);

export const useDeleteTenantCredentialMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteTenantCredentialMutation,
        TError,
        DeleteTenantCredentialMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteTenantCredentialMutation,
        TError,
        DeleteTenantCredentialMutationVariables,
        TContext
    >(
        ["deleteTenantCredential"],
        (variables?: DeleteTenantCredentialMutationVariables) =>
            graphqlFetcher<
                DeleteTenantCredentialMutation,
                DeleteTenantCredentialMutationVariables
            >(DeleteTenantCredentialDocument, variables)(),
        options,
    );
};

export const CreateTenantRegionDocument = new TypedDocumentString(`
    mutation createTenantRegion($tenantId: TenantId!, $name: String!, $key: String!) {
  createTenantRegion(tenantId: $tenantId, name: $name, key: $key) {
    id
  }
}
    `);

export const useCreateTenantRegionMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateTenantRegionMutation,
        TError,
        CreateTenantRegionMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateTenantRegionMutation,
        TError,
        CreateTenantRegionMutationVariables,
        TContext
    >(
        ["createTenantRegion"],
        (variables?: CreateTenantRegionMutationVariables) =>
            graphqlFetcher<
                CreateTenantRegionMutation,
                CreateTenantRegionMutationVariables
            >(CreateTenantRegionDocument, variables)(),
        options,
    );
};

export const UpdateTenantDocument = new TypedDocumentString(`
    mutation updateTenant($id: TenantId!, $name: String, $dateAdded: String, $features: [String!], $sku: String, $customDomains: [String!], $emailProvider: ExternalEmailProvider, $internalEmailSender: String, $supportEmailAddress: String, $customProductName: String, $customAppDomain: String, $whiteLabelContentMode: WhiteLabelContentMode, $baaEnforced: Boolean, $baaDocumentUrl: String, $communicationAcknowledgmentUrl: String, $franchiseManagementEnabled: Boolean) {
  updateTenant(
    id: $id
    name: $name
    dateAdded: $dateAdded
    features: $features
    sku: $sku
    customDomains: $customDomains
    emailProvider: $emailProvider
    internalEmailSender: $internalEmailSender
    supportEmailAddress: $supportEmailAddress
    customProductName: $customProductName
    customAppDomain: $customAppDomain
    whiteLabelContentMode: $whiteLabelContentMode
    baaEnforced: $baaEnforced
    baaDocumentUrl: $baaDocumentUrl
    communicationAcknowledgmentUrl: $communicationAcknowledgmentUrl
    franchiseManagementEnabled: $franchiseManagementEnabled
  ) {
    id
  }
}
    `);

export const useUpdateTenantMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateTenantMutation,
        TError,
        UpdateTenantMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateTenantMutation,
        TError,
        UpdateTenantMutationVariables,
        TContext
    >(
        ["updateTenant"],
        (variables?: UpdateTenantMutationVariables) =>
            graphqlFetcher<UpdateTenantMutation, UpdateTenantMutationVariables>(
                UpdateTenantDocument,
                variables,
            )(),
        options,
    );
};

export const CreateTenantHistoryEntryDocument = new TypedDocumentString(`
    mutation createTenantHistoryEntry($tenantId: TenantId!, $message: String!) {
  createTenantHistoryEntry(tenantId: $tenantId, message: $message) {
    id
    message
    dateAdded
    author {
      preferredName
    }
  }
}
    `);

export const useCreateTenantHistoryEntryMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateTenantHistoryEntryMutation,
        TError,
        CreateTenantHistoryEntryMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateTenantHistoryEntryMutation,
        TError,
        CreateTenantHistoryEntryMutationVariables,
        TContext
    >(
        ["createTenantHistoryEntry"],
        (variables?: CreateTenantHistoryEntryMutationVariables) =>
            graphqlFetcher<
                CreateTenantHistoryEntryMutation,
                CreateTenantHistoryEntryMutationVariables
            >(CreateTenantHistoryEntryDocument, variables)(),
        options,
    );
};

export const UpdateRegionNameDocument = new TypedDocumentString(`
    mutation updateRegionName($tenantId: TenantId!, $regionId: RegionId!, $name: String!) {
  updateRegion(tenantId: $tenantId, regionId: $regionId, name: $name) {
    id
  }
}
    `);

export const useUpdateRegionNameMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateRegionNameMutation,
        TError,
        UpdateRegionNameMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateRegionNameMutation,
        TError,
        UpdateRegionNameMutationVariables,
        TContext
    >(
        ["updateRegionName"],
        (variables?: UpdateRegionNameMutationVariables) =>
            graphqlFetcher<UpdateRegionNameMutation, UpdateRegionNameMutationVariables>(
                UpdateRegionNameDocument,
                variables,
            )(),
        options,
    );
};

export const UpdateRegionDocument = new TypedDocumentString(`
    mutation updateRegion($tenantId: TenantId!, $regionId: RegionId!, $name: String, $parentRegionId: RegionId) {
  updateRegion(
    tenantId: $tenantId
    regionId: $regionId
    name: $name
    parentRegionId: $parentRegionId
  ) {
    id
  }
}
    `);

export const useUpdateRegionMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateRegionMutation,
        TError,
        UpdateRegionMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateRegionMutation,
        TError,
        UpdateRegionMutationVariables,
        TContext
    >(
        ["updateRegion"],
        (variables?: UpdateRegionMutationVariables) =>
            graphqlFetcher<UpdateRegionMutation, UpdateRegionMutationVariables>(
                UpdateRegionDocument,
                variables,
            )(),
        options,
    );
};

export const ModifyUserRoleDocument = new TypedDocumentString(`
    mutation modifyUserRole($tenantId: TenantId!, $userId: UserId!, $regionKey: ID!, $role: String!) {
  modifyUserRole(
    tenantId: $tenantId
    userId: $userId
    regionKey: $regionKey
    role: $role
  )
}
    `);

export const useModifyUserRoleMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        ModifyUserRoleMutation,
        TError,
        ModifyUserRoleMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        ModifyUserRoleMutation,
        TError,
        ModifyUserRoleMutationVariables,
        TContext
    >(
        ["modifyUserRole"],
        (variables?: ModifyUserRoleMutationVariables) =>
            graphqlFetcher<ModifyUserRoleMutation, ModifyUserRoleMutationVariables>(
                ModifyUserRoleDocument,
                variables,
            )(),
        options,
    );
};

export const DestroyPermissionDocument = new TypedDocumentString(`
    mutation destroyPermission($id: UUID!) {
  destroyPermission(id: $id)
}
    `);

export const useDestroyPermissionMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DestroyPermissionMutation,
        TError,
        DestroyPermissionMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DestroyPermissionMutation,
        TError,
        DestroyPermissionMutationVariables,
        TContext
    >(
        ["destroyPermission"],
        (variables?: DestroyPermissionMutationVariables) =>
            graphqlFetcher<DestroyPermissionMutation, DestroyPermissionMutationVariables>(
                DestroyPermissionDocument,
                variables,
            )(),
        options,
    );
};

export const GetTenantsDocument = new TypedDocumentString(`
    query getTenants {
  getTenants {
    id
    name
    activeUsers
    activeHumanUsers
    sku
  }
}
    `);

export const useGetTenantsQuery = <TData = GetTenantsQuery, TError = unknown>(
    variables?: GetTenantsQueryVariables,
    options?: UseQueryOptions<GetTenantsQuery, TError, TData>,
) => {
    return useQuery<GetTenantsQuery, TError, TData>(
        variables === undefined ? ["getTenants"] : ["getTenants", variables],
        graphqlFetcher<GetTenantsQuery, GetTenantsQueryVariables>(
            GetTenantsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetTenantsQuery = <TData = GetTenantsQuery, TError = unknown>(
    variables?: GetTenantsQueryVariables,
    options?: UseInfiniteQueryOptions<GetTenantsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetTenantsQuery, TError, TData>(
        variables === undefined
            ? ["getTenants.infinite"]
            : ["getTenants.infinite", variables],
        metaData =>
            graphqlFetcher<GetTenantsQuery, GetTenantsQueryVariables>(
                GetTenantsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetTenantDocument = new TypedDocumentString(`
    query getTenant($id: TenantId!) {
  getTenant(tenantId: $id) {
    id
    name
    dateAdded
    sku
    customDomains
    emailProvider
    internalEmailSender
    supportEmailAddress
    customProductName
    customAppDomain
    whiteLabelContentMode
    billableEntityId
    baaEnforced
    baaDocumentUrl
    communicationAcknowledgmentUrl
    franchiseManagementEnabled
    users {
      id
      name
      email
      disabled
      hidden
    }
    features {
      name
      enabled
      enabledUpstream
    }
    regions {
      id
      name
      key
      parentRegionId
      billableEntityId
      permissions {
        role
        userId
      }
    }
  }
  getGlobalFeatures {
    skus
    features {
      actual {
        name
        status
      }
      skus
      key
      friendlyTitle
      description
      createdOn
      intent
    }
  }
}
    `);

export const useGetTenantQuery = <TData = GetTenantQuery, TError = unknown>(
    variables: GetTenantQueryVariables,
    options?: UseQueryOptions<GetTenantQuery, TError, TData>,
) => {
    return useQuery<GetTenantQuery, TError, TData>(
        ["getTenant", variables],
        graphqlFetcher<GetTenantQuery, GetTenantQueryVariables>(
            GetTenantDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetTenantQuery = <TData = GetTenantQuery, TError = unknown>(
    variables: GetTenantQueryVariables,
    options?: UseInfiniteQueryOptions<GetTenantQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetTenantQuery, TError, TData>(
        ["getTenant.infinite", variables],
        metaData =>
            graphqlFetcher<GetTenantQuery, GetTenantQueryVariables>(GetTenantDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetTenantHistoryDocument = new TypedDocumentString(`
    query getTenantHistory($tenantId: TenantId!) {
  getTenantHistory(tenantId: $tenantId) {
    id
    message
    dateAdded
    author {
      preferredName
    }
  }
}
    `);

export const useGetTenantHistoryQuery = <TData = GetTenantHistoryQuery, TError = unknown>(
    variables: GetTenantHistoryQueryVariables,
    options?: UseQueryOptions<GetTenantHistoryQuery, TError, TData>,
) => {
    return useQuery<GetTenantHistoryQuery, TError, TData>(
        ["getTenantHistory", variables],
        graphqlFetcher<GetTenantHistoryQuery, GetTenantHistoryQueryVariables>(
            GetTenantHistoryDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetTenantHistoryQuery = <
    TData = GetTenantHistoryQuery,
    TError = unknown,
>(
    variables: GetTenantHistoryQueryVariables,
    options?: UseInfiniteQueryOptions<GetTenantHistoryQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetTenantHistoryQuery, TError, TData>(
        ["getTenantHistory.infinite", variables],
        metaData =>
            graphqlFetcher<GetTenantHistoryQuery, GetTenantHistoryQueryVariables>(
                GetTenantHistoryDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetImportsDocument = new TypedDocumentString(`
    query getImports($tenantId: TenantId!) {
  getImports(tenantId: $tenantId) {
    id
    name
    status
    dateAdded
    runAs
    regionId
  }
}
    `);

export const useGetImportsQuery = <TData = GetImportsQuery, TError = unknown>(
    variables: GetImportsQueryVariables,
    options?: UseQueryOptions<GetImportsQuery, TError, TData>,
) => {
    return useQuery<GetImportsQuery, TError, TData>(
        ["getImports", variables],
        graphqlFetcher<GetImportsQuery, GetImportsQueryVariables>(
            GetImportsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetImportsQuery = <TData = GetImportsQuery, TError = unknown>(
    variables: GetImportsQueryVariables,
    options?: UseInfiniteQueryOptions<GetImportsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetImportsQuery, TError, TData>(
        ["getImports.infinite", variables],
        metaData =>
            graphqlFetcher<GetImportsQuery, GetImportsQueryVariables>(
                GetImportsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetImportDocument = new TypedDocumentString(`
    query getImport($tenantId: TenantId!, $importId: ImportId!) {
  getImport(tenantId: $tenantId, importId: $importId) {
    id
    name
    status
    dateAdded
    runAs
    regionId
    plan {
      inserts {
        entityType
        records
      }
      errors {
        rowNumber
        message
        sheetName
      }
    }
    result {
      results {
        entityType
        inserts {
          rowNumber
          insertedId
          warnings
        }
        errors {
          rowNumber
          message
          sheetName
        }
      }
      error
    }
  }
}
    `);

export const useGetImportQuery = <TData = GetImportQuery, TError = unknown>(
    variables: GetImportQueryVariables,
    options?: UseQueryOptions<GetImportQuery, TError, TData>,
) => {
    return useQuery<GetImportQuery, TError, TData>(
        ["getImport", variables],
        graphqlFetcher<GetImportQuery, GetImportQueryVariables>(
            GetImportDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetImportQuery = <TData = GetImportQuery, TError = unknown>(
    variables: GetImportQueryVariables,
    options?: UseInfiniteQueryOptions<GetImportQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetImportQuery, TError, TData>(
        ["getImport.infinite", variables],
        metaData =>
            graphqlFetcher<GetImportQuery, GetImportQueryVariables>(GetImportDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const ScheduleImportDocument = new TypedDocumentString(`
    mutation scheduleImport($tenantId: TenantId!, $importId: ImportId!) {
  scheduleImport(tenantId: $tenantId, importId: $importId) {
    id
    status
  }
}
    `);

export const useScheduleImportMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        ScheduleImportMutation,
        TError,
        ScheduleImportMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        ScheduleImportMutation,
        TError,
        ScheduleImportMutationVariables,
        TContext
    >(
        ["scheduleImport"],
        (variables?: ScheduleImportMutationVariables) =>
            graphqlFetcher<ScheduleImportMutation, ScheduleImportMutationVariables>(
                ScheduleImportDocument,
                variables,
            )(),
        options,
    );
};

export const ResetTenantDocument = new TypedDocumentString(`
    mutation resetTenant($options: ResetTenantOptions!) {
  resetTenant(options: $options)
}
    `);

export const useResetTenantMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        ResetTenantMutation,
        TError,
        ResetTenantMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        ResetTenantMutation,
        TError,
        ResetTenantMutationVariables,
        TContext
    >(
        ["resetTenant"],
        (variables?: ResetTenantMutationVariables) =>
            graphqlFetcher<ResetTenantMutation, ResetTenantMutationVariables>(
                ResetTenantDocument,
                variables,
            )(),
        options,
    );
};

export const GetTokenForClientDocument = new TypedDocumentString(`
    query getTokenForClient($id: PlacementId!) {
  getTokenForClient(clientId: $id) {
    id
    clientId
    url
    includeContacts
    includeFinancial
    includeLicensing
    sectionKeys
  }
}
    `);

export const useGetTokenForClientQuery = <
    TData = GetTokenForClientQuery,
    TError = unknown,
>(
    variables: GetTokenForClientQueryVariables,
    options?: UseQueryOptions<GetTokenForClientQuery, TError, TData>,
) => {
    return useQuery<GetTokenForClientQuery, TError, TData>(
        ["getTokenForClient", variables],
        graphqlFetcher<GetTokenForClientQuery, GetTokenForClientQueryVariables>(
            GetTokenForClientDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetTokenForClientQuery = <
    TData = GetTokenForClientQuery,
    TError = unknown,
>(
    variables: GetTokenForClientQueryVariables,
    options?: UseInfiniteQueryOptions<GetTokenForClientQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetTokenForClientQuery, TError, TData>(
        ["getTokenForClient.infinite", variables],
        metaData =>
            graphqlFetcher<GetTokenForClientQuery, GetTokenForClientQueryVariables>(
                GetTokenForClientDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const UpdateTokenDocument = new TypedDocumentString(`
    mutation updateToken($updateRequest: UpdateTokenRequest!) {
  updateToken(updateRequest: $updateRequest) {
    id
    clientId
    url
    includeContacts
    includeFinancial
    includeLicensing
    sectionKeys
  }
}
    `);

export const useUpdateTokenMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateTokenMutation,
        TError,
        UpdateTokenMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateTokenMutation,
        TError,
        UpdateTokenMutationVariables,
        TContext
    >(
        ["updateToken"],
        (variables?: UpdateTokenMutationVariables) =>
            graphqlFetcher<UpdateTokenMutation, UpdateTokenMutationVariables>(
                UpdateTokenDocument,
                variables,
            )(),
        options,
    );
};

export const SetupTwoFactorDocument = new TypedDocumentString(`
    mutation setupTwoFactor($input: TwoFactorSetupInput!) {
  setupTwoFactor(input: $input) {
    success
  }
}
    `);

export const useSetupTwoFactorMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        SetupTwoFactorMutation,
        TError,
        SetupTwoFactorMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SetupTwoFactorMutation,
        TError,
        SetupTwoFactorMutationVariables,
        TContext
    >(
        ["setupTwoFactor"],
        (variables?: SetupTwoFactorMutationVariables) =>
            graphqlFetcher<SetupTwoFactorMutation, SetupTwoFactorMutationVariables>(
                SetupTwoFactorDocument,
                variables,
            )(),
        options,
    );
};

export const VerifySetupCodeDocument = new TypedDocumentString(`
    mutation verifySetupCode($input: TwoFactorSetupCodeInput!) {
  verifySetupCode(input: $input) {
    success
    message
  }
}
    `);

export const useVerifySetupCodeMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        VerifySetupCodeMutation,
        TError,
        VerifySetupCodeMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        VerifySetupCodeMutation,
        TError,
        VerifySetupCodeMutationVariables,
        TContext
    >(
        ["verifySetupCode"],
        (variables?: VerifySetupCodeMutationVariables) =>
            graphqlFetcher<VerifySetupCodeMutation, VerifySetupCodeMutationVariables>(
                VerifySetupCodeDocument,
                variables,
            )(),
        options,
    );
};

export const DisableSpecificTwoFactorMethodDocument = new TypedDocumentString(`
    mutation disableSpecificTwoFactorMethod($method: TwoFactorMethod!, $password: String!) {
  disableSpecificTwoFactorMethod(method: $method, password: $password)
}
    `);

export const useDisableSpecificTwoFactorMethodMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        DisableSpecificTwoFactorMethodMutation,
        TError,
        DisableSpecificTwoFactorMethodMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DisableSpecificTwoFactorMethodMutation,
        TError,
        DisableSpecificTwoFactorMethodMutationVariables,
        TContext
    >(
        ["disableSpecificTwoFactorMethod"],
        (variables?: DisableSpecificTwoFactorMethodMutationVariables) =>
            graphqlFetcher<
                DisableSpecificTwoFactorMethodMutation,
                DisableSpecificTwoFactorMethodMutationVariables
            >(DisableSpecificTwoFactorMethodDocument, variables)(),
        options,
    );
};

export const SendTwoFactorCodeDocument = new TypedDocumentString(`
    mutation sendTwoFactorCode($input: TwoFactorSendCodeInput!) {
  sendTwoFactorCode(input: $input)
}
    `);

export const useSendTwoFactorCodeMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        SendTwoFactorCodeMutation,
        TError,
        SendTwoFactorCodeMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SendTwoFactorCodeMutation,
        TError,
        SendTwoFactorCodeMutationVariables,
        TContext
    >(
        ["sendTwoFactorCode"],
        (variables?: SendTwoFactorCodeMutationVariables) =>
            graphqlFetcher<SendTwoFactorCodeMutation, SendTwoFactorCodeMutationVariables>(
                SendTwoFactorCodeDocument,
                variables,
            )(),
        options,
    );
};

export const GetUserGroupsDocument = new TypedDocumentString(`
    query getUserGroups {
  getUserGroups {
    id
    name
  }
}
    `);

export const useGetUserGroupsQuery = <TData = GetUserGroupsQuery, TError = unknown>(
    variables?: GetUserGroupsQueryVariables,
    options?: UseQueryOptions<GetUserGroupsQuery, TError, TData>,
) => {
    return useQuery<GetUserGroupsQuery, TError, TData>(
        variables === undefined ? ["getUserGroups"] : ["getUserGroups", variables],
        graphqlFetcher<GetUserGroupsQuery, GetUserGroupsQueryVariables>(
            GetUserGroupsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetUserGroupsQuery = <
    TData = GetUserGroupsQuery,
    TError = unknown,
>(
    variables?: GetUserGroupsQueryVariables,
    options?: UseInfiniteQueryOptions<GetUserGroupsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetUserGroupsQuery, TError, TData>(
        variables === undefined
            ? ["getUserGroups.infinite"]
            : ["getUserGroups.infinite", variables],
        metaData =>
            graphqlFetcher<GetUserGroupsQuery, GetUserGroupsQueryVariables>(
                GetUserGroupsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetUserMailboxesDocument = new TypedDocumentString(`
    query getUserMailboxes {
  getUserMailboxes {
    id
    status
    email
    provider
    isHistoricalSyncRunning
  }
}
    `);

export const useGetUserMailboxesQuery = <TData = GetUserMailboxesQuery, TError = unknown>(
    variables?: GetUserMailboxesQueryVariables,
    options?: UseQueryOptions<GetUserMailboxesQuery, TError, TData>,
) => {
    return useQuery<GetUserMailboxesQuery, TError, TData>(
        variables === undefined ? ["getUserMailboxes"] : ["getUserMailboxes", variables],
        graphqlFetcher<GetUserMailboxesQuery, GetUserMailboxesQueryVariables>(
            GetUserMailboxesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetUserMailboxesQuery = <
    TData = GetUserMailboxesQuery,
    TError = unknown,
>(
    variables?: GetUserMailboxesQueryVariables,
    options?: UseInfiniteQueryOptions<GetUserMailboxesQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetUserMailboxesQuery, TError, TData>(
        variables === undefined
            ? ["getUserMailboxes.infinite"]
            : ["getUserMailboxes.infinite", variables],
        metaData =>
            graphqlFetcher<GetUserMailboxesQuery, GetUserMailboxesQueryVariables>(
                GetUserMailboxesDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const DeleteUserMailboxDocument = new TypedDocumentString(`
    mutation deleteUserMailbox($id: UserMailboxId!) {
  deleteUserMailbox(id: $id)
}
    `);

export const useDeleteUserMailboxMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteUserMailboxMutation,
        TError,
        DeleteUserMailboxMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteUserMailboxMutation,
        TError,
        DeleteUserMailboxMutationVariables,
        TContext
    >(
        ["deleteUserMailbox"],
        (variables?: DeleteUserMailboxMutationVariables) =>
            graphqlFetcher<DeleteUserMailboxMutation, DeleteUserMailboxMutationVariables>(
                DeleteUserMailboxDocument,
                variables,
            )(),
        options,
    );
};

export const UserManagementGetRolesDocument = new TypedDocumentString(`
    query userManagementGetRoles {
  userManagementGetRoles {
    key
    humanReadableName
  }
}
    `);

export const useUserManagementGetRolesQuery = <
    TData = UserManagementGetRolesQuery,
    TError = unknown,
>(
    variables?: UserManagementGetRolesQueryVariables,
    options?: UseQueryOptions<UserManagementGetRolesQuery, TError, TData>,
) => {
    return useQuery<UserManagementGetRolesQuery, TError, TData>(
        variables === undefined
            ? ["userManagementGetRoles"]
            : ["userManagementGetRoles", variables],
        graphqlFetcher<UserManagementGetRolesQuery, UserManagementGetRolesQueryVariables>(
            UserManagementGetRolesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteUserManagementGetRolesQuery = <
    TData = UserManagementGetRolesQuery,
    TError = unknown,
>(
    variables?: UserManagementGetRolesQueryVariables,
    options?: UseInfiniteQueryOptions<UserManagementGetRolesQuery, TError, TData>,
) => {
    return useInfiniteQuery<UserManagementGetRolesQuery, TError, TData>(
        variables === undefined
            ? ["userManagementGetRoles.infinite"]
            : ["userManagementGetRoles.infinite", variables],
        metaData =>
            graphqlFetcher<
                UserManagementGetRolesQuery,
                UserManagementGetRolesQueryVariables
            >(UserManagementGetRolesDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const UserManagementGetUsersDocument = new TypedDocumentString(`
    query userManagementGetUsers {
  userManagementGetUsers(params: {userTypes: [User]}) {
    user {
      ...UserManagementUser
    }
  }
}
    fragment UserManagementUser on User {
  id
  email
  name
  type
  hidden
  disabled
  twoFactorEmailEnabled
  twoFactorSmsEnabled
  permissions {
    role
    region {
      id
      name
      key
      membershipType
    }
  }
}`);

export const useUserManagementGetUsersQuery = <
    TData = UserManagementGetUsersQuery,
    TError = unknown,
>(
    variables?: UserManagementGetUsersQueryVariables,
    options?: UseQueryOptions<UserManagementGetUsersQuery, TError, TData>,
) => {
    return useQuery<UserManagementGetUsersQuery, TError, TData>(
        variables === undefined
            ? ["userManagementGetUsers"]
            : ["userManagementGetUsers", variables],
        graphqlFetcher<UserManagementGetUsersQuery, UserManagementGetUsersQueryVariables>(
            UserManagementGetUsersDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteUserManagementGetUsersQuery = <
    TData = UserManagementGetUsersQuery,
    TError = unknown,
>(
    variables?: UserManagementGetUsersQueryVariables,
    options?: UseInfiniteQueryOptions<UserManagementGetUsersQuery, TError, TData>,
) => {
    return useInfiniteQuery<UserManagementGetUsersQuery, TError, TData>(
        variables === undefined
            ? ["userManagementGetUsers.infinite"]
            : ["userManagementGetUsers.infinite", variables],
        metaData =>
            graphqlFetcher<
                UserManagementGetUsersQuery,
                UserManagementGetUsersQueryVariables
            >(UserManagementGetUsersDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const UserManagementGetIntegrationUsersDocument = new TypedDocumentString(`
    query userManagementGetIntegrationUsers {
  userManagementGetUsers(params: {userTypes: [System]}) {
    user {
      ...UserManagementUser
    }
  }
}
    fragment UserManagementUser on User {
  id
  email
  name
  type
  hidden
  disabled
  twoFactorEmailEnabled
  twoFactorSmsEnabled
  permissions {
    role
    region {
      id
      name
      key
      membershipType
    }
  }
}`);

export const useUserManagementGetIntegrationUsersQuery = <
    TData = UserManagementGetIntegrationUsersQuery,
    TError = unknown,
>(
    variables?: UserManagementGetIntegrationUsersQueryVariables,
    options?: UseQueryOptions<UserManagementGetIntegrationUsersQuery, TError, TData>,
) => {
    return useQuery<UserManagementGetIntegrationUsersQuery, TError, TData>(
        variables === undefined
            ? ["userManagementGetIntegrationUsers"]
            : ["userManagementGetIntegrationUsers", variables],
        graphqlFetcher<
            UserManagementGetIntegrationUsersQuery,
            UserManagementGetIntegrationUsersQueryVariables
        >(UserManagementGetIntegrationUsersDocument, variables),
        options,
    );
};

export const useInfiniteUserManagementGetIntegrationUsersQuery = <
    TData = UserManagementGetIntegrationUsersQuery,
    TError = unknown,
>(
    variables?: UserManagementGetIntegrationUsersQueryVariables,
    options?: UseInfiniteQueryOptions<
        UserManagementGetIntegrationUsersQuery,
        TError,
        TData
    >,
) => {
    return useInfiniteQuery<UserManagementGetIntegrationUsersQuery, TError, TData>(
        variables === undefined
            ? ["userManagementGetIntegrationUsers.infinite"]
            : ["userManagementGetIntegrationUsers.infinite", variables],
        metaData =>
            graphqlFetcher<
                UserManagementGetIntegrationUsersQuery,
                UserManagementGetIntegrationUsersQueryVariables
            >(UserManagementGetIntegrationUsersDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const UserManagementGetUserDocument = new TypedDocumentString(`
    query userManagementGetUser($userId: UserId!) {
  userManagementGetUser(userId: $userId) {
    canDisable
    user {
      ...UserManagementUser
    }
  }
}
    fragment UserManagementUser on User {
  id
  email
  name
  type
  hidden
  disabled
  twoFactorEmailEnabled
  twoFactorSmsEnabled
  permissions {
    role
    region {
      id
      name
      key
      membershipType
    }
  }
}`);

export const useUserManagementGetUserQuery = <
    TData = UserManagementGetUserQuery,
    TError = unknown,
>(
    variables: UserManagementGetUserQueryVariables,
    options?: UseQueryOptions<UserManagementGetUserQuery, TError, TData>,
) => {
    return useQuery<UserManagementGetUserQuery, TError, TData>(
        ["userManagementGetUser", variables],
        graphqlFetcher<UserManagementGetUserQuery, UserManagementGetUserQueryVariables>(
            UserManagementGetUserDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteUserManagementGetUserQuery = <
    TData = UserManagementGetUserQuery,
    TError = unknown,
>(
    variables: UserManagementGetUserQueryVariables,
    options?: UseInfiniteQueryOptions<UserManagementGetUserQuery, TError, TData>,
) => {
    return useInfiniteQuery<UserManagementGetUserQuery, TError, TData>(
        ["userManagementGetUser.infinite", variables],
        metaData =>
            graphqlFetcher<
                UserManagementGetUserQuery,
                UserManagementGetUserQueryVariables
            >(UserManagementGetUserDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const UserManagementGetImpliedUsersDocument = new TypedDocumentString(`
    query userManagementGetImpliedUsers {
  userManagementGetImpliedUsers {
    user {
      ...UserManagementUser
    }
  }
}
    fragment UserManagementUser on User {
  id
  email
  name
  type
  hidden
  disabled
  twoFactorEmailEnabled
  twoFactorSmsEnabled
  permissions {
    role
    region {
      id
      name
      key
      membershipType
    }
  }
}`);

export const useUserManagementGetImpliedUsersQuery = <
    TData = UserManagementGetImpliedUsersQuery,
    TError = unknown,
>(
    variables?: UserManagementGetImpliedUsersQueryVariables,
    options?: UseQueryOptions<UserManagementGetImpliedUsersQuery, TError, TData>,
) => {
    return useQuery<UserManagementGetImpliedUsersQuery, TError, TData>(
        variables === undefined
            ? ["userManagementGetImpliedUsers"]
            : ["userManagementGetImpliedUsers", variables],
        graphqlFetcher<
            UserManagementGetImpliedUsersQuery,
            UserManagementGetImpliedUsersQueryVariables
        >(UserManagementGetImpliedUsersDocument, variables),
        options,
    );
};

export const useInfiniteUserManagementGetImpliedUsersQuery = <
    TData = UserManagementGetImpliedUsersQuery,
    TError = unknown,
>(
    variables?: UserManagementGetImpliedUsersQueryVariables,
    options?: UseInfiniteQueryOptions<UserManagementGetImpliedUsersQuery, TError, TData>,
) => {
    return useInfiniteQuery<UserManagementGetImpliedUsersQuery, TError, TData>(
        variables === undefined
            ? ["userManagementGetImpliedUsers.infinite"]
            : ["userManagementGetImpliedUsers.infinite", variables],
        metaData =>
            graphqlFetcher<
                UserManagementGetImpliedUsersQuery,
                UserManagementGetImpliedUsersQueryVariables
            >(UserManagementGetImpliedUsersDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const UserManagementGetApiKeyDocument = new TypedDocumentString(`
    query userManagementGetApiKey($userId: UserId!) {
  userManagementGetApiKey(userId: $userId) {
    apiKey
  }
}
    `);

export const useUserManagementGetApiKeyQuery = <
    TData = UserManagementGetApiKeyQuery,
    TError = unknown,
>(
    variables: UserManagementGetApiKeyQueryVariables,
    options?: UseQueryOptions<UserManagementGetApiKeyQuery, TError, TData>,
) => {
    return useQuery<UserManagementGetApiKeyQuery, TError, TData>(
        ["userManagementGetApiKey", variables],
        graphqlFetcher<
            UserManagementGetApiKeyQuery,
            UserManagementGetApiKeyQueryVariables
        >(UserManagementGetApiKeyDocument, variables),
        options,
    );
};

export const useInfiniteUserManagementGetApiKeyQuery = <
    TData = UserManagementGetApiKeyQuery,
    TError = unknown,
>(
    variables: UserManagementGetApiKeyQueryVariables,
    options?: UseInfiniteQueryOptions<UserManagementGetApiKeyQuery, TError, TData>,
) => {
    return useInfiniteQuery<UserManagementGetApiKeyQuery, TError, TData>(
        ["userManagementGetApiKey.infinite", variables],
        metaData =>
            graphqlFetcher<
                UserManagementGetApiKeyQuery,
                UserManagementGetApiKeyQueryVariables
            >(UserManagementGetApiKeyDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const UserManagementCreateUserDocument = new TypedDocumentString(`
    mutation userManagementCreateUser($userEmail: String!, $roleKey: String!, $regionId: RegionId!) {
  userManagementCreateUser(
    userEmail: $userEmail
    roleKey: $roleKey
    regionId: $regionId
  )
}
    `);

export const useUserManagementCreateUserMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UserManagementCreateUserMutation,
        TError,
        UserManagementCreateUserMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UserManagementCreateUserMutation,
        TError,
        UserManagementCreateUserMutationVariables,
        TContext
    >(
        ["userManagementCreateUser"],
        (variables?: UserManagementCreateUserMutationVariables) =>
            graphqlFetcher<
                UserManagementCreateUserMutation,
                UserManagementCreateUserMutationVariables
            >(UserManagementCreateUserDocument, variables)(),
        options,
    );
};

export const UserManagementCreateSystemUserDocument = new TypedDocumentString(`
    mutation userManagementCreateSystemUser($name: String!, $roleKey: String!, $regionId: RegionId!) {
  userManagementCreateSystemUser(
    name: $name
    roleKey: $roleKey
    regionId: $regionId
  )
}
    `);

export const useUserManagementCreateSystemUserMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        UserManagementCreateSystemUserMutation,
        TError,
        UserManagementCreateSystemUserMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UserManagementCreateSystemUserMutation,
        TError,
        UserManagementCreateSystemUserMutationVariables,
        TContext
    >(
        ["userManagementCreateSystemUser"],
        (variables?: UserManagementCreateSystemUserMutationVariables) =>
            graphqlFetcher<
                UserManagementCreateSystemUserMutation,
                UserManagementCreateSystemUserMutationVariables
            >(UserManagementCreateSystemUserDocument, variables)(),
        options,
    );
};

export const UserManagementUpdatePermissionDocument = new TypedDocumentString(`
    mutation userManagementUpdatePermission($userId: UserId!, $regionId: RegionId!, $roleKey: String) {
  userManagementUpdatePermission(
    userId: $userId
    regionId: $regionId
    roleKey: $roleKey
  )
}
    `);

export const useUserManagementUpdatePermissionMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        UserManagementUpdatePermissionMutation,
        TError,
        UserManagementUpdatePermissionMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UserManagementUpdatePermissionMutation,
        TError,
        UserManagementUpdatePermissionMutationVariables,
        TContext
    >(
        ["userManagementUpdatePermission"],
        (variables?: UserManagementUpdatePermissionMutationVariables) =>
            graphqlFetcher<
                UserManagementUpdatePermissionMutation,
                UserManagementUpdatePermissionMutationVariables
            >(UserManagementUpdatePermissionDocument, variables)(),
        options,
    );
};

export const UserManagementDisableUserDocument = new TypedDocumentString(`
    mutation userManagementDisableUser($userId: UserId!) {
  userManagementDisableUser(userId: $userId)
}
    `);

export const useUserManagementDisableUserMutation = <
    TError = unknown,
    TContext = unknown,
>(
    options?: UseMutationOptions<
        UserManagementDisableUserMutation,
        TError,
        UserManagementDisableUserMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UserManagementDisableUserMutation,
        TError,
        UserManagementDisableUserMutationVariables,
        TContext
    >(
        ["userManagementDisableUser"],
        (variables?: UserManagementDisableUserMutationVariables) =>
            graphqlFetcher<
                UserManagementDisableUserMutation,
                UserManagementDisableUserMutationVariables
            >(UserManagementDisableUserDocument, variables)(),
        options,
    );
};

export const UserManagementEnableUserDocument = new TypedDocumentString(`
    mutation userManagementEnableUser($userId: UserId!) {
  userManagementEnableUser(userId: $userId)
}
    `);

export const useUserManagementEnableUserMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UserManagementEnableUserMutation,
        TError,
        UserManagementEnableUserMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UserManagementEnableUserMutation,
        TError,
        UserManagementEnableUserMutationVariables,
        TContext
    >(
        ["userManagementEnableUser"],
        (variables?: UserManagementEnableUserMutationVariables) =>
            graphqlFetcher<
                UserManagementEnableUserMutation,
                UserManagementEnableUserMutationVariables
            >(UserManagementEnableUserDocument, variables)(),
        options,
    );
};

export const UserManagementHideUserDocument = new TypedDocumentString(`
    mutation userManagementHideUser($userId: UserId!) {
  userManagementHideUser(userId: $userId)
}
    `);

export const useUserManagementHideUserMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UserManagementHideUserMutation,
        TError,
        UserManagementHideUserMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UserManagementHideUserMutation,
        TError,
        UserManagementHideUserMutationVariables,
        TContext
    >(
        ["userManagementHideUser"],
        (variables?: UserManagementHideUserMutationVariables) =>
            graphqlFetcher<
                UserManagementHideUserMutation,
                UserManagementHideUserMutationVariables
            >(UserManagementHideUserDocument, variables)(),
        options,
    );
};

export const UserManagementShowUserDocument = new TypedDocumentString(`
    mutation userManagementShowUser($userId: UserId!) {
  userManagementShowUser(userId: $userId)
}
    `);

export const useUserManagementShowUserMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UserManagementShowUserMutation,
        TError,
        UserManagementShowUserMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UserManagementShowUserMutation,
        TError,
        UserManagementShowUserMutationVariables,
        TContext
    >(
        ["userManagementShowUser"],
        (variables?: UserManagementShowUserMutationVariables) =>
            graphqlFetcher<
                UserManagementShowUserMutation,
                UserManagementShowUserMutationVariables
            >(UserManagementShowUserDocument, variables)(),
        options,
    );
};

export const SuperuserAccountRecoveryDocument = new TypedDocumentString(`
    mutation superuserAccountRecovery($userId: UserId!) {
  superuserAccountRecovery(userId: $userId)
}
    `);

export const useSuperuserAccountRecoveryMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        SuperuserAccountRecoveryMutation,
        TError,
        SuperuserAccountRecoveryMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SuperuserAccountRecoveryMutation,
        TError,
        SuperuserAccountRecoveryMutationVariables,
        TContext
    >(
        ["superuserAccountRecovery"],
        (variables?: SuperuserAccountRecoveryMutationVariables) =>
            graphqlFetcher<
                SuperuserAccountRecoveryMutation,
                SuperuserAccountRecoveryMutationVariables
            >(SuperuserAccountRecoveryDocument, variables)(),
        options,
    );
};

export const SuperuserResetPasswordDocument = new TypedDocumentString(`
    mutation superuserResetPassword($userId: UserId!) {
  superuserResetPassword(userId: $userId) {
    userId
    password
  }
}
    `);

export const useSuperuserResetPasswordMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        SuperuserResetPasswordMutation,
        TError,
        SuperuserResetPasswordMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SuperuserResetPasswordMutation,
        TError,
        SuperuserResetPasswordMutationVariables,
        TContext
    >(
        ["superuserResetPassword"],
        (variables?: SuperuserResetPasswordMutationVariables) =>
            graphqlFetcher<
                SuperuserResetPasswordMutation,
                SuperuserResetPasswordMutationVariables
            >(SuperuserResetPasswordDocument, variables)(),
        options,
    );
};

export const GetUsersDocument = new TypedDocumentString(`
    query getUsers {
  getUsers {
    id
    email
    type
    name
    firstName
    disabled
    hidden
  }
}
    `);

export const useGetUsersQuery = <TData = GetUsersQuery, TError = unknown>(
    variables?: GetUsersQueryVariables,
    options?: UseQueryOptions<GetUsersQuery, TError, TData>,
) => {
    return useQuery<GetUsersQuery, TError, TData>(
        variables === undefined ? ["getUsers"] : ["getUsers", variables],
        graphqlFetcher<GetUsersQuery, GetUsersQueryVariables>(
            GetUsersDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetUsersQuery = <TData = GetUsersQuery, TError = unknown>(
    variables?: GetUsersQueryVariables,
    options?: UseInfiniteQueryOptions<GetUsersQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetUsersQuery, TError, TData>(
        variables === undefined
            ? ["getUsers.infinite"]
            : ["getUsers.infinite", variables],
        metaData =>
            graphqlFetcher<GetUsersQuery, GetUsersQueryVariables>(GetUsersDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const CreateUserDocument = new TypedDocumentString(`
    mutation createUser($tenantId: TenantId!, $email: String!, $password: String!) {
  createUser(tenantId: $tenantId, email: $email, password: $password) {
    id
    email
  }
}
    `);

export const useCreateUserMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateUserMutation,
        TError,
        CreateUserMutationVariables,
        TContext
    >,
) => {
    return useMutation<CreateUserMutation, TError, CreateUserMutationVariables, TContext>(
        ["createUser"],
        (variables?: CreateUserMutationVariables) =>
            graphqlFetcher<CreateUserMutation, CreateUserMutationVariables>(
                CreateUserDocument,
                variables,
            )(),
        options,
    );
};

export const CloneUserDocument = new TypedDocumentString(`
    mutation cloneUser($tenantId: TenantId!, $userId: UserId!) {
  cloneUser(tenantId: $tenantId, userId: $userId)
}
    `);

export const useCloneUserMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CloneUserMutation,
        TError,
        CloneUserMutationVariables,
        TContext
    >,
) => {
    return useMutation<CloneUserMutation, TError, CloneUserMutationVariables, TContext>(
        ["cloneUser"],
        (variables?: CloneUserMutationVariables) =>
            graphqlFetcher<CloneUserMutation, CloneUserMutationVariables>(
                CloneUserDocument,
                variables,
            )(),
        options,
    );
};

export const SuperuserUpdateUserDocument = new TypedDocumentString(`
    mutation superuserUpdateUser($userId: UserId!, $disabled: Boolean!, $hidden: Boolean!, $hideSmtpWarning: Boolean!, $email: String!, $firstName: String, $name: String) {
  superuserUpdateUser(
    userId: $userId
    disabled: $disabled
    hidden: $hidden
    hideSmtpWarning: $hideSmtpWarning
    email: $email
    firstName: $firstName
    name: $name
  ) {
    id
  }
}
    `);

export const useSuperuserUpdateUserMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        SuperuserUpdateUserMutation,
        TError,
        SuperuserUpdateUserMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SuperuserUpdateUserMutation,
        TError,
        SuperuserUpdateUserMutationVariables,
        TContext
    >(
        ["superuserUpdateUser"],
        (variables?: SuperuserUpdateUserMutationVariables) =>
            graphqlFetcher<
                SuperuserUpdateUserMutation,
                SuperuserUpdateUserMutationVariables
            >(SuperuserUpdateUserDocument, variables)(),
        options,
    );
};

export const AdminUpdateUserDocument = new TypedDocumentString(`
    mutation adminUpdateUser($userId: UserId!, $name: String!) {
  adminUpdateUser(userId: $userId, name: $name) {
    id
  }
}
    `);

export const useAdminUpdateUserMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        AdminUpdateUserMutation,
        TError,
        AdminUpdateUserMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        AdminUpdateUserMutation,
        TError,
        AdminUpdateUserMutationVariables,
        TContext
    >(
        ["adminUpdateUser"],
        (variables?: AdminUpdateUserMutationVariables) =>
            graphqlFetcher<AdminUpdateUserMutation, AdminUpdateUserMutationVariables>(
                AdminUpdateUserDocument,
                variables,
            )(),
        options,
    );
};

export const GetUserDocument = new TypedDocumentString(`
    query getUser($id: UserId!) {
  getUser(userId: $id) {
    id
    name
    firstName
    type
    email
    disabled
    hidden
    hideSmtpWarning
    token
    twoFactorEmailEnabled
    twoFactorSmsEnabled
    tenant {
      id
      name
      regions {
        id
        key
        name
        permissions {
          id
          role
          userId
          deletedAt
        }
      }
      features {
        name
        enabled
        enabledUpstream
      }
    }
  }
}
    `);

export const useGetUserQuery = <TData = GetUserQuery, TError = unknown>(
    variables: GetUserQueryVariables,
    options?: UseQueryOptions<GetUserQuery, TError, TData>,
) => {
    return useQuery<GetUserQuery, TError, TData>(
        ["getUser", variables],
        graphqlFetcher<GetUserQuery, GetUserQueryVariables>(GetUserDocument, variables),
        options,
    );
};

export const useInfiniteGetUserQuery = <TData = GetUserQuery, TError = unknown>(
    variables: GetUserQueryVariables,
    options?: UseInfiniteQueryOptions<GetUserQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetUserQuery, TError, TData>(
        ["getUser.infinite", variables],
        metaData =>
            graphqlFetcher<GetUserQuery, GetUserQueryVariables>(GetUserDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetUserApiTokenDocument = new TypedDocumentString(`
    query getUserApiToken($id: UserId!) {
  getUser(userId: $id) {
    id
    apiToken
  }
}
    `);

export const useGetUserApiTokenQuery = <TData = GetUserApiTokenQuery, TError = unknown>(
    variables: GetUserApiTokenQueryVariables,
    options?: UseQueryOptions<GetUserApiTokenQuery, TError, TData>,
) => {
    return useQuery<GetUserApiTokenQuery, TError, TData>(
        ["getUserApiToken", variables],
        graphqlFetcher<GetUserApiTokenQuery, GetUserApiTokenQueryVariables>(
            GetUserApiTokenDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetUserApiTokenQuery = <
    TData = GetUserApiTokenQuery,
    TError = unknown,
>(
    variables: GetUserApiTokenQueryVariables,
    options?: UseInfiniteQueryOptions<GetUserApiTokenQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetUserApiTokenQuery, TError, TData>(
        ["getUserApiToken.infinite", variables],
        metaData =>
            graphqlFetcher<GetUserApiTokenQuery, GetUserApiTokenQueryVariables>(
                GetUserApiTokenDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetRolesWithActionsDocument = new TypedDocumentString(`
    query getRolesWithActions($regionId: RegionId!) {
  getRolesWithActions(regionId: $regionId) {
    key
    humanReadableName
    userAssignable
    actions
  }
}
    `);

export const useGetRolesWithActionsQuery = <
    TData = GetRolesWithActionsQuery,
    TError = unknown,
>(
    variables: GetRolesWithActionsQueryVariables,
    options?: UseQueryOptions<GetRolesWithActionsQuery, TError, TData>,
) => {
    return useQuery<GetRolesWithActionsQuery, TError, TData>(
        ["getRolesWithActions", variables],
        graphqlFetcher<GetRolesWithActionsQuery, GetRolesWithActionsQueryVariables>(
            GetRolesWithActionsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetRolesWithActionsQuery = <
    TData = GetRolesWithActionsQuery,
    TError = unknown,
>(
    variables: GetRolesWithActionsQueryVariables,
    options?: UseInfiniteQueryOptions<GetRolesWithActionsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetRolesWithActionsQuery, TError, TData>(
        ["getRolesWithActions.infinite", variables],
        metaData =>
            graphqlFetcher<GetRolesWithActionsQuery, GetRolesWithActionsQueryVariables>(
                GetRolesWithActionsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetCurrentUserDocument = new TypedDocumentString(`
    query getCurrentUser {
  getCurrentUser {
    id
    name
    firstName
    type
    email
    disabled
    hidden
    hideSmtpWarning
    token
    phone
    twoFactorEmailEnabled
    twoFactorSmsEnabled
    tenant {
      id
      name
    }
  }
}
    `);

export const useGetCurrentUserQuery = <TData = GetCurrentUserQuery, TError = unknown>(
    variables?: GetCurrentUserQueryVariables,
    options?: UseQueryOptions<GetCurrentUserQuery, TError, TData>,
) => {
    return useQuery<GetCurrentUserQuery, TError, TData>(
        variables === undefined ? ["getCurrentUser"] : ["getCurrentUser", variables],
        graphqlFetcher<GetCurrentUserQuery, GetCurrentUserQueryVariables>(
            GetCurrentUserDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetCurrentUserQuery = <
    TData = GetCurrentUserQuery,
    TError = unknown,
>(
    variables?: GetCurrentUserQueryVariables,
    options?: UseInfiniteQueryOptions<GetCurrentUserQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetCurrentUserQuery, TError, TData>(
        variables === undefined
            ? ["getCurrentUser.infinite"]
            : ["getCurrentUser.infinite", variables],
        metaData =>
            graphqlFetcher<GetCurrentUserQuery, GetCurrentUserQueryVariables>(
                GetCurrentUserDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetUserProfileDocument = new TypedDocumentString(`
    query getUserProfile {
  getUserProfile {
    ...UserProfileBasics
  }
}
    fragment UserProfileBasics on UserProfile {
  id
  name
  businessName
  website
  email
  phone
  summary
  approach
  certifications
  pictureUrl
  status
  latitude
  longitude
  isComplete
  territories {
    ...UserTerritoryBasics
  }
}
fragment UserTerritoryBasics on UserTerritory {
  id
  type
  distance
  address
  city
  county
  state
  zip
  latitude
  longitude
}`);

export const useGetUserProfileQuery = <TData = GetUserProfileQuery, TError = unknown>(
    variables?: GetUserProfileQueryVariables,
    options?: UseQueryOptions<GetUserProfileQuery, TError, TData>,
) => {
    return useQuery<GetUserProfileQuery, TError, TData>(
        variables === undefined ? ["getUserProfile"] : ["getUserProfile", variables],
        graphqlFetcher<GetUserProfileQuery, GetUserProfileQueryVariables>(
            GetUserProfileDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetUserProfileQuery = <
    TData = GetUserProfileQuery,
    TError = unknown,
>(
    variables?: GetUserProfileQueryVariables,
    options?: UseInfiniteQueryOptions<GetUserProfileQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetUserProfileQuery, TError, TData>(
        variables === undefined
            ? ["getUserProfile.infinite"]
            : ["getUserProfile.infinite", variables],
        metaData =>
            graphqlFetcher<GetUserProfileQuery, GetUserProfileQueryVariables>(
                GetUserProfileDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const PatchUserProfileDocument = new TypedDocumentString(`
    mutation patchUserProfile($params: PatchUserProfileRequest!) {
  patchUserProfile(params: $params) {
    ...UserProfileBasics
  }
}
    fragment UserProfileBasics on UserProfile {
  id
  name
  businessName
  website
  email
  phone
  summary
  approach
  certifications
  pictureUrl
  status
  latitude
  longitude
  isComplete
  territories {
    ...UserTerritoryBasics
  }
}
fragment UserTerritoryBasics on UserTerritory {
  id
  type
  distance
  address
  city
  county
  state
  zip
  latitude
  longitude
}`);

export const usePatchUserProfileMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        PatchUserProfileMutation,
        TError,
        PatchUserProfileMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        PatchUserProfileMutation,
        TError,
        PatchUserProfileMutationVariables,
        TContext
    >(
        ["patchUserProfile"],
        (variables?: PatchUserProfileMutationVariables) =>
            graphqlFetcher<PatchUserProfileMutation, PatchUserProfileMutationVariables>(
                PatchUserProfileDocument,
                variables,
            )(),
        options,
    );
};

export const UpsertUserTerritoryDocument = new TypedDocumentString(`
    mutation upsertUserTerritory($params: UpsertUserTerritoryRequest!) {
  upsertUserTerritory(params: $params) {
    ...UserTerritoryBasics
  }
}
    fragment UserTerritoryBasics on UserTerritory {
  id
  type
  distance
  address
  city
  county
  state
  zip
  latitude
  longitude
}`);

export const useUpsertUserTerritoryMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpsertUserTerritoryMutation,
        TError,
        UpsertUserTerritoryMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpsertUserTerritoryMutation,
        TError,
        UpsertUserTerritoryMutationVariables,
        TContext
    >(
        ["upsertUserTerritory"],
        (variables?: UpsertUserTerritoryMutationVariables) =>
            graphqlFetcher<
                UpsertUserTerritoryMutation,
                UpsertUserTerritoryMutationVariables
            >(UpsertUserTerritoryDocument, variables)(),
        options,
    );
};

export const DeleteUserTerritoryDocument = new TypedDocumentString(`
    mutation deleteUserTerritory($id: UserTerritoryId!) {
  deleteUserTerritory(id: $id)
}
    `);

export const useDeleteUserTerritoryMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteUserTerritoryMutation,
        TError,
        DeleteUserTerritoryMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteUserTerritoryMutation,
        TError,
        DeleteUserTerritoryMutationVariables,
        TContext
    >(
        ["deleteUserTerritory"],
        (variables?: DeleteUserTerritoryMutationVariables) =>
            graphqlFetcher<
                DeleteUserTerritoryMutation,
                DeleteUserTerritoryMutationVariables
            >(DeleteUserTerritoryDocument, variables)(),
        options,
    );
};

export const SearchUserProfilesDocument = new TypedDocumentString(`
    query searchUserProfiles($params: SearchUserProfileRequest!) {
  searchUserProfiles(params: $params) {
    hits {
      ...UserProfileBasics
      verticals
    }
    referenceContacts {
      id
      userProfileId
      appLink
    }
    coordinates {
      lat
      lng
    }
    total
  }
}
    fragment UserProfileBasics on UserProfile {
  id
  name
  businessName
  website
  email
  phone
  summary
  approach
  certifications
  pictureUrl
  status
  latitude
  longitude
  isComplete
  territories {
    ...UserTerritoryBasics
  }
}
fragment UserTerritoryBasics on UserTerritory {
  id
  type
  distance
  address
  city
  county
  state
  zip
  latitude
  longitude
}`);

export const useSearchUserProfilesQuery = <
    TData = SearchUserProfilesQuery,
    TError = unknown,
>(
    variables: SearchUserProfilesQueryVariables,
    options?: UseQueryOptions<SearchUserProfilesQuery, TError, TData>,
) => {
    return useQuery<SearchUserProfilesQuery, TError, TData>(
        ["searchUserProfiles", variables],
        graphqlFetcher<SearchUserProfilesQuery, SearchUserProfilesQueryVariables>(
            SearchUserProfilesDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteSearchUserProfilesQuery = <
    TData = SearchUserProfilesQuery,
    TError = unknown,
>(
    variables: SearchUserProfilesQueryVariables,
    options?: UseInfiniteQueryOptions<SearchUserProfilesQuery, TError, TData>,
) => {
    return useInfiniteQuery<SearchUserProfilesQuery, TError, TData>(
        ["searchUserProfiles.infinite", variables],
        metaData =>
            graphqlFetcher<SearchUserProfilesQuery, SearchUserProfilesQueryVariables>(
                SearchUserProfilesDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const SearchUserProfilesMapViewDocument = new TypedDocumentString(`
    query searchUserProfilesMapView($params: SearchUserProfileRequest!, $mapView: CommunitySearchRequestMapComponent!) {
  searchUserProfilesMapView(params: $params, mapView: $mapView) {
    userProfileCoordinates {
      id
      name
      lat
      lng
    }
    userProfileClusters {
      lat
      lng
      count
    }
    coordinates {
      lat
      lng
    }
    recommendedZoom
  }
}
    `);

export const useSearchUserProfilesMapViewQuery = <
    TData = SearchUserProfilesMapViewQuery,
    TError = unknown,
>(
    variables: SearchUserProfilesMapViewQueryVariables,
    options?: UseQueryOptions<SearchUserProfilesMapViewQuery, TError, TData>,
) => {
    return useQuery<SearchUserProfilesMapViewQuery, TError, TData>(
        ["searchUserProfilesMapView", variables],
        graphqlFetcher<
            SearchUserProfilesMapViewQuery,
            SearchUserProfilesMapViewQueryVariables
        >(SearchUserProfilesMapViewDocument, variables),
        options,
    );
};

export const useInfiniteSearchUserProfilesMapViewQuery = <
    TData = SearchUserProfilesMapViewQuery,
    TError = unknown,
>(
    variables: SearchUserProfilesMapViewQueryVariables,
    options?: UseInfiniteQueryOptions<SearchUserProfilesMapViewQuery, TError, TData>,
) => {
    return useInfiniteQuery<SearchUserProfilesMapViewQuery, TError, TData>(
        ["searchUserProfilesMapView.infinite", variables],
        metaData =>
            graphqlFetcher<
                SearchUserProfilesMapViewQuery,
                SearchUserProfilesMapViewQueryVariables
            >(SearchUserProfilesMapViewDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetCalendarSubscriptionDocument = new TypedDocumentString(`
    query getCalendarSubscription {
  getCalendarSubscription {
    calendarUrl
  }
}
    `);

export const useGetCalendarSubscriptionQuery = <
    TData = GetCalendarSubscriptionQuery,
    TError = unknown,
>(
    variables?: GetCalendarSubscriptionQueryVariables,
    options?: UseQueryOptions<GetCalendarSubscriptionQuery, TError, TData>,
) => {
    return useQuery<GetCalendarSubscriptionQuery, TError, TData>(
        variables === undefined
            ? ["getCalendarSubscription"]
            : ["getCalendarSubscription", variables],
        graphqlFetcher<
            GetCalendarSubscriptionQuery,
            GetCalendarSubscriptionQueryVariables
        >(GetCalendarSubscriptionDocument, variables),
        options,
    );
};

export const useInfiniteGetCalendarSubscriptionQuery = <
    TData = GetCalendarSubscriptionQuery,
    TError = unknown,
>(
    variables?: GetCalendarSubscriptionQueryVariables,
    options?: UseInfiniteQueryOptions<GetCalendarSubscriptionQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetCalendarSubscriptionQuery, TError, TData>(
        variables === undefined
            ? ["getCalendarSubscription.infinite"]
            : ["getCalendarSubscription.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetCalendarSubscriptionQuery,
                GetCalendarSubscriptionQueryVariables
            >(GetCalendarSubscriptionDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetUserTenantRegionOptionsDocument = new TypedDocumentString(`
    query getUserTenantRegionOptions {
  getUserTenantRegionOptions {
    options {
      regionId
      regionName
      regionKey
      tenantId
      tenantName
      userId
    }
    preferredRegionId
  }
}
    `);

export const useGetUserTenantRegionOptionsQuery = <
    TData = GetUserTenantRegionOptionsQuery,
    TError = unknown,
>(
    variables?: GetUserTenantRegionOptionsQueryVariables,
    options?: UseQueryOptions<GetUserTenantRegionOptionsQuery, TError, TData>,
) => {
    return useQuery<GetUserTenantRegionOptionsQuery, TError, TData>(
        variables === undefined
            ? ["getUserTenantRegionOptions"]
            : ["getUserTenantRegionOptions", variables],
        graphqlFetcher<
            GetUserTenantRegionOptionsQuery,
            GetUserTenantRegionOptionsQueryVariables
        >(GetUserTenantRegionOptionsDocument, variables),
        options,
    );
};

export const useInfiniteGetUserTenantRegionOptionsQuery = <
    TData = GetUserTenantRegionOptionsQuery,
    TError = unknown,
>(
    variables?: GetUserTenantRegionOptionsQueryVariables,
    options?: UseInfiniteQueryOptions<GetUserTenantRegionOptionsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetUserTenantRegionOptionsQuery, TError, TData>(
        variables === undefined
            ? ["getUserTenantRegionOptions.infinite"]
            : ["getUserTenantRegionOptions.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetUserTenantRegionOptionsQuery,
                GetUserTenantRegionOptionsQueryVariables
            >(GetUserTenantRegionOptionsDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetUsersForRegionDocument = new TypedDocumentString(`
    query getUsersForRegion($regionId: RegionId!) {
  getUsersForRegion(regionId: $regionId) {
    id
    disabled
    hidden
    preferredName
    email
    type
  }
}
    `);

export const useGetUsersForRegionQuery = <
    TData = GetUsersForRegionQuery,
    TError = unknown,
>(
    variables: GetUsersForRegionQueryVariables,
    options?: UseQueryOptions<GetUsersForRegionQuery, TError, TData>,
) => {
    return useQuery<GetUsersForRegionQuery, TError, TData>(
        ["getUsersForRegion", variables],
        graphqlFetcher<GetUsersForRegionQuery, GetUsersForRegionQueryVariables>(
            GetUsersForRegionDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetUsersForRegionQuery = <
    TData = GetUsersForRegionQuery,
    TError = unknown,
>(
    variables: GetUsersForRegionQueryVariables,
    options?: UseInfiniteQueryOptions<GetUsersForRegionQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetUsersForRegionQuery, TError, TData>(
        ["getUsersForRegion.infinite", variables],
        metaData =>
            graphqlFetcher<GetUsersForRegionQuery, GetUsersForRegionQueryVariables>(
                GetUsersForRegionDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetVisibleUsersAcrossRegionsDocument = new TypedDocumentString(`
    query getVisibleUsersAcrossRegions {
  getVisibleUsersAcrossRegions {
    id
    disabled
    hidden
    preferredName
    email
  }
}
    `);

export const useGetVisibleUsersAcrossRegionsQuery = <
    TData = GetVisibleUsersAcrossRegionsQuery,
    TError = unknown,
>(
    variables?: GetVisibleUsersAcrossRegionsQueryVariables,
    options?: UseQueryOptions<GetVisibleUsersAcrossRegionsQuery, TError, TData>,
) => {
    return useQuery<GetVisibleUsersAcrossRegionsQuery, TError, TData>(
        variables === undefined
            ? ["getVisibleUsersAcrossRegions"]
            : ["getVisibleUsersAcrossRegions", variables],
        graphqlFetcher<
            GetVisibleUsersAcrossRegionsQuery,
            GetVisibleUsersAcrossRegionsQueryVariables
        >(GetVisibleUsersAcrossRegionsDocument, variables),
        options,
    );
};

export const useInfiniteGetVisibleUsersAcrossRegionsQuery = <
    TData = GetVisibleUsersAcrossRegionsQuery,
    TError = unknown,
>(
    variables?: GetVisibleUsersAcrossRegionsQueryVariables,
    options?: UseInfiniteQueryOptions<GetVisibleUsersAcrossRegionsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetVisibleUsersAcrossRegionsQuery, TError, TData>(
        variables === undefined
            ? ["getVisibleUsersAcrossRegions.infinite"]
            : ["getVisibleUsersAcrossRegions.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetVisibleUsersAcrossRegionsQuery,
                GetVisibleUsersAcrossRegionsQueryVariables
            >(GetVisibleUsersAcrossRegionsDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetWorkflowsForTenantDocument = new TypedDocumentString(`
    query getWorkflowsForTenant($tenantId: TenantId!) {
  getWorkflowsForTenant(tenantId: $tenantId) {
    ...WorkflowDefaults
  }
}
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}
fragment WorkflowDefaults on Workflow {
  actions {
    ... on WorkflowActionEmailEntity {
      __typename
      body
      subject
      to
      discreteTo
      attachments
      bccMyself
    }
    ... on WorkflowActionTaskEntity {
      __typename
      dueDate
      text
      assignedTo
      taskTypeId
      notifyOwner
      notification {
        subject
      }
      recurrencePattern
      recurrenceInterval
      recurrenceDaysOfWeek
      recurrenceDayOfMonth
    }
  }
  attachTo
  createdBy
  dateAdded
  delay
  delayRecipe
  description
  id
  name
  status
  defaultActorId
  actorMode
  settings {
    defaultValue
    description
    key
    label
    type
    scope
  }
  trigger {
    eventType
    entityUpdated {
      fieldMatchers {
        fieldName
        previous {
          values
          specialValue
        }
        current {
          values
          specialValue
        }
      }
    }
    noteAdded {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
    }
    emailOpened {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
      activityThresholdDays
    }
    pipelineStageChanged {
      pipelineId
      toStageIds
    }
    schedule {
      cron
      entityCondition {
        ...ConditionFragment
        children {
          ...ConditionFragment
          children {
            ...ConditionFragment
            children {
              ...ConditionFragment
              children {
                ...ConditionFragment
                children {
                  ...ConditionFragment
                  children {
                    ...ConditionFragment
                    children {
                      ...ConditionFragment
                      children {
                        ...ConditionFragment
                        children {
                          ...ConditionFragment
                          children {
                            ...ConditionFragment
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  updatedAt
  updatedBy
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}`);

export const useGetWorkflowsForTenantQuery = <
    TData = GetWorkflowsForTenantQuery,
    TError = unknown,
>(
    variables: GetWorkflowsForTenantQueryVariables,
    options?: UseQueryOptions<GetWorkflowsForTenantQuery, TError, TData>,
) => {
    return useQuery<GetWorkflowsForTenantQuery, TError, TData>(
        ["getWorkflowsForTenant", variables],
        graphqlFetcher<GetWorkflowsForTenantQuery, GetWorkflowsForTenantQueryVariables>(
            GetWorkflowsForTenantDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetWorkflowsForTenantQuery = <
    TData = GetWorkflowsForTenantQuery,
    TError = unknown,
>(
    variables: GetWorkflowsForTenantQueryVariables,
    options?: UseInfiniteQueryOptions<GetWorkflowsForTenantQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetWorkflowsForTenantQuery, TError, TData>(
        ["getWorkflowsForTenant.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetWorkflowsForTenantQuery,
                GetWorkflowsForTenantQueryVariables
            >(GetWorkflowsForTenantDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const GetWorkflowsDocument = new TypedDocumentString(`
    query getWorkflows {
  getWorkflows {
    ...WorkflowDefaults
  }
  getWorkflowSettings {
    user {
      key
      workflowId
      textValue
    }
    tenant {
      key
      workflowId
      textValue
    }
  }
}
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}
fragment WorkflowDefaults on Workflow {
  actions {
    ... on WorkflowActionEmailEntity {
      __typename
      body
      subject
      to
      discreteTo
      attachments
      bccMyself
    }
    ... on WorkflowActionTaskEntity {
      __typename
      dueDate
      text
      assignedTo
      taskTypeId
      notifyOwner
      notification {
        subject
      }
      recurrencePattern
      recurrenceInterval
      recurrenceDaysOfWeek
      recurrenceDayOfMonth
    }
  }
  attachTo
  createdBy
  dateAdded
  delay
  delayRecipe
  description
  id
  name
  status
  defaultActorId
  actorMode
  settings {
    defaultValue
    description
    key
    label
    type
    scope
  }
  trigger {
    eventType
    entityUpdated {
      fieldMatchers {
        fieldName
        previous {
          values
          specialValue
        }
        current {
          values
          specialValue
        }
      }
    }
    noteAdded {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
    }
    emailOpened {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
      activityThresholdDays
    }
    pipelineStageChanged {
      pipelineId
      toStageIds
    }
    schedule {
      cron
      entityCondition {
        ...ConditionFragment
        children {
          ...ConditionFragment
          children {
            ...ConditionFragment
            children {
              ...ConditionFragment
              children {
                ...ConditionFragment
                children {
                  ...ConditionFragment
                  children {
                    ...ConditionFragment
                    children {
                      ...ConditionFragment
                      children {
                        ...ConditionFragment
                        children {
                          ...ConditionFragment
                          children {
                            ...ConditionFragment
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  updatedAt
  updatedBy
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}`);

export const useGetWorkflowsQuery = <TData = GetWorkflowsQuery, TError = unknown>(
    variables?: GetWorkflowsQueryVariables,
    options?: UseQueryOptions<GetWorkflowsQuery, TError, TData>,
) => {
    return useQuery<GetWorkflowsQuery, TError, TData>(
        variables === undefined ? ["getWorkflows"] : ["getWorkflows", variables],
        graphqlFetcher<GetWorkflowsQuery, GetWorkflowsQueryVariables>(
            GetWorkflowsDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetWorkflowsQuery = <TData = GetWorkflowsQuery, TError = unknown>(
    variables?: GetWorkflowsQueryVariables,
    options?: UseInfiniteQueryOptions<GetWorkflowsQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetWorkflowsQuery, TError, TData>(
        variables === undefined
            ? ["getWorkflows.infinite"]
            : ["getWorkflows.infinite", variables],
        metaData =>
            graphqlFetcher<GetWorkflowsQuery, GetWorkflowsQueryVariables>(
                GetWorkflowsDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const GetWorkflowDocument = new TypedDocumentString(`
    query getWorkflow($tenantId: TenantId!, $workflowId: WorkflowId!) {
  getWorkflow(tenantId: $tenantId, workflowId: $workflowId) {
    ...WorkflowDefaults
  }
}
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}
fragment WorkflowDefaults on Workflow {
  actions {
    ... on WorkflowActionEmailEntity {
      __typename
      body
      subject
      to
      discreteTo
      attachments
      bccMyself
    }
    ... on WorkflowActionTaskEntity {
      __typename
      dueDate
      text
      assignedTo
      taskTypeId
      notifyOwner
      notification {
        subject
      }
      recurrencePattern
      recurrenceInterval
      recurrenceDaysOfWeek
      recurrenceDayOfMonth
    }
  }
  attachTo
  createdBy
  dateAdded
  delay
  delayRecipe
  description
  id
  name
  status
  defaultActorId
  actorMode
  settings {
    defaultValue
    description
    key
    label
    type
    scope
  }
  trigger {
    eventType
    entityUpdated {
      fieldMatchers {
        fieldName
        previous {
          values
          specialValue
        }
        current {
          values
          specialValue
        }
      }
    }
    noteAdded {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
    }
    emailOpened {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
      activityThresholdDays
    }
    pipelineStageChanged {
      pipelineId
      toStageIds
    }
    schedule {
      cron
      entityCondition {
        ...ConditionFragment
        children {
          ...ConditionFragment
          children {
            ...ConditionFragment
            children {
              ...ConditionFragment
              children {
                ...ConditionFragment
                children {
                  ...ConditionFragment
                  children {
                    ...ConditionFragment
                    children {
                      ...ConditionFragment
                      children {
                        ...ConditionFragment
                        children {
                          ...ConditionFragment
                          children {
                            ...ConditionFragment
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  updatedAt
  updatedBy
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}`);

export const useGetWorkflowQuery = <TData = GetWorkflowQuery, TError = unknown>(
    variables: GetWorkflowQueryVariables,
    options?: UseQueryOptions<GetWorkflowQuery, TError, TData>,
) => {
    return useQuery<GetWorkflowQuery, TError, TData>(
        ["getWorkflow", variables],
        graphqlFetcher<GetWorkflowQuery, GetWorkflowQueryVariables>(
            GetWorkflowDocument,
            variables,
        ),
        options,
    );
};

export const useInfiniteGetWorkflowQuery = <TData = GetWorkflowQuery, TError = unknown>(
    variables: GetWorkflowQueryVariables,
    options?: UseInfiniteQueryOptions<GetWorkflowQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetWorkflowQuery, TError, TData>(
        ["getWorkflow.infinite", variables],
        metaData =>
            graphqlFetcher<GetWorkflowQuery, GetWorkflowQueryVariables>(
                GetWorkflowDocument,
                { ...variables, ...(metaData.pageParam ?? {}) },
            )(),
        options,
    );
};

export const CreateWorkflowForTenantDocument = new TypedDocumentString(`
    mutation createWorkflowForTenant($tenantId: TenantId!, $workflow: WorkflowPayload!) {
  createWorkflowForTenant(tenantId: $tenantId, workflow: $workflow) {
    ...WorkflowDefaults
  }
}
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}
fragment WorkflowDefaults on Workflow {
  actions {
    ... on WorkflowActionEmailEntity {
      __typename
      body
      subject
      to
      discreteTo
      attachments
      bccMyself
    }
    ... on WorkflowActionTaskEntity {
      __typename
      dueDate
      text
      assignedTo
      taskTypeId
      notifyOwner
      notification {
        subject
      }
      recurrencePattern
      recurrenceInterval
      recurrenceDaysOfWeek
      recurrenceDayOfMonth
    }
  }
  attachTo
  createdBy
  dateAdded
  delay
  delayRecipe
  description
  id
  name
  status
  defaultActorId
  actorMode
  settings {
    defaultValue
    description
    key
    label
    type
    scope
  }
  trigger {
    eventType
    entityUpdated {
      fieldMatchers {
        fieldName
        previous {
          values
          specialValue
        }
        current {
          values
          specialValue
        }
      }
    }
    noteAdded {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
    }
    emailOpened {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
      activityThresholdDays
    }
    pipelineStageChanged {
      pipelineId
      toStageIds
    }
    schedule {
      cron
      entityCondition {
        ...ConditionFragment
        children {
          ...ConditionFragment
          children {
            ...ConditionFragment
            children {
              ...ConditionFragment
              children {
                ...ConditionFragment
                children {
                  ...ConditionFragment
                  children {
                    ...ConditionFragment
                    children {
                      ...ConditionFragment
                      children {
                        ...ConditionFragment
                        children {
                          ...ConditionFragment
                          children {
                            ...ConditionFragment
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  updatedAt
  updatedBy
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}`);

export const useCreateWorkflowForTenantMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateWorkflowForTenantMutation,
        TError,
        CreateWorkflowForTenantMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateWorkflowForTenantMutation,
        TError,
        CreateWorkflowForTenantMutationVariables,
        TContext
    >(
        ["createWorkflowForTenant"],
        (variables?: CreateWorkflowForTenantMutationVariables) =>
            graphqlFetcher<
                CreateWorkflowForTenantMutation,
                CreateWorkflowForTenantMutationVariables
            >(CreateWorkflowForTenantDocument, variables)(),
        options,
    );
};

export const CreateWorkflowDocument = new TypedDocumentString(`
    mutation createWorkflow($workflow: WorkflowPayload!) {
  createWorkflow(workflow: $workflow) {
    ...WorkflowDefaults
  }
}
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}
fragment WorkflowDefaults on Workflow {
  actions {
    ... on WorkflowActionEmailEntity {
      __typename
      body
      subject
      to
      discreteTo
      attachments
      bccMyself
    }
    ... on WorkflowActionTaskEntity {
      __typename
      dueDate
      text
      assignedTo
      taskTypeId
      notifyOwner
      notification {
        subject
      }
      recurrencePattern
      recurrenceInterval
      recurrenceDaysOfWeek
      recurrenceDayOfMonth
    }
  }
  attachTo
  createdBy
  dateAdded
  delay
  delayRecipe
  description
  id
  name
  status
  defaultActorId
  actorMode
  settings {
    defaultValue
    description
    key
    label
    type
    scope
  }
  trigger {
    eventType
    entityUpdated {
      fieldMatchers {
        fieldName
        previous {
          values
          specialValue
        }
        current {
          values
          specialValue
        }
      }
    }
    noteAdded {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
    }
    emailOpened {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
      activityThresholdDays
    }
    pipelineStageChanged {
      pipelineId
      toStageIds
    }
    schedule {
      cron
      entityCondition {
        ...ConditionFragment
        children {
          ...ConditionFragment
          children {
            ...ConditionFragment
            children {
              ...ConditionFragment
              children {
                ...ConditionFragment
                children {
                  ...ConditionFragment
                  children {
                    ...ConditionFragment
                    children {
                      ...ConditionFragment
                      children {
                        ...ConditionFragment
                        children {
                          ...ConditionFragment
                          children {
                            ...ConditionFragment
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  updatedAt
  updatedBy
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}`);

export const useCreateWorkflowMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CreateWorkflowMutation,
        TError,
        CreateWorkflowMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CreateWorkflowMutation,
        TError,
        CreateWorkflowMutationVariables,
        TContext
    >(
        ["createWorkflow"],
        (variables?: CreateWorkflowMutationVariables) =>
            graphqlFetcher<CreateWorkflowMutation, CreateWorkflowMutationVariables>(
                CreateWorkflowDocument,
                variables,
            )(),
        options,
    );
};

export const UpdateWorkflowForTenantDocument = new TypedDocumentString(`
    mutation updateWorkflowForTenant($tenantId: TenantId!, $workflowId: WorkflowId!, $workflow: WorkflowPayload!) {
  updateWorkflowForTenant(
    tenantId: $tenantId
    workflowId: $workflowId
    workflow: $workflow
  ) {
    ...WorkflowDefaults
  }
}
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}
fragment WorkflowDefaults on Workflow {
  actions {
    ... on WorkflowActionEmailEntity {
      __typename
      body
      subject
      to
      discreteTo
      attachments
      bccMyself
    }
    ... on WorkflowActionTaskEntity {
      __typename
      dueDate
      text
      assignedTo
      taskTypeId
      notifyOwner
      notification {
        subject
      }
      recurrencePattern
      recurrenceInterval
      recurrenceDaysOfWeek
      recurrenceDayOfMonth
    }
  }
  attachTo
  createdBy
  dateAdded
  delay
  delayRecipe
  description
  id
  name
  status
  defaultActorId
  actorMode
  settings {
    defaultValue
    description
    key
    label
    type
    scope
  }
  trigger {
    eventType
    entityUpdated {
      fieldMatchers {
        fieldName
        previous {
          values
          specialValue
        }
        current {
          values
          specialValue
        }
      }
    }
    noteAdded {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
    }
    emailOpened {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
      activityThresholdDays
    }
    pipelineStageChanged {
      pipelineId
      toStageIds
    }
    schedule {
      cron
      entityCondition {
        ...ConditionFragment
        children {
          ...ConditionFragment
          children {
            ...ConditionFragment
            children {
              ...ConditionFragment
              children {
                ...ConditionFragment
                children {
                  ...ConditionFragment
                  children {
                    ...ConditionFragment
                    children {
                      ...ConditionFragment
                      children {
                        ...ConditionFragment
                        children {
                          ...ConditionFragment
                          children {
                            ...ConditionFragment
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  updatedAt
  updatedBy
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}`);

export const useUpdateWorkflowForTenantMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateWorkflowForTenantMutation,
        TError,
        UpdateWorkflowForTenantMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateWorkflowForTenantMutation,
        TError,
        UpdateWorkflowForTenantMutationVariables,
        TContext
    >(
        ["updateWorkflowForTenant"],
        (variables?: UpdateWorkflowForTenantMutationVariables) =>
            graphqlFetcher<
                UpdateWorkflowForTenantMutation,
                UpdateWorkflowForTenantMutationVariables
            >(UpdateWorkflowForTenantDocument, variables)(),
        options,
    );
};

export const UpdateWorkflowDocument = new TypedDocumentString(`
    mutation updateWorkflow($workflowId: WorkflowId!, $workflow: WorkflowPayload!) {
  updateWorkflow(workflowId: $workflowId, workflow: $workflow) {
    ...WorkflowDefaults
  }
}
    fragment ConditionFragment on AdvancedSearchCondition {
  nodeType
  fieldName
  operator
  textValue
  dateValue
  calendarDateValue
  relativeDate {
    value
    interval
  }
  numberValue
  questionId
  promptId
}
fragment WorkflowDefaults on Workflow {
  actions {
    ... on WorkflowActionEmailEntity {
      __typename
      body
      subject
      to
      discreteTo
      attachments
      bccMyself
    }
    ... on WorkflowActionTaskEntity {
      __typename
      dueDate
      text
      assignedTo
      taskTypeId
      notifyOwner
      notification {
        subject
      }
      recurrencePattern
      recurrenceInterval
      recurrenceDaysOfWeek
      recurrenceDayOfMonth
    }
  }
  attachTo
  createdBy
  dateAdded
  delay
  delayRecipe
  description
  id
  name
  status
  defaultActorId
  actorMode
  settings {
    defaultValue
    description
    key
    label
    type
    scope
  }
  trigger {
    eventType
    entityUpdated {
      fieldMatchers {
        fieldName
        previous {
          values
          specialValue
        }
        current {
          values
          specialValue
        }
      }
    }
    noteAdded {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
    }
    emailOpened {
      entityFieldMatchers {
        fieldName
        current {
          values
          specialValue
        }
      }
      activityThresholdDays
    }
    pipelineStageChanged {
      pipelineId
      toStageIds
    }
    schedule {
      cron
      entityCondition {
        ...ConditionFragment
        children {
          ...ConditionFragment
          children {
            ...ConditionFragment
            children {
              ...ConditionFragment
              children {
                ...ConditionFragment
                children {
                  ...ConditionFragment
                  children {
                    ...ConditionFragment
                    children {
                      ...ConditionFragment
                      children {
                        ...ConditionFragment
                        children {
                          ...ConditionFragment
                          children {
                            ...ConditionFragment
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  updatedAt
  updatedBy
  regionVisibility {
    editable
    regions {
      mode
      regionId
    }
  }
}`);

export const useUpdateWorkflowMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        UpdateWorkflowMutation,
        TError,
        UpdateWorkflowMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        UpdateWorkflowMutation,
        TError,
        UpdateWorkflowMutationVariables,
        TContext
    >(
        ["updateWorkflow"],
        (variables?: UpdateWorkflowMutationVariables) =>
            graphqlFetcher<UpdateWorkflowMutation, UpdateWorkflowMutationVariables>(
                UpdateWorkflowDocument,
                variables,
            )(),
        options,
    );
};

export const DeleteWorkflowForTenantDocument = new TypedDocumentString(`
    mutation deleteWorkflowForTenant($tenantId: TenantId!, $workflowId: WorkflowId!) {
  deleteWorkflowForTenant(tenantId: $tenantId, workflowId: $workflowId)
}
    `);

export const useDeleteWorkflowForTenantMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteWorkflowForTenantMutation,
        TError,
        DeleteWorkflowForTenantMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteWorkflowForTenantMutation,
        TError,
        DeleteWorkflowForTenantMutationVariables,
        TContext
    >(
        ["deleteWorkflowForTenant"],
        (variables?: DeleteWorkflowForTenantMutationVariables) =>
            graphqlFetcher<
                DeleteWorkflowForTenantMutation,
                DeleteWorkflowForTenantMutationVariables
            >(DeleteWorkflowForTenantDocument, variables)(),
        options,
    );
};

export const DeleteWorkflowDocument = new TypedDocumentString(`
    mutation deleteWorkflow($workflowId: WorkflowId!) {
  deleteWorkflow(workflowId: $workflowId)
}
    `);

export const useDeleteWorkflowMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        DeleteWorkflowMutation,
        TError,
        DeleteWorkflowMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        DeleteWorkflowMutation,
        TError,
        DeleteWorkflowMutationVariables,
        TContext
    >(
        ["deleteWorkflow"],
        (variables?: DeleteWorkflowMutationVariables) =>
            graphqlFetcher<DeleteWorkflowMutation, DeleteWorkflowMutationVariables>(
                DeleteWorkflowDocument,
                variables,
            )(),
        options,
    );
};

export const GetWorkflowExecutionsForEntityDocument = new TypedDocumentString(`
    query getWorkflowExecutionsForEntity($workflowTargetId: WorkflowTargetId!) {
  getWorkflowExecutionsForEntity(workflowTargetId: $workflowTargetId) {
    id
    scheduledAt
    cancelledAt
    isCancelRequested
    finishedAt
    result
    workflow {
      name
      id
    }
  }
}
    `);

export const useGetWorkflowExecutionsForEntityQuery = <
    TData = GetWorkflowExecutionsForEntityQuery,
    TError = unknown,
>(
    variables: GetWorkflowExecutionsForEntityQueryVariables,
    options?: UseQueryOptions<GetWorkflowExecutionsForEntityQuery, TError, TData>,
) => {
    return useQuery<GetWorkflowExecutionsForEntityQuery, TError, TData>(
        ["getWorkflowExecutionsForEntity", variables],
        graphqlFetcher<
            GetWorkflowExecutionsForEntityQuery,
            GetWorkflowExecutionsForEntityQueryVariables
        >(GetWorkflowExecutionsForEntityDocument, variables),
        options,
    );
};

export const useInfiniteGetWorkflowExecutionsForEntityQuery = <
    TData = GetWorkflowExecutionsForEntityQuery,
    TError = unknown,
>(
    variables: GetWorkflowExecutionsForEntityQueryVariables,
    options?: UseInfiniteQueryOptions<GetWorkflowExecutionsForEntityQuery, TError, TData>,
) => {
    return useInfiniteQuery<GetWorkflowExecutionsForEntityQuery, TError, TData>(
        ["getWorkflowExecutionsForEntity.infinite", variables],
        metaData =>
            graphqlFetcher<
                GetWorkflowExecutionsForEntityQuery,
                GetWorkflowExecutionsForEntityQueryVariables
            >(GetWorkflowExecutionsForEntityDocument, {
                ...variables,
                ...(metaData.pageParam ?? {}),
            })(),
        options,
    );
};

export const CancelWorkflowExecutionDocument = new TypedDocumentString(`
    mutation cancelWorkflowExecution($workflowExecutionId: WorkflowExecutionId!) {
  cancelWorkflowExecution(workflowExecutionId: $workflowExecutionId) {
    id
  }
}
    `);

export const useCancelWorkflowExecutionMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        CancelWorkflowExecutionMutation,
        TError,
        CancelWorkflowExecutionMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        CancelWorkflowExecutionMutation,
        TError,
        CancelWorkflowExecutionMutationVariables,
        TContext
    >(
        ["cancelWorkflowExecution"],
        (variables?: CancelWorkflowExecutionMutationVariables) =>
            graphqlFetcher<
                CancelWorkflowExecutionMutation,
                CancelWorkflowExecutionMutationVariables
            >(CancelWorkflowExecutionDocument, variables)(),
        options,
    );
};

export const SetWorkflowSettingDocument = new TypedDocumentString(`
    mutation setWorkflowSetting($params: SetWorkflowSettingInput!) {
  setWorkflowSetting(params: $params)
}
    `);

export const useSetWorkflowSettingMutation = <TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<
        SetWorkflowSettingMutation,
        TError,
        SetWorkflowSettingMutationVariables,
        TContext
    >,
) => {
    return useMutation<
        SetWorkflowSettingMutation,
        TError,
        SetWorkflowSettingMutationVariables,
        TContext
    >(
        ["setWorkflowSetting"],
        (variables?: SetWorkflowSettingMutationVariables) =>
            graphqlFetcher<
                SetWorkflowSettingMutation,
                SetWorkflowSettingMutationVariables
            >(SetWorkflowSettingDocument, variables)(),
        options,
    );
};
